import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import api, {
  SubscriberDto, MilkTypeDto, DeliveryDto, BillDto, UserDto,
  DailyReportDto, UpdateProfileRequest, PauseDto, TenantDto, ClearTenantDataResult,
  MonthlyDeliveryReportDto, MonthlyPaymentReportDto, SubscriberWiseReportDto,
  EmailConfigDto, EmailConfigRequest, NotificationDto, SendNotificationRequest,
  TenantBrandingDto, UpdateBrandingRequest, BillDetailDto, SubscriptionChangeDto,
  WhatsAppConfigDto, WhatsAppConfigRequest, ReminderLogDto, YearlyReportDto,
  GstReportDto, TenantPerformanceReportDto, TenantBackupDto, TenantRestoreResult,
  DailyCashEntryDto, DailyExpenseDto, MonthlyCashSummaryDto, CategoryExpenseDto, TenantCashReportDto,
  YearlyCashSummaryDto, TenantYearlyCashReportDto,
  ExpenseCategoryDto, DashboardMetricsDto, UserSessionDto, DocumentDto,
  PurchaseEntryDto, SaleEntryDto, CreatePurchaseEntryRequest, CreateSaleEntryRequest,
  UpdatePurchaseEntryRequest, UpdateSaleEntryRequest, PurchaseSaleFilter,
  PurchaseSaleDashboard, PurchaseMonthlyReport, SaleMonthlyReport,
  TenantModuleMatrix, TenantEnabledModules, AppModuleDto,
  ProductTypeDto, CreateProductTypeRequest, UpdateProductTypeRequest,
  BulkCreateProductTypesResult, ProductTypeRateDto, EffectiveRateDto, ProductTypeBreakdownDto,
  RecordFileDto, RecordStoreTenantDto, BillIssueDto, DeliveryCorrectionLogDto,
  ConsolidatedBillDto, CashFlowDashboardDto, AddressMasterDto, BuildingMasterDto, FlatMasterDto,
  DeliveryLogDto, DeliveryLogSummaryDto, TenantRequestDto
} from '../services/milk-api';

// ===== Theme Color Utilities =====
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 0xFF, (n >> 8) & 0xFF, n & 0xFF];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
function lightenHex(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    Math.min(255, Math.round(r + (255 - r) * amount)),
    Math.min(255, Math.round(g + (255 - g) * amount)),
    Math.min(255, Math.round(b + (255 - b) * amount))
  );
}
function darkenHex(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    Math.max(0, Math.round(r * (1 - amount))),
    Math.max(0, Math.round(g * (1 - amount))),
    Math.max(0, Math.round(b * (1 - amount)))
  );
}

interface DashboardProps {
  user: { name: string; role: string; tenantId: number; userId: number; tenantName: string; mustChangePassword?: boolean; subscriptionWarningMessage?: string | null };
  onLogout: () => void;
}

type Tab = 'dashboard' | 'cashflowdashboard' | 'subscribers' | 'milktypes' | 'societymaster' | 'buildingmaster' | 'deliveries' | 'mydeliveries' | 'billing' | 'payments' | 'cashmanagement' | 'reports' | 'users' | 'deliveryboys' | 'tenants' | 'tenantrequests' | 'livesessions' | 'profile' | 'subscription' | 'mybills' | 'emailconfig' | 'notifications' | 'dairysetup' | 'documents' | 'aichat' | 'purchasesale' | 'moduleaccess' | 'producttypes' | 'menuorder' | 'recordstore' | 'usermanual' | 'deliverylogs';

export default function Dashboard({ user, onLogout }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<Tab>(user.role === 'Subscriber' ? 'profile' : user.role === 'SuperAdmin' ? 'reports' : user.role === 'DeliveryBoy' ? 'mydeliveries' : 'dashboard');
  const [unreadCount, setUnreadCount] = useState(0);
  const [enabledModuleKeys, setEnabledModuleKeys] = useState<string[]>([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [bellNotifs, setBellNotifs] = useState<NotificationDto[]>([]);
  const [branding, setBranding] = useState<TenantBrandingDto | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOrder, setMenuOrder] = useState<string[]>([]);
  const [showForcePwdModal, setShowForcePwdModal] = useState(user.mustChangePassword === true);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const scrollTabBar = (dir: 'left' | 'right') => {
    if (tabBarRef.current) tabBarRef.current.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const isAdmin = user.role === 'Admin' || user.role === 'SuperAdmin';
  const isSuperAdmin = user.role === 'SuperAdmin';
  const isSubscriber = user.role === 'Subscriber';
  const isDeliveryBoy = user.role === 'DeliveryBoy';
  const themeColor = branding?.themeColor || '#2E7D32';
  const themeColorDark = darkenHex(themeColor, 0.25);
  const tcBtn = lightenHex(themeColor, 0.08);
  const tcHover = darkenHex(themeColor, 0.1);
  const tcLight = lightenHex(themeColor, 0.88);
  const tcBorder = lightenHex(themeColor, 0.7);
  const tcVlight = lightenHex(themeColor, 0.93);

  // Set CSS variables on document root for global access (including standalone HTML)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--tc', themeColor);
    root.style.setProperty('--tc-dark', themeColorDark);
    root.style.setProperty('--tc-btn', tcBtn);
    root.style.setProperty('--tc-hover', tcHover);
    root.style.setProperty('--tc-light', tcLight);
    root.style.setProperty('--tc-border', tcBorder);
    root.style.setProperty('--tc-vlight', tcVlight);
  }, [themeColor, themeColorDark, tcBtn, tcHover, tcLight, tcBorder, tcVlight]);

  const loadBranding = useCallback(async () => {
    try {
      const res = await api.getBranding();
      if (res.data) setBranding(res.data);
    } catch { /* ignore */ }
  }, []);

  const parseMenuOrder = useCallback((value: string | null) => {
    if (!value) return [] as string[];
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [] as string[];
      return parsed
        .map((x: unknown) => {
          if (typeof x === 'string') return x;
          if (x && typeof x === 'object' && 'key' in (x as Record<string, unknown>)) {
            const key = (x as Record<string, unknown>).key;
            return typeof key === 'string' ? key : null;
          }
          return null;
        })
        .filter((x: string | null): x is string => Boolean(x));
    } catch {
      return [] as string[];
    }
  }, []);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await api.getUnreadNotificationCount();
      if (res.data !== null && res.data !== undefined) setUnreadCount(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadUnreadCount();
    loadBranding();
    if (!isSuperAdmin) {
      api.getEnabledModules().then(res => {
        if (res.data) setEnabledModuleKeys(res.data.enabledModuleKeys);
      }).catch(() => {});
    }
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount, loadBranding, isSuperAdmin]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const brandingOrder = parseMenuOrder(branding?.menuOrderJson ?? null);
    if (brandingOrder.length > 0) {
      setMenuOrder(brandingOrder);
      return;
    }

    const saved = parseMenuOrder(localStorage.getItem('menuOrder'));
    if (saved.length > 0) setMenuOrder(saved);
  }, [branding?.menuOrderJson, parseMenuOrder]);

  const handleBellClick = async () => {
    if (!showNotifDropdown) {
      const res = await api.getNotifications();
      if (res.data) setBellNotifs(res.data.slice(0, 10));
    }
    setShowNotifDropdown(!showNotifDropdown);
  };

  const handleMarkAllRead = async () => {
    await api.markAllNotificationsRead();
    setUnreadCount(0);
    setBellNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const isModuleEnabled = (moduleKey: string) => isSuperAdmin || enabledModuleKeys.includes(moduleKey);

  const tabs: { key: Tab; label: string; icon: string; adminOnly?: boolean; superAdminOnly?: boolean; subscriberOnly?: boolean; tenantAdminOnly?: boolean; deliveryBoyOnly?: boolean; hideForDeliveryBoy?: boolean; moduleKey?: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊', tenantAdminOnly: true },
    { key: 'cashflowdashboard', label: 'Cash Flow Dashboard', icon: '💹', tenantAdminOnly: true, moduleKey: 'CashFlowDashboard' },
    { key: 'profile', label: 'My Profile', icon: '👤' },
    { key: 'mydeliveries', label: 'My Deliveries', icon: '🚚', deliveryBoyOnly: true },
    { key: 'subscription', label: 'My Subscription', icon: '📋', subscriberOnly: true },
    { key: 'mybills', label: 'My Bills', icon: '🧾', subscriberOnly: true },
    { key: 'subscribers', label: 'Subscribers', icon: '👥', tenantAdminOnly: true },
    { key: 'milktypes', label: 'Milk Types', icon: '🥛', tenantAdminOnly: true },
    { key: 'societymaster', label: 'Society Master', icon: '🏘️', tenantAdminOnly: true },
    { key: 'buildingmaster', label: 'Building Master', icon: '🏢', tenantAdminOnly: true },
    { key: 'deliveries', label: 'Deliveries', icon: '🚚', tenantAdminOnly: true },
    { key: 'billing', label: 'Billing', icon: '🧾', tenantAdminOnly: true },
    { key: 'payments', label: 'Payments', icon: '💳', tenantAdminOnly: true },
    { key: 'cashmanagement', label: 'Cash Mgmt', icon: '🏪', adminOnly: true, moduleKey: 'CashManagement' },
    { key: 'reports', label: 'Reports', icon: '📊', adminOnly: true, moduleKey: 'Reports' },
    { key: 'emailconfig', label: 'Email Config', icon: '📧', tenantAdminOnly: true },
    { key: 'notifications', label: 'Notifications', icon: '📢', adminOnly: true },
    { key: 'dairysetup', label: 'Dairy Setup', icon: '🎨', adminOnly: true },
    { key: 'deliveryboys', label: 'Delivery Boys', icon: '🚚', tenantAdminOnly: true },
    { key: 'deliverylogs', label: 'Delivery Logs', icon: '📋', tenantAdminOnly: true },
    { key: 'auditlog', label: 'Audit Log', icon: '📝', adminOnly: true },
    { key: 'tenants', label: 'Tenants', icon: '🏢', superAdminOnly: true },
    { key: 'tenantrequests', label: 'Tenant Requests', icon: '📋', superAdminOnly: true },
    { key: 'livesessions', label: 'Live Sessions', icon: '🟢', adminOnly: true, moduleKey: 'LiveSession' },
    { key: 'documents', label: 'Documents', icon: '📄', superAdminOnly: true },
    { key: 'recordstore', label: 'Record Store', icon: '🗂️', adminOnly: true, moduleKey: 'RecordStore' },
    { key: 'usermanual', label: 'User Manual', icon: '📖', adminOnly: true, moduleKey: 'UserManual' },
    { key: 'aichat', label: 'AI Chat', icon: '🤖', moduleKey: 'AiChat', hideForDeliveryBoy: true },
    { key: 'purchasesale', label: 'Purchase/Sale', icon: '🛒', tenantAdminOnly: true, moduleKey: 'PurchaseSale' },
    { key: 'producttypes', label: 'Product Types', icon: '📦', tenantAdminOnly: true, moduleKey: 'PurchaseSale' },
    { key: 'moduleaccess', label: 'Module Access', icon: '🔒', superAdminOnly: true },
    { key: 'menuorder', label: 'Menu Order', icon: '📋', superAdminOnly: true },
  ];

  return (
    <div style={{
      minHeight: '100vh', background: tcVlight,
      '--tc': themeColor, '--tc-dark': themeColorDark, '--tc-btn': tcBtn,
      '--tc-hover': tcHover, '--tc-light': tcLight, '--tc-border': tcBorder, '--tc-vlight': tcVlight,
    } as React.CSSProperties}>
      {showForcePwdModal && <ForcePasswordChangeModal onDone={() => setShowForcePwdModal(false)} />}
      {/* Subscription Warning Banner */}
      {user.subscriptionWarningMessage && (
        <div style={{
          background: 'linear-gradient(90deg, #FF6F00 0%, #FF8F00 100%)',
          color: 'white', padding: '0.75rem 1rem', textAlign: 'center',
          fontWeight: 600, fontSize: '0.95rem', letterSpacing: '0.3px',
          boxShadow: '0 2px 8px rgba(255,111,0,0.3)',
        }}>
          {user.subscriptionWarningMessage}
        </div>
      )}
      {/* Header */}
      <header className="app-header" style={{
        background: `linear-gradient(90deg, ${themeColorDark} 0%, ${themeColor} 50%, ${themeColor} 100%)`,
        color: 'white', padding: '0.75rem 1rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {branding?.logoBase64 ? (
            <img src={branding.logoBase64} alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
          ) : (
            <span style={{
              width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem',
            }}>🥛</span>
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, letterSpacing: '-0.3px' }}>{user.tenantName || 'Milk Subscription'}</h1>
            <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{branding?.mainWording || 'Fresh dairy management'}</span>
          </div>
        </div>
        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Notification Bell */}
          <div style={{ position: 'relative' }}>
            <button onClick={handleBellClick} style={{
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '50%', width: '38px', height: '38px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
              position: 'relative', transition: 'all 0.2s',
            }}
              onMouseEnter={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.15)'}
            >
              🔔
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  background: '#F44336', color: 'white', borderRadius: '50%',
                  width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 700, border: '2px solid var(--tc-dark)',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
            {showNotifDropdown && (
              <div className="notif-dropdown" style={{
                position: 'fixed', top: '60px', right: '8px', width: 'min(340px, calc(100vw - 16px))',
                background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                zIndex: 2000, maxHeight: '80vh', overflow: 'hidden',
              }}>
                <div style={{
                  padding: '0.75rem 1rem', borderBottom: '1px solid #E0E0E0',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <strong style={{ color: 'var(--tc)', fontSize: '0.95rem' }}>Notifications</strong>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} style={{
                      background: 'none', border: 'none', color: 'var(--tc-btn)', cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 600,
                    }}>Mark all read</button>
                  )}
                </div>
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  {bellNotifs.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>No notifications</div>
                  ) : bellNotifs.map(n => (
                    <div key={n.notificationId} onClick={async () => {
                      if (!n.isRead) {
                        await api.markNotificationRead(n.notificationId);
                        setBellNotifs(prev => prev.map(x => x.notificationId === n.notificationId ? { ...x, isRead: true } : x));
                        setUnreadCount(prev => Math.max(0, prev - 1));
                      }
                    }} style={{
                      padding: '0.75rem 1rem', borderBottom: '1px solid #F5F5F5', cursor: 'pointer',
                      background: n.isRead ? 'white' : 'var(--tc-vlight)', transition: 'background 0.2s',
                    }}>
                      <div style={{ fontWeight: n.isRead ? 400 : 700, color: '#333', fontSize: '0.88rem' }}>{n.title}</div>
                      <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '2px' }}>{n.message.length > 80 ? n.message.slice(0, 80) + '...' : n.message}</div>
                      <div style={{ color: '#999', fontSize: '0.72rem', marginTop: '4px' }}>{fmtDate(n.createdDate)} • {n.sentByName}</div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '0.5rem', borderTop: '1px solid #E0E0E0', textAlign: 'center' }}>
                  <button onClick={() => { setActiveTab('notifications' as Tab); setShowNotifDropdown(false); }} style={{
                    background: 'none', border: 'none', color: 'var(--tc-btn)', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: 600,
                  }}>View All Notifications</button>
                </div>
              </div>
            )}
          </div>
          <div className="header-user-info" style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{user.name}</div>
            <div style={{
              fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)',
              padding: '1px 8px', borderRadius: '10px', display: 'inline-block',
            }}>{user.role}</div>
          </div>
          <button onClick={onLogout} style={{
            padding: '0.45rem 1rem', background: 'rgba(255,255,255,0.15)', color: 'white',
            border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 500, transition: 'all 0.2s',
          }}
            onMouseEnter={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.15)'}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Banner Image */}
      {branding?.bannerBase64 && (
        <div style={{ width: '100%', maxHeight: '150px', overflow: 'hidden', lineHeight: 0 }}>
          <img src={branding.bannerBase64} alt="Banner" style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      {/* Floating Sidebar Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.35)', zIndex: 2500, transition: 'opacity 0.3s',
          }}
        />
      )}

      {/* Floating Left Sidebar */}
      <nav style={{
        position: 'fixed', top: 0, left: sidebarOpen ? 0 : -280,
        width: 260, height: '100vh', background: 'white', zIndex: 2600,
        boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.18)' : 'none',
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Sidebar Header */}
        <div style={{
          background: `linear-gradient(135deg, ${themeColorDark} 0%, ${themeColor} 100%)`,
          padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
          borderBottom: `2px solid ${themeColorDark}`,
        }}>
          {branding?.logoBase64 ? (
            <img src={branding.logoBase64} alt="Logo" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
          ) : (
            <span style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🥛</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.tenantName || 'Milk Subscription'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>{user.name} • {user.role}</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
            borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
          }}>✕</button>
        </div>

        {/* Sidebar Menu Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
          {tabs.filter(t => {
            if (t.superAdminOnly && !isSuperAdmin) return false;
            if (t.tenantAdminOnly && !(isAdmin && !isSuperAdmin)) return false;
            if (t.adminOnly && !isAdmin) return false;
            if (t.subscriberOnly && !isSubscriber) return false;
            if (t.deliveryBoyOnly && !isDeliveryBoy) return false;
            if (t.hideForDeliveryBoy && isDeliveryBoy) return false;
            if (t.moduleKey && !isModuleEnabled(t.moduleKey)) return false;
            return true;
          }).sort((a, b) => {
            if (menuOrder.length === 0) return 0;
            const aIndex = menuOrder.indexOf(a.key);
            const bIndex = menuOrder.indexOf(b.key);
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
          }).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSidebarOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.75rem 1.25rem', border: 'none', cursor: 'pointer',
                background: activeTab === tab.key ? tcLight : 'transparent',
                color: activeTab === tab.key ? themeColor : '#555',
                fontWeight: activeTab === tab.key ? 700 : 500,
                fontSize: '0.88rem', textAlign: 'left',
                borderLeft: activeTab === tab.key ? `4px solid ${themeColor}` : '4px solid transparent',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { if (activeTab !== tab.key) { (e.currentTarget as HTMLElement).style.background = tcVlight; (e.currentTarget as HTMLElement).style.color = themeColor; } }}
              onMouseLeave={e => { if (activeTab !== tab.key) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#555'; } }}
            >
              <span style={{ fontSize: '1.1rem', width: '24px', textAlign: 'center' }}>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Sidebar Footer */}
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #E0E0E0', background: '#FAFAFA' }}>
          <button onClick={() => { setSidebarOpen(false); onLogout(); }} style={{
            width: '100%', padding: '0.6rem', border: '1px solid #E0E0E0',
            borderRadius: '8px', background: '#FFF', color: '#C62828', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#FFEBEE'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#FFF'; }}
          >
            🚪 Logout
          </button>
        </div>
      </nav>

      {/* Top Tab Bar - Compact with hamburger */}
      <div style={{
        background: 'white', borderBottom: '2px solid var(--tc-light)',
        display: 'flex', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button onClick={() => setSidebarOpen(true)} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.75rem 1rem',
          fontSize: '1.3rem', color: themeColor, display: 'flex', alignItems: 'center',
          flexShrink: 0,
        }} title="Open menu">
          ☰
        </button>
        <button onClick={() => scrollTabBar('left')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.4rem',
          fontSize: '1rem', color: '#888', flexShrink: 0, lineHeight: 1,
        }} title="Scroll left">◀</button>
        <div ref={tabBarRef} style={{
          display: 'flex', gap: '0', flex: 1, overflowX: 'auto',
          WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
        }}>
          {tabs.filter(t => {
            if (t.superAdminOnly && !isSuperAdmin) return false;
            if (t.tenantAdminOnly && !(isAdmin && !isSuperAdmin)) return false;
            if (t.adminOnly && !isAdmin) return false;
            if (t.subscriberOnly && !isSubscriber) return false;
            if (t.deliveryBoyOnly && !isDeliveryBoy) return false;
            if (t.hideForDeliveryBoy && isDeliveryBoy) return false;
            if (t.moduleKey && !isModuleEnabled(t.moduleKey)) return false;
            return true;
          }).sort((a, b) => {
            if (menuOrder.length === 0) return 0;
            const aIndex = menuOrder.indexOf(a.key);
            const bIndex = menuOrder.indexOf(b.key);
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
          }).map(tab => (
            <button
              className="app-nav-btn"
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.75rem 0.7rem', border: 'none', borderRadius: '0',
                background: activeTab === tab.key ? 'var(--tc-light)' : 'transparent',
                color: activeTab === tab.key ? themeColor : '#666',
                cursor: 'pointer', fontSize: '0.8rem', fontWeight: activeTab === tab.key ? 700 : 500,
                borderBottom: activeTab === tab.key ? `3px solid ${themeColor}` : '3px solid transparent',
                transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              <span style={{ marginRight: '0.2rem' }}>{tab.icon}</span>{tab.label}
            </button>
          ))}
        </div>
        <button onClick={() => scrollTabBar('right')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.4rem',
          fontSize: '1rem', color: '#888', flexShrink: 0, lineHeight: 1,
        }} title="Scroll right">▶</button>
      </div>

      {/* Content */}
      <main className="app-main" style={{ padding: '1.5rem 1rem', maxWidth: '1300px', margin: '0 auto' }}>
        {activeTab === 'dashboard' && <DashboardHomePanel showPurchaseSale={isModuleEnabled('PurchaseSale')} showCashManagement={isModuleEnabled('CashManagement')} />}
        {activeTab === 'cashflowdashboard' && isModuleEnabled('CashFlowDashboard') && <CashFlowDashboardPanel />}
        {activeTab === 'profile' && <ProfilePanel userId={user.userId} />}
        {activeTab === 'mydeliveries' && <MyDeliveriesPanel />}
        {activeTab === 'subscription' && <MySubscriptionPanel />}
        {activeTab === 'mybills' && <BillingPanel isAdmin={false} />}
        {activeTab === 'subscribers' && (
          <ErrorBoundary
            fallback={(error, reset) => (
              <div style={{ ...formStyle, background: '#FFF3E0', borderColor: '#FFCC80' }}>
                <h3 style={{ margin: 0, color: '#E65100' }}>Subscribers tab could not load</h3>
                <div style={{ color: '#6D4C41', fontSize: '0.9rem' }}>{error.message}</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={reset} style={{ ...btnStyle, background: '#E65100' }}>Try Again</button>
                  <button onClick={() => window.location.reload()} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Reload App</button>
                </div>
              </div>
            )}
          >
            <SubscribersPanel isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} tenantName={user.tenantName} />
          </ErrorBoundary>
        )}
        {activeTab === 'milktypes' && <MilkTypesPanel />}
        {activeTab === 'societymaster' && <SocietyMasterPanel />}
        {activeTab === 'buildingmaster' && <BuildingMasterPanel />}
        {activeTab === 'deliveries' && <DeliveriesPanel isAdmin={isAdmin} />}
        {activeTab === 'billing' && <BillingPanel isAdmin={isAdmin} />}
        {activeTab === 'payments' && <PaymentsPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'cashmanagement' && isModuleEnabled('CashManagement') && <CashManagementPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'reports' && <ReportsPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'emailconfig' && <EmailConfigPanel />}
        {activeTab === 'notifications' && <NotificationsPanel isSuperAdmin={isSuperAdmin} isSubscriber={isSubscriber} onCountChange={loadUnreadCount} />}
        {activeTab === 'dairysetup' && <DairySetupPanel onBrandingChange={loadBranding} isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'users' && <UsersPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'deliveryboys' && <DeliveryBoysPanel />}
        {activeTab === 'deliverylogs' && <DeliveryLogsPanel />}
        {activeTab === 'auditlog' && <AuditLogPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'tenants' && <TenantsPanel />}
        {activeTab === 'tenantrequests' && <TenantRequestsPanel />}
        {activeTab === 'livesessions' && <LiveSessionsPanel isSuperAdmin={isSuperAdmin} />}
        {activeTab === 'documents' && <DocumentsPanel />}
        {activeTab === 'recordstore' && isModuleEnabled('RecordStore') && <RecordStorePanel isSuperAdmin={isSuperAdmin} tenantId={user.tenantId} />}
        {activeTab === 'usermanual' && isModuleEnabled('UserManual') && <UserManualPanel role={user.role} />}
        {activeTab === 'aichat' && isModuleEnabled('AiChat') && <ChatPanel />}
        {activeTab === 'purchasesale' && isModuleEnabled('PurchaseSale') && <PurchaseSalePanel isSuperAdmin={isSuperAdmin} tenantId={user.tenantId} />}
        {activeTab === 'producttypes' && <ProductTypesPanel />}
        {activeTab === 'moduleaccess' && isSuperAdmin && <ModuleAccessPanel />}
        {activeTab === 'menuorder' && isSuperAdmin && <MenuOrderPanel onMenuOrderChange={loadBranding} />}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        {isSubscriber && (
          <>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'subscription' ? 'active' : ''}`} onClick={() => setActiveTab('subscription')}>
              <span className="nav-icon">📋</span>
              <span>Subscription</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'mybills' ? 'active' : ''}`} onClick={() => setActiveTab('mybills')}>
              <span className="nav-icon">🧾</span>
              <span>Bills</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <span className="nav-icon">👤</span>
              <span>Profile</span>
            </button>
          </>
        )}
        {isDeliveryBoy && (
          <>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'mydeliveries' ? 'active' : ''}`} onClick={() => setActiveTab('mydeliveries')}>
              <span className="nav-icon">🚚</span>
              <span>Deliveries</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <span className="nav-icon">👤</span>
              <span>Profile</span>
            </button>
          </>
        )}
        {isAdmin && (
          <>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
              <span className="nav-icon">📊</span>
              <span>Home</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'subscribers' ? 'active' : ''}`} onClick={() => setActiveTab('subscribers')}>
              <span className="nav-icon">👥</span>
              <span>Subscribers</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'deliveries' ? 'active' : ''}`} onClick={() => setActiveTab('deliveries')}>
              <span className="nav-icon">🚚</span>
              <span>Deliveries</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}>
              <span className="nav-icon">🧾</span>
              <span>Billing</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')}>
              <span className="nav-icon">💳</span>
              <span>Payments</span>
            </button>
          </>
        )}
        {isSuperAdmin && (
          <>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'tenants' ? 'active' : ''}`} onClick={() => setActiveTab('tenants')}>
              <span className="nav-icon">🏢</span>
              <span>Tenants</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'tenantrequests' ? 'active' : ''}`} onClick={() => setActiveTab('tenantrequests')}>
              <span className="nav-icon">📋</span>
              <span>Requests</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
              <span className="nav-icon">📊</span>
              <span>Reports</span>
            </button>
            <button className={`mobile-bottom-nav-btn ${activeTab === 'moduleaccess' ? 'active' : ''}`} onClick={() => setActiveTab('moduleaccess')}>
              <span className="nav-icon">🔒</span>
              <span>Modules</span>
            </button>
          </>
        )}
        {/* Hamburger menu button for all other tabs */}
        <button className="mobile-bottom-nav-btn" onClick={() => setSidebarOpen(true)}>
          <span className="nav-icon">☰</span>
          <span>More</span>
        </button>
      </nav>

      {/* Footer */}
      <footer style={{
        textAlign: 'center', padding: '0.6rem 1rem',
        borderTop: '1px solid var(--tc-border)', background: '#F9FBE7',
        color: '#777', fontSize: '0.78rem',
      }}>
        🕐 {currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
        {' '}|{' '}
        {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </footer>
    </div>
  );
}

// ===== Subscribers Panel =====
function SubscribersPanel({ isAdmin, isSuperAdmin = false, tenantName = 'Milk Subscription' }: { isAdmin: boolean; isSuperAdmin?: boolean; tenantName?: string }) {
  const [subscribers, setSubscribers] = useState<SubscriberDto[]>([]);
  const [milkTypes, setMilkTypes] = useState<MilkTypeDto[]>([]);
  const [addressMasters, setAddressMasters] = useState<AddressMasterDto[]>([]);
  const [buildingMasters, setBuildingMasters] = useState<BuildingMasterDto[]>([]);
  const [flatMasters, setFlatMasters] = useState<FlatMasterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [tenantFilter, setTenantFilter] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    societyName: '', buildingName: '', flatName: '', areaName: '', city: '',
    milkTypeId: 0, dailyQuantity: 1, subscriptionStartDate: '',
  });
  const [pauseForm, setPauseForm] = useState<{ id: number; name: string; startDate: string; endDate: string } | null>(null);
  const [suspendForm, setSuspendForm] = useState<{ id: number; name: string; reason: string } | null>(null);
  const [mobileMoreAction, setMobileMoreAction] = useState<number | null>(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [calendarSub, setCalendarSub] = useState<SubscriberDto | null>(null);
  const [calendarPauses, setCalendarPauses] = useState<PauseDto[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [resetPwdSubId, setResetPwdSubId] = useState<number | null>(null);
  const [resetPwdSubName, setResetPwdSubName] = useState('');
  const [subNewPassword, setSubNewPassword] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extraMilkForm, setExtraMilkForm] = useState<{ subscriberId: number; subscriberName: string; date: string; quantity: number; milkTypeId: number } | null>(null);
  const [editForm, setEditForm] = useState<{
    subscriberId: number;
    email: string;
    phone: string;
    societyName: string;
    buildingName: string;
    flatName: string;
    areaName: string;
    city: string;
    isActive: boolean;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ subscriberId: number; name: string } | null>(null);
  const [sendingWhatsAppLoginId, setSendingWhatsAppLoginId] = useState<number | null>(null);
  const [whatsAppLoginPrompt, setWhatsAppLoginPrompt] = useState<{ sub: SubscriberDto; phone: string } | null>(null);
  const [credentialsPrompt, setCredentialsPrompt] = useState<SubscriberDto | null>(null);
  const [branding, setBranding] = useState<TenantBrandingDto | null>(null);

  // ── Common grid controls: status filter, date range, sorting, pagination, saved prefs ──
  type SubscriberStatusFilter = 'all' | 'active' | 'inactive' | 'paused' | 'suspended';
  type SubscriberSortKey = 'name' | 'startDate' | 'dailyQuantity';
  const GRID_PREFS_KEY = 'gridPrefs:subscribers';
  const [statusFilter, setStatusFilter] = useState<SubscriberStatusFilter>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState<SubscriberSortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Restore saved filter preferences once on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GRID_PREFS_KEY) || 'null');
      if (saved) {
        if (saved.statusFilter) setStatusFilter(saved.statusFilter);
        if (saved.sortKey) setSortKey(saved.sortKey);
        if (saved.sortDir) setSortDir(saved.sortDir);
        if (saved.pageSize) setPageSize(saved.pageSize);
      }
    } catch { /* ignore malformed prefs */ }
  }, []);

  // Persist filter preferences whenever they change
  useEffect(() => {
    localStorage.setItem(GRID_PREFS_KEY, JSON.stringify({ statusFilter, sortKey, sortDir, pageSize }));
  }, [statusFilter, sortKey, sortDir, pageSize]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1); }, [search, tenantFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (mobileMoreAction === null) return;
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.mc-more-wrapper')) setMobileMoreAction(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [mobileMoreAction]);

  const toggleSort = (key: SubscriberSortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, mtRes, addrRes, buildRes, flatRes] = await Promise.all([
        api.getSubscribers(),
        api.getMilkTypes(),
        api.getAddressMasters(),
        api.getBuildingMasters(),
        api.getFlatMasters(),
      ]);
      if (subRes.data) setSubscribers(subRes.data);
      if (mtRes.data) setMilkTypes(mtRes.data);
      if (addrRes.data) setAddressMasters(addrRes.data);
      if (buildRes.data) setBuildingMasters(buildRes.data);
      if (flatRes.data) setFlatMasters(flatRes.data);
      if (!subRes.success || !mtRes.success || !addrRes.success) {
        setMsg(subRes.message || mtRes.message || addrRes.message || 'Could not load subscribers data');
      }
    } catch {
      setMsg('Could not load subscribers. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.getBranding().then(res => { if (res.data) setBranding(res.data); });
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      api.getTenants().then(res => { if (res.data) setTenants(res.data); });
    }
  }, [isSuperAdmin]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = { ...form };
    if (!payload.societyName) delete payload.societyName;
    if (!payload.buildingName) delete payload.buildingName;
    if (!payload.flatName) delete payload.flatName;
    if (!payload.areaName) delete payload.areaName;
    if (!payload.city) delete payload.city;
    if (!payload.subscriptionStartDate) delete payload.subscriptionStartDate;
    const res = await api.createSubscriber(payload);
    setMsg(res.message);
    if (res.success) {
      setShowForm(false);
      setForm({
        name: '', email: '', password: '', phone: '',
        societyName: '', buildingName: '', flatName: '', areaName: '', city: '',
        milkTypeId: 0, dailyQuantity: 1, subscriptionStartDate: '',
      });
      load();
    }
  };

  const handlePause = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pauseForm) return;
    const res = await api.pauseSubscriber(pauseForm.id, { startDate: pauseForm.startDate, endDate: pauseForm.endDate });
    setMsg(res.message);
    if (res.success) { setPauseForm(null); load(); }
  };

  const handleResume = async (id: number) => {
    const res = await api.resumeSubscriber(id);
    setMsg(res.message);
    load();
  };

  const handleActivate = async (id: number) => {
    const res = await api.activateSubscriber(id);
    setMsg(res.message);
    load();
  };

  const handleSuspendSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suspendForm) return;
    const res = await api.suspendSubscriber(suspendForm.id, suspendForm.reason || undefined);
    setMsg(res.message);
    if (res.success) { setSuspendForm(null); load(); }
  };

  const handleReactivateSubscriber = async (id: number) => {
    const res = await api.reactivateSubscriber(id);
    setMsg(res.message);
    if (res.success) load();
  };

  const handleDeletePause = async (subscriberId: number, pauseId: number) => {
    const res = await api.deleteSubscriberPause(subscriberId, pauseId);
    if (res.success) {
      setCalendarPauses(prev => prev.filter(p => p.pauseId !== pauseId));
      load();
    } else {
      setMsg(res.message || 'Failed to delete pause');
    }
  };

  const handleUnpauseDay = async (subscriberId: number, day: number) => {
    const fmt = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    const d = new Date(calendarMonth.year, calendarMonth.month, day);
    d.setHours(0, 0, 0, 0);
    const pause = calendarPauses.find(p => {
      const s = new Date(p.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(p.endDate);   e.setHours(0, 0, 0, 0);
      return d >= s && d <= e;
    });
    if (!pause) return;
    const pauseStart = new Date(pause.startDate); pauseStart.setHours(0, 0, 0, 0);
    const pauseEnd   = new Date(pause.endDate);   pauseEnd.setHours(0, 0, 0, 0);
    const dayBefore  = new Date(d); dayBefore.setDate(dayBefore.getDate() - 1);
    const dayAfter   = new Date(d); dayAfter.setDate(dayAfter.getDate() + 1);
    await api.deleteSubscriberPause(subscriberId, pause.pauseId);
    if (pauseStart < d)  await api.pauseSubscriber(subscriberId, { startDate: fmt(pauseStart), endDate: fmt(dayBefore) });
    if (pauseEnd   > d)  await api.pauseSubscriber(subscriberId, { startDate: fmt(dayAfter),   endDate: fmt(pauseEnd)   });
    const updated = await api.getSubscriberPauses(subscriberId);
    if (updated.success) setCalendarPauses(updated.data ?? []);
    load();
  };

  const handleResetSubPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPwdSubId || subNewPassword.length < 6) { setMsg('Password must be at least 6 characters'); return; }
    const res = await api.resetSubscriberPassword(resetPwdSubId, subNewPassword);
    setMsg(res.message);
    if (res.success) { setResetPwdSubId(null); setSubNewPassword(''); }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { setMsg('Please select a CSV file'); return; }
    setUploading(true);
    try {
      const res = await api.bulkUploadSubscribers(uploadFile);
      setMsg(res.message + (res.data?.errors?.length ? ' | Errors: ' + res.data.errors.join('; ') : ''));
      if (res.success) { setUploadFile(null); load(); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await api.downloadSampleCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscriber-upload-sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Download sample failed'); }
  };

  const handleExtraMilk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!extraMilkForm) return;
    const res = await api.addExtraDelivery({
      subscriberId: extraMilkForm.subscriberId,
      date: extraMilkForm.date,
      quantity: extraMilkForm.quantity,
      milkTypeId: extraMilkForm.milkTypeId || undefined,
    });
    setMsg(res.message);
    if (res.success) setExtraMilkForm(null);
  };

  const handleEditSubscriber = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;
    const res = await api.updateSubscriber(editForm.subscriberId, {
      email: editForm.email || undefined,
      phone: editForm.phone,
      societyName: editForm.societyName || undefined,
      buildingName: editForm.buildingName || undefined,
      flatName: editForm.flatName || undefined,
      areaName: editForm.areaName || undefined,
      city: editForm.city || undefined,
      isActive: editForm.isActive,
    });
    setMsg(res.message);
    if (res.success) { setEditForm(null); load(); }
  };

  const handleDeleteSubscriber = async () => {
    if (!deleteConfirm) return;
    const res = await api.deleteSubscriber(deleteConfirm.subscriberId);
    setMsg(res.message);
    if (res.success) { setDeleteConfirm(null); load(); }
  };

  const handleSendWhatsAppLogin = async (sub: SubscriberDto) => {
    const phone = sub.userPhone?.replace(/[^0-9]/g, '');
    if (!phone) {
      setMsg(`No phone number found for ${sub.userName}`);
      return;
    }
    setWhatsAppLoginPrompt({ sub, phone });
  };

  const handleSendWhatsAppLoginConfirm = async (pwd: string) => {
    if (!whatsAppLoginPrompt) return;
    const { sub, phone } = whatsAppLoginPrompt;
    const siteUrl = branding?.siteUrl || window.location.origin;
    const message = [
      `Dear ${sub.userName}`,
      '',
      `Your login details for ${tenantName}:`,
      '',
      `Email: ${sub.userEmail}`,
      `Password: ${pwd || '(not provided)'}`,
      '',
      `Login: ${siteUrl}`,
      '',
      tenantName,
    ].join('\n');
    setSendingWhatsAppLoginId(sub.subscriberId);
    try {
      const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
      window.location.href = url;
      setMsg(`Opened WhatsApp for ${sub.userName}`);
    } catch {
      setMsg('Failed to open WhatsApp. Please try again.');
    } finally {
      setSendingWhatsAppLoginId(null);
      setWhatsAppLoginPrompt(null);
    }
  };

  const handleDownloadCredentialsConfirm = (pwd: string) => {
    if (!credentialsPrompt) return;
    const s = credentialsPrompt;
    const lines = [
      '================================',
      '   DAIRY SUBSCRIBER CREDENTIALS ',
      '================================',
      '',
      `Website  : ${window.location.origin}`,
      `User ID  : ${s.userEmail}`,
      `Password : ${pwd || '(not provided)'}`,
      '',
      '--- Subscriber Details ---------',
      `Name     : ${s.userName}`,
      `Phone    : ${s.userPhone || '-'}`,
      `Address  : ${formatStructuredAddress({
        societyName: s.societyName,
        buildingName: s.buildingName,
        flatName: s.flatName,
        areaName: s.areaName,
        city: s.city,
        fallbackAddress: s.userAddress,
      })}`,
      `Milk     : ${s.milkTypeName || '-'} — ${s.dailyQuantity} L/day`,
      '================================',
    ].join('\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${s.userName.replace(/\s+/g, '_')}_credentials.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setCredentialsPrompt(null);
  };

  const openCalendar = async (sub: SubscriberDto) => {
    setCalendarSub(sub);
    const res = await api.getSubscriberPauses(sub.subscriberId);
    if (res.data) setCalendarPauses(res.data);
  };

  const filteredSubscribers = subscribers.filter(s => {
    const matchesSearch = (s.userName || '').toLowerCase().includes(search.toLowerCase());
    const matchesTenant = !isSuperAdmin || tenantFilter === null || s.tenantId === tenantFilter;
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'active' && s.status !== 'Suspended' && s.isActive && !s.isPaused)
      || (statusFilter === 'inactive' && !s.isActive && s.status !== 'Suspended')
      || (statusFilter === 'paused' && s.isPaused)
      || (statusFilter === 'suspended' && s.status === 'Suspended');
    const matchesDateFrom = !dateFrom || s.startDate.slice(0, 10) >= dateFrom;
    const matchesDateTo = !dateTo || s.startDate.slice(0, 10) <= dateTo;
    return matchesSearch && matchesTenant && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const sortedSubscribers = [...filteredSubscribers].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'name') cmp = (a.userName || '').localeCompare(b.userName || '');
    else if (sortKey === 'startDate') cmp = a.startDate.localeCompare(b.startDate);
    else if (sortKey === 'dailyQuantity') cmp = a.dailyQuantity - b.dailyQuantity;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sortedSubscribers.length / pageSize));
  const pagedSubscribers = sortedSubscribers.slice((page - 1) * pageSize, page * pageSize);

  const handleExportSubscribersCsv = () => {
    const rows: Array<Array<string | number>> = [
      ['Tenant', 'Name', 'Email', 'Phone', 'Address', 'Milk Type', 'Qty/Day', 'Rate/L', 'Start Date', 'Status'],
      ...sortedSubscribers.map(s => [
        s.tenantName || '',
        s.userName,
        s.userEmail,
        s.userPhone || '',
        [s.flatName, s.buildingName, s.societyName, s.areaName, s.city].filter(Boolean).join(', '),
        s.milkTypeName,
        s.dailyQuantity,
        s.pricePerLiter,
        s.startDate.slice(0, 10),
        s.status === 'Suspended' ? 'Suspended' : s.isPaused ? 'Paused' : s.isActive ? 'Active' : 'Inactive',
      ]),
    ];
    downloadCsvFile(`subscribers-${today()}.csv`, rows);
  };

  // Calendar helper
  const renderCalendar = () => {
    if (!calendarSub) return null;
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });

    const isDatePaused = (day: number) => {
      const d = new Date(year, month, day);
      return calendarPauses.some(p => {
        const start = new Date(p.startDate);
        const end = new Date(p.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        return d >= start && d <= end;
      });
    };

    const cells: JSX.Element[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
    for (let day = 1; day <= daysInMonth; day++) {
      const paused = isDatePaused(day);
      cells.push(
        <div key={day}
          onClick={paused && calendarSub ? () => handleUnpauseDay(calendarSub.subscriberId, day) : undefined}
          title={paused ? 'Click to unpause this day' : undefined}
          style={{
            padding: '0.4rem', textAlign: 'center', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
            background: paused ? '#FFCDD2' : 'var(--tc-border)',
            color: paused ? '#C62828' : 'var(--tc)',
            cursor: paused ? 'pointer' : 'default',
          }}>
          {day}
        </div>
      );
    }

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }} onClick={() => setCalendarSub(null)}>
        <div style={{
          background: 'white', borderRadius: '16px', padding: '1.5rem', minWidth: '340px', maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: 'var(--tc)' }}>{calendarSub.userName}</h3>
            <button onClick={() => setCalendarSub(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#888' }}>✕</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <button onClick={() => setCalendarMonth(prev => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 })}
              style={{ ...smallBtn, padding: '0.3rem 0.6rem' }}>◀</button>
            <strong style={{ color: '#333' }}>{monthName}</strong>
            <button onClick={() => setCalendarMonth(prev => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 })}
              style={{ ...smallBtn, padding: '0.3rem 0.6rem' }}>▶</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '1rem' }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#888', padding: '0.3rem' }}>{d}</div>
            ))}
            {cells}
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', fontSize: '0.8rem' }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--tc-border)', borderRadius: 3, marginRight: 4 }} />Active</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#FFCDD2', borderRadius: 3, marginRight: 4 }} />Paused (click to unpause)</span>
          </div>
          {calendarPauses.length > 0 && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid #E0E0E0', paddingTop: '0.75rem' }}>
              <strong style={{ fontSize: '0.85rem', color: 'var(--tc)' }}>Pause Periods</strong>
              {calendarPauses.map(p => (
                <div key={p.pauseId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #F5F5F5', fontSize: '0.82rem', color: '#555' }}>
                  <span>{fmtDate(p.startDate)} → {fmtDate(p.endDate)}</span>
                  <button onClick={() => handleDeletePause(calendarSub.subscriberId, p.pauseId)} style={{ background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>🗑️</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PanelHeader title="Subscribers" count={filteredSubscribers.length}>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <GreenButton onClick={() => setShowForm(!showForm)}>+ Add Subscriber</GreenButton>
            <ActionBtn onClick={handleDownloadSample} color="#1976D2">📄 Sample CSV</ActionBtn>
          </div>
        )}
      </PanelHeader>

      {isAdmin && !isSuperAdmin && (
        <div style={{ ...formStyle, marginBottom: '1rem' }}>
          <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>📤 Bulk Upload (CSV):</strong>
          <input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)}
            style={{ fontSize: '0.85rem' }} />
          <GreenButton onClick={handleBulkUpload}>{uploading ? 'Uploading...' : 'Upload'}</GreenButton>
        </div>
      )}

      {isSuperAdmin && (
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>🏢 Filter by Tenant:</label>
          <select
            value={tenantFilter ?? ''}
            onChange={e => setTenantFilter(e.target.value ? Number(e.target.value) : null)}
            style={{ ...inputStyle, minWidth: '220px' }}
          >
            <option value="">All Tenants</option>
            {tenants.filter(t => t.isActive).map(t => (
              <option key={t.tenantId} value={t.tenantId}>{t.name}</option>
            ))}
          </select>
          {tenantFilter && (
            <ActionBtn onClick={() => setTenantFilter(null)} color="#757575">Clear</ActionBtn>
          )}
          <span style={{ fontSize: '0.8rem', color: '#888' }}>Showing {filteredSubscribers.length} subscriber(s)</span>
        </div>
      )}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by subscriber name..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '400px' }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        <label style={{ fontSize: '0.8rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          From <DateInput value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inputStyle, padding: '0.4rem 0.6rem' }} />
        </label>
        <label style={{ fontSize: '0.8rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          To <DateInput value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...inputStyle, padding: '0.4rem 0.6rem' }} />
        </label>
        {(dateFrom || dateTo) && (
          <ActionBtn onClick={() => { setDateFrom(''); setDateTo(''); }} color="#757575">Clear Dates</ActionBtn>
        )}
        <ActionBtn onClick={handleExportSubscribersCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
      </div>

      {msg && <MsgBanner>{msg}</MsgBanner>}

      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name" required style={inputStyle} />
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email Address" required style={inputStyle} />
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" required minLength={6} style={inputStyle} />
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <select value={form.societyName} onChange={e => {
            const society = addressMasters.find(a => a.societyName === e.target.value);
            setForm({ ...form, societyName: e.target.value, buildingName: '', flatName: '', areaName: society?.area || form.areaName, city: society?.city || form.city });
          }} style={inputStyle}>
            <option value="">Select Society</option>
            {addressMasters.filter(a => a.isActive).map(a => <option key={a.addressMasterId} value={a.societyName}>{a.societyName}</option>)}
          </select>
          <select value={form.buildingName} onChange={e => setForm({ ...form, buildingName: e.target.value, flatName: '' })} disabled={!form.societyName} style={inputStyle}>
            <option value="">{form.societyName ? 'Select Building' : 'Select Society First'}</option>
            {buildingMasters.filter(b => b.isActive && b.societyName === form.societyName).map(b => <option key={b.buildingMasterId} value={b.name}>{b.name}</option>)}
          </select>
          <input value={form.flatName} onChange={e => setForm({ ...form, flatName: e.target.value })} disabled={!form.buildingName} placeholder={form.buildingName ? 'Flat' : 'Select Building First'} style={inputStyle} />
          <input value={form.areaName} onChange={e => setForm({ ...form, areaName: e.target.value })} placeholder="Area Name" style={inputStyle} />
          <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" style={inputStyle} />
          <select value={form.milkTypeId} onChange={e => setForm({ ...form, milkTypeId: +e.target.value })} required style={inputStyle}>
            <option value={0}>Select Milk Type</option>
            {milkTypes.map(m => <option key={m.milkTypeId} value={m.milkTypeId}>{m.name} (₹{m.pricePerLiter}/L)</option>)}
          </select>
          <input type="number" step="0.1" min="0.1" max="100" value={form.dailyQuantity} onChange={e => setForm({ ...form, dailyQuantity: +e.target.value })} placeholder="Daily Qty (L)" required style={inputStyle} />
          <DateInput value={form.subscriptionStartDate} onChange={e => setForm({ ...form, subscriptionStartDate: e.target.value })} title={form.subscriptionStartDate ? fmtLongDate(form.subscriptionStartDate) : 'Subscription Start Date (optional, defaults to today)'} style={inputStyle} />
          <GreenButton type="submit">Create</GreenButton>
        </form>
      )}

      {pauseForm && (
        <form onSubmit={handlePause} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Pause Subscriber: {pauseForm.name}</strong>
          <DateInput value={pauseForm.startDate} onChange={e => setPauseForm({ ...pauseForm, startDate: e.target.value })} required style={inputStyle} />
          <DateInput value={pauseForm.endDate} onChange={e => setPauseForm({ ...pauseForm, endDate: e.target.value })} required style={inputStyle} />
          <GreenButton type="submit">Confirm Pause</GreenButton>
          <button type="button" onClick={() => setPauseForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {suspendForm && (
        <form onSubmit={handleSuspendSubmit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Suspend Subscriber: {suspendForm.name}</strong>
          <textarea value={suspendForm.reason} onChange={e => setSuspendForm({ ...suspendForm, reason: e.target.value })} placeholder="Reason (optional)" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          <div style={{ fontSize: '0.8rem', color: '#C62828' }}>Suspending stops all deliveries and marks this subscriber as Suspended. You can Reactivate anytime.</div>
          <GreenButton type="submit">Confirm Suspend</GreenButton>
          <button type="button" onClick={() => setSuspendForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {resetPwdSubId && (
        <form onSubmit={handleResetSubPassword} style={formStyle}>
          <strong style={{ color: '#E65100' }}>🔑 Reset Password for {resetPwdSubName}</strong>
          <input type="password" value={subNewPassword} onChange={e => setSubNewPassword(e.target.value)} placeholder="New Password (min 6)" required minLength={6} style={inputStyle} />
          <GreenButton type="submit">Reset Password</GreenButton>
          <button type="button" onClick={() => { setResetPwdSubId(null); setSubNewPassword(''); }} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {extraMilkForm && (
        <form onSubmit={handleExtraMilk} style={formStyle}>
          <strong style={{ color: '#1565C0' }}>🥛 Extra Milk for {extraMilkForm.subscriberName}</strong>
          <DateInput value={extraMilkForm.date} onChange={e => setExtraMilkForm({ ...extraMilkForm, date: e.target.value })} required style={inputStyle} />
          <input type="number" step="0.1" min="0.1" max="100" value={extraMilkForm.quantity} onChange={e => setExtraMilkForm({ ...extraMilkForm, quantity: +e.target.value })} placeholder="Quantity (L)" required style={inputStyle} />
          <select value={extraMilkForm.milkTypeId} onChange={e => setExtraMilkForm({ ...extraMilkForm, milkTypeId: +e.target.value })} style={inputStyle}>
            <option value={0}>Default Milk Type</option>
            {milkTypes.map(m => <option key={m.milkTypeId} value={m.milkTypeId}>{m.name} (₹{m.pricePerLiter}/L)</option>)}
          </select>
          <GreenButton type="submit">Add Extra Delivery</GreenButton>
          <button type="button" onClick={() => setExtraMilkForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {editForm && (
        <form onSubmit={handleEditSubscriber} style={formStyle}>
          <strong style={{ color: '#1565C0' }}>✏️ Edit Subscriber</strong>
          <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" style={inputStyle} />
          <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <select value={editForm.societyName} onChange={e => {
            const society = addressMasters.find(a => a.societyName === e.target.value);
            setEditForm({ ...editForm, societyName: e.target.value, buildingName: '', flatName: '', areaName: society?.area || editForm.areaName, city: society?.city || editForm.city });
          }} style={inputStyle}>
            <option value="">Select Society</option>
            {addressMasters.filter(a => a.isActive).map(a => <option key={a.addressMasterId} value={a.societyName}>{a.societyName}</option>)}
          </select>
          <select value={editForm.buildingName} onChange={e => setEditForm({ ...editForm, buildingName: e.target.value, flatName: '' })} disabled={!editForm.societyName} style={inputStyle}>
            <option value="">{editForm.societyName ? 'Select Building' : 'Select Society First'}</option>
            {buildingMasters.filter(b => b.isActive && b.societyName === editForm.societyName).map(b => <option key={b.buildingMasterId} value={b.name}>{b.name}</option>)}
          </select>
          <input value={editForm.flatName} onChange={e => setEditForm({ ...editForm, flatName: e.target.value })} disabled={!editForm.buildingName} placeholder={editForm.buildingName ? 'Flat' : 'Select Building First'} style={inputStyle} />
          <input value={editForm.areaName} onChange={e => setEditForm({ ...editForm, areaName: e.target.value })} placeholder="Area Name" style={inputStyle} />
          <input value={editForm.city} onChange={e => setEditForm({ ...editForm, city: e.target.value })} placeholder="City" style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
            <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })} />
            Active
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <GreenButton type="submit">Save Changes</GreenButton>
            <button type="button" onClick={() => setEditForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
          </div>
        </form>
      )}

      {whatsAppLoginPrompt && (
        <PromptModal
          title="Send WhatsApp Login"
          message={`Enter password to send to ${whatsAppLoginPrompt.sub.userName}:`}
          placeholder="Password"
          type="password"
          onConfirm={handleSendWhatsAppLoginConfirm}
          onCancel={() => setWhatsAppLoginPrompt(null)}
        />
      )}

      {credentialsPrompt && (
        <PromptModal
          title="Download Credentials"
          message={`Enter password to include in credentials file for ${credentialsPrompt.userName}:`}
          placeholder="Password"
          type="password"
          onConfirm={handleDownloadCredentialsConfirm}
          onCancel={() => setCredentialsPrompt(null)}
        />
      )}

      {deleteConfirm && (
        <div style={{ ...formStyle, background: '#FFF3E0', borderColor: '#E65100' }}>
          <strong style={{ color: '#C62828' }}>🗑️ Delete Subscriber</strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#333' }}>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will permanently remove all their deliveries, bills, payments, and subscription data. This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ActionBtn onClick={handleDeleteSubscriber} color="#C62828">Yes, Delete</ActionBtn>
            <button type="button" onClick={() => setDeleteConfirm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          {isSuperAdmin && <th style={thStyle}>Tenant</th>}
          <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
          <th style={thStyle}>Email</th><th style={thStyle}>Phone</th>
          <th style={thStyle}>Address</th><th style={thStyle}>Milk</th>
          <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('dailyQuantity')}>Qty/Day {sortKey === 'dailyQuantity' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
          <th style={thStyle}>₹/L</th>
          <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('startDate')}>Start Date {sortKey === 'startDate' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
          <th style={thStyle}>Status</th>
          {isAdmin && !isSuperAdmin && <th style={thStyle}>Actions</th>}
        </tr></thead>
        <tbody>
          {pagedSubscribers.map(s => (
            <tr key={s.subscriberId} style={trStyle}>
              {isSuperAdmin && <td style={tdStyle}><span style={{ background: 'var(--tc-light)', color: 'var(--tc)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>{s.tenantName || `Tenant #${s.tenantId}`}</span></td>}
              <td style={tdStyle}>
                <strong style={{ cursor: 'pointer', color: '#1976D2', textDecoration: 'underline' }} onClick={() => openCalendar(s)}>{s.userName}</strong>
              </td>
              <td style={tdStyle}>{s.userEmail}</td>
              <td style={tdStyle}>{s.userPhone || '—'}</td>
              <td style={tdStyle}>{formatStructuredAddress({
                societyName: s.societyName,
                buildingName: s.buildingName,
                flatName: s.flatName,
                areaName: s.areaName,
                city: s.city,
                fallbackAddress: s.userAddress,
              })}</td>
              <td style={tdStyle}>{s.milkTypeName}</td>
              <td style={tdStyle}>{s.dailyQuantity} L</td>
              <td style={tdStyle}>₹{s.pricePerLiter}</td>
              <td style={tdStyle}>{fmtDate(s.startDate)}</td>
              <td style={tdStyle}>
                <StatusBadge status={s.status === 'Suspended' ? 'suspended' : s.isPaused ? 'paused' : s.isActive ? 'active' : 'inactive'} />
              </td>
              {isAdmin && !isSuperAdmin && (
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    {/* Edit */}
                    <button title="Edit subscriber" onClick={() => setEditForm({
                      subscriberId: s.subscriberId,
                      email: s.userEmail,
                      phone: s.userPhone || '',
                      societyName: s.societyName || '',
                      buildingName: s.buildingName || '',
                      flatName: s.flatName || '',
                      areaName: s.areaName || '',
                      city: s.city || '',
                      isActive: s.isActive,
                    })}
                      style={{ background: '#EDE7F6', color: '#6A1B9A', border: '1px solid #CE93D8', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✏️</button>
                    {/* Calendar */}
                    <button title="View calendar / pauses" onClick={() => openCalendar(s)}
                      style={{ background: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📅</button>
                    {/* Pause / Resume / Activate / Reactivate */}
                    {s.status === 'Suspended'
                      ? <button title="Reactivate subscriber" onClick={() => handleReactivateSubscriber(s.subscriberId)}
                          style={{ background: '#E8F5E9', color: '#2E7D32', border: '1px solid #A5D6A7', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🟢</button>
                      : s.isPaused
                        ? <button title="Resume deliveries" onClick={() => handleResume(s.subscriberId)}
                            style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▶️</button>
                        : s.isActive
                          ? <button title="Pause deliveries" onClick={() => setPauseForm({ id: s.subscriberId, name: s.userName, startDate: today(), endDate: today() })}
                              style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⏸️</button>
                          : <button title="Activate subscriber" onClick={() => handleActivate(s.subscriberId)}
                              style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✅</button>
                    }
                    {/* Suspend */}
                    {s.status !== 'Suspended' && (
                      <button title="Suspend subscriber" onClick={() => setSuspendForm({ id: s.subscriberId, name: s.userName, reason: '' })}
                        style={{ background: '#FCE4EC', color: '#AD1457', border: '1px solid #F48FB1', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⛔</button>
                    )}
                    {/* Extra Milk */}
                    <button title="Add extra milk delivery" onClick={() => setExtraMilkForm({ subscriberId: s.subscriberId, subscriberName: s.userName, date: today(), quantity: s.dailyQuantity, milkTypeId: s.milkTypeId })}
                      style={{ background: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🥛</button>
                    {/* Reset Password */}
                    <button title="Reset password" onClick={() => { setResetPwdSubId(s.subscriberId); setResetPwdSubName(s.userName); setSubNewPassword(''); }}
                      style={{ background: '#FFF8E1', color: '#F57F17', border: '1px solid #FFE082', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔑</button>
                    {/* Download credentials */}
                    <button title="Download login credentials (TXT)" onClick={() => {
                      setCredentialsPrompt(s);
                    }}
                      style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📄</button>
                    {/* Send WhatsApp Login Details */}
                    <button title="Send login details via WhatsApp" onClick={() => handleSendWhatsAppLogin(s)}
                      style={{ background: '#E8F5E9', color: '#25D366', border: '1px solid #A5D6A7', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>💬</button>
                    {/* Delete */}
                    <button title="Delete subscriber" onClick={() => setDeleteConfirm({ subscriberId: s.subscriberId, name: s.userName })}
                      style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🗑️</button>
                  </div>
                </td>
              )}
            </tr>
          ))}
          {pagedSubscribers.length === 0 && <EmptyRow colSpan={isSuperAdmin ? 9 : isAdmin ? 9 : 8} text={search ? 'No subscribers match your search.' : 'No data found.'} />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {pagedSubscribers.map(s => (
          <div key={s.subscriberId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{s.userName}</span>
              <StatusBadge status={s.status === 'Suspended' ? 'suspended' : s.isPaused ? 'paused' : s.isActive ? 'active' : 'inactive'} />
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Phone: </span>{s.userPhone || '—'}</div>
              <div><span className="mobile-card-detail-label">Address: </span>{formatStructuredAddress({ societyName: s.societyName, buildingName: s.buildingName, flatName: s.flatName, areaName: s.areaName, city: s.city, fallbackAddress: s.userAddress })}</div>
              <div><span className="mobile-card-detail-label">Milk: </span>{s.milkTypeName} — {s.dailyQuantity} L/day @ ₹{s.pricePerLiter}/L</div>
              <div><span className="mobile-card-detail-label">Start: </span>{fmtDate(s.startDate)}</div>
            </div>
            {isAdmin && !isSuperAdmin && (
              <div className="mobile-card-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => setEditForm({ subscriberId: s.subscriberId, email: s.userEmail, phone: s.userPhone || '', societyName: s.societyName || '', buildingName: s.buildingName || '', flatName: s.flatName || '', areaName: s.areaName || '', city: s.city || '', isActive: s.isActive })}>✏️ Edit</button>
                {s.status === 'Suspended'
                  ? <button className="mc-btn mc-btn-resume" onClick={() => handleReactivateSubscriber(s.subscriberId)}>🟢 Reactivate</button>
                  : s.isPaused
                    ? <button className="mc-btn mc-btn-resume" onClick={() => handleResume(s.subscriberId)}>▶️ Resume</button>
                    : s.isActive
                      ? <button className="mc-btn mc-btn-pause" onClick={() => setPauseForm({ id: s.subscriberId, name: s.userName, startDate: today(), endDate: today() })}>⏸️ Pause</button>
                      : <button className="mc-btn mc-btn-resume" onClick={() => handleActivate(s.subscriberId)}>✅ Activate</button>
                }
                {s.status !== 'Suspended' && (
                  <button className="mc-btn mc-btn-more" style={{ color: '#AD1457' }} onClick={() => setSuspendForm({ id: s.subscriberId, name: s.userName, reason: '' })}>⛔ Suspend</button>
                )}
                <button className="mc-btn mc-btn-calendar" onClick={() => openCalendar(s)}>📅 Calendar</button>
                <div className="mc-more-wrapper">
                  <button className="mc-btn mc-btn-more" onClick={() => setMobileMoreAction(mobileMoreAction === s.subscriberId ? null : s.subscriberId)}>⋯</button>
                  {mobileMoreAction === s.subscriberId && (
                    <div className="mc-more-dropdown">
                      <button onClick={() => { setExtraMilkForm({ subscriberId: s.subscriberId, subscriberName: s.userName, date: today(), quantity: s.dailyQuantity, milkTypeId: s.milkTypeId }); setMobileMoreAction(null); }}>🥛 Extra Milk</button>
                      <button onClick={() => { setResetPwdSubId(s.subscriberId); setResetPwdSubName(s.userName); setSubNewPassword(''); setMobileMoreAction(null); }}>🔑 Reset Password</button>
                      <button onClick={() => { setCredentialsPrompt(s); setMobileMoreAction(null); }}>📄 Credentials</button>
                      <button onClick={() => { handleSendWhatsAppLogin(s); setMobileMoreAction(null); }} style={{ color: '#25D366', fontWeight: 600 }}>💬 WhatsApp Login</button>
                      <button className="mc-more-danger" onClick={() => { setDeleteConfirm({ subscriberId: s.subscriberId, name: s.userName }); setMobileMoreAction(null); }}>🗑️ Delete</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.85rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
          Rows per page:
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ ...inputStyle, padding: '0.3rem 0.5rem' }}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ ...smallBtn, opacity: page <= 1 ? 0.5 : 1 }}>◀ Prev</button>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ ...smallBtn, opacity: page >= totalPages ? 0.5 : 1 }}>Next ▶</button>
        </div>
      </div>

      {renderCalendar()}
    </div>
  );
}

// ===== My Subscription Panel (Subscriber self-service) =====
function MySubscriptionPanel() {
  const [subscriber, setSubscriber] = useState<SubscriberDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [pauseForm, setPauseForm] = useState<{ startDate: string; endDate: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [pauses, setPauses] = useState<PauseDto[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });

  const load = async () => {
    setLoading(true);
    const res = await api.getSubscribers();
    if (res.data && res.data.length > 0) {
      setSubscriber(res.data[0]);
      const pauseRes = await api.getSubscriberPauses(res.data[0].subscriberId);
      if (pauseRes.data) setPauses(pauseRes.data);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handlePause = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscriber || !pauseForm) return;
    const res = await api.pauseSubscriber(subscriber.subscriberId, { startDate: pauseForm.startDate, endDate: pauseForm.endDate });
    setMsg(res.message);
    if (res.success) { setPauseForm(null); load(); }
  };

  const handleResume = async () => {
    if (!subscriber) return;
    const res = await api.resumeSubscriber(subscriber.subscriberId);
    setMsg(res.message);
    load();
  };

  const handleActivateSelf = async () => {
    if (!subscriber) return;
    const res = await api.activateSubscriber(subscriber.subscriberId);
    setMsg(res.message);
    load();
  };

  const handleDeletePause = async (pauseId: number) => {
    if (!subscriber) return;
    const res = await api.deleteSubscriberPause(subscriber.subscriberId, pauseId);
    if (res.success) {
      setPauses(prev => prev.filter(p => p.pauseId !== pauseId));
    } else {
      setMsg(res.message || 'Failed to delete pause');
    }
  };

  const handleUnpauseDaySelf = async (day: number) => {
    if (!subscriber) return;
    const fmt = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    const d = new Date(calendarMonth.year, calendarMonth.month, day);
    d.setHours(0, 0, 0, 0);
    const pause = pauses.find(p => {
      const s = new Date(p.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(p.endDate);   e.setHours(0, 0, 0, 0);
      return d >= s && d <= e;
    });
    if (!pause) return;
    const pauseStart = new Date(pause.startDate); pauseStart.setHours(0, 0, 0, 0);
    const pauseEnd   = new Date(pause.endDate);   pauseEnd.setHours(0, 0, 0, 0);
    const dayBefore  = new Date(d); dayBefore.setDate(dayBefore.getDate() - 1);
    const dayAfter   = new Date(d); dayAfter.setDate(dayAfter.getDate() + 1);
    await api.deleteSubscriberPause(subscriber.subscriberId, pause.pauseId);
    if (pauseStart < d)  await api.pauseSubscriber(subscriber.subscriberId, { startDate: fmt(pauseStart), endDate: fmt(dayBefore) });
    if (pauseEnd   > d)  await api.pauseSubscriber(subscriber.subscriberId, { startDate: fmt(dayAfter),   endDate: fmt(pauseEnd)   });
    load();
  };

  // Calendar rendering
  const renderMyCalendar = () => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });
    const todayDate = new Date();

    const isDatePaused = (day: number) => {
      const d = new Date(year, month, day);
      return pauses.some(p => {
        const start = new Date(p.startDate);
        const end = new Date(p.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        return d >= start && d <= end;
      });
    };

    const isToday = (day: number) => {
      return year === todayDate.getFullYear() && month === todayDate.getMonth() && day === todayDate.getDate();
    };

    const cells: JSX.Element[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
    for (let day = 1; day <= daysInMonth; day++) {
      const paused = isDatePaused(day);
      const todayMark = isToday(day);
      cells.push(
        <div key={day}
          onClick={paused ? () => handleUnpauseDaySelf(day) : undefined}
          title={paused ? 'Click to unpause this day' : undefined}
          style={{
            padding: '0.4rem', textAlign: 'center', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
            background: paused ? '#FFCDD2' : 'var(--tc-border)',
            color: paused ? '#C62828' : 'var(--tc)',
            border: todayMark ? '2px solid #1565C0' : '2px solid transparent',
            cursor: paused ? 'pointer' : 'default',
          }}>
          {day}
        </div>
      );
    }

    // Count paused days in this month
    const pausedDays = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => isDatePaused(d)).length;

    return (
      <div style={{
        background: 'white', borderRadius: '12px', padding: '1.5rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: '500px', marginBottom: '1.5rem',
      }}>
        <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem', fontSize: '1.05rem' }}>📅 Pause Calendar</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <button onClick={() => setCalendarMonth(prev => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 })}
            style={{ ...smallBtn, padding: '0.3rem 0.6rem' }}>◀</button>
          <strong style={{ color: '#333' }}>{monthName}</strong>
          <button onClick={() => setCalendarMonth(prev => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 })}
            style={{ ...smallBtn, padding: '0.3rem 0.6rem' }}>▶</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '1rem' }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#888', padding: '0.3rem' }}>{d}</div>
          ))}
          {cells}
        </div>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', fontSize: '0.8rem', flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--tc-border)', borderRadius: 3, marginRight: 4 }} />Active</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#FFCDD2', borderRadius: 3, marginRight: 4 }} />Paused (click to unpause)</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #1565C0', borderRadius: 3, marginRight: 4 }} />Today</span>
          {pausedDays > 0 && <span style={{ color: '#C62828', fontWeight: 600 }}>({pausedDays} paused days)</span>}
        </div>
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;
  if (!subscriber) return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No subscription found.</div>;

  return (
    <div>
      <PanelHeader title="My Subscription" />
      {msg && <MsgBanner>{msg}</MsgBanner>}

      <div className="subscription-card" style={{
        background: 'white', borderRadius: '12px', padding: '1.5rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: '500px', marginBottom: '1.5rem',
      }}>
        <ProfileField label="Milk Type" value={subscriber.milkTypeName} />
        <ProfileField label="Quantity" value={`${subscriber.dailyQuantity} L/day`} />
        <ProfileField label="Price" value={`₹${subscriber.pricePerLiter}/L`} />
        <div style={{ display: 'flex', padding: '0.65rem 0', borderBottom: '1px solid var(--tc-vlight)', alignItems: 'center' }}>
          <div style={{ width: '120px', fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>Status</div>
          <StatusBadge status={subscriber.isPaused ? 'paused' : subscriber.isActive ? 'active' : 'inactive'} />
        </div>
        <ProfileField label="Start Date" value={fmtDate(subscriber.startDate)} />
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {subscriber.isPaused ? (
          <GreenButton onClick={handleResume}>▶ Resume Subscription</GreenButton>
        ) : subscriber.isActive ? (
          <ActionBtn onClick={() => setPauseForm({ startDate: today(), endDate: today() })} color="#F57C00">⏸ Pause Subscription</ActionBtn>
        ) : (
          <GreenButton onClick={handleActivateSelf}>✅ Activate Subscription</GreenButton>
        )}
      </div>

      {pauseForm && (
        <form onSubmit={handlePause} style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '500px' }}>
          <strong style={{ color: 'var(--tc)', fontSize: '1rem' }}>Pause for specific days</strong>
          <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.25rem 0 0.75rem' }}>
            Select the date range when you don't need milk delivery.
          </p>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>From Date</label>
          <DateInput value={pauseForm.startDate} onChange={e => setPauseForm({ ...pauseForm, startDate: e.target.value })} required style={inputStyle} />
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>To Date</label>
          <DateInput value={pauseForm.endDate} onChange={e => setPauseForm({ ...pauseForm, endDate: e.target.value })} required style={inputStyle} />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <GreenButton type="submit">Confirm Pause</GreenButton>
            <button type="button" onClick={() => setPauseForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
          </div>
        </form>
      )}

      {renderMyCalendar()}

      {pauses.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: '500px', marginBottom: '1.5rem' }}>
          <strong style={{ color: 'var(--tc)', fontSize: '0.95rem' }}>📋 Scheduled Pauses</strong>
          {pauses.map(p => (
            <div key={p.pauseId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #F5F5F5', fontSize: '0.88rem', color: '#555' }}>
              <span>{fmtDate(p.startDate)} → {fmtDate(p.endDate)}</span>
              <button onClick={() => handleDeletePause(p.pauseId)} style={{ background: '#FFEBEE', border: '1px solid #EF9A9A', color: '#C62828', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Milk Types Panel =====
function MilkTypesPanel() {
  const [types, setTypes] = useState<MilkTypeDto[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', pricePerLiter: 0 });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', pricePerLiter: 0 });
  const [msg, setMsg] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const grid = useGridControls('gridPrefs:milktypes', 'name');

  const load = async () => {
    const res = await api.getMilkTypes();
    if (res.data) setTypes(res.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.createMilkType(form);
    setMsg(res.message);
    if (res.success) { setShowForm(false); setForm({ name: '', pricePerLiter: 0 }); load(); }
  };

  const startEdit = (t: MilkTypeDto) => {
    setEditId(t.milkTypeId);
    setEditForm({ name: t.name, pricePerLiter: t.pricePerLiter });
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    const res = await api.updateMilkType(editId, editForm);
    setMsg(res.message);
    if (res.success) { setEditId(null); load(); }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { setMsg('Please select a CSV file'); return; }
    setUploading(true);
    try {
      const res = await api.bulkUploadMilkTypes(uploadFile);
      setMsg(res.message + (res.data?.errors?.length ? ' | Errors: ' + res.data.errors.join('; ') : ''));
      if (res.success) { setUploadFile(null); load(); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await api.downloadMilkTypeSampleCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'milktype-upload-sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Download sample failed'); }
  };

  const handleExportCsv = () => {
    downloadCsvFile(`milk-types-${today()}.csv`, [
      ['Name', 'Price/Liter', 'Status'],
      ...sortedTypes.map(t => [t.name, t.pricePerLiter, t.isActive ? 'Active' : 'Inactive']),
    ]);
  };

  const filteredTypes = types.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(grid.search.toLowerCase());
    const matchesStatus = grid.statusFilter === 'all' || (grid.statusFilter === 'active' ? t.isActive : !t.isActive);
    return matchesSearch && matchesStatus;
  });
  const sortedTypes = [...filteredTypes].sort((a, b) => {
    const cmp = grid.sortKey === 'price' ? a.pricePerLiter - b.pricePerLiter : a.name.localeCompare(b.name);
    return grid.sortDir === 'asc' ? cmp : -cmp;
  });
  const { paged: pagedTypes, totalPages } = paginate(sortedTypes, grid.page, grid.pageSize);

  return (
    <div>
      <PanelHeader title="Milk Types" count={filteredTypes.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <GreenButton onClick={() => setShowForm(!showForm)}>+ Add Type</GreenButton>
          <ActionBtn onClick={handleDownloadSample} color="#1976D2">📄 Sample CSV</ActionBtn>
          <ActionBtn onClick={handleExportCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
        </div>
      </PanelHeader>

      <div style={{ ...formStyle, marginBottom: '1rem' }}>
        <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>📤 Bulk Upload (CSV):</strong>
        <input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)}
          style={{ fontSize: '0.85rem' }} />
        <GreenButton onClick={handleBulkUpload}>{uploading ? 'Uploading...' : 'Upload'}</GreenButton>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={grid.search}
          onChange={e => grid.setSearch(e.target.value)}
          placeholder="🔍 Search by name..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '360px' }}
        />
        <select value={grid.statusFilter} onChange={e => grid.setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {msg && <MsgBanner>{msg}</MsgBanner>}
      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Cow Milk)" required style={inputStyle} />
          <input type="number" step="0.01" value={form.pricePerLiter || ''} onChange={e => setForm({ ...form, pricePerLiter: +e.target.value })} placeholder="Price per Liter (₹)" required style={inputStyle} />
          <GreenButton type="submit">Create</GreenButton>
        </form>
      )}
      {editId && (
        <form onSubmit={handleEdit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit Milk Type</strong>
          <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" required style={inputStyle} />
          <input type="number" step="0.01" value={editForm.pricePerLiter || ''} onChange={e => setEditForm({ ...editForm, pricePerLiter: +e.target.value })} placeholder="Price/L (₹)" required style={inputStyle} />
          <GreenButton type="submit">Save</GreenButton>
          <button type="button" onClick={() => setEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          <SortableTh label="Name" sortKeyName="name" activeKey={grid.sortKey} dir={grid.sortDir} onSort={grid.toggleSort} />
          <SortableTh label="Price/Liter" sortKeyName="price" activeKey={grid.sortKey} dir={grid.sortDir} onSort={grid.toggleSort} />
          <th style={thStyle}>Status</th><th style={thStyle}>Actions</th></tr></thead>
        <tbody>
          {pagedTypes.map(t => (
            <tr key={t.milkTypeId} style={trStyle}>
              <td style={tdStyle}><strong>{t.name}</strong></td>
              <td style={tdStyle}>₹{t.pricePerLiter}</td>
              <td style={tdStyle}><StatusBadge status={t.isActive ? 'active' : 'inactive'} /></td>
              <td style={tdStyle}>
                <ActionBtn onClick={() => startEdit(t)} color="#1976D2">✏ Edit</ActionBtn>
              </td>
            </tr>
          ))}
          {pagedTypes.length === 0 && <EmptyRow colSpan={4} />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {pagedTypes.map(t => (
          <div key={t.milkTypeId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{t.name}</span>
              <StatusBadge status={t.isActive ? 'active' : 'inactive'} />
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Price/Liter: </span>₹{t.pricePerLiter}</div>
            </div>
            <div className="mobile-card-actions">
              <button className="mc-btn mc-btn-edit" onClick={() => startEdit(t)}>✏️ Edit</button>
            </div>
          </div>
        ))}
      </div>
      </div>
      <GridPagination page={grid.page} setPage={grid.setPage} pageSize={grid.pageSize} setPageSize={grid.setPageSize} totalPages={totalPages} />
    </div>
  );
}

// ===== Society Master Panel =====
function SocietyMasterPanel() {
  const [societies, setSocieties] = useState<AddressMasterDto[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ societyName: '', area: '', city: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ societyName: '', area: '', city: '', isActive: true });
  const [msg, setMsg] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const grid = useGridControls('gridPrefs:societymaster', 'societyName');

  const load = async () => {
    const res = await api.getAddressMasters();
    if (res.data) setSocieties(res.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.createAddressMaster(form);
    setMsg(res.message);
    if (res.success) { setShowForm(false); setForm({ societyName: '', area: '', city: '' }); load(); }
  };

  const startEdit = (s: AddressMasterDto) => {
    setEditId(s.addressMasterId);
    setEditForm({ societyName: s.societyName, area: s.area, city: s.city, isActive: s.isActive });
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    const res = await api.updateAddressMaster(editId, editForm);
    setMsg(res.message);
    if (res.success) { setEditId(null); load(); }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { setMsg('Please select a CSV file'); return; }
    setUploading(true);
    try {
      const res = await api.bulkUploadAddressMasters(uploadFile);
      setMsg(res.message + (res.data?.errors?.length ? ' | Errors: ' + res.data.errors.join('; ') : ''));
      if (res.success) { setUploadFile(null); load(); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await api.downloadAddressMasterSampleCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'society-master-upload-sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Download sample failed'); }
  };

  const filteredSocieties = societies.filter(s => {
    const matchesSearch = `${s.societyName} ${s.area} ${s.city}`.toLowerCase().includes(grid.search.toLowerCase());
    const matchesStatus = grid.statusFilter === 'all' || (grid.statusFilter === 'active' ? s.isActive : !s.isActive);
    return matchesSearch && matchesStatus;
  });
  const sortedSocieties = [...filteredSocieties].sort((a, b) => {
    const cmp = a.societyName.localeCompare(b.societyName);
    return grid.sortDir === 'asc' ? cmp : -cmp;
  });
  const { paged: pagedSocieties, totalPages: societiesTotalPages } = paginate(sortedSocieties, grid.page, grid.pageSize);
  const handleExportCsv = () => {
    downloadCsvFile(`society-master-${today()}.csv`, [
      ['Society', 'Area', 'City', 'Status'],
      ...sortedSocieties.map(s => [s.societyName, s.area, s.city, s.isActive ? 'Active' : 'Inactive']),
    ]);
  };

  return (
    <div>
      <PanelHeader title="Society Master" count={filteredSocieties.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <GreenButton onClick={() => setShowForm(!showForm)}>+ Add Society</GreenButton>
          <ActionBtn onClick={handleDownloadSample} color="#1976D2">📄 Sample CSV</ActionBtn>
          <ActionBtn onClick={handleExportCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
        </div>
      </PanelHeader>

      <div style={{ ...formStyle, marginBottom: '1rem' }}>
        <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>📤 Bulk Upload (CSV):</strong>
        <input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)}
          style={{ fontSize: '0.85rem' }} />
        <GreenButton onClick={handleBulkUpload}>{uploading ? 'Uploading...' : 'Upload'}</GreenButton>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={grid.search}
          onChange={e => grid.setSearch(e.target.value)}
          placeholder="🔍 Search by society, area, or city..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '360px' }}
        />
        <select value={grid.statusFilter} onChange={e => grid.setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {msg && <MsgBanner>{msg}</MsgBanner>}
      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <input value={form.societyName} onChange={e => setForm({ ...form, societyName: e.target.value })} placeholder="Society Name" required style={inputStyle} />
          <input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="Area" required style={inputStyle} />
          <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" required style={inputStyle} />
          <GreenButton type="submit">Create</GreenButton>
        </form>
      )}
      {editId && (
        <form onSubmit={handleEdit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit Society</strong>
          <input value={editForm.societyName} onChange={e => setEditForm({ ...editForm, societyName: e.target.value })} placeholder="Society Name" required style={inputStyle} />
          <input value={editForm.area} onChange={e => setEditForm({ ...editForm, area: e.target.value })} placeholder="Area" required style={inputStyle} />
          <input value={editForm.city} onChange={e => setEditForm({ ...editForm, city: e.target.value })} placeholder="City" required style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
            <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })} />
            Active
          </label>
          <GreenButton type="submit">Save</GreenButton>
          <button type="button" onClick={() => setEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}><th style={thStyle}>Society</th><th style={thStyle}>Area</th><th style={thStyle}>City</th><th style={thStyle}>Status</th><th style={thStyle}>Actions</th></tr></thead>
        <tbody>
          {pagedSocieties.map(s => (
            <tr key={s.addressMasterId} style={trStyle}>
              <td style={tdStyle}><strong>{s.societyName}</strong></td>
              <td style={tdStyle}>{s.area}</td>
              <td style={tdStyle}>{s.city}</td>
              <td style={tdStyle}><StatusBadge status={s.isActive ? 'active' : 'inactive'} /></td>
              <td style={tdStyle}>
                <ActionBtn onClick={() => startEdit(s)} color="#1976D2">✏ Edit</ActionBtn>
              </td>
            </tr>
          ))}
          {pagedSocieties.length === 0 && <EmptyRow colSpan={5} />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {pagedSocieties.map(s => (
          <div key={s.addressMasterId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{s.societyName}</span>
              <StatusBadge status={s.isActive ? 'active' : 'inactive'} />
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Area: </span>{s.area}</div>
              <div><span className="mobile-card-detail-label">City: </span>{s.city}</div>
            </div>
            <div className="mobile-card-actions">
              <button className="mc-btn mc-btn-edit" onClick={() => startEdit(s)}>✏️ Edit</button>
            </div>
          </div>
        ))}
      </div>
      </div>
      <GridPagination page={grid.page} setPage={grid.setPage} pageSize={grid.pageSize} setPageSize={grid.setPageSize} totalPages={societiesTotalPages} />
    </div>
  );
}

// ===== Building Master Panel =====
function BuildingMasterPanel() {
  const [buildings, setBuildings] = useState<BuildingMasterDto[]>([]);
  const [societies, setSocieties] = useState<AddressMasterDto[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ addressMasterId: 0, name: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', isActive: true });
  const [msg, setMsg] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const grid = useGridControls('gridPrefs:buildingmaster', 'name');

  const load = async () => {
    const [buildRes, socRes] = await Promise.all([api.getBuildingMasters(), api.getAddressMasters()]);
    if (buildRes.data) setBuildings(buildRes.data);
    if (socRes.data) setSocieties(socRes.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.addressMasterId) { setMsg('Please select a society'); return; }
    const res = await api.createBuildingMaster(form);
    setMsg(res.message);
    if (res.success) { setShowForm(false); setForm({ addressMasterId: 0, name: '' }); load(); }
  };

  const startEdit = (b: BuildingMasterDto) => {
    setEditId(b.buildingMasterId);
    setEditForm({ name: b.name, isActive: b.isActive });
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    const res = await api.updateBuildingMaster(editId, editForm);
    setMsg(res.message);
    if (res.success) { setEditId(null); load(); }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { setMsg('Please select a CSV file'); return; }
    setUploading(true);
    try {
      const res = await api.bulkUploadBuildingMasters(uploadFile);
      setMsg(res.message + (res.data?.errors?.length ? ' | Errors: ' + res.data.errors.join('; ') : ''));
      if (res.success) { setUploadFile(null); load(); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await api.downloadBuildingMasterSampleCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'building-master-upload-sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Download sample failed'); }
  };

  const activeSocieties = societies.filter(s => s.isActive);

  const filteredBuildings = buildings.filter(b => {
    const matchesSearch = `${b.societyName} ${b.name}`.toLowerCase().includes(grid.search.toLowerCase());
    const matchesStatus = grid.statusFilter === 'all' || (grid.statusFilter === 'active' ? b.isActive : !b.isActive);
    return matchesSearch && matchesStatus;
  });
  const sortedBuildings = [...filteredBuildings].sort((a, b) => {
    const cmp = a.name.localeCompare(b.name);
    return grid.sortDir === 'asc' ? cmp : -cmp;
  });
  const { paged: pagedBuildings, totalPages: buildingsTotalPages } = paginate(sortedBuildings, grid.page, grid.pageSize);
  const handleExportCsv = () => {
    downloadCsvFile(`building-master-${today()}.csv`, [
      ['Society', 'Building', 'Status'],
      ...sortedBuildings.map(b => [b.societyName, b.name, b.isActive ? 'Active' : 'Inactive']),
    ]);
  };

  return (
    <div>
      <PanelHeader title="Building Master" count={filteredBuildings.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <GreenButton onClick={() => setShowForm(!showForm)}>+ Add Building</GreenButton>
          <ActionBtn onClick={handleDownloadSample} color="#1976D2">📄 Sample CSV</ActionBtn>
          <ActionBtn onClick={handleExportCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
        </div>
      </PanelHeader>

      <div style={{ ...formStyle, marginBottom: '1rem' }}>
        <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>📤 Bulk Upload (CSV):</strong>
        <input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)}
          style={{ fontSize: '0.85rem' }} />
        <GreenButton onClick={handleBulkUpload}>{uploading ? 'Uploading...' : 'Upload'}</GreenButton>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={grid.search}
          onChange={e => grid.setSearch(e.target.value)}
          placeholder="🔍 Search by society or building..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '360px' }}
        />
        <select value={grid.statusFilter} onChange={e => grid.setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {msg && <MsgBanner>{msg}</MsgBanner>}
      {activeSocieties.length === 0 && (
        <MsgBanner>No societies found yet. Add one in Society Master first.</MsgBanner>
      )}
      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <select value={form.addressMasterId} onChange={e => setForm({ ...form, addressMasterId: +e.target.value })} required style={inputStyle}>
            <option value={0}>Select Society</option>
            {activeSocieties.map(s => <option key={s.addressMasterId} value={s.addressMasterId}>{s.societyName}</option>)}
          </select>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Building Name (e.g. A Wing)" required style={inputStyle} />
          <GreenButton type="submit">Create</GreenButton>
        </form>
      )}
      {editId && (
        <form onSubmit={handleEdit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit Building</strong>
          <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Building Name" required style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
            <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })} />
            Active
          </label>
          <GreenButton type="submit">Save</GreenButton>
          <button type="button" onClick={() => setEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}><th style={thStyle}>Society</th><th style={thStyle}>Building</th><th style={thStyle}>Status</th><th style={thStyle}>Actions</th></tr></thead>
        <tbody>
          {pagedBuildings.map(b => (
            <tr key={b.buildingMasterId} style={trStyle}>
              <td style={tdStyle}>{b.societyName}</td>
              <td style={tdStyle}><strong>{b.name}</strong></td>
              <td style={tdStyle}><StatusBadge status={b.isActive ? 'active' : 'inactive'} /></td>
              <td style={tdStyle}>
                <ActionBtn onClick={() => startEdit(b)} color="#1976D2">✏ Edit</ActionBtn>
              </td>
            </tr>
          ))}
          {pagedBuildings.length === 0 && <EmptyRow colSpan={4} />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {pagedBuildings.map(b => (
          <div key={b.buildingMasterId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{b.name}</span>
              <StatusBadge status={b.isActive ? 'active' : 'inactive'} />
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Society: </span>{b.societyName}</div>
            </div>
            <div className="mobile-card-actions">
              <button className="mc-btn mc-btn-edit" onClick={() => startEdit(b)}>✏️ Edit</button>
            </div>
          </div>
        ))}
      </div>
      </div>
      <GridPagination page={grid.page} setPage={grid.setPage} pageSize={grid.pageSize} setPageSize={grid.setPageSize} totalPages={buildingsTotalPages} />
    </div>
  );
}

// ===== Flat Master Panel =====
// 3D "pressed" button styling: raised drop-shadow when idle, inset shadow + shift down when active/selected.
// shape controls the icon + corner radius so Society/Building/Flat read as visually distinct button types.
// Shared by DeliveriesPanel (admin) and MyDeliveriesPanel (delivery boy) so both drill-downs look identical.
function getProgressStyle(percent: number, active: boolean, baseColor: string, shape: 'society' | 'building' | 'flat' = 'society'): React.CSSProperties {
  return {
    padding: '0.7rem 1rem',
    borderRadius: shape === 'flat' ? 999 : shape === 'building' ? 10 : 14,
    border: active ? '2px solid #1B5E20' : '1px solid #D7E3F4',
    background: `linear-gradient(90deg, ${baseColor} 0%, ${baseColor} ${percent}%, #FFF7ED ${percent}%, #FFF7ED 100%)`,
    color: '#102A43',
    fontWeight: 800,
    cursor: 'pointer',
    minWidth: 120,
    transform: active ? 'translateY(2px)' : 'translateY(0)',
    boxShadow: active
      ? 'inset 0 3px 6px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(0,0,0,0.08)'
      : '0 3px 0 rgba(0,0,0,0.2), 0 6px 14px rgba(15, 23, 42, 0.12)',
    transition: 'transform 0.08s ease, box-shadow 0.08s ease',
  };
}

function getBuildingColor(percent: number) {
  if (percent >= 100) return '#1565C0';
  if (percent >= 50) return '#1E88E5';
  return '#64B5F6';
}

function getSocietyColor() {
  // Society buttons are always green-themed per branding requirement, regardless of delivery %
  return '#2E7D32';
}

function getFlatColor(percent: number) {
  if (percent >= 100) return '#2E7D32';
  if (percent >= 50) return '#66BB6A';
  return '#F57C00';
}

// ===== Deliveries Panel =====
function DeliveriesPanel({ isAdmin }: { isAdmin: boolean }) {
  const [deliveries, setDeliveries] = useState<DeliveryDto[]>([]);
  const [month, setMonth] = useState(currentMonth());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [msg, setMsg] = useState('');
  const [viewDate, setViewDate] = useState<string | null>(null);
  const [correctionForm, setCorrectionForm] = useState<{
    deliveryId: number;
    subscriberName: string;
    status: string;
    quantity: number;
    correctionReason: string;
  } | null>(null);
  const [correctionLogsView, setCorrectionLogsView] = useState<{
    subscriberName: string;
    logs: DeliveryCorrectionLogDto[];
  } | null>(null);
  const [selectedSociety, setSelectedSociety] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFlat, setSelectedFlat] = useState('');
  const [showExtraDrilldown, setShowExtraDrilldown] = useState(false);
  const [extraDrillSociety, setExtraDrillSociety] = useState('');
  const [extraDrillBuilding, setExtraDrillBuilding] = useState('');

  const todayStr = today();

  const load = async () => {
    const res = await api.getMonthlyDeliveries(month);
    if (res.data) setDeliveries(res.data);
    setSelected(new Set());
    setSelectedSociety('');
    setSelectedBuilding('');
    setSelectedFlat('');
    setViewDate(todayStr);
  };
  useEffect(() => { load(); }, [month]);

  const handleExport = async () => {
    try {
      const blob = await api.exportDeliveriesCsv(viewDate || todayStr);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deliveries-${viewDate || todayStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Export failed'); }
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const dayDeliveries = currentLevelDeliveries.filter(d => !d.isPaused);
    if (selected.size === dayDeliveries.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(dayDeliveries.map(d => d.deliveryId)));
    }
  };

  const handleBatchStatus = async (status: string) => {
    if (selected.size === 0) { setMsg('Please select at least one delivery'); return; }
    const res = await api.batchUpdateDeliveryStatus([...selected], status);
    setMsg(res.message);
    load();
  };

  const handleSubmitCorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!correctionForm) return;
    const res = await api.correctDelivery(correctionForm.deliveryId, {
      status: correctionForm.status,
      quantity: correctionForm.quantity,
      correctionReason: correctionForm.correctionReason || undefined,
    });
    setMsg(res.message);
    if (res.success) {
      setCorrectionForm(null);
      load();
    }
  };

  const openCorrectionLogs = async (deliveryId: number, subscriberName: string) => {
    const res = await api.getDeliveryCorrectionLogs(deliveryId);
    if (res.success) {
      setCorrectionLogsView({ subscriberName, logs: res.data ?? [] });
      return;
    }
    setMsg(res.message || 'Could not load correction logs');
  };

  const selectedDateDeliveries = deliveries.filter(d => d.date.startsWith(viewDate || todayStr));
  const hierarchyDeliveries = selectedFlat
    ? selectedDateDeliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding && d.flatName === selectedFlat)
    : selectedBuilding
      ? selectedDateDeliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding)
      : selectedSociety
        ? selectedDateDeliveries.filter(d => d.societyName === selectedSociety)
        : selectedDateDeliveries;

  const societies = Array.from(new Set(selectedDateDeliveries.map(d => d.societyName).filter((value): value is string => Boolean(value)))).sort((a, b) => a.localeCompare(b));
  const buildingOptions = Array.from(new Set(selectedDateDeliveries.filter(d => d.societyName === selectedSociety).map(d => d.buildingName).filter((value): value is string => Boolean(value)))).sort((a, b) => a.localeCompare(b));
  const flatOptions = Array.from(new Set(selectedDateDeliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding).map(d => d.flatName).filter((value): value is string => Boolean(value)))).sort((a, b) => a.localeCompare(b));

  const currentLevelDeliveries = hierarchyDeliveries;
  const pendingCount = currentLevelDeliveries.filter(d => d.status === 'Pending').length;
  const deliveredCount = currentLevelDeliveries.filter(d => d.status === 'Delivered').length;
  const failedCount = currentLevelDeliveries.filter(d => d.status === 'Failed').length;
  const totalCount = currentLevelDeliveries.length;
  const deliveredPercent = totalCount > 0 ? Math.round((deliveredCount / totalCount) * 100) : 0;

  const societyStats = societies.map(society => {
    const items = selectedDateDeliveries.filter(d => d.societyName === society);
    const delivered = items.filter(d => d.status === 'Delivered').length;
    const percent = items.length > 0 ? Math.round((delivered / items.length) * 100) : 0;
    return { society, items, delivered, percent };
  });

  const buildingStats = buildingOptions.map(building => {
    const items = selectedDateDeliveries.filter(d => d.societyName === selectedSociety && d.buildingName === building);
    const delivered = items.filter(d => d.status === 'Delivered').length;
    const percent = items.length > 0 ? Math.round((delivered / items.length) * 100) : 0;
    return { building, items, delivered, percent };
  });

  const flatStats = flatOptions.map(flat => {
    const items = selectedDateDeliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding && d.flatName === flat);
    const delivered = items.filter(d => d.status === 'Delivered').length;
    const percent = items.length > 0 ? Math.round((delivered / items.length) * 100) : 0;
    return { flat, items, delivered, percent };
  });

  return (
    <div>
      <PanelHeader title="Monthly Deliveries" count={deliveries.length}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={inputStyle} />
        </div>
      </PanelHeader>

      {msg && <MsgBanner>{msg}</MsgBanner>}

      {/* Extra Delivery Summary & Drill-down */}
      {viewDate && (() => {
        const dayExtras = deliveries.filter(d => d.date.startsWith(viewDate) && d.isExtra);
        const extraDelivered = dayExtras.filter(d => d.status === 'Delivered').length;
        const extraPending = dayExtras.filter(d => d.status !== 'Delivered').length;
        const hasExtras = dayExtras.length > 0;
        const extraSocieties = Array.from(new Set(dayExtras.map(d => d.societyName).filter((v): v is string => Boolean(v)))).sort();
        const extraBuildings = extraDrillSociety
          ? Array.from(new Set(dayExtras.filter(d => d.societyName === extraDrillSociety).map(d => d.buildingName).filter((v): v is string => Boolean(v)))).sort()
          : [];
        const extraFlats = extraDrillSociety && extraDrillBuilding
          ? dayExtras.filter(d => d.societyName === extraDrillSociety && d.buildingName === extraDrillBuilding)
          : [];
        return (
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 12, background: hasExtras ? '#E8F5E9' : '#F5F5F5', border: `1px solid ${hasExtras ? '#A5D6A7' : '#E0E0E0'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.88rem' }}>
                <span>Extra Deliveries — <strong style={{ color: '#2E7D32' }}>Delivered: {extraDelivered}</strong></span>
                <span><strong style={{ color: '#E65100' }}>Undelivered: {extraPending}</strong></span>
              </div>
              {hasExtras && (
                <button type="button" onClick={() => { setShowExtraDrilldown(!showExtraDrilldown); setExtraDrillSociety(''); setExtraDrillBuilding(''); }}
                  style={{ background: showExtraDrilldown ? '#E65100' : '#1565C0', color: 'white', border: 'none', borderRadius: 8, padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                  {showExtraDrilldown ? 'Close Extra Deliveries' : 'Extra Delivery'}
                </button>
              )}
            </div>
            {showExtraDrilldown && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', borderRadius: 10, background: 'white', border: '1px solid #C8E6C9' }}>
                {!extraDrillSociety && extraSocieties.map(soc => {
                  const socExtras = dayExtras.filter(d => d.societyName === soc);
                  const socDelivered = socExtras.filter(d => d.status === 'Delivered').length;
                  return (
                    <button key={soc} type="button" onClick={() => setExtraDrillSociety(soc)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: '1px solid #A5D6A7', background: '#F1F8E9', cursor: 'pointer', fontSize: '0.88rem' }}>
                      🏘️ {soc} — <strong>{socExtras.length}</strong> extra(s), <span style={{ color: '#2E7D32' }}>{socDelivered} delivered</span>
                    </button>
                  );
                })}
                {extraDrillSociety && !extraDrillBuilding && extraBuildings.map(bld => {
                  const bldExtras = dayExtras.filter(d => d.societyName === extraDrillSociety && d.buildingName === bld);
                  const bldDelivered = bldExtras.filter(d => d.status === 'Delivered').length;
                  return (
                    <div key={bld}>
                      <button type="button" onClick={() => setExtraDrillSociety('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#1565C0', padding: '0.2rem 0', marginBottom: '0.3rem' }}>← Back to societies</button>
                      <button type="button" onClick={() => setExtraDrillBuilding(bld)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: '1px solid #90CAF9', background: '#E3F2FD', cursor: 'pointer', fontSize: '0.88rem' }}>
                        🏢 {bld} — <strong>{bldExtras.length}</strong> extra(s), <span style={{ color: '#2E7D32' }}>{bldDelivered} delivered</span>
                      </button>
                    </div>
                  );
                })}
                {extraDrillSociety && extraDrillBuilding && (
                  <div>
                    <button type="button" onClick={() => setExtraDrillBuilding('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#1565C0', padding: '0.2rem 0', marginBottom: '0.3rem' }}>← Back to buildings</button>
                    {extraFlats.map(d => (
                      <div key={d.deliveryId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: `1px solid ${d.status === 'Delivered' ? '#A5D6A7' : '#FFCC80'}`, background: d.status === 'Delivered' ? '#F1F8E9' : '#FFF8E1', fontSize: '0.88rem' }}>
                        <span>🏠 {d.flatName} — {d.subscriberName} — {d.milkTypeName} — {d.quantity}L</span>
                        <span style={{ fontWeight: 600, color: d.status === 'Delivered' ? '#2E7D32' : '#E65100' }}>{d.status === 'Delivered' ? '✓ Delivered' : '⏳ Pending'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: 'grid', gap: '0.9rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.85rem 1rem', borderRadius: 14, background: '#F8FBFF', border: '1px solid #D7E3F4' }}>
          <strong style={{ color: '#1E88E5' }}>Select date</strong>
          <input type="date" value={viewDate || todayStr} onChange={e => { setViewDate(e.target.value); setSelectedSociety(''); setSelectedBuilding(''); setSelectedFlat(''); setSelected(new Set()); }} style={inputStyle} />
          <button type="button" onClick={handleExport} style={{ ...smallBtn, background: '#1E88E5', color: 'white' }}>Download CSV</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {societyStats.map(({ society, percent }) => (
            <button
              key={society}
              type="button"
                onClick={() => { setSelectedSociety(society); setSelectedBuilding(''); setSelectedFlat(''); }}
                style={getProgressStyle(percent, society === selectedSociety, getSocietyColor(), 'society')}
              >
                <div style={{ display: 'grid', gap: '0.15rem' }}>
                  <span>🏘️ {society}</span>
                  <small>{percent}% delivered</small>
                </div>
              </button>
            ))}
          </div>

          {selectedSociety && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {buildingStats.map(({ building, percent }) => (
                <button
                  key={building}
                  type="button"
                  onClick={() => { setSelectedBuilding(building); setSelectedFlat(''); }}
                  style={getProgressStyle(percent, building === selectedBuilding, getBuildingColor(percent), 'building')}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <span>🏢 {building}</span>
                    <small>{percent}% delivered</small>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedBuilding && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {flatStats.map(({ flat, percent }) => (
                <button
                  key={flat}
                  type="button"
                  onClick={() => setSelectedFlat(flat)}
                  style={getProgressStyle(percent, flat === selectedFlat, getFlatColor(percent), 'flat')}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <span>🏠 {flat}</span>
                    <small>{percent}% delivered</small>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedSociety && (
            <div style={{ padding: '0.9rem 1rem', borderRadius: 14, background: '#F8FBFF', border: '1px solid #D7E3F4' }}>
              <strong>{selectedSociety}{selectedBuilding ? ` / ${selectedBuilding}` : ''}{selectedFlat ? ` / ${selectedFlat}` : ''}</strong>
              <div style={{ marginTop: '0.45rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                <span>Pending: {pendingCount}</span>
                <span>Delivered: {deliveredCount}</span>
                <span>Failed: {failedCount}</span>
                <span>Progress: {deliveredPercent}%</span>
              </div>
              <div style={{ marginTop: '0.6rem', height: 10, borderRadius: 999, background: '#FFE0B2', overflow: 'hidden' }}>
                <div style={{ width: `${deliveredPercent}%`, height: '100%', background: '#2E7D32' }} />
              </div>
            </div>
          )}
        </div>

      {selectedFlat && currentLevelDeliveries.length > 0 && (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={toggleSelectAll} style={{ ...smallBtn, background: '#1E88E5', color: 'white' }}>
              {selected.size === currentLevelDeliveries.filter(d => !d.isPaused).length ? 'Clear Selection' : 'Select All'}
            </button>
            <button type="button" onClick={() => handleBatchStatus('Delivered')} style={{ ...smallBtn, background: '#2E7D32', color: 'white' }}>Mark Selected Delivered</button>
            <button type="button" onClick={() => handleBatchStatus('Failed')} style={{ ...smallBtn, background: '#E53935', color: 'white' }}>Mark Selected Undelivered</button>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {currentLevelDeliveries.map(d => {
              const isDelivered = d.status === 'Delivered';
              const isFailed = d.status === 'Failed';
              const isPaused = d.isPaused;
              const isPending = !isDelivered && !isFailed && !isPaused;
              return (
                <div key={d.deliveryId} style={{ border: '1px solid #D7E3F4', borderRadius: 18, background: 'white', padding: '1rem', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 700 }}>
                      <input type="checkbox" checked={selected.has(d.deliveryId)} onChange={() => toggleSelect(d.deliveryId)} disabled={isPaused} />
                      {d.subscriberName}
                    </label>
                    <StatusBadge status={isPaused ? 'paused' : isDelivered ? 'delivered' : isFailed ? 'failed' : 'pending'} />
                  </div>
                  <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.35rem', color: '#334155' }}>
                    <div><strong>Flat No:</strong> <span style={isPaused ? { color: '#F9A825', fontWeight: 800 } : undefined}>{d.flatName || '—'}</span></div>
                    <div><strong>Milk:</strong> {d.milkTypeName}</div>
                    <div><strong>Qty:</strong> {d.quantity} L</div>
                    <div><strong>Date:</strong> {fmtDate(d.date)}</div>
                  </div>
                  {isPaused ? (
                    <div style={{ marginTop: '0.9rem', color: '#F9A825', fontWeight: 700 }}>⏸️ Delivery paused for this date</div>
                  ) : (
                    <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => api.batchUpdateDeliveryStatus([d.deliveryId], 'Delivered').then(res => { setMsg(res.message); load(); })} style={{ ...smallBtn, background: '#2E7D32', color: 'white' }}>Delivered</button>
                      <button type="button" onClick={() => api.batchUpdateDeliveryStatus([d.deliveryId], 'Failed').then(res => { setMsg(res.message); load(); })} style={{ ...smallBtn, background: '#E53935', color: 'white' }}>Undelivered</button>
                      {isAdmin && (
                        <button type="button" onClick={() => setCorrectionForm({ deliveryId: d.deliveryId, subscriberName: d.subscriberName, status: d.status, quantity: d.quantity, correctionReason: '' })} style={{ ...smallBtn, background: '#E65100', color: 'white' }}>Correct</button>
                      )}
                      <button type="button" onClick={() => openCorrectionLogs(d.deliveryId, d.subscriberName)} style={{ ...smallBtn, background: '#6A1B9A', color: 'white' }}>Logs</button>
                    </div>
                  )}
                  <div style={{ marginTop: '0.75rem', height: 10, borderRadius: 999, background: '#FFE0B2', overflow: 'hidden' }}>
                    <div style={{ width: isDelivered ? '100%' : isPending ? '0%' : '0%', height: '100%', background: isDelivered ? '#2E7D32' : '#FB8C00' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedFlat && currentLevelDeliveries.length === 0 && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: 12 }}>No deliveries found for this flat.</div>
      )}

      {correctionForm && (
        <div style={{ ...formStyle, marginTop: '1rem', background: '#FFF8E1', borderColor: '#FFCC80' }}>
          <form onSubmit={handleSubmitCorrection} style={{ display: 'grid', gap: '0.75rem' }}>
            <strong>Correct delivery for {correctionForm.subscriberName}</strong>
            <label>
              Status
              <select value={correctionForm.status} onChange={e => setCorrectionForm(prev => prev ? { ...prev, status: e.target.value } : prev)} style={inputStyle}>
                <option value="Pending">Pending</option>
                <option value="Delivered">Delivered</option>
                <option value="Failed">Failed</option>
              </select>
            </label>
            <label>
              Quantity
              <input type="number" value={correctionForm.quantity} onChange={e => setCorrectionForm(prev => prev ? { ...prev, quantity: Number(e.target.value) } : prev)} style={inputStyle} />
            </label>
            <label>
              Reason
              <textarea value={correctionForm.correctionReason} onChange={e => setCorrectionForm(prev => prev ? { ...prev, correctionReason: e.target.value } : prev)} style={inputStyle} />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <GreenButton type="submit">Save Correction</GreenButton>
              <ActionBtn type="button" onClick={() => setCorrectionForm(null)} color="#757575">Cancel</ActionBtn>
            </div>
          </form>
        </div>
      )}

      {correctionLogsView && (
        <div style={{ ...formStyle, marginTop: '1rem', background: '#F3E5F5', borderColor: '#CE93D8' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: '#6A1B9A' }}>🧾 Correction Logs: {correctionLogsView.subscriberName}</strong>
            <button type="button" onClick={() => setCorrectionLogsView(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Close</button>
          </div>
          {correctionLogsView.logs.length === 0 && <div style={{ fontSize: '0.9rem', color: '#777' }}>No correction logs found.</div>}
          {correctionLogsView.logs.length > 0 && (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {correctionLogsView.logs.map(log => (
                <div key={log.deliveryCorrectionLogId} style={{ background: 'white', border: '1px solid #E1BEE7', borderRadius: '8px', padding: '0.6rem 0.8rem', fontSize: '0.85rem' }}>
                  <div><strong>{fmtDate(log.correctedDate)}</strong> by {log.correctedByName}</div>
                  <div>Status: {log.oldStatus} → {log.newStatus}</div>
                  <div>Quantity: {log.oldQuantity ?? '—'} → {log.newQuantity ?? '—'}</div>
                  {log.correctionReason && <div>Reason: {log.correctionReason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Billing Panel =====
function BillingPanel({ isAdmin, readOnly = false }: { isAdmin: boolean; readOnly?: boolean }) {
  const navigate = useNavigate();
  const [bills, setBills] = useState<BillDto[]>([]);
  const [subscribers, setSubscribers] = useState<SubscriberDto[]>([]);
  const [selectedSubId, setSelectedSubId] = useState(0);
  const [month, setMonth] = useState(currentMonth());
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [msg, setMsg] = useState('');
  const [billDetail, setBillDetail] = useState<BillDetailDto | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [billIssues, setBillIssues] = useState<BillIssueDto[]>([]);
  const [revokeForm, setRevokeForm] = useState<{ billId: number; reason: string } | null>(null);
  const [issueForm, setIssueForm] = useState<{ billId: number; description: string } | null>(null);
  const [resolveIssueForm, setResolveIssueForm] = useState<{ issueId: number; resolutionNote: string } | null>(null);
  const [confirmGenerateAll, setConfirmGenerateAll] = useState(false);
  const [confirmSuspendForBill, setConfirmSuspendForBill] = useState<{ subId: number; subName: string } | null>(null);
  const [confirmUndoPaid, setConfirmUndoPaid] = useState<number | null>(null);

  const load = async () => {
    if (isAdmin) {
      const [billRes, subRes, issueRes] = await Promise.all([
        api.getBills(),
        api.getSubscribers(),
        api.getBillIssues(),
      ]);
      if (billRes.data) setBills(billRes.data);
      if (!readOnly && subRes.data) setSubscribers(subRes.data);
      if (issueRes.data) setBillIssues(issueRes.data);
    } else {
      // Subscriber: get own subscriber record, then fetch bills by subscriberId
      const subRes = await api.getSubscribers();
      if (subRes.data && subRes.data.length > 0) {
        const mySubId = subRes.data[0].subscriberId;
        const billRes = await api.getBillsBySubscriber(mySubId);
        if (billRes.data) setBills(billRes.data);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const handleGenerateAll = async () => {
    setConfirmGenerateAll(true);
  };

  const handleGenerateAllConfirm = async () => {
    setConfirmGenerateAll(false);
    const res = await api.generateBills(month);
    setMsg(res.message);
    load();
  };

  const handleGenerateForSubscriber = async () => {
    if (!selectedSubId) { setMsg('Please select a subscriber'); return; }
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const isLastDay = today.getDate() === lastDayOfMonth;
    if (!isLastDay) {
      const sub = subscribers.find(s => s.subscriberId === selectedSubId);
      setConfirmSuspendForBill({ subId: selectedSubId, subName: sub?.userName || '' });
      return;
    }
    const res = await api.generateBillForSubscriber(selectedSubId);
    setMsg(res.message);
    load();
  };

  const handleGenerateForSubscriberConfirm = async () => {
    if (!confirmSuspendForBill) return;
    const today = new Date();
    const suspendRes = await api.suspendSubscriber(confirmSuspendForBill.subId, 'Auto-suspended from billing on ' + today.toISOString().split('T')[0]);
    setConfirmSuspendForBill(null);
    if (!suspendRes.success) { setMsg(suspendRes.message); return; }
    const res = await api.generateBillForSubscriber(confirmSuspendForBill.subId);
    setMsg(res.message);
    load();
  };

  const handleMarkPaid = async (billId: number) => {
    const res = await api.markBillPaid(billId);
    setMsg(res.message);
    load();
  };

  const handleUndoPaid = async (billId: number) => {
    setConfirmUndoPaid(billId);
  };

  const handleUndoPaidConfirm = async () => {
    if (confirmUndoPaid === null) return;
    const billId = confirmUndoPaid;
    setConfirmUndoPaid(null);
    const res = await api.undoBillPaid(billId);
    setMsg(res.message);
    load();
  };

  const handleRevokeBill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!revokeForm) return;
    const res = await api.revokeBill(revokeForm.billId, { reason: revokeForm.reason });
    setMsg(res.message);
    if (res.success) {
      setRevokeForm(null);
      load();
    }
  };

  const handleReGenerateBill = async (billId: number) => {
    const res = await api.reGenerateBill(billId);
    setMsg(res.message);
    load();
  };

  const handleRaiseIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueForm) return;
    const res = await api.raiseBillIssue(issueForm.billId, { description: issueForm.description });
    setMsg(res.message);
    if (res.success) {
      setIssueForm(null);
      load();
    }
  };

  const handleResolveIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolveIssueForm) return;
    const res = await api.resolveBillIssue(resolveIssueForm.issueId, { resolutionNote: resolveIssueForm.resolutionNote || undefined });
    setMsg(res.message);
    if (res.success) {
      setResolveIssueForm(null);
      load();
    }
  };

  const handleViewDetail = async (billId: number) => {
    setLoadingDetail(true);
    const res = await api.getBillDetail(billId);
    if (res.data) setBillDetail(res.data);
    else setMsg(res.message);
    setLoadingDetail(false);
  };

  const filteredBills = filterMonth
    ? bills.filter(b => billOverlapsMonth(b.startDate, b.endDate, filterMonth))
    : bills;

  const buildBillHtml = (detail: BillDetailDto) => {
    const outstanding = (detail.totalAmount - detail.paidAmount).toFixed(2);
    const tc = getComputedStyle(document.documentElement).getPropertyValue('--tc').trim() || '#2E7D32';
    return `<!DOCTYPE html><html><head><title>Bill #${detail.billId}</title><style>
body{font-family:Arial,sans-serif;margin:20px;color:#333}
.header{text-align:center;border-bottom:2px solid ${tc};padding-bottom:10px;margin-bottom:15px}
.header h1{color:${tc};margin:0;font-size:1.4em}
.header p{margin:2px 0;font-size:0.85em;color:#666}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:15px;font-size:0.9em}
.info-grid div{padding:4px 0}
.info-grid strong{color:${tc}}
table{width:100%;border-collapse:collapse;margin:10px 0;font-size:0.85em}
th{background:${tc};color:white;padding:6px 8px;text-align:left}
td{padding:5px 8px;border-bottom:1px solid #eee}
.total-row{font-weight:bold;background:#f5f5f5}
.status-paid{color:${tc};font-weight:bold}
.status-unpaid{color:#D32F2F;font-weight:bold}
.footer{text-align:center;margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:0.8em;color:#888}
@media print{body{margin:10px}button{display:none!important}}
</style></head><body>
<div class="header">
<h1>${detail.dairyName}</h1>
${detail.dairyAddress ? `<p>${detail.dairyAddress}</p>` : ''}
<p>${detail.dairyEmail}</p>
${detail.dairyUpiId ? `<p><strong>UPI:</strong> ${detail.dairyUpiId}</p>` : ''}
</div>
<div class="info-grid">
<div><strong>Bill #:</strong> ${detail.billId}</div>
<div><strong>Date:</strong> ${new Date(detail.createdDate).toLocaleDateString()}</div>
<div><strong>Subscriber:</strong> ${detail.subscriberName}</div>
<div><strong>Bill No:</strong> #${detail.billId}</div>
<div><strong>Phone:</strong> ${detail.subscriberPhone || '-'}</div>
<div><strong>Period:</strong> ${new Date(detail.startDate).toLocaleDateString()} – ${new Date(detail.endDate).toLocaleDateString()}</div>
<div><strong>Status:</strong> <span class="status-${detail.status.toLowerCase()}">${detail.status}</span></div>
</div>
<h3 style="color:${tc};margin:10px 0 5px">Daily Breakdown</h3>
<table><thead><tr><th>Date</th><th>Milk Type</th><th>Qty (L)</th><th>₹/L</th><th>Amount</th><th>Extra</th><th>Status</th></tr></thead><tbody>
${detail.lineItems.map(item => `<tr><td>${new Date(item.date).toLocaleDateString()}</td><td>${item.milkTypeName}</td><td>${item.quantity}</td><td>₹${item.pricePerLiter}</td><td>₹${item.amount.toFixed(2)}</td><td>${item.isExtra ? '⭐' : ''}</td><td><span class="status-${(item.status || '').toLowerCase()}">${item.status || 'Delivered'}</span></td></tr>`).join('')}
<tr class="total-row"><td colspan="5" style="text-align:right">Total:</td><td>₹${detail.totalAmount.toFixed(2)}</td><td></td></tr>
</tbody></table>
${detail.payments && detail.payments.length > 0 ? `<h3 style="color:${tc};margin:10px 0 5px">Payments</h3><table><thead><tr><th>Date</th><th>Amount</th><th>Mode</th></tr></thead><tbody>${detail.payments.map(p => `<tr><td>${new Date(p.paymentDate).toLocaleDateString()}</td><td>₹${p.amount.toFixed(2)}</td><td>${p.mode}</td></tr>`).join('')}</tbody></table>` : ''}
${detail.dairyQrCodeBase64 || detail.dairyUpiId ? `<h3 style="color:${tc};margin:10px 0 5px">Payment QR / UPI</h3><div style="display:flex;gap:16px;align-items:center;border:1px solid #eee;padding:10px;border-radius:8px">${detail.dairyQrCodeBase64 ? `<img src="${detail.dairyQrCodeBase64}" alt="UPI QR" style="width:120px;height:120px;border:1px solid #ddd;border-radius:8px"/>` : ''}<div>${detail.dairyUpiId ? `<div><strong>UPI ID:</strong> ${detail.dairyUpiId}</div>` : ''}<div style="font-size:0.85em;color:#666">Scan and pay using any UPI app</div></div></div>` : ''}
<div class="info-grid" style="margin-top:10px;font-size:1em">
<div><strong>Total:</strong> ₹${detail.totalAmount.toFixed(2)}</div>
<div><strong>Paid:</strong> ₹${detail.paidAmount.toFixed(2)}</div>
<div><strong>Outstanding:</strong> <span class="status-${Number(outstanding) > 0 ? 'unpaid' : 'paid'}">₹${outstanding}</span></div>
</div>
<div class="footer"><p>Thank you for your patronage!</p><p>Generated on ${new Date().toLocaleString()}</p></div>
</body></html>`;
  };

  const handleDownloadBill = (detail: BillDetailDto) => {
    const html = buildBillHtml(detail);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bill-${detail.billId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PanelHeader title={readOnly ? 'View Bills' : (isAdmin ? 'Billing Management' : 'My Bills')} count={filteredBills.length}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Month:</label>
          <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={inputStyle} />
          {filterMonth && <ActionBtn onClick={() => setFilterMonth('')} color="#757575">All</ActionBtn>}
        </div>
      </PanelHeader>
      {isAdmin && !readOnly && (
        <div style={{ ...formStyle, marginBottom: '1.25rem' }}>
          <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>Individual (1st to today):</strong>
          <select value={selectedSubId} onChange={e => setSelectedSubId(+e.target.value)} style={inputStyle}>
            <option value={0}>Select Subscriber</option>
            {subscribers.map(s => <option key={s.subscriberId} value={s.subscriberId}>{s.userName}</option>)}
          </select>
          <GreenButton onClick={handleGenerateForSubscriber}>Generate Bill</GreenButton>
          <span style={{ color: '#888', fontSize: '0.85rem' }}>|</span>
          <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>All subscribers:</strong>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={inputStyle} />
          <GreenButton onClick={handleGenerateAll}>Generate All Bills</GreenButton>
        </div>
      )}

      {revokeForm && (
        <form onSubmit={handleRevokeBill} style={{ ...formStyle, marginBottom: '1rem', background: '#FFF3E0', borderColor: '#FFCC80' }}>
          <strong style={{ color: '#E65100' }}>Revoke Bill #{revokeForm.billId}</strong>
          <input
            value={revokeForm.reason}
            onChange={e => setRevokeForm({ ...revokeForm, reason: e.target.value })}
            placeholder="Reason for revoke"
            required
            style={{ ...inputStyle, minWidth: '260px' }}
          />
          <button type="submit" style={{ ...btnStyle, background: '#8D6E63' }}>Submit Revoke</button>
          <button type="button" onClick={() => setRevokeForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {issueForm && (
        <form onSubmit={handleRaiseIssue} style={{ ...formStyle, marginBottom: '1rem', background: '#E3F2FD', borderColor: '#90CAF9' }}>
          <strong style={{ color: '#1565C0' }}>Raise Issue for Bill #{issueForm.billId}</strong>
          <input
            value={issueForm.description}
            onChange={e => setIssueForm({ ...issueForm, description: e.target.value })}
            placeholder="Describe issue"
            required
            style={{ ...inputStyle, minWidth: '280px' }}
          />
          <GreenButton type="submit">Submit Issue</GreenButton>
          <button type="button" onClick={() => setIssueForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}

      {confirmGenerateAll && (
        <ConfirmModal
          title="Generate All Bills"
          message="Please generate all bills at end of month or 1st day of next month. Do you want to continue?"
          onConfirm={handleGenerateAllConfirm}
          onCancel={() => setConfirmGenerateAll(false)}
        />
      )}

      {confirmSuspendForBill && (
        <ConfirmModal
          title="Suspend Subscriber for Billing"
          message={`I will generate bill assuming this subscriber ${confirmSuspendForBill.subName} is suspended from tomorrow for delivery.`}
          onConfirm={handleGenerateForSubscriberConfirm}
          onCancel={() => setConfirmSuspendForBill(null)}
          danger
        />
      )}

      {confirmUndoPaid !== null && (
        <ConfirmModal
          title="Undo Payment"
          message="Are you sure you want to undo this payment? All payment records for this bill will be removed."
          onConfirm={handleUndoPaidConfirm}
          onCancel={() => setConfirmUndoPaid(null)}
          danger
        />
      )}

      {resolveIssueForm && (
        <form onSubmit={handleResolveIssue} style={{ ...formStyle, marginBottom: '1rem', background: '#E8F5E9', borderColor: '#A5D6A7' }}>
          <strong style={{ color: 'var(--tc)' }}>Resolve Issue #{resolveIssueForm.issueId}</strong>
          <input
            value={resolveIssueForm.resolutionNote}
            onChange={e => setResolveIssueForm({ ...resolveIssueForm, resolutionNote: e.target.value })}
            placeholder="Resolution note (optional)"
            style={{ ...inputStyle, minWidth: '280px' }}
          />
          <GreenButton type="submit">Resolve</GreenButton>
          <button type="button" onClick={() => setResolveIssueForm(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      {msg && <MsgBanner>{msg}</MsgBanner>}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          <th style={thStyle}>Bill #</th>
          {isAdmin && <th style={thStyle}>Subscriber</th>}
          <th style={thStyle}>Period</th>
          <th style={thStyle}>Total</th><th style={thStyle}>Paid</th><th style={thStyle}>Due</th><th style={thStyle}>Status</th>
          <th style={thStyle}>Actions</th>
        </tr></thead>
        <tbody>
          {filteredBills.map(b => (
            <tr key={b.billId} style={trStyle}>
              <td style={tdStyle}><strong>#{b.billId}</strong></td>
              {isAdmin && <td style={tdStyle}><strong>{b.subscriberName}</strong></td>}
              <td style={tdStyle}>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</td>
              <td style={tdStyle}>₹{b.totalAmount.toFixed(2)}</td>
              <td style={tdStyle}>₹{b.paidAmount.toFixed(2)}</td>
              <td style={tdStyle}><strong>₹{(b.totalAmount - b.paidAmount).toFixed(2)}</strong></td>
              <td style={tdStyle}><StatusBadge status={b.status === 'Paid' ? 'paid' : b.status === 'Revoked' ? 'revoked' : 'unpaid'} /></td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  <ActionBtn onClick={() => handleViewDetail(b.billId)} color="#1565C0">{loadingDetail ? '⏳' : '👁 View Bill'}</ActionBtn>
                  {isAdmin && !readOnly && b.status !== 'Paid' && (
                    <ActionBtn onClick={() => handleMarkPaid(b.billId)} color="var(--tc-btn)">✓ Mark Paid</ActionBtn>
                  )}
                  {isAdmin && !readOnly && b.status === 'Paid' && (
                    <ActionBtn onClick={() => handleUndoPaid(b.billId)} color="#E65100">↩ Undo</ActionBtn>
                  )}
                  {isAdmin && !readOnly && b.status !== 'Revoked' && (
                    <ActionBtn onClick={() => setRevokeForm({ billId: b.billId, reason: '' })} color="#8D6E63">Revoke</ActionBtn>
                  )}
                  {isAdmin && !readOnly && b.status === 'Revoked' && (
                    <ActionBtn onClick={() => handleReGenerateBill(b.billId)} color="#6A1B9A">Re-Generate</ActionBtn>
                  )}
                  <ActionBtn onClick={() => setIssueForm({ billId: b.billId, description: '' })} color="#1565C0">Raise Issue</ActionBtn>
                </div>
              </td>
            </tr>
          ))}
          {filteredBills.length === 0 && <EmptyRow colSpan={isAdmin ? 9 : 8} text="No bills for this month." />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {filteredBills.map(b => (
          <div key={b.billId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">Bill #{b.billId}</span>
              <StatusBadge status={b.status === 'Paid' ? 'paid' : b.status === 'Revoked' ? 'revoked' : 'unpaid'} />
            </div>
            <div className="mobile-card-details">
              {isAdmin && <div><span className="mobile-card-detail-label">Subscriber: </span><strong>{b.subscriberName}</strong></div>}
              <div><span className="mobile-card-detail-label">Period: </span>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</div>
              <div><span className="mobile-card-detail-label">Amount: </span>₹{b.totalAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Paid: </span>₹{b.paidAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Due: </span><strong>₹{(b.totalAmount - b.paidAmount).toFixed(2)}</strong></div>
            </div>
            <div className="mobile-card-actions">
              <button style={{ background: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9' }} onClick={() => navigate(`/bill/${b.billId}`)}>{loadingDetail ? '⏳' : '👁 View'}</button>
              {isAdmin && !readOnly && b.status !== 'Paid' && (
                <button style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)' }} onClick={() => handleMarkPaid(b.billId)}>✓ Mark Paid</button>
              )}
              {isAdmin && !readOnly && b.status === 'Paid' && (
                <button style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80' }} onClick={() => handleUndoPaid(b.billId)}>↩ Undo</button>
              )}
              {isAdmin && !readOnly && b.status !== 'Revoked' && (
                <button style={{ background: '#EFEBE9', color: '#8D6E63', border: '1px solid #BCAAA4' }} onClick={() => setRevokeForm({ billId: b.billId, reason: '' })}>Revoke</button>
              )}
              {isAdmin && !readOnly && b.status === 'Revoked' && (
                <button style={{ background: '#F3E5F5', color: '#6A1B9A', border: '1px solid #CE93D8' }} onClick={() => handleReGenerateBill(b.billId)}>Re-Generate</button>
              )}
              <button style={{ background: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9' }} onClick={() => setIssueForm({ billId: b.billId, description: '' })}>Raise Issue</button>
            </div>
          </div>
        ))}
      </div>
      </div>

      {isAdmin && billIssues.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', marginBottom: '0.5rem' }}>Bill Issues</h3>
          <TableWrap>
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <th style={thStyle}>Issue</th><th style={thStyle}>Bill</th><th style={thStyle}>Subscriber</th>
                <th style={thStyle}>Status</th><th style={thStyle}>Submitted</th><th style={thStyle}>Resolution</th><th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>
                {billIssues.map(issue => (
                  <tr key={issue.billIssueId} style={trStyle}>
                    <td style={tdStyle}>{issue.description}</td>
                    <td style={tdStyle}>#{issue.billId}</td>
                    <td style={tdStyle}>{issue.subscriberName}</td>
                    <td style={tdStyle}><StatusBadge status={issue.status.toLowerCase()} /></td>
                    <td style={tdStyle}>{fmtDate(issue.submittedDate)}</td>
                    <td style={tdStyle}>{issue.resolutionNote || '—'}</td>
                    <td style={tdStyle}>
                      {issue.status === 'Open' && (
                        <ActionBtn onClick={() => setResolveIssueForm({ issueId: issue.billIssueId, resolutionNote: '' })} color="var(--tc-btn)">Resolve</ActionBtn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </div>
      )}

      {/* Bill Detail Popup */}
      {billDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setBillDetail(null)}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '700px', width: '95%',
            maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: 'var(--tc)', margin: 0 }}>🧾 Bill Detail</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => {
                  const printWin = window.open('', '_blank', 'width=800,height=900');
                  if (!printWin) return;
                  printWin.document.write(buildBillHtml(billDetail));
                  printWin.document.close();
                  printWin.focus();
                  printWin.print();
                }} style={{ ...smallBtn, background: '#1565C0', color: 'white', fontSize: '0.85rem', padding: '0.3rem 0.8rem' }}>🖨️ Print</button>
                <button onClick={() => handleDownloadBill(billDetail)} style={{ ...smallBtn, background: 'var(--tc)', color: 'white', fontSize: '0.85rem', padding: '0.3rem 0.8rem' }}>⬇ Download</button>
                <button onClick={() => setBillDetail(null)} style={{ ...smallBtn, fontSize: '1.1rem', padding: '0.2rem 0.6rem' }}>✕</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
              <div><strong>Bill #:</strong> {billDetail.billId}</div>
              <div><strong>Subscriber:</strong> {billDetail.subscriberName}</div>
              <div><strong>Dairy:</strong> {billDetail.dairyName}</div>
              <div><strong>Period:</strong> {fmtDate(billDetail.startDate)} – {fmtDate(billDetail.endDate)}</div>
              <div><strong>Status:</strong> <StatusBadge status={billDetail.status === 'Paid' ? 'paid' : 'unpaid'} /></div>
              {billDetail.dairyUpiId && <div><strong>UPI ID:</strong> {billDetail.dairyUpiId}</div>}
              <div><strong>Total:</strong> <span style={{ color: 'var(--tc)', fontWeight: 700 }}>₹{billDetail.totalAmount.toFixed(2)}</span></div>
              <div><strong>Paid:</strong> ₹{billDetail.paidAmount.toFixed(2)}</div>
              <div><strong>Outstanding:</strong> <span style={{ color: (billDetail.totalAmount - billDetail.paidAmount) > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 700 }}>₹{(billDetail.totalAmount - billDetail.paidAmount).toFixed(2)}</span></div>
            </div>
            {billDetail.dairyQrCodeBase64 && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #E0E0E0', borderRadius: '10px', display: 'inline-block' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>Payment QR</div>
                <img src={billDetail.dairyQrCodeBase64} alt="Payment QR" style={{ width: '140px', height: '140px', borderRadius: '8px' }} />
              </div>
            )}
            <h3 style={{ color: 'var(--tc)', margin: '0.75rem 0 0.5rem' }}>Daily Breakdown</h3>
            <TableWrap>
              <table style={{ ...tableStyle, fontSize: '0.85rem' }}>
                <thead><tr style={thRowStyle}>
                  <th style={thStyle}>Date</th><th style={thStyle}>Milk Type</th><th style={thStyle}>Qty (L)</th><th style={thStyle}>₹/L</th><th style={thStyle}>Amount</th><th style={thStyle}>Extra</th>
                </tr></thead>
                <tbody>
                  {billDetail.lineItems.map((item, i) => (
                    <tr key={i} style={trStyle}>
                      <td style={tdStyle}>{fmtDate(item.date)}</td>
                      <td style={tdStyle}>{item.milkTypeName}</td>
                      <td style={tdStyle}>{item.quantity}</td>
                      <td style={tdStyle}>₹{item.pricePerLiter}</td>
                      <td style={tdStyle}>₹{item.amount.toFixed(2)}</td>
                      <td style={tdStyle}>{item.isExtra ? '⭐' : ''}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </TableWrap>

            {/* Payments */}
            {billDetail.payments && billDetail.payments.length > 0 && (
              <>
                <h3 style={{ color: 'var(--tc)', margin: '0.75rem 0 0.5rem' }}>Payments</h3>
                <TableWrap>
                  <table style={{ ...tableStyle, fontSize: '0.85rem' }}>
                    <thead><tr style={thRowStyle}>
                      <th style={thStyle}>Date</th><th style={thStyle}>Amount</th><th style={thStyle}>Mode</th>
                    </tr></thead>
                    <tbody>
                      {billDetail.payments.map((p, i) => (
                        <tr key={i} style={trStyle}>
                          <td style={tdStyle}>{fmtDate(p.paymentDate)}</td>
                          <td style={tdStyle}>₹{p.amount.toFixed(2)}</td>
                          <td style={tdStyle}>{p.mode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              </>
            )}

            {/* Subscription Changes */}
            {billDetail.subscriptionChanges && billDetail.subscriptionChanges.length > 0 && (
              <>
                <h3 style={{ color: 'var(--tc)', margin: '0.75rem 0 0.5rem' }}>Subscription Changes</h3>
                <TableWrap>
                  <table style={{ ...tableStyle, fontSize: '0.85rem' }}>
                    <thead><tr style={thRowStyle}>
                      <th style={thStyle}>Date</th><th style={thStyle}>Old</th><th style={thStyle}>New</th>
                    </tr></thead>
                    <tbody>
                      {billDetail.subscriptionChanges.map((c, i) => (
                        <tr key={i} style={trStyle}>
                          <td style={tdStyle}>{fmtDate(c.effectiveDate)}</td>
                          <td style={tdStyle}>{c.oldMilkTypeName} × {c.oldQuantity}L</td>
                          <td style={tdStyle}>{c.newMilkTypeName} × {c.newQuantity}L</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Payments Panel =====
function PaymentsPanel({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const [bills, setBills] = useState<BillDto[]>([]);
  const [form, setForm] = useState({ billId: 0, amount: 0, mode: 'Cash' });
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [msg, setMsg] = useState('');
  const [reminderLogs, setReminderLogs] = useState<ReminderLogDto[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState<number | null>(null);
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [tenantFilter, setTenantFilter] = useState<number | null>(null);
  const [consolidatedBills, setConsolidatedBills] = useState<ConsolidatedBillDto[]>([]);
  const [expandedSubscriberId, setExpandedSubscriberId] = useState<number | null>(null);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<number>>(new Set());
  const [sendingConsolidatedId, setSendingConsolidatedId] = useState<number | null>(null);
  const [logFilterName, setLogFilterName] = useState('');
  const [logFilterFrom, setLogFilterFrom] = useState('');
  const [logFilterTo, setLogFilterTo] = useState('');
  const [billSearch, setBillSearch] = useState('');
  const [billSortKey, setBillSortKey] = useState('period');
  const [billSortDir, setBillSortDir] = useState<'asc' | 'desc'>('asc');
  const [subscriberMap, setSubscriberMap] = useState<Map<number, { society: string; building: string; flat: string }>>(new Map());
  const [drillSociety, setDrillSociety] = useState('');
  const [drillBuilding, setDrillBuilding] = useState('');
  const [filterSociety, setFilterSociety] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [confirmUndoPayment, setConfirmUndoPayment] = useState<number | null>(null);
  const [confirmSendAllReminders, setConfirmSendAllReminders] = useState(false);
  const toggleBillSort = (key: string) => {
    if (billSortKey === key) setBillSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setBillSortKey(key); setBillSortDir('asc'); }
  };

  const load = async () => {
    const [billsRes, subscribersRes] = await Promise.all([api.getBills(), api.getSubscribers()]);
    if (billsRes.data) setBills(billsRes.data);
    if (subscribersRes.data) {
      const map = new Map<number, { society: string; building: string; flat: string }>();
      subscribersRes.data.forEach((s: any) => {
        map.set(s.subscriberId, {
          society: s.societyName || 'Unassigned',
          building: s.buildingName || 'Unassigned',
          flat: s.flatName || '',
        });
      });
      setSubscriberMap(map);
    }
  };
  const loadLogs = async () => {
    const res = await api.getReminderLogs({
      subscriberName: logFilterName || undefined,
      from: logFilterFrom || undefined,
      to: logFilterTo || undefined,
    });
    if (res.data) setReminderLogs(res.data);
  };
  const loadConsolidated = async () => {
    const res = await api.getConsolidatedBills(2);
    if (res.data) setConsolidatedBills(res.data.filter(c => c.unpaidBillCount >= 2));
  };
  useEffect(() => { load(); loadConsolidated(); }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      api.getTenants().then(res => { if (res.data) setTenants(res.data); });
    }
  }, [isSuperAdmin]);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.createPayment(form);
    setMsg(res.message);
    if (res.success) { setForm({ billId: 0, amount: 0, mode: 'Cash' }); load(); }
  };

  const handleMarkPaid = async (billId: number) => {
    const res = await api.markBillPaid(billId);
    setMsg(res.message);
    load();
  };

  const handleUndoPaid = async (billId: number) => {
    setConfirmUndoPayment(billId);
  };

  const handleUndoPaymentConfirm = async () => {
    if (confirmUndoPayment === null) return;
    const billId = confirmUndoPayment;
    setConfirmUndoPayment(null);
    const res = await api.undoBillPaid(billId);
    setMsg(res.message);
    load();
  };

  const handleSendReminderAll = async () => {
    setConfirmSendAllReminders(true);
  };

  const handleSendReminderAllConfirm = async () => {
    setConfirmSendAllReminders(false);
    setSendingAll(true);
    const res = await api.sendPaymentReminder({ sendEmail: true, sendNotification: true });
    setMsg(res.message);
    setSendingAll(false);
  };

  const handleSendReminderOne = async (subscriberId: number) => {
    setSendingId(subscriberId);
    const res = await api.sendPaymentReminder({ subscriberId, sendEmail: true, sendNotification: true });
    setMsg(res.message);
    setSendingId(null);
  };

  const billUrl = (billId: number) => `${window.location.origin}/bill/${billId}`;

  const openWaMe = (phone: string, message: string) => {
    const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  const handleSendWhatsApp = async (bill: BillDto) => {
    const phone = bill.subscriberPhone?.replace(/[^0-9]/g, '');
    if (!phone) {
      setMsg(`No phone number found for ${bill.subscriberName}`);
      return;
    }
    setSendingWhatsAppId(bill.subscriberId);
    try {
      const res = await api.sendBillWhatsApp(bill.billId);
      if (res.success) {
        setMsg(res.message);
        setSendingWhatsAppId(null);
        return;
      }
      const due = bill.totalAmount - bill.paidAmount;
      const message = `Dear ${bill.subscriberName},\n\nYour milk bill is pending:\nBill #${bill.billId}\nPeriod: ${fmtDate(bill.startDate)} to ${fmtDate(bill.endDate)}\nTotal: ₹${bill.totalAmount.toFixed(2)}\nPaid: ₹${bill.paidAmount.toFixed(2)}\nDue: ₹${due.toFixed(2)}\n\n🔗 View Bill: ${billUrl(bill.billId)}\n\nPlease pay at your earliest convenience.\n\nThank you!`;
      openWaMe(phone, message);
      setMsg(`WhatsApp Cloud not configured — opened ${bill.subscriberName}'s WhatsApp with bill info & link instead.`);
    } catch {
      setMsg('Failed to send WhatsApp. Please try again.');
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const handleSendConsolidatedWhatsApp = async (subscriberId: number) => {
    if (selectedBillIds.size === 0) { setMsg('Select at least one bill to include in the WhatsApp message'); return; }
    const subscriber = consolidatedBills.find(c => c.subscriberId === subscriberId);
    const phone = subscriber?.subscriberPhone?.replace(/[^0-9]/g, '');
    if (!phone) {
      setMsg(`No phone number found for ${subscriber?.subscriberName || 'this subscriber'}`);
      return;
    }
    setSendingWhatsAppId(subscriberId);
    try {
      const res = await api.sendConsolidatedWhatsApp({ subscriberId, billIds: Array.from(selectedBillIds) });
      if (res.success) {
        setMsg(res.message);
        setSendingWhatsAppId(null);
        return;
      }
      const selectedBills = bills.filter(b => selectedBillIds.has(b.billId));
      const totalDue = selectedBills.reduce((sum, b) => sum + (b.totalAmount - b.paidAmount), 0);
      let message = `Dear ${subscriber?.subscriberName},\n\nYour pending milk bills:\n`;
      selectedBills.forEach(b => {
        const due = b.totalAmount - b.paidAmount;
        message += `\nBill #${b.billId} | ${fmtDate(b.startDate)} to ${fmtDate(b.endDate)} | ₹${due.toFixed(2)} due\n🔗 ${billUrl(b.billId)}`;
      });
      message += `\n\nTotal Due: ₹${totalDue.toFixed(2)}\n\nPlease pay at your earliest convenience.\n\nThank you!`;
      openWaMe(phone, message);
      setMsg(`WhatsApp Cloud not configured — opened ${subscriber?.subscriberName || 'subscriber'}'s WhatsApp with bill info & links instead.`);
    } catch {
      setMsg('Failed to send WhatsApp. Please try again.');
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const toggleExpandSubscriber = (subscriberId: number, billIds: number[]) => {
    if (expandedSubscriberId === subscriberId) {
      setExpandedSubscriberId(null);
      setSelectedBillIds(new Set());
    } else {
      setExpandedSubscriberId(subscriberId);
      setSelectedBillIds(new Set(billIds)); // pre-select all unpaid bills for this subscriber
    }
  };

  const toggleBillSelection = (billId: number) => {
    setSelectedBillIds(prev => {
      const next = new Set(prev);
      if (next.has(billId)) next.delete(billId); else next.add(billId);
      return next;
    });
  };

  const handleSendConsolidatedReminder = async (subscriberId: number) => {
    if (selectedBillIds.size === 0) { setMsg('Select at least one bill to include in the reminder'); return; }
    setSendingConsolidatedId(subscriberId);
    const res = await api.sendConsolidatedReminder({ subscriberId, billIds: Array.from(selectedBillIds) });
    setMsg(res.message);
    setSendingConsolidatedId(null);
    if (res.success) { setExpandedSubscriberId(null); setSelectedBillIds(new Set()); if (showLogs) loadLogs(); }
  };

  const monthFiltered = bills.filter(b => {
    const matchesMonth = !filterMonth || billOverlapsMonth(b.startDate, b.endDate, filterMonth);
    const matchesTenant = !isSuperAdmin || tenantFilter === null || b.tenantId === tenantFilter;
    const matchesSearch = !billSearch || `${b.subscriberName} #${b.billId}`.toLowerCase().includes(billSearch.toLowerCase());
    return matchesMonth && matchesTenant && matchesSearch;
  });
  const sortBills = (list: BillDto[]) => [...list].sort((a, b) => {
    let cmp = 0;
    if (billSortKey === 'period') cmp = a.startDate.localeCompare(b.startDate);
    else if (billSortKey === 'total') cmp = a.totalAmount - b.totalAmount;
    else if (billSortKey === 'due') cmp = (a.totalAmount - a.paidAmount) - (b.totalAmount - b.paidAmount);
    return billSortDir === 'asc' ? cmp : -cmp;
  });
  const unpaidBills = sortBills(monthFiltered.filter(b => b.status === 'Unpaid'));
  const paidBills = sortBills(monthFiltered.filter(b => b.status === 'Paid'));

  const billLocation = (b: BillDto) => subscriberMap.get(b.subscriberId) || { society: 'Unassigned', building: 'Unassigned', flat: '' };

  const billSocieties = Array.from(new Set(unpaidBills.map(b => billLocation(b).society))).sort();
  const billBuildings = drillSociety
    ? Array.from(new Set(unpaidBills.filter(b => billLocation(b).society === drillSociety).map(b => billLocation(b).building))).sort()
    : filterSociety
      ? Array.from(new Set(unpaidBills.filter(b => billLocation(b).society === filterSociety).map(b => billLocation(b).building))).sort()
      : [];

  const mobileDrillBills = drillBuilding
    ? unpaidBills.filter(b => { const loc = billLocation(b); return loc.society === drillSociety && loc.building === drillBuilding; })
    : drillSociety
      ? unpaidBills.filter(b => billLocation(b).society === drillSociety)
      : [];

  const societyStats = billSocieties.map(society => {
    const items = unpaidBills.filter(b => billLocation(b).society === society);
    const totalDue = items.reduce((sum, b) => sum + (b.totalAmount - b.paidAmount), 0);
    return { society, count: items.length, totalDue };
  });

  const buildingStats = billBuildings.map(building => {
    const items = unpaidBills.filter(b => { const loc = billLocation(b); return loc.society === (drillSociety || filterSociety) && loc.building === building; });
    const totalDue = items.reduce((sum, b) => sum + (b.totalAmount - b.paidAmount), 0);
    return { building, count: items.length, totalDue };
  });

  const webFilteredBills = (list: BillDto[]) => {
    if (!filterSociety && !filterBuilding) return list;
    return list.filter(b => {
      const loc = billLocation(b);
      if (filterSociety && loc.society !== filterSociety) return false;
      if (filterBuilding && loc.building !== filterBuilding) return false;
      return true;
    });
  };

  const webUnpaidBills = webFilteredBills(unpaidBills);
  const webPaidBills = webFilteredBills(paidBills);

  const drillTotalDue = mobileDrillBills.reduce((sum, b) => sum + (b.totalAmount - b.paidAmount), 0);

  return (
    <div>
      {confirmUndoPayment !== null && (
        <ConfirmModal
          title="Undo Payment"
          message="Are you sure you want to undo this payment? All payment records for this bill will be removed."
          onConfirm={handleUndoPaymentConfirm}
          onCancel={() => setConfirmUndoPayment(null)}
          danger
        />
      )}
      {confirmSendAllReminders && (
        <ConfirmModal
          title="Send All Reminders"
          message="Send payment reminder to ALL unpaid subscribers via email & notification?"
          onConfirm={handleSendReminderAllConfirm}
          onCancel={() => setConfirmSendAllReminders(false)}
        />
      )}
      <PanelHeader title="Payments">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            value={billSearch}
            onChange={e => setBillSearch(e.target.value)}
            placeholder="🔍 Search by subscriber or bill #..."
            style={{ ...inputStyle, minWidth: '220px' }}
          />
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Month:</label>
          <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={inputStyle} />
          {filterMonth && <ActionBtn onClick={() => setFilterMonth('')} color="#757575">All</ActionBtn>}
          {!isSuperAdmin && (
            <ActionBtn onClick={() => { setShowLogs(!showLogs); if (!showLogs) loadLogs(); }} color="#6A1B9A">
              📋 {showLogs ? 'Hide' : 'Reminder'} Log
            </ActionBtn>
          )}
        </div>
      </PanelHeader>
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {isSuperAdmin && (
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>🏢 Filter by Tenant:</label>
          <select
            value={tenantFilter ?? ''}
            onChange={e => setTenantFilter(e.target.value ? Number(e.target.value) : null)}
            style={{ ...inputStyle, minWidth: '220px' }}
          >
            <option value="">All Tenants</option>
            {tenants.filter(t => t.isActive).map(t => (
              <option key={t.tenantId} value={t.tenantId}>{t.name}</option>
            ))}
          </select>
          {tenantFilter && (
            <ActionBtn onClick={() => setTenantFilter(null)} color="#757575">Clear</ActionBtn>
          )}
          <span style={{ fontSize: '0.8rem', color: '#888' }}>Showing {monthFiltered.length} bill(s)</span>
        </div>
      )}

      {/* Society + Building dropdown filters (web) */}
      <div className="responsive-table-wrap filter-row" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '0.75rem 1rem', background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid var(--tc-light)' }}>
        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>🏘️ Society:</label>
        <select
          value={filterSociety}
          onChange={e => { setFilterSociety(e.target.value); setFilterBuilding(''); }}
          style={{ ...inputStyle, minWidth: '180px' }}
        >
          <option value="">All Societies</option>
          {billSocieties.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>🏢 Building:</label>
        <select
          value={filterBuilding}
          onChange={e => setFilterBuilding(e.target.value)}
          disabled={!filterSociety}
          style={{ ...inputStyle, minWidth: '180px' }}
        >
          <option value="">{filterSociety ? 'All Buildings' : 'Select Society First'}</option>
          {billBuildings.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {(filterSociety || filterBuilding) && (
          <ActionBtn onClick={() => { setFilterSociety(''); setFilterBuilding(''); }} color="#757575">Clear</ActionBtn>
        )}
      </div>

      {/* Reminder Log Section */}
      {!isSuperAdmin && showLogs && (
        <div style={{ marginBottom: '1.25rem' }}>
          <PanelHeader title="Reminder Log (last 6 months)" count={reminderLogs.length} />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <input value={logFilterName} onChange={e => setLogFilterName(e.target.value)} placeholder="Filter by subscriber name" style={inputStyle} />
            <label style={{ fontSize: '0.85rem', color: '#555' }}>From:</label>
            <input type="date" value={logFilterFrom} onChange={e => setLogFilterFrom(e.target.value)} style={inputStyle} />
            <label style={{ fontSize: '0.85rem', color: '#555' }}>To:</label>
            <input type="date" value={logFilterTo} onChange={e => setLogFilterTo(e.target.value)} style={inputStyle} />
            <ActionBtn onClick={loadLogs} color="var(--tc-btn)">Apply Filter</ActionBtn>
            {(logFilterName || logFilterFrom || logFilterTo) && (
              <ActionBtn onClick={() => { setLogFilterName(''); setLogFilterFrom(''); setLogFilterTo(''); loadLogs(); }} color="#757575">Clear</ActionBtn>
            )}
          </div>
          <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <th style={thStyle}>Date</th><th style={thStyle}>Subscriber</th><th style={thStyle}>Amount Due</th>
                <th style={thStyle}>Type</th><th style={thStyle}>Sent By</th>
              </tr></thead>
              <tbody>
                {reminderLogs.map(r => (
                  <tr key={r.reminderLogId} style={trStyle}>
                    <td style={tdStyle}>{fmtDate(r.sentDate)}</td>
                    <td style={tdStyle}><strong>{r.subscriberName}</strong></td>
                    <td style={tdStyle}><strong style={{ color: '#C62828' }}>₹{r.amountDue.toFixed(2)}</strong></td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '0.78rem', fontWeight: 600,
                        background: r.type === 'Email' ? '#E3F2FD' : r.type === 'Notification' ? '#FFF3E0' : 'var(--tc-light)',
                        color: r.type === 'Email' ? '#1565C0' : r.type === 'Notification' ? '#E65100' : 'var(--tc)',
                      }}>{r.type === 'Both' ? '📧+🔔' : r.type === 'Email' ? '📧' : '🔔'} {r.type}</span>
                    </td>
                    <td style={tdStyle}>{r.sentByName}</td>
                  </tr>
                ))}
                {reminderLogs.length === 0 && <EmptyRow colSpan={5} text="No reminders sent yet." />}
              </tbody>
            </table>
            <div className="mobile-card-list">
              {reminderLogs.map(r => (
                <div key={r.reminderLogId} className="mobile-card-item">
                  <div className="mobile-card-header">
                    <span className="mobile-card-title">{r.subscriberName}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                      background: r.type === 'Email' ? '#E3F2FD' : r.type === 'Notification' ? '#FFF3E0' : 'var(--tc-light)',
                      color: r.type === 'Email' ? '#1565C0' : r.type === 'Notification' ? '#E65100' : 'var(--tc)',
                    }}>{r.type === 'Both' ? '📧+🔔' : r.type === 'Email' ? '📧' : '🔔'} {r.type}</span>
                  </div>
                  <div className="mobile-card-details">
                    <div><span className="mobile-card-detail-label">Date: </span>{fmtDate(r.sentDate)}</div>
                    <div><span className="mobile-card-detail-label">Amount Due: </span><strong style={{ color: '#C62828' }}>₹{r.amountDue.toFixed(2)}</strong></div>
                    <div><span className="mobile-card-detail-label">Sent By: </span>{r.sentByName}</div>
                  </div>
                </div>
              ))}
              {reminderLogs.length === 0 && <div style={{ padding: '1rem', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>No reminders sent yet.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Consolidated Reminders: subscribers with 2+ unpaid bills - select specific bills and send ONE combined reminder */}
      {!isSuperAdmin && consolidatedBills.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <PanelHeader title="Consolidated Reminders" count={consolidatedBills.length} />
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
            Subscribers below have multiple pending bills. Select the bills to include and send one combined reminder (itemized list + total + payment QR).
          </div>
          <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <th style={thStyle}>Subscriber</th><th style={thStyle}>Unpaid Bills</th><th style={thStyle}>Total Due</th>
                <th style={thStyle}>Period</th><th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>
                {consolidatedBills.map(c => (
                  <Fragment key={c.subscriberId}>
                    <tr style={trStyle}>
                      <td style={tdStyle}><strong>{c.subscriberName}</strong></td>
                      <td style={tdStyle}>{c.unpaidBillCount}</td>
                      <td style={tdStyle}><strong style={{ color: '#C62828' }}>₹{c.totalUnpaidAmount.toFixed(2)}</strong></td>
                      <td style={tdStyle}>{fmtDate(c.oldestBillDate)} – {fmtDate(c.latestBillDate)}</td>
                      <td style={tdStyle}>
                        <ActionBtn onClick={() => toggleExpandSubscriber(c.subscriberId, c.billIds)} color="#6A1B9A">
                          {expandedSubscriberId === c.subscriberId ? 'Close' : 'Select Bills'}
                        </ActionBtn>
                      </td>
                    </tr>
                    {expandedSubscriberId === c.subscriberId && (
                      <tr>
                        <td colSpan={5} style={{ ...tdStyle, background: '#FAF5FF' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            {bills.filter(b => c.billIds.includes(b.billId)).map(b => (
                              <label key={b.billId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                                <input type="checkbox" checked={selectedBillIds.has(b.billId)} onChange={() => toggleBillSelection(b.billId)} />
                                Bill #{b.billId} — {fmtDate(b.startDate)} to {fmtDate(b.endDate)} — <strong style={{ color: '#C62828' }}>₹{(b.totalAmount - b.paidAmount).toFixed(2)} due</strong>
                              </label>
                            ))}
                            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                              <ActionBtn onClick={() => handleSendConsolidatedReminder(c.subscriberId)} color="#D32F2F">
                                {sendingConsolidatedId === c.subscriberId ? '⏳ Sending...' : `📤 Send Consolidated Reminder (${selectedBillIds.size} selected)`}
                              </ActionBtn>
<ActionBtn onClick={() => handleSendConsolidatedWhatsApp(c.subscriberId)} color="#25D366">
                                {sendingWhatsAppId === c.subscriberId ? '📨 Sending WhatsApp...' : `📲 Send WhatsApp (${selectedBillIds.size} selected)`}
                              </ActionBtn>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <div className="mobile-card-list">
              {consolidatedBills.map(c => (
                <div key={c.subscriberId} className="mobile-card-item">
                  <div className="mobile-card-header">
                    <span className="mobile-card-title">{c.subscriberName}</span>
                    <span style={{ background: '#F3E5F5', color: '#6A1B9A', borderRadius: '10px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>{c.unpaidBillCount} bill(s)</span>
                  </div>
                  <div className="mobile-card-details">
                    <div><span className="mobile-card-detail-label">Unpaid Bills: </span>{c.unpaidBillCount}</div>
                    <div><span className="mobile-card-detail-label">Total Due: </span><strong style={{ color: '#C62828' }}>₹{c.totalUnpaidAmount.toFixed(2)}</strong></div>
                    <div><span className="mobile-card-detail-label">Period: </span>{fmtDate(c.oldestBillDate)} – {fmtDate(c.latestBillDate)}</div>
                  </div>
                  <div className="mobile-card-actions">
                    <button style={{ background: '#F3E5F5', color: '#6A1B9A', border: '1px solid #CE93D8' }} onClick={() => toggleExpandSubscriber(c.subscriberId, c.billIds)}>
                      {expandedSubscriberId === c.subscriberId ? 'Close' : 'Select Bills'}
                    </button>
                  </div>
                  {expandedSubscriberId === c.subscriberId && (
                    <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: '#FAF5FF', borderRadius: '8px', display: 'grid', gap: '0.4rem' }}>
                      {bills.filter(b => c.billIds.includes(b.billId)).map(b => (
                        <label key={b.billId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                          <input type="checkbox" checked={selectedBillIds.has(b.billId)} onChange={() => toggleBillSelection(b.billId)} />
                          Bill #{b.billId} — {fmtDate(b.startDate)} to {fmtDate(b.endDate)} — <strong style={{ color: '#C62828' }}>₹{(b.totalAmount - b.paidAmount).toFixed(2)} due</strong>
                        </label>
                      ))}
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        <button style={{ background: '#D32F2F', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, flex: 1, minWidth: '110px' }} onClick={() => handleSendConsolidatedReminder(c.subscriberId)}>
                          {sendingConsolidatedId === c.subscriberId ? '⏳ Sending...' : `📤 Send Reminder (${selectedBillIds.size})`}
                        </button>
<button style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, flex: 1, minWidth: '110px' }} onClick={() => handleSendConsolidatedWhatsApp(c.subscriberId)}>
                          {sendingWhatsAppId === c.subscriberId ? '📨 Sending...' : `📲 WhatsApp (${selectedBillIds.size})`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isSuperAdmin && (
        <div style={{ marginBottom: '1.25rem' }}>
          <PanelHeader title="Record Payment" />
          <form onSubmit={handlePay} style={formStyle}>
            <select value={form.billId} onChange={e => {
              const bill = bills.find(b => b.billId === +e.target.value);
              setForm({ ...form, billId: +e.target.value, amount: bill ? bill.totalAmount - bill.paidAmount : 0 });
            }} required style={inputStyle}>
              <option value={0}>Select Unpaid Bill</option>
              {bills.filter(b => b.status === 'Unpaid').map(b => <option key={b.billId} value={b.billId}>Bill #{b.billId} - {b.subscriberName} – ₹{(b.totalAmount - b.paidAmount).toFixed(2)} due</option>)}
            </select>
            <input type="number" step="0.01" value={form.amount || ''} onChange={e => setForm({ ...form, amount: +e.target.value })} placeholder="Amount (₹)" required style={inputStyle} />
            <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })} style={inputStyle}>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Bank Transfer">Bank Transfer</option>
            </select>
            <GreenButton type="submit">Record Payment</GreenButton>
          </form>
        </div>
      )}

      <PanelHeader title="Unpaid Bills" count={filterSociety || filterBuilding ? webUnpaidBills.length : unpaidBills.length}>
        {!isSuperAdmin && unpaidBills.length > 0 && (
          <ActionBtn onClick={handleSendReminderAll} color="#D32F2F">
            {sendingAll ? '⏳ Sending...' : '📤 Send Reminder on Email to All'}
          </ActionBtn>
        )}
      </PanelHeader>

      {/* Mobile drill-down: Society → Building → Bills */}
      <div className="mobile-card-list" style={{ padding: '0.75rem 0' }}>
        {/* Path summary bar */}
        {(drillSociety || drillBuilding) && (
          <div className="drilldown-breadcrumbs" style={{ padding: '0.7rem 1rem', borderRadius: 12, background: '#F8FBFF', border: '1px solid #D7E3F4', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.88rem' }}>
              <strong>{drillSociety}{drillBuilding ? ` / ${drillBuilding}` : ''}</strong>
              <span style={{ marginLeft: '0.75rem', color: '#555' }}>
                {mobileDrillBills.length} bill{mobileDrillBills.length !== 1 ? 's' : ''} — <strong style={{ color: '#C62828' }}>₹{drillTotalDue.toFixed(2)} due</strong>
              </span>
            </div>
            <button
              type="button"
              onClick={() => drillBuilding ? setDrillBuilding('') : setDrillSociety('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: '#1565C0', fontWeight: 600, padding: '0.2rem 0' }}
            >
              ← Back to {drillBuilding ? 'buildings' : 'societies'}
            </button>
          </div>
        )}

        {/* Level 1: Society chips */}
        {!drillSociety && societyStats.map(({ society, count, totalDue }) => {
          const percent = unpaidBills.length > 0 ? Math.round((count / unpaidBills.length) * 100) : 0;
          return (
            <button
              key={society}
              type="button"
              onClick={() => setDrillSociety(society)}
              style={getProgressStyle(percent, false, getSocietyColor(), 'society')}
            >
              <div style={{ display: 'grid', gap: '0.15rem' }}>
                <span>🏘️ {society}</span>
                <small>{count} bill{count !== 1 ? 's' : ''} — ₹{totalDue.toFixed(2)} due</small>
              </div>
            </button>
          );
        })}

        {/* Level 2: Building chips */}
        {drillSociety && !drillBuilding && buildingStats.map(({ building, count, totalDue }) => {
          const societyTotal = unpaidBills.filter(b => billLocation(b).society === drillSociety).length;
          const percent = societyTotal > 0 ? Math.round((count / societyTotal) * 100) : 0;
          return (
            <button
              key={building}
              type="button"
              onClick={() => setDrillBuilding(building)}
              style={getProgressStyle(percent, false, getBuildingColor(percent), 'building')}
            >
              <div style={{ display: 'grid', gap: '0.15rem' }}>
                <span>🏢 {building}</span>
                <small>{count} bill{count !== 1 ? 's' : ''} — ₹{totalDue.toFixed(2)} due</small>
              </div>
            </button>
          );
        })}

        {/* Level 3: Bill cards */}
        {drillSociety && drillBuilding && mobileDrillBills.map(b => (
          <div key={b.billId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">#{b.billId} — {b.subscriberName}</span>
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Period: </span>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</div>
              <div><span className="mobile-card-detail-label">Total: </span>₹{b.totalAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Paid: </span>₹{b.paidAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Due: </span><strong style={{ color: '#C62828' }}>₹{(b.totalAmount - b.paidAmount).toFixed(2)}</strong></div>
            </div>
            {!isSuperAdmin && (
              <div className="mobile-card-actions">
                <button style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)' }} onClick={() => handleMarkPaid(b.billId)}>✓ Mark Paid</button>
                <button style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80' }} onClick={() => handleSendReminderOne(b.subscriberId)}>{sendingId === b.subscriberId ? '⏳' : '📤'} Remind</button>
                <button style={{ background: '#E8F5E9', color: '#25D366', border: '1px solid #A5D6A7' }} onClick={() => handleSendWhatsApp(b)}>{sendingWhatsAppId === b.subscriberId ? '📨 Sending...' : '💬 WhatsApp'}</button>
              </div>
            )}
          </div>
        ))}
        {drillSociety && drillBuilding && mobileDrillBills.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>No unpaid bills for this building.</div>
        )}
      </div>

      {/* Web table: uses webUnpaidBills (filtered by society/building dropdowns) */}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          {isSuperAdmin && <th style={thStyle}>Tenant</th>}
          <th style={thStyle}>Bill ID</th>
          <th style={thStyle}>Subscriber</th>
          <th style={thStyle}>Society</th>
          <th style={thStyle}>Building</th>
          <SortableTh label="Period" sortKeyName="period" activeKey={billSortKey} dir={billSortDir} onSort={toggleBillSort} />
          <SortableTh label="Total" sortKeyName="total" activeKey={billSortKey} dir={billSortDir} onSort={toggleBillSort} />
          <th style={thStyle}>Paid</th>
          <SortableTh label="Due" sortKeyName="due" activeKey={billSortKey} dir={billSortDir} onSort={toggleBillSort} />
          {!isSuperAdmin && <th style={thStyle}>Actions</th>}
        </tr></thead>
        <tbody>
          {webUnpaidBills.map(b => {
            const loc = billLocation(b);
            return (
              <tr key={b.billId} style={trStyle}>
                {isSuperAdmin && <td style={tdStyle}><span style={{ background: 'var(--tc-light)', color: 'var(--tc)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>{b.tenantName || `Tenant #${b.tenantId}`}</span></td>}
                <td style={tdStyle}><strong>#{b.billId}</strong></td>
                <td style={tdStyle}><strong>{b.subscriberName}</strong></td>
                <td style={tdStyle}>{loc.society}</td>
                <td style={tdStyle}>{loc.building}</td>
                <td style={tdStyle}>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</td>
                <td style={tdStyle}>₹{b.totalAmount.toFixed(2)}</td>
                <td style={tdStyle}>₹{b.paidAmount.toFixed(2)}</td>
                <td style={tdStyle}><strong style={{ color: '#C62828' }}>₹{(b.totalAmount - b.paidAmount).toFixed(2)}</strong></td>
                {!isSuperAdmin && (
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      <ActionBtn onClick={() => handleMarkPaid(b.billId)} color="var(--tc-btn)">✓ Mark Paid</ActionBtn>
                      <ActionBtn onClick={() => handleSendReminderOne(b.subscriberId)} color="#E65100">
                        {sendingId === b.subscriberId ? '⏳' : '📤'} Remind on Email
                      </ActionBtn>
                      <ActionBtn onClick={() => handleSendWhatsApp(b)} color="#25D366">
                        {sendingWhatsAppId === b.subscriberId ? '📨 Sending...' : '📲 Send WhatsApp'}
                      </ActionBtn>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {webUnpaidBills.length === 0 && <EmptyRow colSpan={isSuperAdmin ? 7 : 8} text="All bills are paid for this month! 🎉" />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {webUnpaidBills.map(b => (
          <div key={b.billId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">#{b.billId} — {b.subscriberName}</span>
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Period: </span>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</div>
              <div><span className="mobile-card-detail-label">Total: </span>₹{b.totalAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Paid: </span>₹{b.paidAmount.toFixed(2)}</div>
              <div><span className="mobile-card-detail-label">Due: </span><strong style={{ color: '#C62828' }}>₹{(b.totalAmount - b.paidAmount).toFixed(2)}</strong></div>
            </div>
            {!isSuperAdmin && (
              <div className="mobile-card-actions">
                <button style={{ background: 'var(--tc-light)', color: 'var(--tc)', border: '1px solid var(--tc-border)' }} onClick={() => handleMarkPaid(b.billId)}>✓ Mark Paid</button>
                <button style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80' }} onClick={() => handleSendReminderOne(b.subscriberId)}>{sendingId === b.subscriberId ? '⏳' : '📤'} Remind</button>
                <button style={{ background: '#E8F5E9', color: '#25D366', border: '1px solid #A5D6A7' }} onClick={() => handleSendWhatsApp(b)}>{sendingWhatsAppId === b.subscriberId ? '📨 Sending...' : '💬 WhatsApp'}</button>
              </div>
            )}
          </div>
        ))}
      </div>
      </div>

      {webPaidBills.length > 0 && (
        <>
          <div style={{ marginTop: '1.5rem' }} />
          <PanelHeader title="Paid Bills" count={webPaidBills.length} />
          <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
          <table style={tableStyle}>
            <thead><tr style={thRowStyle}>
              {isSuperAdmin && <th style={thStyle}>Tenant</th>}
              <th style={thStyle}>Subscriber</th><th style={thStyle}>Period</th><th style={thStyle}>Total</th>
              <th style={thStyle}>Paid</th><th style={thStyle}>Status</th>
              {!isSuperAdmin && <th style={thStyle}>Actions</th>}
            </tr></thead>
            <tbody>
              {webPaidBills.map(b => (
                <tr key={b.billId} style={trStyle}>
                  {isSuperAdmin && <td style={tdStyle}><span style={{ background: 'var(--tc-light)', color: 'var(--tc)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>{b.tenantName || `Tenant #${b.tenantId}`}</span></td>}
                  <td style={tdStyle}><strong>{b.subscriberName}</strong></td>
                  <td style={tdStyle}>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</td>
                  <td style={tdStyle}>₹{b.totalAmount.toFixed(2)}</td>
                  <td style={tdStyle}>₹{b.paidAmount.toFixed(2)}</td>
                  <td style={tdStyle}><StatusBadge status="paid" /></td>
                  {!isSuperAdmin && (
                    <td style={tdStyle}>
                      <ActionBtn onClick={() => handleUndoPaid(b.billId)} color="#E65100">↩ Undo Payment</ActionBtn>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mobile-card-list">
            {webPaidBills.map(b => (
              <div key={b.billId} className="mobile-card-item">
                <div className="mobile-card-header">
                  <span className="mobile-card-title">{b.subscriberName}</span>
                  <StatusBadge status="paid" />
                </div>
                <div className="mobile-card-details">
                  {isSuperAdmin && <div><span className="mobile-card-detail-label">Tenant: </span>{b.tenantName || `Tenant #${b.tenantId}`}</div>}
                  <div><span className="mobile-card-detail-label">Period: </span>{fmtDate(b.startDate)} – {fmtDate(b.endDate)}</div>
                  <div><span className="mobile-card-detail-label">Total: </span>₹{b.totalAmount.toFixed(2)}</div>
                  <div><span className="mobile-card-detail-label">Paid: </span>₹{b.paidAmount.toFixed(2)}</div>
                </div>
                {!isSuperAdmin && (
                  <div className="mobile-card-actions">
                    <button style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80' }} onClick={() => handleUndoPaid(b.billId)}>↩ Undo</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </>
      )}
    </div>
  );
}

// ===== Reports Panel =====
function ReportsPanel({ isSuperAdmin }: { isSuperAdmin?: boolean }) {
  // SuperAdmin: show tenant-wise performance report
  if (isSuperAdmin) return <SuperAdminReportsPanel />;

  type ReportTab = 'daily' | 'monthly-delivery' | 'monthly-payment' | 'subscriber-wise' | 'yearly' | 'gst';
  const [reportTab, setReportTab] = useState<ReportTab>('daily');
  const [dailyReport, setDailyReport] = useState<DailyReportDto | null>(null);
  const [deliveryReport, setDeliveryReport] = useState<MonthlyDeliveryReportDto | null>(null);
  const [paymentReport, setPaymentReport] = useState<MonthlyPaymentReportDto | null>(null);
  const [subscriberReport, setSubscriberWiseReport] = useState<SubscriberWiseReportDto | null>(null);
  const [yearlyReport, setYearlyReport] = useState<YearlyReportDto | null>(null);
  const [gstReport, setGstReport] = useState<GstReportDto | null>(null);
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(today().substring(0, 7)); // "2026-04"
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const loadDaily = async () => {
    setLoading(true);
    const res = await api.getDailyReport(date);
    if (res.data) setDailyReport(res.data);
    setLoading(false);
  };

  const loadMonthlyDelivery = async () => {
    setLoading(true);
    const res = await api.getMonthlyDeliveryReport(month);
    if (res.data) setDeliveryReport(res.data);
    setLoading(false);
  };

  const loadMonthlyPayment = async () => {
    setLoading(true);
    const res = await api.getMonthlyPaymentReport(month);
    if (res.data) setPaymentReport(res.data);
    setLoading(false);
  };

  const loadSubscriberWise = async () => {
    setLoading(true);
    const res = await api.getSubscriberWiseReport(month);
    if (res.data) setSubscriberWiseReport(res.data);
    setLoading(false);
  };

  const loadYearly = async () => {
    setLoading(true);
    const res = await api.getYearlyReport(year);
    if (res.data) setYearlyReport(res.data);
    setLoading(false);
  };

  const loadGst = async () => {
    setLoading(true);
    const res = await api.getGstReport(month);
    if (res.data) setGstReport(res.data);
    setLoading(false);
  };

  useEffect(() => {
    if (reportTab === 'daily') loadDaily();
    else if (reportTab === 'monthly-delivery') loadMonthlyDelivery();
    else if (reportTab === 'monthly-payment') loadMonthlyPayment();
    else if (reportTab === 'subscriber-wise') loadSubscriberWise();
    else if (reportTab === 'yearly') loadYearly();
    else if (reportTab === 'gst') loadGst();
  }, [reportTab, date, month, year]);

  const reportTabs: { key: ReportTab; label: string; icon: string }[] = [
    { key: 'daily', label: 'Daily', icon: '📅' },
    { key: 'monthly-delivery', label: 'Monthly Delivery', icon: '🚚' },
    { key: 'monthly-payment', label: 'Monthly Payment', icon: '💰' },
    { key: 'subscriber-wise', label: 'Subscriber Wise', icon: '👥' },
    { key: 'yearly', label: 'Yearly', icon: '📈' },
    { key: 'gst', label: 'GST', icon: '🧾' },
  ];

  const subTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.5rem 1rem', border: 'none', borderRadius: '8px 8px 0 0',
    background: active ? 'var(--tc)' : 'var(--tc-light)', color: active ? 'white' : 'var(--tc)',
    cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: '0.85rem',
    transition: 'all 0.2s',
  });

  // Helper: bar component for simple chart
  const Bar = ({ value, max, color, label }: { value: number; max: number; color: string; label: string }) => {
    const pct = max > 0 ? (value / max) * 100 : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
        <div style={{ width: '100px', fontSize: '0.8rem', color: '#555', textAlign: 'right' }}>{label}</div>
        <div style={{ flex: 1, background: 'var(--tc-light)', borderRadius: '4px', height: '22px', overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(pct, 1)}%`, background: color, height: '100%', borderRadius: '4px', transition: 'width 0.5s' }} />
        </div>
        <div style={{ width: '80px', fontSize: '0.8rem', fontWeight: 600 }}>{value.toLocaleString()}</div>
      </div>
    );
  };

  const handleDownloadReport = () => {
    if (reportTab === 'daily' && dailyReport) {
      const rows: Array<Array<string | number>> = [
        ['Date', 'Subscriber', 'Milk Type', 'Quantity (L)', 'Status'],
        ...dailyReport.deliveries.map(d => [
          new Date(d.date).toLocaleDateString('en-IN'),
          d.subscriberName,
          d.milkTypeName,
          d.quantity,
          d.status,
        ]),
      ];
      downloadCsvFile(`daily-report-${date}.csv`, rows);
      return;
    }

    if (reportTab === 'monthly-delivery' && deliveryReport) {
      const rows: Array<Array<string | number>> = [
        ['Date', 'Delivered', 'Skipped', 'Quantity (L)', 'Revenue'],
        ...deliveryReport.dailyBreakdown.map(d => [
          new Date(d.date).toLocaleDateString('en-IN'),
          d.delivered,
          d.skipped,
          d.quantity,
          d.revenue,
        ]),
      ];
      downloadCsvFile(`monthly-delivery-report-${month}.csv`, rows);
      return;
    }

    if (reportTab === 'monthly-payment' && paymentReport) {
      const rows: Array<Array<string | number>> = [
        ['Payment Date', 'Subscriber', 'Amount', 'Mode', 'Bill ID'],
        ...paymentReport.recentPayments.map(p => [
          new Date(p.paymentDate).toLocaleDateString('en-IN'),
          p.subscriberName,
          p.amount,
          p.mode,
          p.billId,
        ]),
      ];
      downloadCsvFile(`monthly-payment-report-${month}.csv`, rows);
      return;
    }

    if (reportTab === 'subscriber-wise' && subscriberReport) {
      const rows: Array<Array<string | number>> = [
        ['Subscriber', 'Milk Type', 'Qty/Day', 'Delivered', 'Skipped', 'Paused', 'Total Qty', 'Total Amount', 'Paid', 'Outstanding'],
        ...subscriberReport.subscribers.map(s => [
          s.name,
          s.milkType,
          s.dailyQuantity,
          s.deliveredDays,
          s.skippedDays,
          s.pausedDays,
          s.totalQuantity,
          s.totalAmount,
          s.paidAmount,
          s.outstandingAmount,
        ]),
      ];
      downloadCsvFile(`subscriber-wise-report-${month}.csv`, rows);
      return;
    }

    if (reportTab === 'yearly' && yearlyReport) {
      const rows: Array<Array<string | number>> = [
        ['Month', 'Revenue', 'Collected', 'Outstanding', 'Quantity (L)', 'Delivered', 'Skipped'],
        ...yearlyReport.months.map(m => [
          m.month,
          m.revenue,
          m.collected,
          m.outstanding,
          m.quantity,
          m.deliveredCount,
          m.skippedCount,
        ]),
      ];
      downloadCsvFile(`yearly-report-${year}.csv`, rows);
      return;
    }

    if (reportTab === 'gst' && gstReport) {
      const rows: Array<Array<string | number>> = [
        ['Month', 'Taxable Amount', 'GST %', 'CGST', 'SGST', 'Invoice Total', 'Collected', 'Outstanding', 'Total Bills', 'Paid Bills', 'Unpaid Bills'],
        [
          gstReport.month,
          gstReport.taxableAmount,
          gstReport.gstRatePercent,
          gstReport.cgstAmount,
          gstReport.sgstAmount,
          gstReport.totalInvoiceAmount,
          gstReport.totalCollected,
          gstReport.outstandingAmount,
          gstReport.totalBills,
          gstReport.paidBills,
          gstReport.unpaidBills,
        ],
      ];
      downloadCsvFile(`gst-report-${month}.csv`, rows);
    }
  };

  return (
    <div>
      <PanelHeader title="Reports">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {reportTab === 'daily' ? (
            <DateInput value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          ) : reportTab === 'yearly' ? (
            <input
              type="number"
              min="2020"
              max="2100"
              value={year}
              onChange={e => setYear(Math.max(2020, Math.min(2100, Number(e.target.value) || new Date().getFullYear())))}
              style={inputStyle}
            />
          ) : (
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={inputStyle} />
          )}
          <GreenButton onClick={handleDownloadReport}>⬇ Download</GreenButton>
          <ActionBtn onClick={() => {
            if (reportTab === 'daily') loadDaily();
            else if (reportTab === 'monthly-delivery') loadMonthlyDelivery();
            else if (reportTab === 'monthly-payment') loadMonthlyPayment();
            else if (reportTab === 'subscriber-wise') loadSubscriberWise();
            else if (reportTab === 'yearly') loadYearly();
            else if (reportTab === 'gst') loadGst();
          }} color="#1565C0">🔄 Refresh</ActionBtn>
        </div>
      </PanelHeader>

      {/* Sub-tabs */}
      <div className="sub-tab-bar" style={{ display: 'flex', gap: '2px', marginBottom: '0', overflowX: 'auto' }}>
        {reportTabs.map(t => (
          <button key={t.key} onClick={() => setReportTab(t.key)} style={subTabStyle(reportTab === t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <div style={{ background: 'white', borderRadius: '0 8px 8px 8px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', minHeight: '300px' }}>
        {loading ? <LoadingSpinner /> : (
          <>
            {/* ===== Daily Report ===== */}
            {reportTab === 'daily' && dailyReport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Total Deliveries" value={dailyReport.totalDeliveries} color="var(--tc)" />
                  <StatCard label="Skipped" value={dailyReport.skippedDeliveries} color="#F57C00" />
                  <StatCard label="Total Quantity" value={`${dailyReport.totalQuantity} L`} color="var(--tc-btn)" />
                </div>
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}><th style={thStyle}>Subscriber</th><th style={thStyle}>Milk</th><th style={thStyle}>Qty</th><th style={thStyle}>Status</th></tr></thead>
                  <tbody>
                    {dailyReport.deliveries.map(d => (
                      <tr key={d.deliveryId} style={trStyle}>
                        <td style={tdStyle}><strong>{d.subscriberName}</strong></td>
                        <td style={tdStyle}>{d.milkTypeName}</td>
                        <td style={tdStyle}>{d.quantity} L</td>
                        <td style={tdStyle}><StatusBadge status={d.status === 'Delivered' ? 'delivered' : 'skipped'} /></td>
                      </tr>
                    ))}
                    {dailyReport.deliveries.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>No deliveries for this date</td></tr>}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {dailyReport.deliveries.map(d => (
                    <div key={d.deliveryId} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{d.subscriberName}</span>
                        <StatusBadge status={d.status === 'Delivered' ? 'delivered' : 'skipped'} />
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Milk: </span>{d.milkTypeName}</div>
                        <div><span className="mobile-card-detail-label">Qty: </span>{d.quantity} L</div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}

            {/* ===== Monthly Delivery Report ===== */}
            {reportTab === 'monthly-delivery' && deliveryReport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Total Deliveries" value={deliveryReport.totalDeliveries} color="var(--tc)" />
                  <StatCard label="Skipped" value={deliveryReport.skippedDeliveries} color="#F57C00" />
                  <StatCard label="Quantity Delivered" value={`${deliveryReport.totalQuantityDelivered.toFixed(1)} L`} color="var(--tc-btn)" />
                  <StatCard label="Revenue" value={`₹${deliveryReport.totalRevenue.toFixed(0)}`} color="#1565C0" />
                  <StatCard label="Active Subscribers" value={deliveryReport.activeSubscribers} color="#6A1B9A" />
                </div>

                {/* Milk Type Breakdown */}
                {deliveryReport.milkTypeBreakdown.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: 'var(--tc)', fontSize: '1rem', marginBottom: '0.75rem' }}>🥛 Milk Type Breakdown</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      {deliveryReport.milkTypeBreakdown.map(mt => (
                        <div key={mt.milkType} style={{ background: 'var(--tc-vlight)', borderRadius: '10px', padding: '1rem', border: '1px solid var(--tc-border)' }}>
                          <div style={{ fontWeight: 700, color: 'var(--tc)', marginBottom: '0.5rem' }}>{mt.milkType}</div>
                          <div style={{ fontSize: '0.85rem', color: '#555' }}>
                            <div>📦 {mt.deliveryCount} deliveries</div>
                            <div>🥛 {mt.quantity.toFixed(1)} L</div>
                            <div>💰 ₹{mt.revenue.toFixed(0)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Breakdown Chart */}
                {deliveryReport.dailyBreakdown.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h3 style={{ color: 'var(--tc)', fontSize: '1rem', marginBottom: '0.75rem' }}>📊 Daily Breakdown</h3>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {(() => {
                        const maxQty = Math.max(...deliveryReport.dailyBreakdown.map(d => d.quantity), 1);
                        return deliveryReport.dailyBreakdown.map(d => (
                          <Bar key={d.date} value={d.quantity} max={maxQty} color="var(--tc-btn)"
                            label={new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Daily Table */}
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}>
                    <th style={thStyle}>Date</th><th style={thStyle}>Delivered</th><th style={thStyle}>Skipped</th>
                    <th style={thStyle}>Quantity</th><th style={thStyle}>Revenue</th>
                  </tr></thead>
                  <tbody>
                    {deliveryReport.dailyBreakdown.map(d => (
                      <tr key={d.date} style={trStyle}>
                        <td style={tdStyle}>{new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' })}</td>
                        <td style={tdStyle}><span style={{ color: 'var(--tc)', fontWeight: 600 }}>{d.delivered}</span></td>
                        <td style={tdStyle}><span style={{ color: '#F57C00', fontWeight: 600 }}>{d.skipped}</span></td>
                        <td style={tdStyle}>{d.quantity.toFixed(1)} L</td>
                        <td style={tdStyle}>₹{d.revenue.toFixed(0)}</td>
                      </tr>
                    ))}
                    {deliveryReport.dailyBreakdown.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>No delivery data for this month</td></tr>}
                    {deliveryReport.dailyBreakdown.length > 0 && (
                      <tr style={{ ...thRowStyle, fontWeight: 700 }}>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>Total</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--tc)' }}>{deliveryReport.totalDeliveries}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: '#F57C00' }}>{deliveryReport.skippedDeliveries}</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{deliveryReport.totalQuantityDelivered.toFixed(1)} L</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>₹{deliveryReport.totalRevenue.toFixed(0)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {deliveryReport.dailyBreakdown.map(d => (
                    <div key={d.date} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' })}</span>
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Delivered: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>{d.delivered}</span></div>
                        <div><span className="mobile-card-detail-label">Skipped: </span><span style={{ color: '#F57C00', fontWeight: 600 }}>{d.skipped}</span></div>
                        <div><span className="mobile-card-detail-label">Quantity: </span>{d.quantity.toFixed(1)} L</div>
                        <div><span className="mobile-card-detail-label">Revenue: </span>₹{d.revenue.toFixed(0)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}

            {/* ===== Monthly Payment Report ===== */}
            {reportTab === 'monthly-payment' && paymentReport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Total Billed" value={`₹${paymentReport.totalBilled.toFixed(0)}`} color="#1565C0" />
                  <StatCard label="Collected" value={`₹${paymentReport.totalCollected.toFixed(0)}`} color="var(--tc)" />
                  <StatCard label="Outstanding" value={`₹${paymentReport.totalOutstanding.toFixed(0)}`} color="#D32F2F" />
                  <StatCard label="Collection Rate" value={`${paymentReport.collectionRate}%`} color={paymentReport.collectionRate >= 80 ? 'var(--tc)' : '#F57C00'} />
                </div>

                {/* Bills summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ background: '#E3F2FD', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1565C0' }}>{paymentReport.totalBills}</div>
                    <div style={{ fontSize: '0.8rem', color: '#555' }}>Total Bills</div>
                  </div>
                  <div style={{ background: 'var(--tc-light)', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--tc)' }}>{paymentReport.paidBills}</div>
                    <div style={{ fontSize: '0.8rem', color: '#555' }}>Paid</div>
                  </div>
                  <div style={{ background: '#FFF3E0', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: '#E65100' }}>{paymentReport.unpaidBills}</div>
                    <div style={{ fontSize: '0.8rem', color: '#555' }}>Unpaid</div>
                  </div>
                </div>

                {/* Collection progress bar */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--tc)' }}>Collection Progress</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{paymentReport.collectionRate}%</span>
                  </div>
                  <div style={{ background: 'var(--tc-light)', borderRadius: '8px', height: '24px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${paymentReport.collectionRate}%`, height: '100%', borderRadius: '8px',
                      background: paymentReport.collectionRate >= 80 ? 'linear-gradient(90deg, var(--tc-btn), var(--tc))' : paymentReport.collectionRate >= 50 ? 'linear-gradient(90deg, #FFA726, #F57C00)' : 'linear-gradient(90deg, #EF5350, #D32F2F)',
                      transition: 'width 0.5s'
                    }} />
                  </div>
                </div>

                {/* Payment mode breakdown */}
                {paymentReport.paymentModes.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: 'var(--tc)', fontSize: '1rem', marginBottom: '0.75rem' }}>💳 Payment Mode Breakdown</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                      {paymentReport.paymentModes.map(pm => (
                        <div key={pm.mode} style={{
                          background: pm.mode === 'Cash' ? '#FFF3E0' : pm.mode === 'UPI' ? '#E3F2FD' : '#F3E5F5',
                          borderRadius: '10px', padding: '1rem', textAlign: 'center',
                          border: `1px solid ${pm.mode === 'Cash' ? '#FFE0B2' : pm.mode === 'UPI' ? '#BBDEFB' : '#E1BEE7'}`
                        }}>
                          <div style={{ fontSize: '1.3rem', marginBottom: '0.3rem' }}>{pm.mode === 'Cash' ? '💵' : pm.mode === 'UPI' ? '📱' : '💳'}</div>
                          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#333' }}>₹{pm.amount.toFixed(0)}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{pm.mode} • {pm.count} payments</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Payments Table */}
                {paymentReport.recentPayments.length > 0 && (
                  <>
                    <h3 style={{ color: 'var(--tc)', fontSize: '1rem', marginBottom: '0.75rem' }}>📜 Recent Payments</h3>
                    <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                    <table style={tableStyle}>
                      <thead><tr style={thRowStyle}>
                        <th style={thStyle}>Date</th><th style={thStyle}>Subscriber</th>
                        <th style={thStyle}>Amount</th><th style={thStyle}>Mode</th><th style={thStyle}>Bill #</th>
                      </tr></thead>
                      <tbody>
                        {paymentReport.recentPayments.map(p => (
                          <tr key={p.paymentId} style={trStyle}>
                            <td style={tdStyle}>{new Date(p.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                            <td style={tdStyle}><strong>{p.subscriberName}</strong></td>
                            <td style={tdStyle}>₹{p.amount.toFixed(0)}</td>
                            <td style={tdStyle}><StatusBadge status={p.mode.toLowerCase()} /></td>
                            <td style={tdStyle}>#{p.billId}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mobile-card-list">
                      {paymentReport.recentPayments.map(p => (
                        <div key={p.paymentId} className="mobile-card-item">
                          <div className="mobile-card-header">
                            <span className="mobile-card-title">{p.subscriberName}</span>
                            <StatusBadge status={p.mode.toLowerCase()} />
                          </div>
                          <div className="mobile-card-details">
                            <div><span className="mobile-card-detail-label">Date: </span>{new Date(p.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                            <div><span className="mobile-card-detail-label">Amount: </span>₹{p.amount.toFixed(0)}</div>
                            <div><span className="mobile-card-detail-label">Bill #: </span>#{p.billId}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  </>
                )}
                {paymentReport.totalBills === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No bills generated for this month. Generate bills from the Billing tab first.</div>
                )}
              </>
            )}

            {/* ===== Subscriber Wise Report ===== */}
            {reportTab === 'subscriber-wise' && subscriberReport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Total Billed" value={`₹${subscriberReport.grandTotalBilled.toFixed(0)}`} color="#1565C0" />
                  <StatCard label="Total Paid" value={`₹${subscriberReport.grandTotalPaid.toFixed(0)}`} color="var(--tc)" />
                  <StatCard label="Outstanding" value={`₹${subscriberReport.grandTotalOutstanding.toFixed(0)}`} color="#D32F2F" />
                  <StatCard label="Subscribers" value={subscriberReport.subscribers.length} color="#6A1B9A" />
                </div>

                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}>
                    <th style={thStyle}>Subscriber</th><th style={thStyle}>Milk</th><th style={thStyle}>Qty/Day</th>
                    <th style={thStyle}>Delivered</th><th style={thStyle}>Skipped</th><th style={thStyle}>Paused</th>
                    <th style={thStyle}>Total Qty</th><th style={thStyle}>Amount</th><th style={thStyle}>Paid</th>
                    <th style={thStyle}>Due</th>
                  </tr></thead>
                  <tbody>
                    {subscriberReport.subscribers.map(s => (
                      <tr key={s.subscriberId} style={trStyle}>
                        <td style={tdStyle}><strong>{s.name}</strong></td>
                        <td style={tdStyle}>{s.milkType}</td>
                        <td style={tdStyle}>{s.dailyQuantity} L</td>
                        <td style={tdStyle}><span style={{ color: 'var(--tc)', fontWeight: 600 }}>{s.deliveredDays}</span></td>
                        <td style={tdStyle}><span style={{ color: '#F57C00', fontWeight: 600 }}>{s.skippedDays}</span></td>
                        <td style={tdStyle}><span style={{ color: '#D32F2F', fontWeight: 600 }}>{s.pausedDays}</span></td>
                        <td style={tdStyle}>{s.totalQuantity.toFixed(1)} L</td>
                        <td style={tdStyle}>₹{s.totalAmount.toFixed(0)}</td>
                        <td style={tdStyle}><span style={{ color: 'var(--tc)' }}>₹{s.paidAmount.toFixed(0)}</span></td>
                        <td style={tdStyle}><span style={{ color: s.outstandingAmount > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{s.outstandingAmount.toFixed(0)}</span></td>
                      </tr>
                    ))}
                    {subscriberReport.subscribers.length === 0 && <tr><td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>No active subscribers</td></tr>}
                    {subscriberReport.subscribers.length > 0 && (
                      <tr style={{ ...thRowStyle, fontWeight: 700 }}>
                        <td colSpan={7} style={{ ...tdStyle, fontWeight: 700 }}>Grand Total</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>₹{subscriberReport.grandTotalBilled.toFixed(0)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--tc)' }}>₹{subscriberReport.grandTotalPaid.toFixed(0)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: '#D32F2F' }}>₹{subscriberReport.grandTotalOutstanding.toFixed(0)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {subscriberReport.subscribers.map(s => (
                    <div key={s.subscriberId} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{s.name}</span>
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Milk: </span>{s.milkType} — {s.dailyQuantity} L/day</div>
                        <div><span className="mobile-card-detail-label">Delivered: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>{s.deliveredDays}</span></div>
                        <div><span className="mobile-card-detail-label">Skipped: </span><span style={{ color: '#F57C00', fontWeight: 600 }}>{s.skippedDays}</span></div>
                        <div><span className="mobile-card-detail-label">Paused: </span><span style={{ color: '#D32F2F', fontWeight: 600 }}>{s.pausedDays}</span></div>
                        <div><span className="mobile-card-detail-label">Total Qty: </span>{s.totalQuantity.toFixed(1)} L</div>
                        <div><span className="mobile-card-detail-label">Amount: </span>₹{s.totalAmount.toFixed(0)}</div>
                        <div><span className="mobile-card-detail-label">Paid: </span><span style={{ color: 'var(--tc)' }}>₹{s.paidAmount.toFixed(0)}</span></div>
                        <div><span className="mobile-card-detail-label">Due: </span><span style={{ color: s.outstandingAmount > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{s.outstandingAmount.toFixed(0)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}

            {/* ===== Yearly Report ===== */}
            {reportTab === 'yearly' && yearlyReport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Total Revenue" value={`₹${yearlyReport.totalRevenue.toFixed(0)}`} color="#1565C0" />
                  <StatCard label="Collected" value={`₹${yearlyReport.totalCollected.toFixed(0)}`} color="var(--tc)" />
                  <StatCard label="Outstanding" value={`₹${yearlyReport.totalOutstanding.toFixed(0)}`} color="#D32F2F" />
                  <StatCard label="Total Quantity" value={`${yearlyReport.totalQuantity.toFixed(1)} L`} color="#6A1B9A" />
                </div>
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}>
                    <th style={thStyle}>Month</th><th style={thStyle}>Revenue</th><th style={thStyle}>Collected</th>
                    <th style={thStyle}>Outstanding</th><th style={thStyle}>Quantity</th><th style={thStyle}>Delivered</th><th style={thStyle}>Skipped</th>
                  </tr></thead>
                  <tbody>
                    {yearlyReport.months.map(m => (
                      <tr key={m.month} style={trStyle}>
                        <td style={tdStyle}><strong>{m.month}</strong></td>
                        <td style={tdStyle}>₹{m.revenue.toFixed(0)}</td>
                        <td style={tdStyle}>₹{m.collected.toFixed(0)}</td>
                        <td style={tdStyle}><span style={{ color: m.outstanding > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{m.outstanding.toFixed(0)}</span></td>
                        <td style={tdStyle}>{m.quantity.toFixed(1)} L</td>
                        <td style={tdStyle}>{m.deliveredCount}</td>
                        <td style={tdStyle}>{m.skippedCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {yearlyReport.months.map(m => (
                    <div key={m.month} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{m.month}</span>
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Revenue: </span>₹{m.revenue.toFixed(0)}</div>
                        <div><span className="mobile-card-detail-label">Collected: </span>₹{m.collected.toFixed(0)}</div>
                        <div><span className="mobile-card-detail-label">Outstanding: </span><span style={{ color: m.outstanding > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{m.outstanding.toFixed(0)}</span></div>
                        <div><span className="mobile-card-detail-label">Quantity: </span>{m.quantity.toFixed(1)} L</div>
                        <div><span className="mobile-card-detail-label">Delivered: </span>{m.deliveredCount}</div>
                        <div><span className="mobile-card-detail-label">Skipped: </span>{m.skippedCount}</div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}

            {/* ===== GST Report ===== */}
            {reportTab === 'gst' && gstReport && (
              <>
                <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '1rem', color: '#8D6E00', fontSize: '0.85rem' }}>
                  GST summary for filing. Verify rates and compliance rules with your accountant before final filing.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <StatCard label="Taxable Amount" value={`₹${gstReport.taxableAmount.toFixed(2)}`} color="#1565C0" />
                  <StatCard label="CGST" value={`₹${gstReport.cgstAmount.toFixed(2)}`} color="var(--tc)" />
                  <StatCard label="SGST" value={`₹${gstReport.sgstAmount.toFixed(2)}`} color="var(--tc-btn)" />
                  <StatCard label="Invoice Total" value={`₹${gstReport.totalInvoiceAmount.toFixed(2)}`} color="#6A1B9A" />
                </div>
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}><th style={thStyle}>Metric</th><th style={thStyle}>Value</th></tr></thead>
                  <tbody>
                    <tr style={trStyle}><td style={tdStyle}>Month</td><td style={tdStyle}><strong>{gstReport.month}</strong></td></tr>
                    <tr style={trStyle}><td style={tdStyle}>GST Rate</td><td style={tdStyle}>{gstReport.gstRatePercent}%</td></tr>
                    <tr style={trStyle}><td style={tdStyle}>Total Collected</td><td style={tdStyle}>₹{gstReport.totalCollected.toFixed(2)}</td></tr>
                    <tr style={trStyle}><td style={tdStyle}>Outstanding Amount</td><td style={tdStyle}><span style={{ color: gstReport.outstandingAmount > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{gstReport.outstandingAmount.toFixed(2)}</span></td></tr>
                    <tr style={trStyle}><td style={tdStyle}>Total Bills</td><td style={tdStyle}>{gstReport.totalBills}</td></tr>
                    <tr style={trStyle}><td style={tdStyle}>Paid Bills</td><td style={tdStyle}>{gstReport.paidBills}</td></tr>
                    <tr style={trStyle}><td style={tdStyle}>Unpaid Bills</td><td style={tdStyle}>{gstReport.unpaidBills}</td></tr>
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  <div className="mobile-card-item">
                    <div className="mobile-card-header">
                      <span className="mobile-card-title">{gstReport.month}</span>
                    </div>
                    <div className="mobile-card-details">
                      <div><span className="mobile-card-detail-label">GST Rate: </span>{gstReport.gstRatePercent}%</div>
                      <div><span className="mobile-card-detail-label">Total Collected: </span>₹{gstReport.totalCollected.toFixed(2)}</div>
                      <div><span className="mobile-card-detail-label">Outstanding: </span><span style={{ color: gstReport.outstandingAmount > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{gstReport.outstandingAmount.toFixed(2)}</span></div>
                      <div><span className="mobile-card-detail-label">Total Bills: </span>{gstReport.totalBills}</div>
                      <div><span className="mobile-card-detail-label">Paid: </span>{gstReport.paidBills}</div>
                      <div><span className="mobile-card-detail-label">Unpaid: </span>{gstReport.unpaidBills}</div>
                    </div>
                  </div>
                </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ===== SuperAdmin Reports Panel (Tenant-wise) =====
function SuperAdminReportsPanel() {
  const [performanceMonth, setPerformanceMonth] = useState(currentMonth());
  const [performance, setPerformance] = useState<TenantPerformanceReportDto | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPerformance = async () => {
    setLoading(true);
    const res = await api.getTenantPerformanceReport(performanceMonth);
    if (res.data) setPerformance(res.data);
    setLoading(false);
  };

  useEffect(() => { loadPerformance(); }, [performanceMonth]);

  const handleDownloadCsv = () => {
    if (!performance) return;
    const rows: Array<Array<string | number>> = [
      ['Rank', 'Tenant ID', 'Tenant Name', 'Active Subscribers', 'Delivered', 'Skipped', 'Delivery %', 'Total Billed', 'Collected', 'Outstanding', 'Collection %', 'Score'],
      ...performance.tenants.map((t, idx) => [
        idx + 1, t.tenantId, t.tenantName, t.activeSubscribers,
        t.deliveredCount, t.skippedCount, t.deliverySuccessRate,
        t.totalBilled, t.totalCollected, t.outstanding,
        t.collectionRate, t.performanceScore,
      ]),
    ];
    downloadCsvFile(`tenant-performance-${performanceMonth}.csv`, rows);
  };

  // Compute totals
  const totals = performance ? {
    subscribers: performance.tenants.reduce((a, t) => a + t.activeSubscribers, 0),
    delivered: performance.tenants.reduce((a, t) => a + t.deliveredCount, 0),
    skipped: performance.tenants.reduce((a, t) => a + t.skippedCount, 0),
    billed: performance.tenants.reduce((a, t) => a + t.totalBilled, 0),
    collected: performance.tenants.reduce((a, t) => a + t.totalCollected, 0),
    outstanding: performance.tenants.reduce((a, t) => a + t.outstanding, 0),
  } : null;

  return (
    <div>
      <PanelHeader title="Tenant-wise Reports">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input type="month" value={performanceMonth} onChange={e => setPerformanceMonth(e.target.value)} style={inputStyle} />
          <GreenButton onClick={loadPerformance}>Refresh</GreenButton>
          <GreenButton onClick={handleDownloadCsv}>⬇ Download CSV</GreenButton>
        </div>
      </PanelHeader>

      {loading ? <LoadingSpinner /> : performance && (
        <>
          {/* Summary Cards */}
          {totals && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <StatCard label="Total Tenants" value={performance.tenants.length} color="#6A1B9A" />
              <StatCard label="Total Subscribers" value={totals.subscribers} color="var(--tc)" />
              <StatCard label="Total Delivered" value={totals.delivered} color="var(--tc-btn)" />
              <StatCard label="Total Billed" value={`₹${totals.billed.toFixed(0)}`} color="#1565C0" />
              <StatCard label="Total Collected" value={`₹${totals.collected.toFixed(0)}`} color="var(--tc)" />
              <StatCard label="Total Outstanding" value={`₹${totals.outstanding.toFixed(0)}`} color="#D32F2F" />
            </div>
          )}

          {/* Performance Table */}
          <TableWrap>
          <table style={tableStyle}>
            <thead><tr style={thRowStyle}>
              <th style={thStyle}>Rank</th><th style={thStyle}>Tenant</th><th style={thStyle}>Subscribers</th>
              <th style={thStyle}>Delivered</th><th style={thStyle}>Skipped</th><th style={thStyle}>Delivery %</th>
              <th style={thStyle}>Billed</th><th style={thStyle}>Collected</th><th style={thStyle}>Outstanding</th>
              <th style={thStyle}>Collection %</th><th style={thStyle}>Score</th>
            </tr></thead>
            <tbody>
              {performance.tenants.map((t, idx) => (
                <tr key={t.tenantId} style={trStyle}>
                  <td style={tdStyle}><strong>#{idx + 1}</strong></td>
                  <td style={tdStyle}><strong>{t.tenantName}</strong></td>
                  <td style={tdStyle}>{t.activeSubscribers}</td>
                  <td style={tdStyle}><span style={{ color: 'var(--tc)', fontWeight: 600 }}>{t.deliveredCount}</span></td>
                  <td style={tdStyle}><span style={{ color: '#F57C00', fontWeight: 600 }}>{t.skippedCount}</span></td>
                  <td style={tdStyle}>{t.deliverySuccessRate}%</td>
                  <td style={tdStyle}>₹{t.totalBilled.toFixed(0)}</td>
                  <td style={tdStyle}><span style={{ color: 'var(--tc)' }}>₹{t.totalCollected.toFixed(0)}</span></td>
                  <td style={tdStyle}><span style={{ color: t.outstanding > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{t.outstanding.toFixed(0)}</span></td>
                  <td style={tdStyle}>{t.collectionRate}%</td>
                  <td style={tdStyle}><strong>{t.performanceScore}</strong></td>
                </tr>
              ))}
              {performance.tenants.length === 0 && <EmptyRow colSpan={11} text="No tenant performance data for this month." />}
            </tbody>
          </table>
          </TableWrap>
        </>
      )}
    </div>
  );
}

// ===== Email Config Panel =====
function EmailConfigPanel() {
  const [config, setConfig] = useState<EmailConfigDto | null>(null);
  const [form, setForm] = useState<EmailConfigRequest>({ provider: 'Gmail', senderEmail: '', senderPassword: '', senderName: '', smtpPort: 587, useSsl: true });
  const [testEmail, setTestEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // WhatsApp state
  const [waConfig, setWaConfig] = useState<WhatsAppConfigDto | null>(null);
  const [waForm, setWaForm] = useState<WhatsAppConfigRequest>({ phoneNumberId: '', businessAccountId: '', accessToken: '', displayPhoneNumber: '', businessName: '' });
  const [testPhone, setTestPhone] = useState('');
  const [waTesting, setWaTesting] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const [waMsg, setWaMsg] = useState('');

  const load = async () => {
    const res = await api.getEmailConfig();
    if (res.data) {
      setConfig(res.data);
      setForm({ provider: res.data.provider, senderEmail: res.data.senderEmail, senderPassword: '', senderName: res.data.senderName, smtpPort: res.data.smtpPort, useSsl: res.data.useSsl });
    }
    const waRes = await api.getWhatsAppConfig();
    if (waRes.data) {
      setWaConfig(waRes.data);
      setWaForm({ phoneNumberId: waRes.data.phoneNumberId, businessAccountId: waRes.data.businessAccountId, accessToken: '', displayPhoneNumber: waRes.data.displayPhoneNumber, businessName: waRes.data.businessName });
    }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const res = await api.saveEmailConfig(form);
    setMsg(res.message);
    if (res.success) load();
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testEmail) { setMsg('Enter a test email address'); return; }
    setTesting(true);
    const res = await api.testEmail(testEmail);
    setMsg(res.message);
    setTesting(false);
  };

  return (
    <div>
      <PanelHeader title="Email Configuration" />
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {config && (
        <div style={{ ...formStyle, background: 'var(--tc-light)', border: '1px solid var(--tc-border)', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.9rem' }}>✅ <strong>Current Config:</strong> {config.provider} — {config.senderEmail} ({config.senderName})</span>
          <span style={{ fontSize: '0.85rem', color: '#555' }}>Port: {config.smtpPort} | SSL: {config.useSsl ? 'Yes' : 'No'}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="email-config-section" style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '550px' }}>
        <h3 style={{ color: 'var(--tc)', margin: '0 0 0.5rem' }}>📧 SMTP Email Setup</h3>
        <div style={{
          background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: '8px',
          padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#E65100',
        }}>
          <strong>⚠️ Gmail Users:</strong> Gmail does NOT allow your regular password for SMTP. You must:
          <ol style={{ margin: '0.3rem 0 0 1.2rem', padding: 0 }}>
            <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" style={{ color: '#1565C0' }}>Google Account → Security</a> and enable <strong>2-Step Verification</strong></li>
            <li>Then go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ color: '#1565C0' }}>App Passwords</a> and generate one</li>
            <li>Use that <strong>16-character App Password</strong> below (not your Gmail password)</li>
          </ol>
        </div>

        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Email Provider</label>
        <select value={form.provider} onChange={e => {
          const p = e.target.value;
          setForm({ ...form, provider: p, smtpPort: 587, useSsl: true });
        }} style={inputStyle}>
          <option value="Gmail">Gmail (smtp.gmail.com)</option>
          <option value="Yahoo">Yahoo (smtp.mail.yahoo.com)</option>
          <option value="Outlook">Outlook (smtp.office365.com)</option>
        </select>

        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Sender Email</label>
        <input type="email" value={form.senderEmail} onChange={e => setForm({ ...form, senderEmail: e.target.value })} placeholder="your.email@gmail.com" required style={inputStyle} />

        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>App Password</label>
        <input type="password" value={form.senderPassword} onChange={e => setForm({ ...form, senderPassword: e.target.value })} placeholder={config ? '(leave blank to keep current)' : 'Enter app password'} style={inputStyle} />
        <span style={{ fontSize: '0.78rem', color: '#888' }}>For Gmail: Google Account → Security → 2-Step Verification → App Passwords</span>

        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Sender Name</label>
        <input value={form.senderName} onChange={e => setForm({ ...form, senderName: e.target.value })} placeholder="Gokul Dairy" required style={inputStyle} />

        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>SMTP Port</label>
            <input type="number" value={form.smtpPort} onChange={e => setForm({ ...form, smtpPort: +e.target.value })} style={{ ...inputStyle, width: '100%' }} />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.useSsl} onChange={e => setForm({ ...form, useSsl: e.target.checked })} />
              <span style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Use SSL/TLS</span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <GreenButton type="submit">{saving ? '⏳ Saving...' : '💾 Save Configuration'}</GreenButton>
        </div>
      </form>

      {config && (
        <div style={{ ...formStyle, maxWidth: '550px', marginTop: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: 0, width: '100%' }}>🧪 Test Email</h3>
          <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Send test email to..." style={{ ...inputStyle, flex: 1 }} />
          <ActionBtn onClick={handleTest} color="#1565C0">{testing ? '⏳ Sending...' : '📤 Send Test'}</ActionBtn>
        </div>
      )}

      {/* ====== WhatsApp Business API Configuration ====== */}
      <div style={{ borderTop: '2px solid #E0E0E0', marginTop: '2rem', paddingTop: '1.5rem' }}>
        <h2 style={{ color: '#25D366', margin: '0 0 0.5rem', fontSize: '1.1rem' }}>📱 WhatsApp Business API</h2>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem' }}>
          Send bill notifications and payment reminders via WhatsApp. Requires a <a href="https://business.facebook.com/latest/whatsapp_manager" target="_blank" rel="noreferrer" style={{ color: '#1565C0' }}>Meta WhatsApp Business account</a>.
        </p>

        {waMsg && <MsgBanner>{waMsg}</MsgBanner>}

        {waConfig && (
          <div style={{ ...formStyle, background: 'var(--tc-light)', border: '1px solid var(--tc-border)', marginBottom: '1rem', maxWidth: '550px' }}>
            <span style={{ fontSize: '0.9rem' }}>✅ <strong>WhatsApp Active:</strong> {waConfig.businessName} — {waConfig.displayPhoneNumber}</span>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>Phone ID: {waConfig.phoneNumberId}</span>
          </div>
        )}

        <form onSubmit={async (e) => {
          e.preventDefault();
          setWaSaving(true);
          const res = await api.saveWhatsAppConfig(waForm);
          setWaMsg(res.message);
          if (res.success) load();
          setWaSaving(false);
        }} className="email-config-section" style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '550px' }}>
          <h3 style={{ color: '#25D366', margin: '0 0 0.5rem' }}>🔑 API Credentials</h3>
          <p style={{ color: '#666', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
            Get these from <strong>Meta Business Suite → WhatsApp Manager → API Setup</strong>
          </p>

          <label style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem' }}>Phone Number ID</label>
          <input value={waForm.phoneNumberId} onChange={e => setWaForm({ ...waForm, phoneNumberId: e.target.value })} placeholder="e.g. 100234567890123" required style={inputStyle} />
          <span style={{ fontSize: '0.78rem', color: '#888' }}>Found in WhatsApp Manager → Phone Numbers → Phone Number ID</span>

          <label style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem', marginTop: '0.5rem' }}>Business Account ID</label>
          <input value={waForm.businessAccountId} onChange={e => setWaForm({ ...waForm, businessAccountId: e.target.value })} placeholder="e.g. 109876543210987" required style={inputStyle} />

          <label style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem', marginTop: '0.5rem' }}>Permanent Access Token</label>
          <input type="password" value={waForm.accessToken} onChange={e => setWaForm({ ...waForm, accessToken: e.target.value })} placeholder={waConfig ? '(leave blank to keep current)' : 'System User token from Business Settings'} style={inputStyle} />
          <span style={{ fontSize: '0.78rem', color: '#888' }}>Business Settings → System Users → Generate Token (whatsapp_business_messaging permission)</span>

          <label style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem', marginTop: '0.5rem' }}>Display Phone Number</label>
          <input value={waForm.displayPhoneNumber} onChange={e => setWaForm({ ...waForm, displayPhoneNumber: e.target.value })} placeholder="+91 98765 43210" required style={inputStyle} />

          <label style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem', marginTop: '0.5rem' }}>Business Name</label>
          <input value={waForm.businessName} onChange={e => setWaForm({ ...waForm, businessName: e.target.value })} placeholder="Gokul Dairy" required style={inputStyle} />

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <GreenButton type="submit">{waSaving ? '⏳ Saving...' : '💾 Save WhatsApp Config'}</GreenButton>
          </div>
        </form>

        {waConfig && (
          <div style={{ ...formStyle, maxWidth: '550px', marginTop: '1rem' }}>
            <h3 style={{ color: '#25D366', margin: 0, width: '100%' }}>🧪 Test WhatsApp</h3>
            <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="Phone number (e.g. +919876543210)" style={{ ...inputStyle, flex: 1 }} />
            <ActionBtn onClick={async () => {
              if (!testPhone) { setWaMsg('Enter a phone number'); return; }
              setWaTesting(true);
              const res = await api.testWhatsApp(testPhone);
              setWaMsg(res.message);
              setWaTesting(false);
            }} color="#25D366">{waTesting ? '⏳ Sending...' : '📤 Send Test'}</ActionBtn>
          </div>
        )}

        <div style={{ ...formStyle, maxWidth: '550px', marginTop: '1rem', background: '#E3F2FD', border: '1px solid #90CAF9' }}>
          <h4 style={{ color: '#1565C0', margin: '0 0 0.5rem', width: '100%' }}>ℹ️ Setup Guide</h4>
          <ol style={{ color: '#555', fontSize: '0.82rem', margin: 0, paddingLeft: '1.2rem', lineHeight: '1.7' }}>
            <li>Create a <a href="https://business.facebook.com" target="_blank" rel="noreferrer" style={{ color: '#1565C0' }}>Meta Business account</a></li>
            <li>Go to <strong>WhatsApp Manager</strong> → Add phone number & verify</li>
            <li>Go to <strong>API Setup</strong> → Copy Phone Number ID & Business Account ID</li>
            <li>Create a <strong>System User</strong> → Generate permanent token with <code>whatsapp_business_messaging</code></li>
            <li>Paste all values above and save</li>
            <li>Send a test message to verify</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

// ===== Notifications Panel =====
function NotificationsPanel({ isSuperAdmin, isSubscriber, onCountChange }: { isSuperAdmin: boolean; isSubscriber: boolean; onCountChange: () => void }) {
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [form, setForm] = useState<SendNotificationRequest>({ title: '', message: '', sendEmail: false, sendWhatsApp: false });
  const [targetTenantId, setTargetTenantId] = useState<number>(0);
  const [showCompose, setShowCompose] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    const res = await api.getNotifications();
    if (res.data) setNotifications(res.data);
    if (isSuperAdmin) {
      const tRes = await api.getTenants();
      if (tRes.data) setTenants(tRes.data);
    }
  };
  useEffect(() => { load(); }, []);

  const execCmd = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const handleInsertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { setMsg('Image must be under 2MB'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        editorRef.current?.focus();
        document.execCommand('insertImage', false, base64);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const htmlContent = editorRef.current?.innerHTML || '';
    if (!htmlContent.replace(/<[^>]*>/g, '').trim()) { setMsg('Please enter a message'); return; }
    const payload = { ...form, message: htmlContent };
    setSending(true);
    let res;
    if (isSuperAdmin) {
      res = await api.sendNotificationToTenants({ ...payload, targetTenantId: targetTenantId || undefined });
    } else if (isSubscriber) {
      res = await api.sendNotificationToAdmin(payload);
    } else {
      res = await api.sendNotificationToSubscribers(payload);
    }
    setMsg(res.message);
    if (res.success) {
      setForm({ title: '', message: '', sendEmail: false, sendWhatsApp: false });
      if (editorRef.current) editorRef.current.innerHTML = '';
      setTargetTenantId(0);
      setShowCompose(false);
      load();
      onCountChange();
    }
    setSending(false);
  };

  const handleMarkRead = async (id: number) => {
    await api.markNotificationRead(id);
    setNotifications(prev => prev.map(n => n.notificationId === id ? { ...n, isRead: true } : n));
    onCountChange();
  };

  const handleMarkAllRead = async () => {
    await api.markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    onCountChange();
  };

  const panelTitle = isSuperAdmin ? 'Send to Tenant Admins' : isSubscriber ? 'Message to Dairy' : 'Send to Subscribers';
  const composeTitle = isSubscriber ? '📝 Message to Dairy Admin' : '📝 Compose Notification';

  const toolbarBtnStyle: React.CSSProperties = {
    background: '#F5F5F5', border: '1px solid #DDD', borderRadius: '4px', padding: '4px 8px',
    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, minWidth: '30px', textAlign: 'center',
  };

  return (
    <div>
      <PanelHeader title={panelTitle} count={notifications.length}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <GreenButton onClick={() => setShowCompose(!showCompose)}>✉ Compose</GreenButton>
          <ActionBtn onClick={handleMarkAllRead} color="#1565C0">Mark All Read</ActionBtn>
        </div>
      </PanelHeader>
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {showCompose && (
        <form onSubmit={handleSend} style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '700px', marginBottom: '1.5rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: 0 }}>{composeTitle}</h3>

          {isSuperAdmin && (
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Target Tenant</label>
              <select value={targetTenantId} onChange={e => setTargetTenantId(+e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value={0}>All Tenant Admins</option>
                {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
              </select>
            </div>
          )}

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Title</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Notification title..." required style={inputStyle} />

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Message</label>
          {/* Rich Text Toolbar */}
          <div className="notif-toolbar" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', padding: '6px 8px', background: '#FAFAFA', border: '1px solid #DDD', borderBottom: 'none', borderRadius: '8px 8px 0 0' }}>
            <button type="button" onClick={() => execCmd('bold')} style={toolbarBtnStyle} title="Bold"><b>B</b></button>
            <button type="button" onClick={() => execCmd('italic')} style={toolbarBtnStyle} title="Italic"><i>I</i></button>
            <button type="button" onClick={() => execCmd('underline')} style={toolbarBtnStyle} title="Underline"><u>U</u></button>
            <button type="button" onClick={() => execCmd('strikeThrough')} style={toolbarBtnStyle} title="Strikethrough"><s>S</s></button>
            <span style={{ borderLeft: '1px solid #CCC', margin: '0 4px' }} />
            <button type="button" onClick={() => execCmd('formatBlock', 'h3')} style={toolbarBtnStyle} title="Heading">H</button>
            <button type="button" onClick={() => execCmd('insertUnorderedList')} style={toolbarBtnStyle} title="Bullet List">• List</button>
            <button type="button" onClick={() => execCmd('insertOrderedList')} style={toolbarBtnStyle} title="Numbered List">1. List</button>
            <span style={{ borderLeft: '1px solid #CCC', margin: '0 4px' }} />
            <select onChange={e => { if (e.target.value) execCmd('foreColor', e.target.value); e.target.value = ''; }} style={{ ...toolbarBtnStyle, padding: '2px 4px' }}>
              <option value="">🎨 Color</option>
              <option value="#000000" style={{ color: '#000' }}>Black</option>
              <option value="#C62828" style={{ color: '#C62828' }}>Red</option>
              <option value="#2E7D32" style={{ color: 'var(--tc)' }}>Green</option>
              <option value="#1565C0" style={{ color: '#1565C0' }}>Blue</option>
              <option value="#E65100" style={{ color: '#E65100' }}>Orange</option>
            </select>
            <select onChange={e => { if (e.target.value) execCmd('fontSize', e.target.value); e.target.value = ''; }} style={{ ...toolbarBtnStyle, padding: '2px 4px' }}>
              <option value="">📏 Size</option>
              <option value="2">Small</option>
              <option value="3">Normal</option>
              <option value="4">Large</option>
              <option value="5">X-Large</option>
            </select>
            <span style={{ borderLeft: '1px solid #CCC', margin: '0 4px' }} />
            <button type="button" onClick={() => execCmd('justifyLeft')} style={toolbarBtnStyle} title="Align Left">⬅</button>
            <button type="button" onClick={() => execCmd('justifyCenter')} style={toolbarBtnStyle} title="Align Center">⬌</button>
            <button type="button" onClick={() => execCmd('justifyRight')} style={toolbarBtnStyle} title="Align Right">➡</button>
            <span style={{ borderLeft: '1px solid #CCC', margin: '0 4px' }} />
            <button type="button" onClick={handleInsertImage} style={{ ...toolbarBtnStyle, color: '#1565C0' }} title="Insert Image">🖼️ Image</button>
            <button type="button" onClick={() => execCmd('removeFormat')} style={{ ...toolbarBtnStyle, color: '#888' }} title="Clear Formatting">✕ Clear</button>
          </div>
          {/* Editable Content Area */}
          <div
            ref={editorRef}
            contentEditable
            style={{
              ...inputStyle, minHeight: '160px', maxHeight: '400px', overflowY: 'auto',
              borderRadius: '0 0 8px 8px', padding: '0.75rem', lineHeight: 1.6,
              outline: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}
            onPaste={(e) => {
              // Allow paste but sanitize scripts
              const html = e.clipboardData.getData('text/html');
              if (html && html.includes('<script')) {
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain');
                document.execCommand('insertText', false, text);
              }
            }}
          />
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
            Use toolbar for formatting. Click 🖼️ to insert images inline. Images render in email & in-app.
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.sendEmail || false} onChange={e => setForm({ ...form, sendEmail: e.target.checked })} />
            <span style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>📧 Also send via Email</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.sendWhatsApp || false} onChange={e => setForm({ ...form, sendWhatsApp: e.target.checked })} />
            <span style={{ fontWeight: 600, color: '#25D366', fontSize: '0.85rem' }}>📱 Also send via WhatsApp</span>
          </label>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <GreenButton type="submit">{sending ? '⏳ Sending...' : '📤 Send'}</GreenButton>
            <button type="button" onClick={() => setShowCompose(false)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Notification List */}
      <div style={{ maxWidth: '700px' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#888', background: 'white', borderRadius: '12px' }}>No notifications yet.</div>
        ) : notifications.map(n => (
          <div key={n.notificationId} style={{
            background: n.isRead ? 'white' : 'var(--tc-vlight)', borderRadius: '12px', padding: '1rem 1.25rem',
            marginBottom: '0.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: n.isRead ? '4px solid #E0E0E0' : '4px solid var(--tc-btn)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }}>{n.title}</div>
                <div
                  className="notif-html-content"
                  style={{ color: '#555', fontSize: '0.88rem', marginTop: '0.3rem', lineHeight: 1.6, wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.message) }}
                />
                {n.imageUrl && !n.message.includes('<img') && (
                  <img src={n.imageUrl} alt="" style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: '8px', marginTop: '0.5rem' }} />
                )}
                <div style={{ color: '#999', fontSize: '0.78rem', marginTop: '0.5rem' }}>
                  {fmtDate(n.createdDate)} • By: {n.sentByName} • {n.sendEmail ? '📧 Email' : ''}{n.sendEmail && n.sendWhatsApp ? ' + ' : ''}{n.sendWhatsApp ? '📱 WhatsApp' : ''}{!n.sendEmail && !n.sendWhatsApp ? '🔔 In-app only' : ''}
                </div>
              </div>
              {!n.isRead && (
                <ActionBtn onClick={() => handleMarkRead(n.notificationId)} color="var(--tc-btn)">✓ Read</ActionBtn>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Theme Presets =====
interface ThemePreset {
  id: string;
  name: string;
  description: string;
  category: string;
  themeColor: string;
  headerGradient: [string, string];
  accentColor: string;
  bgColor: string;
  cardBorder: string;
  tags: string[];
}

const DAIRY_THEME_PRESETS: ThemePreset[] = [
  {
    id: 'classic-green', name: 'Classic Green', description: 'Traditional dairy green — fresh and natural',
    category: 'Classic', themeColor: '#2E7D32', headerGradient: ['#1B5E20', '#2E7D32'],
    accentColor: '#43A047', bgColor: '#F1F8E9', cardBorder: '#C8E6C9', tags: ['Popular', 'Default'],
  },
  {
    id: 'royal-blue', name: 'Royal Blue', description: 'Professional and trustworthy modern dairy look',
    category: 'Professional', themeColor: '#1565C0', headerGradient: ['#0D47A1', '#1565C0'],
    accentColor: '#1E88E5', bgColor: '#E3F2FD', cardBorder: '#BBDEFB', tags: ['Professional'],
  },
  {
    id: 'sunset-orange', name: 'Sunset Orange', description: 'Warm and vibrant morning delivery vibe',
    category: 'Vibrant', themeColor: '#E65100', headerGradient: ['#BF360C', '#E65100'],
    accentColor: '#FF6D00', bgColor: '#FFF3E0', cardBorder: '#FFE0B2', tags: ['Warm'],
  },
  {
    id: 'lavender-dream', name: 'Lavender Dream', description: 'Elegant and soothing purple dairy theme',
    category: 'Elegant', themeColor: '#6A1B9A', headerGradient: ['#4A148C', '#6A1B9A'],
    accentColor: '#8E24AA', bgColor: '#F3E5F5', cardBorder: '#E1BEE7', tags: ['Elegant'],
  },
  {
    id: 'ocean-teal', name: 'Ocean Teal', description: 'Cool and calming — clean dairy management',
    category: 'Modern', themeColor: '#00695C', headerGradient: ['#004D40', '#00695C'],
    accentColor: '#00897B', bgColor: '#E0F2F1', cardBorder: '#B2DFDB', tags: ['Modern', 'Clean'],
  },
  {
    id: 'cherry-red', name: 'Cherry Red', description: 'Bold and eye-catching for modern dairies',
    category: 'Bold', themeColor: '#C62828', headerGradient: ['#B71C1C', '#C62828'],
    accentColor: '#E53935', bgColor: '#FFEBEE', cardBorder: '#FFCDD2', tags: ['Bold'],
  },
  {
    id: 'golden-harvest', name: 'Golden Harvest', description: 'Rich golden tones for premium dairy brands',
    category: 'Premium', themeColor: '#F57F17', headerGradient: ['#E65100', '#F57F17'],
    accentColor: '#FFA000', bgColor: '#FFFDE7', cardBorder: '#FFF9C4', tags: ['Premium', 'Gold'],
  },
  {
    id: 'deep-indigo', name: 'Deep Indigo', description: 'Sophisticated dark theme for urban dairies',
    category: 'Dark', themeColor: '#283593', headerGradient: ['#1A237E', '#283593'],
    accentColor: '#3949AB', bgColor: '#E8EAF6', cardBorder: '#C5CAE9', tags: ['Dark', 'Sophisticated'],
  },
  {
    id: 'forest-brown', name: 'Forest Brown', description: 'Earthy and organic — farm-to-table dairy feel',
    category: 'Organic', themeColor: '#4E342E', headerGradient: ['#3E2723', '#4E342E'],
    accentColor: '#6D4C41', bgColor: '#EFEBE9', cardBorder: '#D7CCC8', tags: ['Organic', 'Earthy'],
  },
  {
    id: 'rose-pink', name: 'Rose Pink', description: 'Soft and friendly — great for family dairies',
    category: 'Friendly', themeColor: '#AD1457', headerGradient: ['#880E4F', '#AD1457'],
    accentColor: '#D81B60', bgColor: '#FCE4EC', cardBorder: '#F8BBD0', tags: ['Friendly'],
  },
  {
    id: 'slate-grey', name: 'Slate Grey', description: 'Minimalist and clean corporate dairy style',
    category: 'Minimal', themeColor: '#37474F', headerGradient: ['#263238', '#37474F'],
    accentColor: '#546E7A', bgColor: '#ECEFF1', cardBorder: '#CFD8DC', tags: ['Minimal', 'Corporate'],
  },
  {
    id: 'emerald-mint', name: 'Emerald Mint', description: 'Fresh minty green for health-focused dairies',
    category: 'Fresh', themeColor: '#00796B', headerGradient: ['#00695C', '#00796B'],
    accentColor: '#009688', bgColor: '#E0F2F1', cardBorder: '#B2DFDB', tags: ['Fresh', 'Health'],
  },
];

const THEME_CATEGORIES = ['All', ...Array.from(new Set(DAIRY_THEME_PRESETS.map(t => t.category)))];

// ===== Theme Gallery Component =====
function ThemeGallery({ currentThemeColor, onApply }: { currentThemeColor: string; onApply: (preset: ThemePreset) => void }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewTheme, setPreviewTheme] = useState<ThemePreset | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  const filtered = selectedCategory === 'All'
    ? DAIRY_THEME_PRESETS
    : DAIRY_THEME_PRESETS.filter(t => t.category === selectedCategory);

  const handleApply = async (preset: ThemePreset) => {
    setApplyingId(preset.id);
    await onApply(preset);
    setApplyingId(null);
    setPreviewTheme(null);
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.25rem', fontSize: '1.1rem' }}>🎨 Choose a Theme</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
            Select a pre-designed theme for your dairy. Applied to you and all your subscribers.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
          <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: currentThemeColor, display: 'inline-block', border: '2px solid #E0E0E0' }} />
          Current: {currentThemeColor}
        </div>
      </div>

      {/* Category Filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {THEME_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            style={{
              padding: '0.4rem 1rem', border: '2px solid', borderRadius: '20px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.2s',
              background: selectedCategory === cat ? 'var(--tc)' : 'white',
              color: selectedCategory === cat ? 'white' : '#555',
              borderColor: selectedCategory === cat ? 'var(--tc)' : '#E0E0E0',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Theme Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '1rem',
      }}>
        {filtered.map(theme => {
          const isActive = currentThemeColor?.toLowerCase() === theme.themeColor.toLowerCase();
          const isHovered = hoveredId === theme.id;
          return (
            <div
              key={theme.id}
              onMouseEnter={() => setHoveredId(theme.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                background: 'white', borderRadius: '12px', overflow: 'hidden',
                border: isActive ? `3px solid ${theme.themeColor}` : '2px solid #E8E8E8',
                boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
                transform: isHovered ? 'translateY(-4px)' : 'none',
                transition: 'all 0.25s ease', cursor: 'pointer', position: 'relative',
              }}
              onClick={() => setPreviewTheme(theme)}
            >
              {/* Active Badge */}
              {isActive && (
                <div style={{
                  position: 'absolute', top: '10px', right: '10px', zIndex: 2,
                  background: theme.themeColor, color: 'white', padding: '2px 10px',
                  borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700,
                }}>
                  ✓ Active
                </div>
              )}

              {/* Theme Preview Header */}
              <div style={{
                background: `linear-gradient(135deg, ${theme.headerGradient[0]} 0%, ${theme.headerGradient[1]} 100%)`,
                padding: '1.25rem 1rem', color: 'white', position: 'relative', minHeight: '100px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '1.1rem',
                  }}>🥛</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>My Dairy Name</div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Fresh dairy management</div>
                  </div>
                </div>
                {/* Mini nav preview */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {['Home', 'Orders', 'Bills'].map((n, i) => (
                    <div key={n} style={{
                      padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.65rem',
                      background: i === 0 ? 'rgba(255,255,255,0.25)' : 'transparent',
                      color: 'rgba(255,255,255,0.9)', fontWeight: i === 0 ? 700 : 400,
                    }}>{n}</div>
                  ))}
                </div>
              </div>

              {/* Mini body preview */}
              <div style={{ background: theme.bgColor, padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={{
                      flex: 1, background: 'white', borderRadius: '6px',
                      padding: '0.5rem', borderLeft: `3px solid ${theme.accentColor}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    }}>
                      <div style={{ width: '60%', height: '6px', background: '#E0E0E0', borderRadius: '3px', marginBottom: '4px' }} />
                      <div style={{ width: '40%', height: '10px', background: theme.accentColor, borderRadius: '3px', opacity: 0.7 }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Theme Info */}
              <div style={{ padding: '0.85rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#333' }}>{theme.name}</span>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: theme.themeColor, border: '2px solid #E0E0E0',
                  }} />
                </div>
                <div style={{ color: '#777', fontSize: '0.8rem', lineHeight: 1.4, marginBottom: '0.5rem' }}>{theme.description}</div>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  {theme.tags.map(tag => (
                    <span key={tag} style={{
                      padding: '1px 8px', borderRadius: '10px', fontSize: '0.65rem',
                      background: theme.bgColor, color: theme.themeColor, fontWeight: 600,
                      border: `1px solid ${theme.cardBorder}`,
                    }}>{tag}</span>
                  ))}
                </div>
              </div>

              {/* Hover overlay: Apply button */}
              {isHovered && !isActive && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(255,255,255,0.95))',
                  padding: '1.5rem 1rem 0.85rem', textAlign: 'center',
                }}>
                  <button
                    onClick={e => { e.stopPropagation(); handleApply(theme); }}
                    disabled={applyingId === theme.id}
                    style={{
                      padding: '0.5rem 1.5rem', background: theme.themeColor, color: 'white',
                      border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700,
                      fontSize: '0.85rem', boxShadow: `0 2px 8px ${theme.themeColor}44`,
                      transition: 'all 0.2s', width: '100%',
                    }}
                  >
                    {applyingId === theme.id ? '⏳ Applying...' : '🎨 Apply Theme'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Theme Preview Modal */}
      {previewTheme && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3000,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }} onClick={() => setPreviewTheme(null)}>
          <div style={{
            background: 'white', borderRadius: '16px', maxWidth: '600px', width: '100%',
            overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            {/* Preview header */}
            <div style={{
              background: `linear-gradient(135deg, ${previewTheme.headerGradient[0]} 0%, ${previewTheme.headerGradient[1]} 100%)`,
              padding: '1.5rem', color: 'white',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '1.5rem',
                  }}>🥛</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>Your Dairy Name</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Fresh dairy management</div>
                  </div>
                </div>
                <button onClick={() => setPreviewTheme(null)} style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
                  fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕</button>
              </div>
              {/* Nav preview */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                {['Subscribers', 'Deliveries', 'Billing', 'Reports'].map((n, i) => (
                  <div key={n} style={{
                    padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem',
                    background: i === 0 ? 'rgba(255,255,255,0.2)' : 'transparent',
                    color: 'rgba(255,255,255,0.9)', fontWeight: i === 0 ? 700 : 400,
                    borderBottom: i === 0 ? '2px solid rgba(255,255,255,0.5)' : 'none',
                  }}>{n}</div>
                ))}
              </div>
            </div>

            {/* Preview body */}
            <div style={{ background: previewTheme.bgColor, padding: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                {[{ label: 'Total Subscribers', val: '128' }, { label: 'Deliveries Today', val: '96' }, { label: 'Revenue', val: '₹45,200' }].map(s => (
                  <div key={s.label} style={{
                    background: 'white', padding: '1rem', borderRadius: '10px',
                    borderLeft: `4px solid ${previewTheme.accentColor}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.3rem' }}>{s.label}</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: previewTheme.themeColor }}>{s.val}</div>
                  </div>
                ))}
              </div>
              {/* Sample table */}
              <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ background: previewTheme.bgColor, padding: '0.6rem 1rem', display: 'flex', gap: '2rem' }}>
                  {['Name', 'Milk Type', 'Qty', 'Status'].map(h => (
                    <div key={h} style={{ fontWeight: 700, color: previewTheme.themeColor, fontSize: '0.8rem', flex: 1 }}>{h}</div>
                  ))}
                </div>
                {[['Amit Patel', 'Full Cream', '2L', 'Active'], ['Priya Shah', 'Toned', '1L', 'Paused']].map((row, i) => (
                  <div key={i} style={{ padding: '0.5rem 1rem', display: 'flex', gap: '2rem', borderTop: `1px solid ${previewTheme.cardBorder}` }}>
                    {row.map((cell, j) => (
                      <div key={j} style={{ flex: 1, fontSize: '0.8rem', color: j === 3 ? (cell === 'Active' ? previewTheme.accentColor : '#999') : '#333' }}>{cell}</div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal footer */}
            <div style={{
              padding: '1rem 1.25rem', borderTop: '1px solid #E8E8E8',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#333' }}>{previewTheme.name}</div>
                <div style={{ color: '#777', fontSize: '0.8rem' }}>{previewTheme.description}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setPreviewTheme(null)} style={{
                  padding: '0.5rem 1rem', background: '#F5F5F5', border: '1px solid #E0E0E0',
                  borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                }}>Cancel</button>
                <button
                  onClick={() => handleApply(previewTheme)}
                  disabled={applyingId === previewTheme.id}
                  style={{
                    padding: '0.5rem 1.5rem', background: previewTheme.themeColor, color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700,
                    fontSize: '0.85rem', boxShadow: `0 2px 8px ${previewTheme.themeColor}44`,
                  }}
                >
                  {applyingId === previewTheme.id ? '⏳ Applying...' : '🎨 Apply This Theme'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Dairy Setup / Branding Panel =====
function DairySetupPanel({ onBrandingChange, isSuperAdmin }: { onBrandingChange?: () => void; isSuperAdmin?: boolean }) {
  // SuperAdmin: show tenant-wise branding view
  if (isSuperAdmin) return <SuperAdminDairySetupPanel />;

  const [branding, setBranding] = useState<TenantBrandingDto | null>(null);
  const [form, setForm] = useState<UpdateBrandingRequest>({});
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // Tenant settings (auto-complete delivery time)
  const [tenantSettings, setTenantSettings] = useState<{ autoCompleteDeliveryHour: number | null; autoCompleteDeliveryMinute: number | null }>({ autoCompleteDeliveryHour: null, autoCompleteDeliveryMinute: null });
  const [savingTenant, setSavingTenant] = useState(false);

  // Expense category management
  const [categories, setCategories] = useState<ExpenseCategoryDto[]>([]);
  const [newCatName, setNewCatName] = useState('');
  const [catLoading, setCatLoading] = useState(false);
  const [catMsg, setCatMsg] = useState('');

  const loadCategories = async () => {
    setCatLoading(true);
    const res = await api.getExpenseCategories();
    if (res.data) setCategories(res.data);
    setCatLoading(false);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setCatMsg('');
    const res = await api.createExpenseCategory(newCatName.trim());
    if (res.success) {
      setNewCatName('');
      setCatMsg('✅ Category added');
      loadCategories();
    } else {
      setCatMsg(res.message || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    if (!confirm(`Remove category "${name}"? Existing expenses with this category will be preserved.`)) return;
    const res = await api.deleteExpenseCategory(id);
    if (res.success) {
      setCatMsg('✅ Category removed');
      loadCategories();
    } else {
      setCatMsg(res.message || 'Failed to remove category');
    }
  };

  const load = async () => {
    const res = await api.getBranding();
    if (res.data) {
      setBranding(res.data);
      setForm({
        themeColor: res.data.themeColor || '#2E7D32',
        mainWording: res.data.mainWording || '',
        billHeader: res.data.billHeader || '',
        billFooter: res.data.billFooter || '',
        gstNo: res.data.gstNo || '',
        gstRatePercent: res.data.gstRatePercent ?? 5,
        gstLegalName: res.data.gstLegalName || '',
        gstPlaceOfSupply: res.data.gstPlaceOfSupply || '',
        gstFilingFrequency: res.data.gstFilingFrequency || 'Monthly',
        upiId: res.data.upiId || '',
        address: res.data.address || '',
        remindViaEmail: res.data.remindViaEmail ?? true,
        remindViaWhatsApp: res.data.remindViaWhatsApp ?? false,
        siteUrl: res.data.siteUrl || '',
      });
      if (res.data.logoBase64) setLogoPreview(res.data.logoBase64);
      if (res.data.bannerBase64) setBannerPreview(res.data.bannerBase64);
      if (res.data.qrCodeBase64) setQrPreview(res.data.qrCodeBase64);
    }
    // Load tenant settings for auto-complete delivery time
    const tenantRes = await api.getTenants();
    if (tenantRes.data && tenantRes.data.length > 0) {
      const currentTenant = tenantRes.data[0]; // Assuming single tenant for admin
      setTenantSettings({
        autoCompleteDeliveryHour: currentTenant.autoCompleteDeliveryHour,
        autoCompleteDeliveryMinute: currentTenant.autoCompleteDeliveryMinute,
      });
    }
  };
  useEffect(() => { load(); loadCategories(); }, []);

  const handleImageUpload = (type: 'logo' | 'banner' | 'qr', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsg('Image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      if (type === 'logo') {
        setLogoPreview(base64);
        setForm({ ...form, logoBase64: base64 });
      } else if (type === 'banner') {
        setBannerPreview(base64);
        setForm({ ...form, bannerBase64: base64 });
      } else {
        setQrPreview(base64);
        setForm({ ...form, qrCodeBase64: base64 });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const res = await api.updateBranding(form);
    setMsg(res.message);
    if (res.success) { load(); onBrandingChange?.(); }
    setSaving(false);
  };

  const handleSaveTenantSettings = async () => {
    setSavingTenant(true);
    const tenantRes = await api.getTenants();
    if (tenantRes.data && tenantRes.data.length > 0) {
      const currentTenant = tenantRes.data[0];
      const res = await api.updateTenant(currentTenant.tenantId, {
        autoCompleteDeliveryHour: tenantSettings.autoCompleteDeliveryHour ?? undefined,
        autoCompleteDeliveryMinute: tenantSettings.autoCompleteDeliveryMinute ?? undefined,
      });
      if (res.success) {
        setMsg('✅ Delivery auto-complete time saved');
      } else {
        setMsg(res.message || 'Failed to save delivery settings');
      }
    }
    setSavingTenant(false);
  };

  // Theme gallery apply handler
  const handleThemeApply = async (preset: ThemePreset) => {
    setSaving(true);
    setMsg('');
    const res = await api.updateBranding({ ...form, themeColor: preset.themeColor });
    if (res.success) {
      setForm(prev => ({ ...prev, themeColor: preset.themeColor }));
      setMsg(`✅ Theme "${preset.name}" applied successfully!`);
      load();
      onBrandingChange?.();
    } else {
      setMsg(res.message || 'Failed to apply theme');
    }
    setSaving(false);
  };

  return (
    <div>
      <PanelHeader title="Dairy Setup & Branding" />
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {/* Theme Gallery */}
      <div style={{ maxWidth: '900px', marginBottom: '1.5rem' }}>
        <ThemeGallery currentThemeColor={form.themeColor || '#2E7D32'} onApply={handleThemeApply} />
      </div>

      <form onSubmit={handleSave} style={{ maxWidth: '650px' }}>
        {/* Logo & Banner */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🖼️ Images</h3>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Logo Image</label>
              {logoPreview && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <img src={logoPreview} alt="Logo" style={{ maxWidth: '120px', maxHeight: '120px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => handleImageUpload('logo', e)} style={{ fontSize: '0.8rem' }} />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>Square image recommended. Max 2MB.</div>
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Banner Image</label>
              {bannerPreview && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <img src={bannerPreview} alt="Banner" style={{ maxWidth: '250px', maxHeight: '100px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => handleImageUpload('banner', e)} style={{ fontSize: '0.8rem' }} />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>Wide banner image. Max 2MB.</div>
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>UPI QR Code</label>
              {qrPreview && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <img src={qrPreview} alt="UPI QR" style={{ maxWidth: '140px', maxHeight: '140px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => handleImageUpload('qr', e)} style={{ fontSize: '0.8rem' }} />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>Upload UPI QR image shown on subscriber bills. Max 2MB.</div>
            </div>
          </div>
        </div>

        {/* Theme & Info */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🎨 Theme & Wording</h3>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.3rem' }}>Custom Theme Color</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={form.themeColor || '#2E7D32'} onChange={e => setForm({ ...form, themeColor: e.target.value })}
                  style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer', borderRadius: '6px' }} />
                <input value={form.themeColor || ''} onChange={e => setForm({ ...form, themeColor: e.target.value })}
                  placeholder="#2E7D32" style={{ ...inputStyle, width: '120px' }} />
              </div>
              <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '4px' }}>Or pick from the theme gallery above</div>
            </div>
          </div>

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.75rem' }}>Main Wording / Tagline</label>
          <input value={form.mainWording || ''} onChange={e => setForm({ ...form, mainWording: e.target.value })}
            placeholder="e.g. Fresh Pure Milk Delivered Daily" style={inputStyle} />
        </div>

        {/* Bill Customization */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🧾 Bill Format</h3>

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Bill Header Text</label>
          <textarea value={form.billHeader || ''} onChange={e => setForm({ ...form, billHeader: e.target.value })}
            placeholder="e.g. GOKUL DAIRY - Monthly Milk Bill" style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }} />

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Bill Footer Text</label>
          <textarea value={form.billFooter || ''} onChange={e => setForm({ ...form, billFooter: e.target.value })}
            placeholder="e.g. Thank you for choosing Gokul Dairy! For queries: 9876543210" style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }} />

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>GST Number</label>
          <input value={form.gstNo || ''} onChange={e => setForm({ ...form, gstNo: e.target.value })}
            placeholder="e.g. 24AABCU9603R1ZM" style={inputStyle} />

          <div style={{ background: '#FFF9C4', border: '2px solid #F9A825', borderRadius: '8px', padding: '0.75rem', marginTop: '0.75rem' }}>
            <label style={{ fontWeight: 700, color: '#E65100', fontSize: '1rem', display: 'block', marginBottom: '0.3rem' }}>⚠️ GST Rate (%)</label>
            <input type="number" step="0.01" min="0" max="100" value={form.gstRatePercent ?? 5}
              onChange={e => setForm({ ...form, gstRatePercent: parseFloat(e.target.value) || 0 })}
              placeholder="e.g. 5" style={{ ...inputStyle, fontWeight: 700, fontSize: '1.05rem', border: '2px solid #F9A825' }} />
            <div style={{ fontSize: '0.8rem', color: '#6D4C41', marginTop: '4px', fontWeight: 500 }}>⚖️ Applied as CGST + SGST on every bill. E.g. 5% = 2.5% CGST + 2.5% SGST.</div>
          </div>

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>UPI ID</label>
          <input value={form.upiId || ''} onChange={e => setForm({ ...form, upiId: e.target.value })}
            placeholder="e.g. gokuldairy@okicici" style={inputStyle} />

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>Business Address</label>
          <textarea value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })}
            placeholder="e.g. Near Bus Stand, Anand, Gujarat 388001" style={{ ...inputStyle, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }} />

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.5rem' }}>🔗 Site URL (Login Link)</label>
          <input value={form.siteUrl || ''} onChange={e => setForm({ ...form, siteUrl: e.target.value })}
            placeholder="e.g. https://milk-subscription-app.azurewebsites.net/sitai/login" style={inputStyle} />
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>Used in WhatsApp messages sent to subscribers. Update here when your login URL changes.</div>
        </div>

        {/* Reminder Preferences */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🔔 Payment Reminder Channel</h3>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>Choose how payment reminders are sent to subscribers. Selecting both sends every reminder via Email AND WhatsApp.</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600, color: 'var(--tc)' }}>
              <input type="checkbox" checked={form.remindViaEmail ?? true}
                onChange={e => setForm({ ...form, remindViaEmail: e.target.checked })} />
              📧 Remind via Email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600, color: 'var(--tc)' }}>
              <input type="checkbox" checked={form.remindViaWhatsApp ?? false}
                onChange={e => setForm({ ...form, remindViaWhatsApp: e.target.checked })} />
              💬 Remind via WhatsApp
            </label>
          </div>
        </div>

        {/* Delivery Auto-Complete Time */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🚚 Delivery Auto-Complete Time</h3>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
            Set the time when pending deliveries from yesterday and older are automatically marked as "Delivered". 
            Delivery boys should complete today's deliveries before this time.
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.3rem' }}>Hour (IST)</label>
              <select
                value={tenantSettings.autoCompleteDeliveryHour ?? 13}
                onChange={e => setTenantSettings({ ...tenantSettings, autoCompleteDeliveryHour: parseInt(e.target.value) })}
                style={{ ...inputStyle, width: '100px' }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.3rem' }}>Minute</label>
              <select
                value={tenantSettings.autoCompleteDeliveryMinute ?? 0}
                onChange={e => setTenantSettings({ ...tenantSettings, autoCompleteDeliveryMinute: parseInt(e.target.value) })}
                style={{ ...inputStyle, width: '100px' }}
              >
                {[0, 15, 30, 45].map(m => (
                  <option key={m} value={m}>:{m.toString().padStart(2, '0')}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleSaveTenantSettings}
              disabled={savingTenant}
              style={{
                background: 'var(--tc)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                cursor: savingTenant ? 'default' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                opacity: savingTenant ? 0.7 : 1,
              }}
            >
              {savingTenant ? '⏳ Saving...' : '💾 Save Time'}
            </button>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem' }}>
            Current setting: <strong>{(tenantSettings.autoCompleteDeliveryHour ?? 13).toString().padStart(2, '0')}:{(tenantSettings.autoCompleteDeliveryMinute ?? 0).toString().padStart(2, '0')} IST</strong> (default: 13:00 = 1:00 PM)
          </div>
        </div>

        {/* GST Details */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>🧾 GST Details</h3>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', display: 'block', marginBottom: '0.3rem' }}>GST Filing Frequency</label>
              <select value={form.gstFilingFrequency || 'Monthly'}
                onChange={e => setForm({ ...form, gstFilingFrequency: e.target.value })} style={inputStyle}>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </div>
          </div>

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.75rem' }}>GST Legal Name</label>
          <input value={form.gstLegalName || ''} onChange={e => setForm({ ...form, gstLegalName: e.target.value })}
            placeholder="e.g. Gokul Dairy Products Pvt Ltd" style={inputStyle} />
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>Legal business name as registered with GST.</div>

          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem', marginTop: '0.75rem' }}>GST Place of Supply</label>
          <input value={form.gstPlaceOfSupply || ''} onChange={e => setForm({ ...form, gstPlaceOfSupply: e.target.value })}
            placeholder="e.g. Gujarat (24)" style={inputStyle} />
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>State and code as per GST. E.g. Maharashtra (27), Gujarat (24).</div>
        </div>

        {/* Expense Categories */}
        <div style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem' }}>📂 Expense Categories</h3>
          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
            Manage expense categories used in Cash Management. Default categories cannot be removed.
          </div>

          {catMsg && <div style={{ padding: '0.4rem 0.75rem', marginBottom: '0.5rem', borderRadius: '6px', background: catMsg.startsWith('✅') ? 'var(--tc-light)' : '#FFEBEE', color: catMsg.startsWith('✅') ? 'var(--tc)' : '#C62828', fontSize: '0.85rem' }}>{catMsg}</div>}

          {/* Add new category */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
              placeholder="New category name..."
              style={{ ...inputStyle, flex: 1 }}
              maxLength={100}
            />
            <button
              type="button"
              onClick={handleAddCategory}
              disabled={!newCatName.trim()}
              style={{
                background: newCatName.trim() ? 'var(--tc)' : '#ccc',
                color: 'white', border: 'none', borderRadius: '6px',
                padding: '0.5rem 1rem', cursor: newCatName.trim() ? 'pointer' : 'default',
                fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap'
              }}
            >
              ➕ Add
            </button>
          </div>

          {/* Category list */}
          {catLoading ? (
            <div style={{ color: '#888', fontSize: '0.85rem' }}>Loading categories...</div>
          ) : categories.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.85rem' }}>No categories yet. Add your first one above.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {categories.map(cat => (
                <div key={cat.expenseCategoryId} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  background: cat.isDefault ? 'var(--tc-light)' : '#FFF3E0',
                  border: `1px solid ${cat.isDefault ? 'var(--tc-border)' : '#FFE0B2'}`,
                  borderRadius: '20px', padding: '0.3rem 0.75rem', fontSize: '0.85rem'
                }}>
                  <span>{cat.name}</span>
                  {cat.isDefault ? (
                    <span style={{ fontSize: '0.7rem', color: '#888' }}>(default)</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(cat.expenseCategoryId, cat.name)}
                      style={{
                        background: 'none', border: 'none', color: '#E53935',
                        cursor: 'pointer', fontWeight: 700, fontSize: '1rem', padding: '0 2px',
                        lineHeight: 1
                      }}
                      title="Remove category"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <GreenButton type="submit">{saving ? '⏳ Saving...' : '💾 Save Dairy Setup'}</GreenButton>
        </div>
      </form>

      {/* Preview */}
      {branding && (
        <div style={{ marginTop: '1.5rem', maxWidth: '650px' }}>
          <h3 style={{ color: 'var(--tc)', marginBottom: '0.75rem' }}>👁 Preview</h3>
          <div style={{
            background: 'white', borderRadius: '12px', overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: `2px solid ${branding.themeColor || '#2E7D32'}`,
          }}>
            {bannerPreview && (
              <img src={bannerPreview} alt="Banner" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover' }} />
            )}
            <div style={{ padding: '1.25rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              {logoPreview && (
                <img src={logoPreview} alt="Logo" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${branding.themeColor}` }} />
              )}
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.15rem', color: branding.themeColor }}>{branding.tenantName}</div>
                {branding.mainWording && <div style={{ color: '#666', fontSize: '0.9rem' }}>{branding.mainWording}</div>}
                {branding.gstNo && <div style={{ color: '#888', fontSize: '0.8rem' }}>GST: {branding.gstNo}</div>}
                {branding.gstLegalName && <div style={{ color: '#888', fontSize: '0.8rem' }}>Legal Name: {branding.gstLegalName}</div>}
                {branding.gstPlaceOfSupply && <div style={{ color: '#888', fontSize: '0.8rem' }}>Place of Supply: {branding.gstPlaceOfSupply}</div>}
                <div style={{ color: '#888', fontSize: '0.8rem' }}>GST Rate: {branding.gstRatePercent}% (CGST {(branding.gstRatePercent / 2).toFixed(2)}% + SGST {(branding.gstRatePercent / 2).toFixed(2)}%)</div>
                {branding.gstFilingFrequency && <div style={{ color: '#888', fontSize: '0.8rem' }}>Filing: {branding.gstFilingFrequency}</div>}
                {branding.upiId && <div style={{ color: '#888', fontSize: '0.8rem' }}>UPI: {branding.upiId}</div>}
                {branding.address && <div style={{ color: '#888', fontSize: '0.8rem' }}>{branding.address}</div>}
              </div>
            </div>
            {(qrPreview || branding.qrCodeBase64) && (
              <div style={{ padding: '0 1.25rem 1.25rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.4rem' }}>UPI QR Preview</div>
                <img src={qrPreview || branding.qrCodeBase64 || ''} alt="UPI QR Preview" style={{ width: '120px', height: '120px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Dashboard Home Panel =====
function DashboardHomePanel({ showPurchaseSale = true, showCashManagement = true }: { showPurchaseSale?: boolean; showCashManagement?: boolean }) {
  const [metrics, setMetrics] = useState<DashboardMetricsDto | null>(null);
  const [psSummary, setPsSummary] = useState<PurchaseSaleDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const calls: Promise<unknown>[] = [api.getDashboardMetrics()];
        if (showPurchaseSale) calls.push(api.getPurchaseSaleDashboard());
        const [mRes, psRes] = await Promise.allSettled(calls);
        if (mRes.status === 'fulfilled' && (mRes.value as { data?: DashboardMetricsDto }).data) setMetrics((mRes.value as { data: DashboardMetricsDto }).data);
        if (psRes && psRes.status === 'fulfilled' && (psRes.value as { data?: PurchaseSaleDashboard }).data) setPsSummary((psRes.value as { data: PurchaseSaleDashboard }).data);
      } catch { setError('Failed to load dashboard metrics'); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <MsgBanner>{error}</MsgBanner>;
  if (!metrics) return <MsgBanner>No metrics available</MsgBanner>;

  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  return (
    <div>
      <PanelHeader icon="📊" title="Dashboard" subtitle={`Overview for ${metrics.currentMonth}`} />

      {/* Subscriber Metrics */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>👥 Subscriber Metrics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <StatCard label="Total Subscribers" value={metrics.totalSubscribers} icon="👥" />
          <StatCard label="Active Subscribers" value={metrics.activeSubscribers} icon="✅" color="#2E7D32" />
          <StatCard label="Inactive Subscribers" value={metrics.inactiveSubscribers} icon="⛔" color="#757575" />
          <StatCard label="New This Month" value={metrics.newSubscribersThisMonth} icon="🆕" color="#0277BD" />
        </div>
      </div>

      {/* Today's Activity */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📅 Today's Activity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <StatCard label="Pending Deliveries" value={metrics.pendingDeliveriesToday} icon="⏳" color="#F57F17" />
          <StatCard label="Completed Deliveries" value={metrics.todaysCompletedDeliveries} icon="✅" color="#2E7D32" />
          <StatCard label="Missed Deliveries" value={metrics.failedDeliveriesToday} icon="❌" color="#D32F2F" />
          <StatCard label="Delivery Success Rate" value={`${metrics.deliverySuccessRatePercent}%`} icon="🎯" color={metrics.deliverySuccessRatePercent >= 90 ? '#2E7D32' : '#F57F17'} />
          <StatCard label="Today's Qty (L)" value={metrics.todayQuantity} icon="🥛" />
        </div>
      </div>

      {/* Financial Overview */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>💰 Billing Metrics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <StatCard label="Pending Bills (Unbilled)" value={fmt(metrics.unbilledAmount)} icon="📋" color="#E65100" />
          <StatCard label="Total Outstanding" value={fmt(metrics.totalOutstanding)} icon="⚠️" color="#D32F2F" />
          <StatCard label="Today's Collections" value={fmt(metrics.todaysCollections)} icon="💵" color="#2E7D32" />
          <StatCard label="Monthly Revenue" value={fmt(metrics.monthlyRevenue)} icon="📈" color="#2E7D32" />
        </div>
      </div>

      {/* Previous Month Summary */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📆 Previous Month Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <StatCard label="Last Month Total Bill" value={fmt(metrics.lastMonthTotalBillAmount)} icon="🧾" color="#0277BD" />
          <StatCard label="Last Month Collected" value={fmt(metrics.lastMonthCollectedAmount)} icon="💰" color="#2E7D32" />
          <StatCard label="Last Month Pending" value={fmt(metrics.lastMonthPendingAmount)} icon="⏳" color="#D32F2F" />
        </div>
      </div>

      {/* Cash Flow */}
      {showCashManagement && (
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>🏪 Cash Flow ({metrics.currentMonth})</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <StatCard label="Cash Inflow" value={fmt(metrics.monthlyCashInflow)} icon="💵" color="#2E7D32" />
          <StatCard label="Expenses" value={fmt(metrics.monthlyExpenses)} icon="💸" color="#D32F2F" />
          <StatCard label="Net Profit" value={fmt(metrics.monthlyProfit)} icon="✨" color={metrics.monthlyProfit >= 0 ? '#2E7D32' : '#D32F2F'} />
        </div>
      </div>
      )}

      {/* Purchase / Sale Summary */}
      {showPurchaseSale && psSummary && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ color: 'var(--tc-dark)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>🛒 Purchase / Sale Overview</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <StatCard label="Today's Purchase" value={fmt(psSummary.todayPurchaseAmount)} icon="📦" color="#7B1FA2" />
            <StatCard label="Today's Sale" value={fmt(psSummary.todaySaleAmount)} icon="💰" color="#0277BD" />
            <StatCard label="Monthly Purchase" value={fmt(psSummary.monthlyPurchaseAmount)} icon="🏭" color="#7B1FA2" />
            <StatCard label="Monthly Sale" value={fmt(psSummary.monthlySaleAmount)} icon="🛍️" color="#0277BD" />
            <StatCard label="Monthly Profit/Loss" value={fmt(psSummary.monthlyProfitLoss)} icon={psSummary.monthlyProfitLoss >= 0 ? '📈' : '📉'} color={psSummary.monthlyProfitLoss >= 0 ? '#2E7D32' : '#D32F2F'} />
            <StatCard label="Pending Purchase Payments" value={fmt(psSummary.pendingPurchasePayments)} icon="⏳" color="#E65100" />
            <StatCard label="Pending Sale Payments" value={fmt(psSummary.pendingSalePayments)} icon="⏳" color="#F57F17" />
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Cash Flow Dashboard Panel (dedicated financial metrics dashboard) =====
function CashFlowDashboardPanel() {
  const [dashboard, setDashboard] = useState<CashFlowDashboardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.getCashFlowDashboard();
        if (res.data) setDashboard(res.data);
        else setError(res.message || 'Failed to load cash flow dashboard');
      } catch { setError('Failed to load cash flow dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <MsgBanner>{error}</MsgBanner>;
  if (!dashboard) return <MsgBanner>No data available</MsgBanner>;

  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  const maxTrend = Math.max(1, ...dashboard.monthlyRevenueTrend.map(t => t.amount));
  const totalByMode = dashboard.paymentMethodBreakdown.reduce((sum, p) => sum + p.amount, 0) || 1;
  const modeColors: Record<string, string> = { Cash: '#2E7D32', UPI: '#0277BD' };

  return (
    <div>
      <PanelHeader icon="💹" title="Cash Flow Dashboard" subtitle="Financial overview for your dairy" />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Today's Income" value={fmt(dashboard.todayIncome)} icon="💵" color="#2E7D32" />
        <StatCard label="This Month Income" value={fmt(dashboard.thisMonthIncome)} icon="💰" color="#2E7D32" />
        <StatCard label="This Month Expenses" value={fmt(dashboard.thisMonthExpenses)} icon="💸" color="#D32F2F" />
        <StatCard label="Net Cash Flow" value={fmt(dashboard.netCashFlow)} icon="✨" color={dashboard.netCashFlow >= 0 ? '#2E7D32' : '#D32F2F'} />
        <StatCard label="Outstanding Receivables" value={fmt(dashboard.outstandingReceivables)} icon="⚠️" color="#E65100" />
        <StatCard label="Collection %" value={`${dashboard.collectionPercentage}%`} icon="🎯" color={dashboard.collectionPercentage >= 80 ? '#2E7D32' : '#F57F17'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        {/* Monthly Revenue Trend */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 1rem', color: 'var(--tc-dark)', fontSize: '1rem' }}>📈 Monthly Revenue Trend</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', height: '160px' }}>
            {dashboard.monthlyRevenueTrend.map(point => (
              <div key={point.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ fontSize: '0.72rem', color: '#666' }}>{fmt(point.amount)}</div>
                <div style={{
                  width: '100%', maxWidth: '36px',
                  height: `${Math.max(4, (point.amount / maxTrend) * 120)}px`,
                  background: 'var(--tc-btn)', borderRadius: '4px 4px 0 0',
                }} />
                <div style={{ fontSize: '0.72rem', color: '#888' }}>{point.month.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Method Breakdown */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 1rem', color: 'var(--tc-dark)', fontSize: '1rem' }}>🥧 Payment Method Breakdown</h3>
          {dashboard.paymentMethodBreakdown.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.9rem' }}>No payments recorded this month.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {dashboard.paymentMethodBreakdown.map(p => {
                const pct = Math.round((p.amount / totalByMode) * 100);
                return (
                  <div key={p.mode}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 600 }}>{p.mode} ({p.count})</span>
                      <span>{fmt(p.amount)} · {pct}%</span>
                    </div>
                    <div style={{ background: '#F0F0F0', borderRadius: '6px', overflow: 'hidden', height: '10px' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: modeColors[p.mode] || 'var(--tc-btn)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== My Profile Panel =====
function ProfilePanel({ userId }: { userId: number }) {
  const [profile, setProfile] = useState<UserDto | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateProfileRequest>({ name: '', email: '', phone: '', address: '' });
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const res = await api.getProfile();
    if (res.data) {
      setProfile(res.data);
      setForm({ name: res.data.name, email: res.data.email, phone: res.data.phone || '', address: res.data.address || '' });
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [userId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.updateProfile(form);
    setMsg(res.message);
    if (res.success) { setEditing(false); load(); }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwdForm.newPassword.length < 6) {
      setMsg('New password must be at least 6 characters');
      return;
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setMsg('New password and confirm password do not match');
      return;
    }

    const res = await api.changeOwnPassword(pwdForm.currentPassword, pwdForm.newPassword);
    setMsg(res.message);
    if (res.success) {
      setShowPwdForm(false);
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!profile) return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Profile not found.</div>;

  return (
    <div>
      <PanelHeader title="My Profile">
        {!editing && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <GreenButton onClick={() => setEditing(true)}>Edit Profile</GreenButton>
            <GreenButton onClick={() => setShowPwdForm(v => !v)}>🔑 Change Password</GreenButton>
          </div>
        )}
      </PanelHeader>
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {editing ? (
        <form onSubmit={handleSave} style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '500px' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Name</label>
          <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name" style={inputStyle} />
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Email</label>
          <input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" style={inputStyle} />
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Phone</label>
          <input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone Number" style={inputStyle} />
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Address</label>
          <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full Address" style={inputStyle} />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <GreenButton type="submit">Save</GreenButton>
            <button type="button" onClick={() => { setEditing(false); setForm({ name: profile.name, email: profile.email, phone: profile.phone || '', address: profile.address || '' }); }} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'grid', gap: '1rem', maxWidth: '500px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <ProfileField label="Name" value={profile.name} />
            <ProfileField label="Email" value={profile.email} />
            <ProfileField label="Phone" value={profile.phone || '—'} />
            <ProfileField label="Address" value={profile.address || '—'} />
            <ProfileField label="Role" value={profile.role} />
          </div>

          {showPwdForm && (
            <form onSubmit={handlePasswordChange} style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', margin: 0 }}>
              <strong style={{ color: 'var(--tc)' }}>Change Password</strong>
              <input
                type="password"
                value={pwdForm.currentPassword}
                onChange={e => setPwdForm({ ...pwdForm, currentPassword: e.target.value })}
                placeholder="Current Password"
                required
                minLength={6}
                style={inputStyle}
              />
              <input
                type="password"
                value={pwdForm.newPassword}
                onChange={e => setPwdForm({ ...pwdForm, newPassword: e.target.value })}
                placeholder="New Password"
                required
                minLength={6}
                style={inputStyle}
              />
              <input
                type="password"
                value={pwdForm.confirmPassword}
                onChange={e => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })}
                placeholder="Confirm New Password"
                required
                minLength={6}
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <GreenButton type="submit">Update Password</GreenButton>
                <button
                  type="button"
                  onClick={() => {
                    setShowPwdForm(false);
                    setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  style={{ ...smallBtn, background: '#757575', color: 'white' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', padding: '0.65rem 0', borderBottom: '1px solid var(--tc-vlight)' }}>
      <div style={{ width: '120px', fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>{label}</div>
      <div style={{ color: '#333', fontSize: '0.9rem' }}>{value}</div>
    </div>
  );
}

// ===== SuperAdmin Dairy Setup Panel (Tenant-wise) =====
function SuperAdminDairySetupPanel() {
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [branding, setBranding] = useState<TenantBrandingDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await api.getTenants();
      if (res.data) setTenants(res.data);
    })();
  }, []);

  useEffect(() => {
    if (!selectedTenantId) { setBranding(null); return; }
    (async () => {
      setLoading(true);
      const res = await api.getBrandingByTenantId(selectedTenantId);
      setBranding(res.data ?? null);
      setLoading(false);
    })();
  }, [selectedTenantId]);

  const fieldRow = (label: string, value: string | null | undefined) => (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
      <span style={{ fontWeight: 600, color: 'var(--tc)', minWidth: '140px' }}>{label}:</span>
      <span style={{ color: '#333' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div>
      <PanelHeader title="Dairy Setup (Tenant-wise)" />

      <div style={{ marginBottom: '1.5rem', maxWidth: '400px' }}>
        <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem', display: 'block', marginBottom: '0.4rem' }}>Select Tenant</label>
        <select
          value={selectedTenantId ?? ''}
          onChange={e => setSelectedTenantId(e.target.value ? Number(e.target.value) : null)}
          style={{ ...inputStyle, width: '100%' }}
        >
          <option value="">-- Select a tenant --</option>
          {tenants.filter(t => t.isActive).map(t => (
            <option key={t.tenantId} value={t.tenantId}>{t.name} (ID: {t.tenantId})</option>
          ))}
        </select>
      </div>

      {!selectedTenantId && (
        <div style={{ textAlign: 'center', color: '#888', padding: '3rem', fontSize: '1rem' }}>
          Select a tenant to view their dairy setup &amp; branding.
        </div>
      )}

      {selectedTenantId && loading && <LoadingSpinner />}

      {selectedTenantId && !loading && branding && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {/* Images Row */}
          <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem', fontSize: '1rem' }}>🖼️ Images</h3>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.3rem' }}>Logo</div>
              {branding.logoBase64 ? (
                <img src={branding.logoBase64} alt="Logo" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
              ) : (
                <div style={{ width: '100px', height: '100px', background: '#F5F5F5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '0.8rem' }}>No logo</div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.3rem' }}>Banner</div>
              {branding.bannerBase64 ? (
                <img src={branding.bannerBase64} alt="Banner" style={{ maxWidth: '250px', maxHeight: '100px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
              ) : (
                <div style={{ width: '250px', height: '100px', background: '#F5F5F5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '0.8rem' }}>No banner</div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.3rem' }}>UPI QR</div>
              {branding.qrCodeBase64 ? (
                <img src={branding.qrCodeBase64} alt="QR" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '8px', border: '2px solid #E0E0E0' }} />
              ) : (
                <div style={{ width: '100px', height: '100px', background: '#F5F5F5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '0.8rem' }}>No QR</div>
              )}
            </div>
          </div>

          {/* Theme & Info */}
          <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem', fontSize: '1rem' }}>🎨 Theme & Details</h3>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.9rem' }}>Theme Color:</span>
            <div style={{ width: '30px', height: '30px', borderRadius: '6px', background: branding.themeColor || '#2E7D32', border: '2px solid #E0E0E0' }} />
            <span style={{ fontSize: '0.85rem', color: '#555' }}>{branding.themeColor || '#2E7D32'}</span>
          </div>
          {fieldRow('Tenant Name', branding.tenantName)}
          {fieldRow('Email', branding.tenantEmail)}
          {fieldRow('Address', branding.address)}
          {fieldRow('Main Wording', branding.mainWording)}
          {fieldRow('Bill Header', branding.billHeader)}
          {fieldRow('Bill Footer', branding.billFooter)}
          {fieldRow('UPI ID', branding.upiId)}

          {/* GST Info */}
          <h3 style={{ color: 'var(--tc)', margin: '1.5rem 0 1rem', fontSize: '1rem' }}>🧾 GST Details</h3>
          {fieldRow('GST No', branding.gstNo)}
          {fieldRow('GST Rate', branding.gstRatePercent != null ? `${branding.gstRatePercent}%` : null)}
          {fieldRow('Legal Name', branding.gstLegalName)}
          {fieldRow('Place of Supply', branding.gstPlaceOfSupply)}
          {fieldRow('Filing Frequency', branding.gstFilingFrequency)}
        </div>
      )}

      {selectedTenantId && !loading && !branding && (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem', fontSize: '0.95rem' }}>
          No branding configured for this tenant yet.
        </div>
      )}
    </div>
  );
}

// ===== Tenants Panel (SuperAdmin only) =====
function TenantsPanel() {
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', billingCycleDay: 30, reportEmail: '', adminPassword: '', address: '', themeColor: '#2E7D32' });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [clearResult, setClearResult] = useState<ClearTenantDataResult | null>(null);
  const [clearing, setClearing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [downloadingBackup, setDownloadingBackup] = useState<number | null>(null);
  const [restoreTenantId, setRestoreTenantId] = useState<number | ''>('');
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState<TenantRestoreResult | null>(null);
  const [performanceMonth, setPerformanceMonth] = useState(currentMonth());
  // Copy Tenant state
  const [copySource, setCopySource] = useState<TenantDto | null>(null);
  const [copyForm, setCopyForm] = useState({ newName: '', newEmail: '', adminPassword: '', copyBranding: true, copyMilkTypes: true, copyAddressMasters: true });
  const [copying, setCopying] = useState(false);
  const [performance, setPerformance] = useState<TenantPerformanceReportDto | null>(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [resettingTenantPassword, setResettingTenantPassword] = useState<number | null>(null);
  // Excel onboarding state for new tenant creation
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelErrors, setExcelErrors] = useState<Array<{ sheetName: string; rowNumber: number; columnName: string; errorCode: string; errorMessage: string; suggestion: string; originalValue?: string }> | null>(null);
  // Onboard Dairy (existing tenant) modal state
  const [onboardTenant, setOnboardTenant] = useState<TenantDto | null>(null);
  const [onboardFile, setOnboardFile] = useState<File | null>(null);
  const [onboardUploading, setOnboardUploading] = useState(false);
  const [onboardMsg, setOnboardMsg] = useState('');
  const [onboardErrors, setOnboardErrors] = useState<string[] | null>(null);
  const [onboardErrorExcel, setOnboardErrorExcel] = useState<string | null>(null);
  // Address Setup (Society/Building/Flat) onboarding state
  const [addressSetupTenant, setAddressSetupTenant] = useState<TenantDto | null>(null);
  const [addressSetupMsg, setAddressSetupMsg] = useState('');
  const [addressSetupUploading, setAddressSetupUploading] = useState(false);
  const [confirmClearTenant, setConfirmClearTenant] = useState<{ tenantId: number; tenantName: string } | null>(null);
  const [confirmDeleteTenant, setConfirmDeleteTenant] = useState<{ tenantId: number; tenantName: string } | null>(null);

  const load = async () => {
    const res = await api.getTenants();
    if (res.data) setTenants(res.data);
  };
  useEffect(() => { load(); }, []);

  const loadPerformance = async () => {
    setPerformanceLoading(true);
    const res = await api.getTenantPerformanceReport(performanceMonth);
    if (res.data) setPerformance(res.data);
    setPerformanceLoading(false);
  };
  useEffect(() => {
    loadPerformance();
  }, [performanceMonth]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setExcelUploading(true);
    setExcelErrors(null);
    try {
      if (excelFile) {
        const res = await api.createTenantWithExcel({
          name: form.name,
          email: form.email,
          billingCycleDay: form.billingCycleDay,
          reportEmail: form.reportEmail || undefined,
          adminPassword: form.adminPassword || undefined,
          address: form.address || undefined,
          excelFile,
        });
        setMsg(res.message || (res.success ? 'Tenant created with Excel onboarding' : 'Failed'));
        if (res.data?.onboarding?.hasErrors && res.data?.onboarding?.errorExcelBytes) {
          setExcelErrors(res.data.onboarding.allErrors || []);
        }
        if (res.success) {
          setShowForm(false);
          setForm({ name: '', email: '', billingCycleDay: 30, reportEmail: '', adminPassword: '', address: '', themeColor: '#2E7D32' });
          setLogoPreview(null);
          setBannerPreview(null);
          setExcelFile(null);
          setExcelErrors(null);
          load();
        }
      } else {
        const res = await api.createTenant({
          name: form.name,
          email: form.email,
          billingCycleDay: form.billingCycleDay,
          reportEmail: form.reportEmail || undefined,
          adminPassword: form.adminPassword || undefined,
          address: form.address || undefined,
          logoBase64: logoPreview || undefined,
          bannerBase64: bannerPreview || undefined,
          themeColor: form.themeColor || undefined,
        });
        setMsg(res.message);
        if (res.success) {
          setShowForm(false);
          setForm({ name: '', email: '', billingCycleDay: 30, reportEmail: '', adminPassword: '', address: '', themeColor: '#2E7D32' });
          setLogoPreview(null);
          setBannerPreview(null);
          setExcelFile(null);
          load();
        }
      }
    } catch {
      setMsg('Tenant creation failed');
    } finally {
      setExcelUploading(false);
    }
  };

  const handleOnboardUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onboardTenant || !onboardFile) {
      setOnboardMsg('Please select an Excel file first');
      return;
    }
    setOnboardUploading(true);
    setOnboardMsg('');
    setOnboardErrors(null);
    setOnboardErrorExcel(null);
    try {
      const res = await api.onboardFromExcel(onboardTenant.tenantId, onboardFile);
      const totalSuccess = res.data?.onboarding?.totalSuccess ?? 0;
      const totalFailed = res.data?.onboarding?.totalFailed ?? 0;
      if (res.data?.hasErrors) {
        setOnboardErrors(res.data.onboarding?.allErrors || []);
        setOnboardErrorExcel(res.data.errorExcelBytes || null);
        setOnboardMsg(`Onboarded: ${totalSuccess} success, ${totalFailed} errors. Please fix errors and re-upload.`);
      } else {
        setOnboardMsg(`Onboarded: ${totalSuccess} records imported successfully`);
        setOnboardFile(null);
        load();
      }
    } catch {
      setOnboardMsg('Onboarding failed. Please try again.');
    } finally {
      setOnboardUploading(false);
    }
  };

  const handleOnboardDownloadError = () => {
    if (!onboardErrorExcel) return;
    try {
      const binary = atob(onboardErrorExcel);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'onboarding-errors.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setOnboardMsg('Error Excel download failed');
    }
  };

  const handleClearData = async (tenantId: number, tenantName: string) => {
    setConfirmClearTenant({ tenantId, tenantName });
  };

  const handleClearDataConfirm = async () => {
    if (!confirmClearTenant) return;
    setClearing(confirmClearTenant.tenantId);
    setClearResult(null);
    const res = await api.clearTenantData(confirmClearTenant.tenantId);
    setMsg(res.message);
    if (res.data) setClearResult(res.data);
    setClearing(null);
    setConfirmClearTenant(null);
    load();
  };

  const handleDeleteTenant = async (tenantId: number, tenantName: string) => {
    setConfirmDeleteTenant({ tenantId, tenantName });
  };

  const handleDeleteTenantConfirm = async () => {
    if (!confirmDeleteTenant) return;
    setDeleting(confirmDeleteTenant.tenantId);
    const res = await api.deleteTenant(confirmDeleteTenant.tenantId);
    setMsg(res.message);
    setDeleting(null);
    setConfirmDeleteTenant(null);
    load();
  };

  const handleDownloadBackup = async (tenantId: number, tenantName: string) => {
    try {
      setDownloadingBackup(tenantId);
      const blob = await api.downloadTenantBackup(tenantId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantName.replace(/\s+/g, '-').toLowerCase()}-backup-${today()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMsg(`Backup downloaded for ${tenantName}`);
    } catch {
      setMsg('Backup download failed');
    } finally {
      setDownloadingBackup(null);
    }
  };

  const handleRestoreFile = async (file: File) => {
    if (!restoreTenantId) {
      setMsg('Select a tenant before restore');
      return;
    }

    try {
      setRestoring(true);
      const text = await file.text();
      const payload = JSON.parse(text) as TenantBackupDto;
      const res = await api.restoreTenantBackup(Number(restoreTenantId), payload);
      setMsg(res.message);
      if (res.data) setRestoreResult(res.data);
      load();
      loadPerformance();
    } catch {
      setMsg('Restore failed. Check backup JSON format and tenant selection.');
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadPerformanceCsv = () => {
    if (!performance) return;
    const rows: Array<Array<string | number>> = [
      ['Tenant ID', 'Tenant Name', 'Active Subscribers', 'Delivered', 'Skipped', 'Delivery Success %', 'Total Billed', 'Collected', 'Outstanding', 'Collection %', 'Performance Score'],
      ...performance.tenants.map(t => [
        t.tenantId,
        t.tenantName,
        t.activeSubscribers,
        t.deliveredCount,
        t.skippedCount,
        t.deliverySuccessRate,
        t.totalBilled,
        t.totalCollected,
        t.outstanding,
        t.collectionRate,
        t.performanceScore,
      ]),
    ];
    downloadCsvFile(`tenant-performance-${performanceMonth}.csv`, rows);
  };

  const handleResetTenantPassword = async (tenantId: number, tenantName: string) => {
    const newPassword = prompt(`Enter new password for ${tenantName} admin (min 6 chars):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      setMsg('Password must be at least 6 characters');
      return;
    }

    try {
      setResettingTenantPassword(tenantId);
      const res = await api.resetTenantAdminPassword(tenantId, newPassword);
      setMsg(res.message);
    } finally {
      setResettingTenantPassword(null);
    }
  };

  const handleCopyTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!copySource) return;
    try {
      setCopying(true);
      const res = await api.copyTenant(copySource.tenantId, {
        newName: copyForm.newName,
        newEmail: copyForm.newEmail,
        adminPassword: copyForm.adminPassword,
        copyBranding: copyForm.copyBranding,
        copyMilkTypes: copyForm.copyMilkTypes,
        copyAddressMasters: copyForm.copyAddressMasters,
      });
      setMsg(res.message);
      if (res.success) {
        setCopySource(null);
        setCopyForm({ newName: '', newEmail: '', adminPassword: '', copyBranding: true, copyMilkTypes: true, copyAddressMasters: true });
        load();
      }
    } finally {
      setCopying(false);
    }
  };

  const handleUpgradePlan = async (tenantId: number, plan: 'Monthly' | 'Yearly') => {
    const res = await api.upgradeTenantSubscription(tenantId, plan);
    setMsg(res.message);
    load();
  };

  const handleRenew = async (tenantId: number) => {
    const res = await api.renewTenantSubscription(tenantId);
    setMsg(res.message);
    load();
  };

  const handleSuspend = async (tenantId: number, tenantName: string) => {
    const reason = prompt(`Reason for suspending "${tenantName}" (optional):`) || undefined;
    const res = await api.suspendTenant(tenantId, reason);
    setMsg(res.message);
    load();
  };

  const handleReactivate = async (tenantId: number) => {
    const res = await api.reactivateTenant(tenantId);
    setMsg(res.message);
    load();
  };

  const handleToggleTenantActive = async (t: TenantDto) => {
    const res = await api.updateTenant(t.tenantId, { isActive: !t.isActive });
    setMsg(res.message);
    load();
  };

  return (
    <div>
      {confirmClearTenant && (
        <ConfirmModal
          title="Clear Tenant Data"
          message={`⚠️ WARNING: This will permanently delete ALL data for "${confirmClearTenant.tenantName}" including: All payments, bills, deliveries, subscribers & their users, milk types. The tenant record and admin user will be kept. Are you sure?`}
          onConfirm={handleClearDataConfirm}
          onCancel={() => setConfirmClearTenant(null)}
          danger
        />
      )}
      {confirmDeleteTenant && (
        <ConfirmModal
          title="Delete Tenant Permanently"
          message={`🚨 PERMANENT DELETE: This will irreversibly delete "${confirmDeleteTenant.tenantName}" and ALL its data including: All users, payments, bills, deliveries, subscribers, milk types, addresses, audit logs, sessions, configurations, and the tenant record itself. This action CANNOT be undone. Are you absolutely sure?`}
          onConfirm={handleDeleteTenantConfirm}
          onCancel={() => setConfirmDeleteTenant(null)}
          danger
        />
      )}
      <PanelHeader title="Tenant Management" count={tenants.length}>
        <GreenButton onClick={() => setShowForm(!showForm)}>+ Add Tenant</GreenButton>
      </PanelHeader>
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {/* Copy Tenant Modal */}
      {copySource && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form onSubmit={handleCopyTenant} style={{ background: '#ffffff', borderRadius: '12px', padding: '2rem', maxWidth: '480px', width: '90%', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: 0, color: '#1a1a1a' }}>📋 Copy Tenant</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>Copying from: <strong>{copySource.name}</strong></p>
            <input
              value={copyForm.newName}
              onChange={e => setCopyForm({ ...copyForm, newName: e.target.value })}
              placeholder="New Tenant Name"
              required
              style={inputStyle}
            />
            <input
              type="email"
              value={copyForm.newEmail}
              onChange={e => setCopyForm({ ...copyForm, newEmail: e.target.value })}
              placeholder="New Admin Email"
              required
              style={inputStyle}
            />
            <input
              type="password"
              value={copyForm.adminPassword}
              onChange={e => setCopyForm({ ...copyForm, adminPassword: e.target.value })}
              placeholder="Admin Password (min 6 chars)"
              required
              minLength={6}
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={copyForm.copyBranding} onChange={e => setCopyForm({ ...copyForm, copyBranding: e.target.checked })} />
                Copy Branding (logo, theme, GST)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={copyForm.copyMilkTypes} onChange={e => setCopyForm({ ...copyForm, copyMilkTypes: e.target.checked })} />
                Copy Milk Types
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={copyForm.copyAddressMasters} onChange={e => setCopyForm({ ...copyForm, copyAddressMasters: e.target.checked })} />
                Copy Society/Building/Flat Masters
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <GreenButton type="submit" disabled={copying}>{copying ? '⏳ Copying...' : '📋 Create Copy'}</GreenButton>
              <button type="button" onClick={() => setCopySource(null)} style={{ ...smallBtn, background: '#888', color: 'white', border: 'none' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Address Setup Modal (Society/Building/Flat CSV onboarding for SuperAdmin) */}
      {addressSetupTenant && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '2rem', maxWidth: '520px', width: '90%', display: 'flex', flexDirection: 'column', gap: '0.9rem', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: 0, color: '#1a1a1a' }}>🏘️ Address Setup: {addressSetupTenant.name}</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
              Upload Society, Building, and Flat CSVs to set up this dairy's address structure as part of onboarding.
              Upload Society first, then Building, then Flat — each level depends on the one before it.
            </p>
            {addressSetupMsg && <MsgBanner>{addressSetupMsg}</MsgBanner>}

            {([
              { label: '1️⃣ Society Master', sample: () => api.downloadAddressMasterSampleCsv(), sampleName: 'society-master-upload-sample.csv', upload: (f: File) => api.bulkUploadAddressMasters(f, addressSetupTenant.tenantId) },
              { label: '2️⃣ Building Master', sample: () => api.downloadBuildingMasterSampleCsv(), sampleName: 'building-master-upload-sample.csv', upload: (f: File) => api.bulkUploadBuildingMasters(f, addressSetupTenant.tenantId) },
              { label: '3️⃣ Milk Types', sample: () => api.downloadMilkTypeSampleCsv(), sampleName: 'milk-type-upload-sample.csv', upload: (f: File) => api.bulkUploadMilkTypes(f, addressSetupTenant.tenantId) },
            ] as const).map(step => (
              <div key={step.label} style={{ ...formStyle, margin: 0, flexDirection: 'row', flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--tc)', fontSize: '0.88rem', flex: '1 1 140px' }}>{step.label}</strong>
                <ActionBtn onClick={async () => {
                  const blob = await step.sample();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = step.sampleName; a.click();
                  URL.revokeObjectURL(url);
                }} color="#1976D2">📄 Sample</ActionBtn>
                <input
                  type="file"
                  accept=".csv"
                  disabled={addressSetupUploading}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    e.currentTarget.value = '';
                    if (!file) return;
                    setAddressSetupUploading(true);
                    setAddressSetupMsg('');
                    try {
                      const res = await step.upload(file);
                      setAddressSetupMsg(res.message || `${step.label}: uploaded`);
                    } catch {
                      setAddressSetupMsg(`${step.label}: upload failed`);
                    } finally {
                      setAddressSetupUploading(false);
                    }
                  }}
                  style={{ fontSize: '0.82rem' }}
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button type="button" onClick={() => setAddressSetupTenant(null)} style={{ ...smallBtn, background: '#888', color: 'white', border: 'none' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Onboard Dairy Modal (upload full Excel for an existing tenant) */}
      {onboardTenant && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form onSubmit={handleOnboardUpload} style={{ background: '#ffffff', borderRadius: '12px', padding: '2rem', maxWidth: '560px', width: '90%', display: 'flex', flexDirection: 'column', gap: '0.9rem', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: 0, color: '#1a1a1a' }}>📥 Onboard Dairy: {onboardTenant.name}</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>
              Upload the dairy onboarding Excel to import Society Master, Building Master, Milk Types, Subscribers, Product Types (optional) and Delivery Boys (optional) into this tenant.
            </p>
            {onboardMsg && <MsgBanner>{onboardMsg}</MsgBanner>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={async () => {
                try {
                  const blob = await api.downloadOnboardingSampleExcel();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'dairy-onboarding-sample.xlsx';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { setOnboardMsg('Download sample failed'); }
              }} style={{ background: 'none', border: '1px solid var(--tc)', color: 'var(--tc)', borderRadius: '6px', padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                Download Sample Excel
              </button>
            </div>

            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={onboardUploading}
              onChange={e => { const f = e.target.files?.[0] || null; setOnboardFile(f); setOnboardErrors(null); setOnboardErrorExcel(null); e.currentTarget.value = ''; }}
              style={{ fontSize: '0.82rem' }}
            />
            {onboardFile && (
              <div style={{ fontSize: '0.82rem', color: 'var(--tc)' }}>
                Selected: <strong>{onboardFile.name}</strong>
                <button type="button" onClick={() => { setOnboardFile(null); setOnboardErrors(null); setOnboardErrorExcel(null); }} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Remove</button>
              </div>
            )}

            {onboardErrors && onboardErrors.length > 0 && (
              <div style={{ padding: '0.75rem', background: '#FFEBEE', borderRadius: '8px', border: '1px solid #FFCDD2' }}>
                <div style={{ fontWeight: 600, color: '#C62828', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  {onboardErrors.length} validation error(s) found — fix them in the Excel and re-upload
                </div>
                <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  {onboardErrors.slice(0, 10).map((err, i) => (
                    <div key={i} style={{ padding: '2px 0', borderBottom: '1px solid #FFCDD2' }}>{err}</div>
                  ))}
                  {onboardErrors.length > 10 && <div style={{ color: '#888', marginTop: '4px' }}>...and {onboardErrors.length - 10} more errors</div>}
                </div>
                {onboardErrorExcel && (
                  <button type="button" onClick={handleOnboardDownloadError} style={{ background: '#C62828', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    Download Error Excel with Suggestions
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <GreenButton type="submit" disabled={onboardUploading || !onboardFile}>{onboardUploading ? '⏳ Onboarding...' : '📥 Upload & Onboard'}</GreenButton>
              <button type="button" onClick={() => setOnboardTenant(null)} style={{ ...smallBtn, background: '#888', color: 'white', border: 'none' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ ...formStyle, alignItems: 'flex-end', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.82rem' }}>Restore Target Tenant</label>
          <select
            value={restoreTenantId}
            onChange={e => setRestoreTenantId(e.target.value ? Number(e.target.value) : '')}
            style={{ ...inputStyle, minWidth: '220px' }}
          >
            <option value="">Select tenant</option>
            {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.82rem' }}>Restore Backup JSON</label>
          <input
            type="file"
            accept="application/json"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleRestoreFile(file);
              e.currentTarget.value = '';
            }}
            style={{ ...inputStyle, minWidth: '260px' }}
            disabled={restoring}
          />
        </div>
        <div style={{ color: '#666', fontSize: '0.8rem', maxWidth: '360px' }}>
          SuperAdmin can download a tenant backup and restore it into any selected tenant.
        </div>
      </div>

      {restoreResult && (
        <div style={{ ...formStyle, background: '#E3F2FD', border: '1px solid #BBDEFB', marginBottom: '1rem' }}>
          <strong style={{ color: '#1565C0', width: '100%' }}>♻ Restore Summary:</strong>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', width: '100%' }}>
            <span>Milk Types: <strong>{restoreResult.milkTypesRestored}</strong></span>
            <span>Users: <strong>{restoreResult.usersRestored}</strong></span>
            <span>Subscribers: <strong>{restoreResult.subscribersRestored}</strong></span>
            <span>Pauses: <strong>{restoreResult.pausesRestored}</strong></span>
            <span>Deliveries: <strong>{restoreResult.deliveriesRestored}</strong></span>
            <span>Bills: <strong>{restoreResult.billsRestored}</strong></span>
            <span>Payments: <strong>{restoreResult.paymentsRestored}</strong></span>
            <span>Changes: <strong>{restoreResult.subscriptionChangesRestored}</strong></span>
            <span>Cash Entries: <strong>{restoreResult.dailyCashEntriesRestored}</strong></span>
            <span>Expenses: <strong>{restoreResult.dailyExpensesRestored}</strong></span>
            <span>Exp Categories: <strong>{restoreResult.expenseCategoriesRestored}</strong></span>
            <span>Product Types: <strong>{restoreResult.productTypesRestored}</strong></span>
            <span>Product Rates: <strong>{restoreResult.productTypeRatesRestored}</strong></span>
          </div>
          <button onClick={() => setRestoreResult(null)} style={{ ...smallBtn, background: '#1565C0', color: 'white', border: 'none' }}>Dismiss</button>
        </div>
      )}

      {clearResult && (
        <div style={{ ...formStyle, background: '#FFF3E0', border: '1px solid #FFE0B2', marginBottom: '1rem' }}>
          <strong style={{ color: '#E65100', width: '100%' }}>🗑️ Data Cleared Summary:</strong>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', width: '100%' }}>
            <span>Payments: <strong>{clearResult.paymentsDeleted}</strong></span>
            <span>Bills: <strong>{clearResult.billsDeleted}</strong></span>
            <span>Deliveries: <strong>{clearResult.deliveriesDeleted}</strong></span>
            <span>Pauses: <strong>{clearResult.pausesDeleted}</strong></span>
            <span>Subscribers: <strong>{clearResult.subscribersDeleted}</strong></span>
            <span>Users: <strong>{clearResult.usersDeleted}</strong></span>
            <span>Milk Types: <strong>{clearResult.milkTypesDeleted}</strong></span>
          </div>
          <button onClick={() => setClearResult(null)} style={{ ...smallBtn, background: '#E65100', color: 'white', border: 'none' }}>Dismiss</button>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ ...formStyle, flexDirection: 'column', alignItems: 'stretch', maxWidth: '600px' }}>
          <h3 style={{ color: 'var(--tc)', margin: 0 }}>🏢 New Tenant</h3>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Tenant Name (e.g. Gokul Dairy)" required style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Admin Email" required style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input type="password" value={form.adminPassword} onChange={e => setForm({ ...form, adminPassword: e.target.value })} placeholder="Admin Password (default: Admin@123)" style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input type="email" value={form.reportEmail} onChange={e => setForm({ ...form, reportEmail: e.target.value })} placeholder="Report Email (optional)" style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address (optional)" style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Logo Image</label>
              <input type="file" accept="image/*" onChange={e => {
                const file = e.target.files?.[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = () => setLogoPreview(reader.result as string);
                reader.readAsDataURL(file);
              }} style={{ fontSize: '0.8rem' }} />
              {logoPreview && <img src={logoPreview} alt="Logo" style={{ maxWidth: '80px', maxHeight: '80px', borderRadius: '6px', marginTop: '0.3rem', display: 'block' }} />}
            </div>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Banner Image</label>
              <input type="file" accept="image/*" onChange={e => {
                const file = e.target.files?.[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = () => setBannerPreview(reader.result as string);
                reader.readAsDataURL(file);
              }} style={{ fontSize: '0.8rem' }} />
              {bannerPreview && <img src={bannerPreview} alt="Banner" style={{ maxWidth: '150px', maxHeight: '60px', borderRadius: '6px', marginTop: '0.3rem', display: 'block' }} />}
            </div>
            <div>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Theme Color</label>
              <input type="color" value={form.themeColor} onChange={e => setForm({ ...form, themeColor: e.target.value })}
                style={{ width: '50px', height: '38px', border: 'none', cursor: 'pointer', borderRadius: '6px' }} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#F1F8E9', borderRadius: '8px', border: '1px dashed var(--tc)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.85rem' }}>Excel Onboarding (optional)</label>
              <button type="button" onClick={async () => {
                try {
                  const blob = await api.downloadOnboardingSampleExcel();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'dairy-onboarding-sample.xlsx';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { setMsg('Download sample failed'); }
              }} style={{ background: 'none', border: '1px solid var(--tc)', color: 'var(--tc)', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                Download Sample Excel
              </button>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.8rem' }}>
              Upload an Excel file with separate tabs: Society Master, Building Master, Milk Types, Subscribers, Product Types (optional), Delivery Boys (optional)
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={excelUploading}
              onChange={e => { const f = e.target.files?.[0] || null; setExcelFile(f); setExcelErrors(null); e.currentTarget.value = ''; }}
              style={{ fontSize: '0.82rem' }}
            />
            {excelFile && (
              <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: 'var(--tc)' }}>
                Selected: <strong>{excelFile.name}</strong>
                <button type="button" onClick={() => { setExcelFile(null); setExcelErrors(null); }} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Remove</button>
              </div>
            )}
            {excelErrors && excelErrors.length > 0 && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#FFEBEE', borderRadius: '8px', border: '1px solid #FFCDD2' }}>
                <div style={{ fontWeight: 600, color: '#C62828', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  {excelErrors.length} validation error(s) found
                </div>
                <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  {excelErrors.slice(0, 10).map((err, i) => (
                    <div key={i} style={{ padding: '2px 0', borderBottom: '1px solid #FFCDD2' }}>
                      <strong>{err.sheetName}</strong> Row {err.rowNumber}: {err.errorMessage}
                      <span style={{ color: '#1565C0', marginLeft: '4px' }}>({err.suggestion})</span>
                    </div>
                  ))}
                  {excelErrors.length > 10 && <div style={{ color: '#888', marginTop: '4px' }}>...and {excelErrors.length - 10} more errors</div>}
                </div>
                <button type="button" onClick={async () => {
                  try {
                    const blob = await api.downloadErrorExcel(excelErrors);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'onboarding-errors.xlsx';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { setMsg('Error download failed'); }
                }} style={{ background: '#C62828', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                  Download Error Excel with Suggestions
                </button>
              </div>
            )}
          </div>
          <GreenButton type="submit" disabled={excelUploading}>{excelUploading ? 'Creating & Importing...' : excelFile ? 'Create Tenant & Import Excel' : 'Create Tenant'}</GreenButton>
        </form>
      )}

      <TableWrap>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          <th style={thStyle}>ID</th>
          <th style={thStyle}>Name</th>
          <th style={thStyle}>Email</th>
          <th style={thStyle}>Address</th>
          <th style={thStyle}>Status</th>
          <th style={thStyle}>Subscription</th>
          <th style={thStyle}>Created</th>
          <th style={thStyle}>Actions</th>
        </tr></thead>
        <tbody>
          {tenants.map(t => (
            <tr key={t.tenantId} style={trStyle}>
              <td style={tdStyle}>{t.tenantId}</td>
              <td style={tdStyle}><strong>{t.name}</strong></td>
              <td style={tdStyle}>{t.email}</td>
              <td style={tdStyle}>{t.address || '—'}</td>
              <td style={tdStyle}><StatusBadge status={t.isActive ? 'active' : 'inactive'} /></td>
              <td style={tdStyle}>
                <div style={{ fontSize: '0.82rem' }}>
                  <div>
                    <span style={{
                      background: t.isSuspended ? '#FFCDD2' : 'var(--tc-light)',
                      color: t.isSuspended ? '#C62828' : 'var(--tc)',
                      padding: '2px 8px', borderRadius: '10px', fontWeight: 600, fontSize: '0.78rem',
                    }}>{t.isSuspended ? '⏸ Suspended' : t.subscriptionPlan}</span>
                  </div>
                  {t.subscriptionExpiresAt && (
                    <div style={{ color: t.daysUntilExpiry != null && t.daysUntilExpiry <= 3 ? '#D32F2F' : '#888', marginTop: '2px' }}>
                      {t.daysUntilExpiry != null && t.daysUntilExpiry >= 0
                        ? `Expires in ${t.daysUntilExpiry}d (${fmtDate(t.subscriptionExpiresAt)})`
                        : `Expired ${fmtDate(t.subscriptionExpiresAt)}`}
                    </div>
                  )}
                </div>
              </td>
              <td style={tdStyle}>{fmtDate(t.createdDate)}</td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  <ActionBtn
                    onClick={() => handleDownloadBackup(t.tenantId, t.name)}
                    color="#1565C0"
                  >
                    {downloadingBackup === t.tenantId ? '⏳ Backup...' : '⬇ Backup'}
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => handleClearData(t.tenantId, t.name)}
                    color="#C62828"
                  >
                    {clearing === t.tenantId ? '⏳ Clearing...' : '🗑️ Clear Data'}
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => handleResetTenantPassword(t.tenantId, t.name)}
                    color="#E65100"
                  >
                    {resettingTenantPassword === t.tenantId ? '⏳ Resetting...' : '🔑 Reset Admin Pwd'}
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => { setCopySource(t); setCopyForm({ newName: `${t.name} - Copy`, newEmail: '', adminPassword: '', copyBranding: true, copyMilkTypes: true, copyAddressMasters: true }); }}
                    color="#6A1B9A"
                  >
                    📋 Copy Tenant
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => { setAddressSetupTenant(t); setAddressSetupMsg(''); }}
                    color="#00838F"
                  >
                    🏘️ Address Setup
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => { setOnboardTenant(t); setOnboardFile(null); setOnboardMsg(''); setOnboardErrors(null); setOnboardErrorExcel(null); }}
                    color="#00695C"
                    title="Upload the dairy onboarding Excel to import data into this tenant"
                  >
                    📥 Onboard Dairy
                  </ActionBtn>
                  <ActionBtn onClick={() => handleUpgradePlan(t.tenantId, 'Monthly')} color="#00897B">⬆ Monthly</ActionBtn>
                  <ActionBtn onClick={() => handleUpgradePlan(t.tenantId, 'Yearly')} color="#00695C">⬆ Yearly</ActionBtn>
                  <ActionBtn onClick={() => handleRenew(t.tenantId)} color="#2E7D32">🔄 Renew</ActionBtn>
                  {t.isSuspended ? (
                    <ActionBtn onClick={() => handleReactivate(t.tenantId)} color="#2E7D32">▶ Reactivate</ActionBtn>
                  ) : (
                    <ActionBtn onClick={() => handleSuspend(t.tenantId, t.name)} color="#F57F17">⏸ Suspend</ActionBtn>
                  )}
                  <ActionBtn onClick={() => handleToggleTenantActive(t)} color={t.isActive ? '#C62828' : '#2E7D32'}>
                    {t.isActive ? '🚫 Disable Dairy' : '✅ Enable Dairy'}
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => handleDeleteTenant(t.tenantId, t.name)}
                    color="#880E4F"
                  >
                    {deleting === t.tenantId ? '⏳ Deleting...' : '💀 Delete'}
                  </ActionBtn>
                </div>
              </td>
            </tr>
          ))}
          {tenants.length === 0 && <EmptyRow colSpan={9} text="No tenants found." />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {tenants.map(t => (
          <div key={t.tenantId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{t.name}</span>
              <StatusBadge status={t.isActive ? 'active' : 'inactive'} />
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Email: </span>{t.email}</div>
              <div><span className="mobile-card-detail-label">Address: </span>{t.address || '—'}</div>
              <div><span className="mobile-card-detail-label">Created: </span>{fmtDate(t.createdDate)}</div>
              <div><span className="mobile-card-detail-label">Expires: </span>{t.subscriptionExpiresAt ? fmtDate(t.subscriptionExpiresAt) : '—'}</div>
            </div>
            <div className="mobile-card-actions">
              <ActionBtn onClick={() => handleDownloadBackup(t.tenantId, t.name)} color="#1565C0">
                {downloadingBackup === t.tenantId ? '⏳...' : '⬇ Backup'}
              </ActionBtn>
              <ActionBtn onClick={() => setCopySource(t)} color="#6A1B9A">📋 Copy</ActionBtn>
              <ActionBtn onClick={() => handleToggleTenantActive(t)} color={t.isActive ? '#C62828' : '#2E7D32'}>
                {t.isActive ? '🚫 Disable' : '✅ Enable'}
              </ActionBtn>
            </div>
          </div>
        ))}
        {tenants.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>No tenants found.</div>}
      </div>
      </TableWrap>

      <div style={{ marginTop: '1.5rem' }}>
        <PanelHeader title="Tenant Performance Report">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input type="month" value={performanceMonth} onChange={e => setPerformanceMonth(e.target.value)} style={inputStyle} />
            <GreenButton onClick={loadPerformance}>Refresh</GreenButton>
            <GreenButton onClick={handleDownloadPerformanceCsv}>⬇ Download</GreenButton>
          </div>
        </PanelHeader>
        {performanceLoading ? <LoadingSpinner /> : performance && (
          <TableWrap>
          <table style={tableStyle}>
            <thead><tr style={thRowStyle}>
              <th style={thStyle}>Rank</th><th style={thStyle}>Tenant</th><th style={thStyle}>Subscribers</th>
              <th style={thStyle}>Delivered</th><th style={thStyle}>Skipped</th><th style={thStyle}>Delivery %</th>
              <th style={thStyle}>Billed</th><th style={thStyle}>Collected</th><th style={thStyle}>Outstanding</th>
              <th style={thStyle}>Collection %</th><th style={thStyle}>Score</th>
            </tr></thead>
            <tbody>
              {performance.tenants.map((t, idx) => (
                <tr key={t.tenantId} style={trStyle}>
                  <td style={tdStyle}><strong>#{idx + 1}</strong></td>
                  <td style={tdStyle}><strong>{t.tenantName}</strong></td>
                  <td style={tdStyle}>{t.activeSubscribers}</td>
                  <td style={tdStyle}>{t.deliveredCount}</td>
                  <td style={tdStyle}>{t.skippedCount}</td>
                  <td style={tdStyle}>{t.deliverySuccessRate}%</td>
                  <td style={tdStyle}>₹{t.totalBilled.toFixed(0)}</td>
                  <td style={tdStyle}>₹{t.totalCollected.toFixed(0)}</td>
                  <td style={tdStyle}><span style={{ color: t.outstanding > 0 ? '#D32F2F' : 'var(--tc)', fontWeight: 600 }}>₹{t.outstanding.toFixed(0)}</span></td>
                  <td style={tdStyle}>{t.collectionRate}%</td>
                  <td style={tdStyle}><strong>{t.performanceScore}</strong></td>
                </tr>
              ))}
              {performance.tenants.length === 0 && <EmptyRow colSpan={11} text="No tenant performance data for this month." />}
            </tbody>
          </table>
          </TableWrap>
        )}
      </div>
    </div>
  );
}

// ===== Tenant Requests Panel (SuperAdmin only) =====
function TenantRequestsPanel() {
  const [requests, setRequests] = useState<TenantRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<TenantRequestDto | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveEmail, setApproveEmail] = useState('');
  const [approvePassword, setApprovePassword] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const load = async () => {
    try {
      const res = await api.getTenantRequests();
      setRequests(res);
    } catch { setMsg('Failed to load requests'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filteredRequests = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);
  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  const handleApprove = async () => {
    if (!selectedRequest || !approveEmail || !approvePassword) return;
    setActionLoading(true);
    try {
      await api.approveTenantRequest(selectedRequest.tenantRequestId, { adminEmail: approveEmail, adminPassword: approvePassword });
      setMsg(`Request approved! Tenant created for ${selectedRequest.dairyName}`);
      setShowApproveModal(false);
      setSelectedRequest(null);
      setApproveEmail('');
      setApprovePassword('');
      load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : 'Approval failed');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await api.rejectTenantRequest(selectedRequest.tenantRequestId, { reason: rejectReason || undefined });
      setMsg(`Request rejected for ${selectedRequest.dairyName}`);
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : 'Rejection failed');
    }
    setActionLoading(false);
  };

  const openApprove = (req: TenantRequestDto) => {
    setSelectedRequest(req);
    setApproveEmail(req.email);
    setApprovePassword('Admin@123');
    setShowApproveModal(true);
  };

  const openReject = (req: TenantRequestDto) => {
    setSelectedRequest(req);
    setRejectReason('');
    setShowRejectModal(true);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;

  return (
    <div>
      <PanelHeader title="Tenant Requests" count={pendingCount} icon="📋" subtitle="Review and approve access requests from the landing page" />

      {msg && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '8px', background: msg.includes('approved') || msg.includes('rejected') ? '#E8F5E9' : '#FFEBEE', color: msg.includes('approved') || msg.includes('rejected') ? '#2E7D32' : '#C62828', fontSize: '0.9rem' }}>
          {msg}
          <button onClick={() => setMsg('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>&times;</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {['all', 'Pending', 'Approved', 'Rejected'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '6px 14px', borderRadius: '20px', border: '1px solid',
            borderColor: statusFilter === s ? themeColor : '#ddd',
            background: statusFilter === s ? tcLight : 'white',
            color: statusFilter === s ? themeColor : '#666',
            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
          }}>{s === 'all' ? 'All' : s} {s === 'Pending' ? `(${pendingCount})` : ''}</button>
        ))}
      </div>

      {filteredRequests.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#999' }}>No requests found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredRequests.map(req => (
            <div key={req.tenantRequestId} style={{
              background: 'white', borderRadius: '12px', padding: '1.25rem',
              border: `1px solid ${req.status === 'Pending' ? '#FFE082' : req.status === 'Approved' ? '#C8E6C9' : '#FFCDD2'}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>{req.dairyName}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                    {req.ownerName} &middot; {req.city} &middot; {req.mobile}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>{req.email}</div>
                  <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '4px' }}>
                    {req.subscriberCount} subscribers &middot; Plan: {req.preferredPlan} &middot; {new Date(req.createdDate).toLocaleDateString()}
                  </div>
                  {req.message && <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '6px', fontStyle: 'italic' }}>"{req.message}"</div>}
                  {req.rejectionReason && <div style={{ fontSize: '0.85rem', color: '#C62828', marginTop: '4px' }}>Rejection reason: {req.rejectionReason}</div>}
                  {req.createdTenantId && <div style={{ fontSize: '0.8rem', color: '#2E7D32', marginTop: '4px' }}>Created Tenant ID: {req.createdTenantId}</div>}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
                    background: req.status === 'Pending' ? '#FFF8E1' : req.status === 'Approved' ? '#E8F5E9' : '#FFEBEE',
                    color: req.status === 'Pending' ? '#F57F17' : req.status === 'Approved' ? '#2E7D32' : '#C62828',
                  }}>{req.status}</span>
                  {req.status === 'Pending' && (
                    <>
                      <button onClick={() => openApprove(req)} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#43A047', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Approve</button>
                      <button onClick={() => openReject(req)} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#E53935', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Reject</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>Approve: {selectedRequest.dairyName}</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>Set the admin login credentials for this dairy.</p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>Admin Email</label>
              <input type="email" value={approveEmail} onChange={e => setApproveEmail(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>Admin Password</label>
              <input type="text" value={approvePassword} onChange={e => setApprovePassword(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowApproveModal(false); setSelectedRequest(null); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleApprove} disabled={actionLoading || !approveEmail || !approvePassword} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#43A047', color: 'white', cursor: 'pointer', fontWeight: 600, opacity: actionLoading || !approveEmail || !approvePassword ? 0.6 : 1 }}>
                {actionLoading ? 'Creating...' : 'Approve & Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>Reject: {selectedRequest.dairyName}</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>Reason (optional)</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical' }} placeholder="Why is this request being rejected?" />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRejectModal(false); setSelectedRequest(null); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleReject} disabled={actionLoading} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#E53935', color: 'white', cursor: 'pointer', fontWeight: 600, opacity: actionLoading ? 0.6 : 1 }}>
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Users Panel (SuperAdmin only) =====
function UsersPanel({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [tenantFilter, setTenantFilter] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Subscriber', phone: '', address: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; email: string; phone: string; address: string }>({ name: '', email: '', phone: '', address: '' });
  const [resetPwdId, setResetPwdId] = useState<number | null>(null);
  const [resetPwdName, setResetPwdName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: number; name: string } | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);
  const [msg, setMsg] = useState('');
  const [confirmDisableTenant, setConfirmDisableTenant] = useState<{ tenantId: number; tenantName: string } | null>(null);
  const grid = useGridControls('gridPrefs:users', 'name');

  const load = async () => {
    const res = await api.getUsers();
    if (res.data) setUsers(res.data);
    const tr = await api.getTenants();
    if (tr.data) setTenants(tr.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.createUser(form);
    setMsg(res.message);
    if (res.success) { setShowForm(false); setForm({ name: '', email: '', password: '', role: 'Subscriber', phone: '', address: '' }); load(); }
  };

  const startEdit = (u: UserDto) => {
    setEditId(u.userId);
    setEditForm({ name: u.name, email: u.email, phone: u.phone || '', address: u.address || '' });
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    const res = await api.updateUser(editId, editForm);
    setMsg(res.message);
    if (res.success) { setEditId(null); load(); }
  };

  const handleExport = async () => {
    try {
      const blob = await api.exportUsersCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-report-${today()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Export failed'); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPwdId || newPassword.length < 6) { setMsg('Password must be at least 6 characters'); return; }
    const res = await api.resetUserPassword(resetPwdId, newPassword);
    setMsg(res.message);
    if (res.success) { setResetPwdId(null); setNewPassword(''); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await api.deleteUser(deleteConfirm.userId);
      setMsg(res.message);
      setDeleteConfirm(null);
      if (res.success) load();
    } catch {
      setMsg('❌ Delete failed. Please try again.');
      setDeleteConfirm(null);
    }
  };

  const handleToggleActive = async (u: UserDto) => {
    setTogglingUserId(u.userId);
    setMsg('');
    try {
      if (u.isActive) {
        const res = await api.deactivateUser(u.userId);
        setMsg(res.message);
      } else {
        const res = await api.activateUser(u.userId);
        setMsg(res.message);
      }
      load();
    } catch {
      setMsg('❌ Toggle failed.');
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleUnlock = async (u: UserDto) => {
    const res = await api.unlockUser(u.userId);
    setMsg(res.message);
    if (res.success) load();
  };

  const handleForcePasswordReset = async (u: UserDto) => {
    const res = await api.forcePasswordResetUser(u.userId);
    setMsg(res.message);
    if (res.success) load();
  };

  const handleDisableAllForTenant = async (tenantId: number, tenantName: string) => {
    setConfirmDisableTenant({ tenantId, tenantName });
  };

  const handleDisableAllForTenantConfirm = async () => {
    if (!confirmDisableTenant) return;
    const res = await api.disableAllTenantUsers(confirmDisableTenant.tenantId);
    setMsg(res.message);
    setConfirmDisableTenant(null);
    load();
  };

  const filtered = (tenantFilter ? users.filter(u => u.tenantId === tenantFilter) : users)
    .filter(u => u.role !== 'DeliveryBoy') // Exclude delivery boys - they have their own panel
    .filter(u => `${u.name} ${u.email} ${u.phone || ''}`.toLowerCase().includes(grid.search.toLowerCase()))
    .filter(u => grid.statusFilter === 'all'
      || (grid.statusFilter === 'active' && u.isActive)
      || (grid.statusFilter === 'inactive' && !u.isActive)
      || (grid.statusFilter === 'locked' && u.isLocked));
  const sortedUsers = [...filtered].sort((a, b) => {
    const cmp = a.name.localeCompare(b.name);
    return grid.sortDir === 'asc' ? cmp : -cmp;
  });
  const { paged: pagedUsers, totalPages: usersTotalPages } = paginate(sortedUsers, grid.page, grid.pageSize);

  return (
    <div>
      <PanelHeader title="Users" count={filtered.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Tenant filter - only for SuperAdmin */}
          {isSuperAdmin && (
            <select
              value={tenantFilter ?? ''}
              onChange={e => setTenantFilter(e.target.value ? Number(e.target.value) : null)}
              style={{ ...inputStyle, minWidth: '160px' }}
            >
              <option value="">All Tenants</option>
              {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
            </select>
          )}
          {isSuperAdmin && tenantFilter != null && (
            <button
              onClick={() => handleDisableAllForTenant(tenantFilter, tenants.find(t => t.tenantId === tenantFilter)?.name || 'this dairy')}
              style={{ ...smallBtn, background: '#C62828', color: 'white', border: 'none' }}
              title="Disable every user account for the selected dairy"
            >🚫 Disable All Users</button>
          )}
          <GreenButton onClick={handleExport}>Download Report</GreenButton>
          <button
            onClick={() => setShowForm(!showForm)}
            disabled={isSuperAdmin}
            title={isSuperAdmin ? 'SuperAdmin cannot add users directly — use Tenants to manage' : 'Add a new user'}
            style={{
              padding: '0.45rem 1rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem',
              background: isSuperAdmin ? '#B0BEC5' : 'var(--tc)', color: 'white',
              border: 'none', cursor: isSuperAdmin ? 'not-allowed' : 'pointer', opacity: isSuperAdmin ? 0.6 : 1,
            }}
          >+ Add User</button>
        </div>
      </PanelHeader>
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={grid.search}
          onChange={e => grid.setSearch(e.target.value)}
          placeholder="🔍 Search by name, email, or phone..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '360px' }}
        />
        <select value={grid.statusFilter} onChange={e => grid.setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="locked">Locked</option>
        </select>
      </div>
      {msg && <MsgBanner>{msg}</MsgBanner>}
      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name" required style={inputStyle} />
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" required style={inputStyle} />
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" required minLength={6} style={inputStyle} />
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" style={inputStyle} />
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
            <option value="Subscriber">Subscriber</option>
            <option value="Admin">Admin</option>
          </select>
          <GreenButton type="submit">Create</GreenButton>
        </form>
      )}
      {editId && (
        <form onSubmit={handleEdit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit User</strong>
          <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Full Name" style={inputStyle} />
          <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" style={inputStyle} />
          <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} placeholder="Address" style={inputStyle} />
          <GreenButton type="submit">Save</GreenButton>
          <button type="button" onClick={() => setEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      {resetPwdId && (
        <form onSubmit={handleResetPassword} style={formStyle}>
          <strong style={{ color: '#E65100' }}>🔑 Reset Password for {resetPwdName}</strong>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Password (min 6)" required minLength={6} style={inputStyle} />
          <GreenButton type="submit">Reset Password</GreenButton>
          <button type="button" onClick={() => { setResetPwdId(null); setNewPassword(''); }} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ color: '#C62828', margin: '0 0 1rem 0' }}>🗑 Delete User</h3>
            <p style={{ color: '#333', marginBottom: '1.5rem' }}>
              Are you sure you want to permanently delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ ...smallBtn, background: '#757575', color: 'white', border: 'none' }}>Cancel</button>
              <button onClick={handleDelete} style={{ ...smallBtn, background: '#C62828', color: 'white', border: 'none' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {confirmDisableTenant && (
        <ConfirmModal
          title="Disable All Users"
          message={`Disable ALL users in "${confirmDisableTenant.tenantName}"? They will be unable to log in until re-enabled individually.`}
          onConfirm={handleDisableAllForTenantConfirm}
          onCancel={() => setConfirmDisableTenant(null)}
          danger
        />
      )}
      <TableWrap>
      <table style={tableStyle}>
        <thead><tr style={thRowStyle}>
          <SortableTh label="Name" sortKeyName="name" activeKey={grid.sortKey} dir={grid.sortDir} onSort={grid.toggleSort} />
          <th style={thStyle}>Email</th><th style={thStyle}>Phone</th><th style={thStyle}>Role</th><th style={thStyle}>Tenant</th><th style={thStyle}>Active</th><th style={thStyle}>Actions</th></tr></thead>
        <tbody>
          {pagedUsers.map(u => (
            <tr key={u.userId} style={trStyle}>
              <td style={tdStyle}><strong>{u.name}</strong></td>
              <td style={tdStyle}>{u.email}</td>
              <td style={tdStyle}>{u.phone || '—'}</td>
              <td style={tdStyle}>
                <span style={{
                  background: u.role === 'Admin' ? 'var(--tc-btn)' : u.role === 'SuperAdmin' ? 'var(--tc-dark)' : u.role === 'DeliveryBoy' ? '#047857' : '#757575',
                  color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                }}>{u.role}</span>
              </td>
              <td style={tdStyle}>{u.tenantName || '—'}</td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={u.isActive}
                  disabled={togglingUserId === u.userId || u.role === 'SuperAdmin'}
                  title={u.role === 'SuperAdmin' ? 'Cannot deactivate SuperAdmin' : u.isActive ? 'Click to deactivate' : 'Click to activate'}
                  onChange={() => handleToggleActive(u)}
                  style={{ width: '16px', height: '16px', cursor: u.role === 'SuperAdmin' ? 'not-allowed' : 'pointer', accentColor: 'var(--tc)' }}
                />
                {u.isLocked && <div style={{ fontSize: '0.7rem', color: '#C62828', fontWeight: 700, marginTop: '2px' }}>🔒 Locked</div>}
                {u.mustChangePassword && <div style={{ fontSize: '0.7rem', color: '#E65100', fontWeight: 700, marginTop: '2px' }}>Reset pending</div>}
              </td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  <ActionBtn onClick={() => startEdit(u)} color="#1976D2">✏ Edit</ActionBtn>
                  <ActionBtn onClick={() => { setResetPwdId(u.userId); setResetPwdName(u.name); setNewPassword(''); }} color="#E65100">🔑 Pwd</ActionBtn>
                  {isSuperAdmin && u.isLocked && (
                    <ActionBtn onClick={() => handleUnlock(u)} color="#6A1B9A">🔓 Unlock</ActionBtn>
                  )}
                  {isSuperAdmin && !u.mustChangePassword && (
                    <ActionBtn onClick={() => handleForcePasswordReset(u)} color="#455A64">🔁 Force Reset</ActionBtn>
                  )}
                  {u.role === 'Subscriber' && (
                    <ActionBtn onClick={() => setDeleteConfirm({ userId: u.userId, name: u.name })} color="#C62828">🗑 Del</ActionBtn>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {pagedUsers.length === 0 && <EmptyRow colSpan={7} />}
        </tbody>
      </table>
      </TableWrap>
      <GridPagination page={grid.page} setPage={grid.setPage} pageSize={grid.pageSize} setPageSize={grid.setPageSize} totalPages={usersTotalPages} />
    </div>
  );
}

// ===== My Deliveries Panel (Delivery Boy role) =====
function MyDeliveriesPanel() {
  const [deliveries, setDeliveries] = useState<DeliveryDto[]>([]);
  const [viewDate, setViewDate] = useState(today());
  const [selectedSociety, setSelectedSociety] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFlat, setSelectedFlat] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [showExtraDrilldown, setShowExtraDrilldown] = useState(false);
  const [extraDrillSociety, setExtraDrillSociety] = useState('');
  const [extraDrillBuilding, setExtraDrillBuilding] = useState('');
  const [showMsgAdmin, setShowMsgAdmin] = useState(false);
  const [msgAdminTitle, setMsgAdminTitle] = useState('');
  const [msgAdminText, setMsgAdminText] = useState('');
  const [sendingMsgAdmin, setSendingMsgAdmin] = useState(false);

  const handleSendToAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!msgAdminTitle.trim() || !msgAdminText.trim()) { setMsg('Please enter both a subject and a message'); return; }
    setSendingMsgAdmin(true);
    try {
      const res = await api.sendNotificationToAdmin({ title: msgAdminTitle.trim(), message: msgAdminText.trim() });
      setMsg(res.message);
      if (res.success) {
        setShowMsgAdmin(false);
        setMsgAdminTitle('');
        setMsgAdminText('');
      }
    } catch {
      setMsg('❌ Could not send the message. Please try again.');
    } finally {
      setSendingMsgAdmin(false);
    }
  };

  const todayStr = today();
  const isToday = viewDate === todayStr;
  const isFutureDate = viewDate > todayStr;

  const load = async (date: string) => {
    setLoading(true);
    try {
      const res = await api.getDeliveries(date);
      if (res.data) setDeliveries(res.data);
      if (!res.success) setMsg(res.message || 'Could not load deliveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(viewDate); }, [viewDate]);

  const handleStatusChange = async (id: number, status: string) => {
    const res = await api.updateDelivery(id, { status });
    setMsg(res.message);
    if (res.success) load(viewDate);
  };

  const resetDrilldown = () => { setSelectedSociety(''); setSelectedBuilding(''); setSelectedFlat(''); };
  const percentOf = (items: DeliveryDto[]) => items.length > 0 ? Math.round((items.filter(d => d.status === 'Delivered').length / items.length) * 100) : 0;

  const societies = Array.from(new Set(deliveries.map(d => d.societyName).filter((v): v is string => Boolean(v)))).sort((a, b) => a.localeCompare(b));
  const buildingOptions = Array.from(new Set(deliveries.filter(d => d.societyName === selectedSociety).map(d => d.buildingName).filter((v): v is string => Boolean(v)))).sort((a, b) => a.localeCompare(b));
  const flatOptions = Array.from(new Set(deliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding).map(d => d.flatName).filter((v): v is string => Boolean(v)))).sort((a, b) => a.localeCompare(b));

  const societyStats = societies.map(society => ({ key: society, percent: percentOf(deliveries.filter(d => d.societyName === society)) }));
  const buildingStats = buildingOptions.map(building => ({ key: building, percent: percentOf(deliveries.filter(d => d.societyName === selectedSociety && d.buildingName === building)) }));
  const flatStats = flatOptions.map(flat => ({ key: flat, percent: percentOf(deliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding && d.flatName === flat)) }));

  const filtered = selectedFlat
    ? deliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding && d.flatName === selectedFlat)
    : selectedBuilding
      ? deliveries.filter(d => d.societyName === selectedSociety && d.buildingName === selectedBuilding)
      : selectedSociety
        ? deliveries.filter(d => d.societyName === selectedSociety)
        : deliveries;
  const pendingCount = filtered.filter(d => d.status === 'Pending').length;
  const deliveredCount = filtered.filter(d => d.status === 'Delivered').length;
  const failedCount = filtered.filter(d => d.status === 'Failed').length;
  const deliveredPercent = percentOf(filtered);

  return (
    <div>
      <PanelHeader
        title="My Deliveries"
        count={filtered.length}
        subtitle={isToday ? "Today's deliveries \u2014 mark each as you complete it" : isFutureDate ? 'Future delivery \u2014 view only' : 'Past delivery \u2014 view only'}
      >
        <DateInput value={viewDate} onChange={e => { setViewDate(e.target.value); resetDrilldown(); }} style={inputStyle} />
        <button type="button" onClick={() => setShowMsgAdmin(true)}
          style={{ background: '#1565C0', color: 'white', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>
          📩 Message Admin
        </button>
      </PanelHeader>

      {msg && <MsgBanner>{msg}</MsgBanner>}

      {/* Extra Delivery Summary & Drill-down */}
      {!loading && deliveries.length > 0 && (() => {
        const dayExtras = deliveries.filter(d => d.isExtra);
        const extraDelivered = dayExtras.filter(d => d.status === 'Delivered').length;
        const extraPending = dayExtras.filter(d => d.status !== 'Delivered').length;
        const hasExtras = dayExtras.length > 0;
        const extraSocieties = Array.from(new Set(dayExtras.map(d => d.societyName).filter((v): v is string => Boolean(v)))).sort();
        const extraBuildings = extraDrillSociety
          ? Array.from(new Set(dayExtras.filter(d => d.societyName === extraDrillSociety).map(d => d.buildingName).filter((v): v is string => Boolean(v)))).sort()
          : [];
        const extraFlats = extraDrillSociety && extraDrillBuilding
          ? dayExtras.filter(d => d.societyName === extraDrillSociety && d.buildingName === extraDrillBuilding)
          : [];
        return (
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 12, background: hasExtras ? '#E8F5E9' : '#F5F5F5', border: `1px solid ${hasExtras ? '#A5D6A7' : '#E0E0E0'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.88rem' }}>
                <span>Extra Deliveries — <strong style={{ color: '#2E7D32' }}>Delivered: {extraDelivered}</strong></span>
                <span><strong style={{ color: '#E65100' }}>Undelivered: {extraPending}</strong></span>
              </div>
              {hasExtras && (
                <button type="button" onClick={() => { setShowExtraDrilldown(!showExtraDrilldown); setExtraDrillSociety(''); setExtraDrillBuilding(''); }}
                  style={{ background: showExtraDrilldown ? '#E65100' : '#1565C0', color: 'white', border: 'none', borderRadius: 8, padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                  {showExtraDrilldown ? 'Close Extra Deliveries' : 'Extra Delivery'}
                </button>
              )}
            </div>
            {showExtraDrilldown && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', borderRadius: 10, background: 'white', border: '1px solid #C8E6C9' }}>
                {!extraDrillSociety && extraSocieties.map(soc => {
                  const socExtras = dayExtras.filter(d => d.societyName === soc);
                  const socDelivered = socExtras.filter(d => d.status === 'Delivered').length;
                  return (
                    <button key={soc} type="button" onClick={() => setExtraDrillSociety(soc)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: '1px solid #A5D6A7', background: '#F1F8E9', cursor: 'pointer', fontSize: '0.88rem' }}>
                      🏘️ {soc} — <strong>{socExtras.length}</strong> extra(s), <span style={{ color: '#2E7D32' }}>{socDelivered} delivered</span>
                    </button>
                  );
                })}
                {extraDrillSociety && !extraDrillBuilding && extraBuildings.map(bld => {
                  const bldExtras = dayExtras.filter(d => d.societyName === extraDrillSociety && d.buildingName === bld);
                  const bldDelivered = bldExtras.filter(d => d.status === 'Delivered').length;
                  return (
                    <div key={bld}>
                      <button type="button" onClick={() => setExtraDrillSociety('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#1565C0', padding: '0.2rem 0', marginBottom: '0.3rem' }}>← Back to societies</button>
                      <button type="button" onClick={() => setExtraDrillBuilding(bld)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: '1px solid #90CAF9', background: '#E3F2FD', cursor: 'pointer', fontSize: '0.88rem' }}>
                        🏢 {bld} — <strong>{bldExtras.length}</strong> extra(s), <span style={{ color: '#2E7D32' }}>{bldDelivered} delivered</span>
                      </button>
                    </div>
                  );
                })}
                {extraDrillSociety && extraDrillBuilding && (
                  <div>
                    <button type="button" onClick={() => setExtraDrillBuilding('')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#1565C0', padding: '0.2rem 0', marginBottom: '0.3rem' }}>← Back to buildings</button>
                    {extraFlats.map(d => (
                      <div key={d.deliveryId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', marginBottom: '0.3rem', borderRadius: 8, border: `1px solid ${d.status === 'Delivered' ? '#A5D6A7' : '#FFCC80'}`, background: d.status === 'Delivered' ? '#F1F8E9' : '#FFF8E1', fontSize: '0.88rem' }}>
                        <span>🏠 {d.flatName} — {d.subscriberName} — {d.milkTypeName} — {d.quantity}L</span>
                        <span style={{ fontWeight: 600, color: d.status === 'Delivered' ? '#2E7D32' : '#E65100' }}>{d.status === 'Delivered' ? '✓ Delivered' : '⏳ Pending'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {loading && <LoadingSpinner />}

      {!loading && deliveries.length === 0 && (
        <div style={{ ...formStyle, justifyContent: 'center', color: '#888' }}>
          No deliveries found for {fmtDate(viewDate)}. If this looks wrong, ask your admin to confirm your assigned societies.
        </div>
      )}

      {!loading && deliveries.length > 0 && (
        <div style={{ display: 'grid', gap: '0.9rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {societyStats.map(({ key, percent }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setSelectedSociety(key); setSelectedBuilding(''); setSelectedFlat(''); }}
                style={getProgressStyle(percent, key === selectedSociety, getSocietyColor(), 'society')}
              >
                <div style={{ display: 'grid', gap: '0.15rem' }}>
                  <span>🏘️ {key}</span>
                  <small>{percent}% delivered</small>
                </div>
              </button>
            ))}
          </div>

          {selectedSociety && buildingStats.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {buildingStats.map(({ key, percent }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setSelectedBuilding(key); setSelectedFlat(''); }}
                  style={getProgressStyle(percent, key === selectedBuilding, getBuildingColor(percent), 'building')}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <span>🏢 {key}</span>
                    <small>{percent}% delivered</small>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedBuilding && flatStats.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {flatStats.map(({ key, percent }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedFlat(key)}
                  style={getProgressStyle(percent, key === selectedFlat, getFlatColor(percent), 'flat')}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <span>🏠 {key}</span>
                    <small>{percent}% delivered</small>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedSociety && (
            <div style={{ padding: '0.9rem 1rem', borderRadius: 14, background: '#F8FBFF', border: '1px solid #D7E3F4' }}>
              <strong>{selectedSociety}{selectedBuilding ? ` / ${selectedBuilding}` : ''}{selectedFlat ? ` / ${selectedFlat}` : ''}</strong>
              <div style={{ marginTop: '0.45rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                <span>Pending: {pendingCount}</span>
                <span>Delivered: {deliveredCount}</span>
                <span>Failed: {failedCount}</span>
                <span>Progress: {deliveredPercent}%</span>
              </div>
              <div style={{ marginTop: '0.6rem', height: 10, borderRadius: 999, background: '#FFE0B2', overflow: 'hidden' }}>
                <div style={{ width: `${deliveredPercent}%`, height: '100%', background: '#2E7D32' }} />
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {filtered.map(d => {
            const isDelivered = d.status === 'Delivered';
            const isFailed = d.status === 'Failed';
            const isPaused = d.isPaused;
            return (
              <div key={d.deliveryId} style={{ border: '1px solid #D7E3F4', borderRadius: 14, background: 'white', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <strong>{d.subscriberName}</strong>
                  <StatusBadge status={d.status.toLowerCase()} />
                </div>
                <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.3rem', color: '#334155', fontSize: '0.88rem' }}>
                  <div style={isPaused ? { color: '#F9A825', fontWeight: 700 } : undefined}>🏘️ {[d.societyName, d.buildingName, d.flatName].filter(Boolean).join(' / ') || '—'}</div>
                  <div>📞 {d.subscriberPhone || 'N/A'}</div>
                  <div>🥛 {d.milkTypeName} • {d.quantity} L</div>
                </div>
                {isPaused ? (
                  <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', color: '#F9A825', fontWeight: 700 }}>⏸️ Delivery paused for this date</div>
                ) : isToday ? (
                  <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem' }}>
                    <button type="button" onClick={() => handleStatusChange(d.deliveryId, 'Delivered')} disabled={isDelivered} style={{ ...smallBtn, background: '#2E7D32', color: 'white', opacity: isDelivered ? 0.6 : 1 }}>✅ Delivered</button>
                    <button type="button" onClick={() => handleStatusChange(d.deliveryId, 'Failed')} disabled={isFailed} style={{ ...smallBtn, background: '#E53935', color: 'white', opacity: isFailed ? 0.6 : 1 }}>❌ Undelivered</button>
                  </div>
                ) : (
                  <div style={{ marginTop: '0.6rem', fontSize: '0.78rem', color: '#999' }}>{isFutureDate ? 'Future delivery — read only' : 'Past delivery — read only'}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showMsgAdmin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form onSubmit={handleSendToAdmin} style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem 0' }}>📩 Message Your Dairy Admin</h3>
            <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>Report a delivery issue, missing packet, route problem or anything else — your admin will see it in their notifications.</p>
            <input value={msgAdminTitle} onChange={e => setMsgAdminTitle(e.target.value)} placeholder="Subject (e.g. Delivery issue at Green Valley)" required style={{ ...inputStyle, marginBottom: '0.75rem' }} />
            <textarea value={msgAdminText} onChange={e => setMsgAdminText(e.target.value)} placeholder="Describe the issue..." required rows={4} style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: '1rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowMsgAdmin(false)} disabled={sendingMsgAdmin} style={{ ...smallBtn, background: '#757575', color: 'white', border: 'none' }}>Cancel</button>
              <button type="submit" disabled={sendingMsgAdmin} style={{ ...smallBtn, background: '#1565C0', color: 'white', border: 'none', opacity: sendingMsgAdmin ? 0.6 : 1 }}>{sendingMsgAdmin ? 'Sending...' : 'Send'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ===== Delivery Boys Panel =====
function DeliveryBoysPanel() {
  const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
  const [availableSocieties, setAvailableSocieties] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', address: '', assignedSocieties: [] as string[] });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; email: string; phone: string; address: string }>({ name: '', email: '', phone: '', address: '' });
  const [societyEditId, setSocietyEditId] = useState<number | null>(null);
  const [selectedSocieties, setSelectedSocieties] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: number; name: string } | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const grid = useGridControls('gridPrefs:deliveryboys', 'name');

  const load = async () => {
    const res = await api.getDeliveryBoys();
    if (res.data) setDeliveryBoys(res.data);
    const socRes = await api.getAvailableSocieties();
    if (socRes.data) setAvailableSocieties(socRes.data);
    else if (!socRes.success) setMsg(socRes.message || 'Could not load available societies');
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.createDeliveryBoy(form);
    setMsg(res.message);
    if (res.success) {
      setShowForm(false);
      setForm({ name: '', email: '', password: '', phone: '', address: '', assignedSocieties: [] });
      load();
    }
  };

  const startEdit = (db: any) => {
    setEditId(db.userId);
    setEditForm({ name: db.name, email: db.email, phone: db.phone || '', address: db.address || '' });
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    const res = await api.updateDeliveryBoy(editId, editForm);
    setMsg(res.message);
    if (res.success) { setEditId(null); load(); }
  };

  const startEditSocieties = (db: any) => {
    setSocietyEditId(db.userId);
    setSelectedSocieties([...(db.assignedSocieties ?? [])]);
  };

  const handleUpdateSocieties = async () => {
    if (!societyEditId) return;
    const res = await api.updateDeliveryBoySocieties(societyEditId, selectedSocieties);
    setMsg(res.message);
    if (res.success) { setSocietyEditId(null); load(); }
  };

  const toggleSociety = (society: string) => {
    setSelectedSocieties(prev =>
      prev.includes(society) ? prev.filter(s => s !== society) : [...prev, society]
    );
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await api.deleteDeliveryBoy(deleteConfirm.userId);
      setMsg(res.message);
      setDeleteConfirm(null);
      if (res.success) load();
    } catch {
      setMsg('❌ Delete failed. Please try again.');
      setDeleteConfirm(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { setMsg('Please select a CSV file'); return; }
    setUploading(true);
    try {
      const res = await api.bulkUploadDeliveryBoys(uploadFile);
      setMsg(res.message + (res.data?.errors?.length ? ' | Errors: ' + res.data.errors.join('; ') : ''));
      if (res.success) { setUploadFile(null); load(); }
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await api.downloadDeliveryBoySampleCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deliveryboy-upload-sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Download sample failed'); }
  };

  const filteredBoys = deliveryBoys.filter(db => {
    const matchesSearch = `${db.name} ${db.email} ${db.phone || ''} ${(db.assignedSocieties ?? []).join(' ')}`.toLowerCase().includes(grid.search.toLowerCase());
    const matchesStatus = grid.statusFilter === 'all' || (grid.statusFilter === 'active' ? db.isActive : !db.isActive);
    return matchesSearch && matchesStatus;
  });
  const sortedBoys = [...filteredBoys].sort((a, b) => {
    const cmp = String(a.name).localeCompare(String(b.name));
    return grid.sortDir === 'asc' ? cmp : -cmp;
  });
  const { paged: pagedBoys, totalPages: boysTotalPages } = paginate(sortedBoys, grid.page, grid.pageSize);
  const handleExportCsv = () => {
    downloadCsvFile(`delivery-boys-${today()}.csv`, [
      ['Name', 'Email', 'Phone', 'Assigned Societies', 'Status'],
      ...sortedBoys.map(db => [db.name, db.email, db.phone || '', (db.assignedSocieties ?? []).join('; '), db.isActive ? 'Active' : 'Inactive']),
    ]);
  };

  return (
    <div>
      <PanelHeader title="Delivery Boys" count={filteredBoys.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: '0.45rem 1rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem',
              background: 'var(--tc)', color: 'white', border: 'none', cursor: 'pointer',
            }}
          >+ Add Delivery Boy</button>
          <ActionBtn onClick={handleDownloadSample} color="#1976D2">📄 Sample CSV</ActionBtn>
          <ActionBtn onClick={handleExportCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
        </div>
      </PanelHeader>
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={grid.search}
          onChange={e => grid.setSearch(e.target.value)}
          placeholder="🔍 Search by name, email, phone, or society..."
          style={{ ...inputStyle, flex: '1 1 240px', maxWidth: '360px' }}
        />
        <select value={grid.statusFilter} onChange={e => grid.setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }} title="Filter by status">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      {msg && <MsgBanner>{msg}</MsgBanner>}
      <div style={{ ...formStyle, marginBottom: '1rem' }}>
        <strong style={{ color: 'var(--tc)', fontSize: '0.9rem' }}>📤 Bulk Upload (CSV):</strong>
        <input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)}
          style={{ fontSize: '0.85rem' }} />
        <GreenButton onClick={handleBulkUpload}>{uploading ? 'Uploading...' : 'Upload'}</GreenButton>
      </div>
      {showForm && (
        <form onSubmit={handleCreate} style={formStyle}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name" required style={inputStyle} />
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" required style={inputStyle} />
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" required minLength={6} style={inputStyle} />
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" style={inputStyle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <strong style={{ color: 'var(--tc)' }}>Assign Societies (Route) — select one or more:</strong>
            {availableSocieties.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: '#999' }}>
                No societies found yet. Add a subscriber with a society name (Subscribers tab) first, then reopen this form to assign societies.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, assignedSocieties: [...availableSocieties] }))} style={smallBtn}>Select All</button>
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, assignedSocieties: [] }))} style={smallBtn}>Clear All</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {availableSocieties.map(soc => (
                    <label key={soc} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="checkbox"
                        checked={form.assignedSocieties.includes(soc)}
                        onChange={() => setForm(prev => ({
                          ...prev,
                          assignedSocieties: prev.assignedSocieties.includes(soc)
                            ? prev.assignedSocieties.filter(s => s !== soc)
                            : [...prev.assignedSocieties, soc]
                        }))}
                      />
                      {soc}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          <GreenButton type="submit">Create Delivery Boy</GreenButton>
        </form>
      )}
      {editId && (
        <form onSubmit={handleEdit} style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit Delivery Boy</strong>
          <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Full Name" style={inputStyle} />
          <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" style={inputStyle} />
          <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} placeholder="Address" style={inputStyle} />
          <GreenButton type="submit">Save</GreenButton>
          <button type="button" onClick={() => setEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </form>
      )}
      {societyEditId && (
        <div style={formStyle}>
          <strong style={{ color: 'var(--tc)' }}>Edit Assigned Societies</strong>
          {availableSocieties.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: '#999' }}>
              No societies found yet. Add a subscriber with a society name (Subscribers tab) first.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" onClick={() => setSelectedSocieties([...availableSocieties])} style={smallBtn}>Select All</button>
                <button type="button" onClick={() => setSelectedSocieties([])} style={smallBtn}>Clear All</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {availableSocieties.map(soc => (
                  <label key={soc} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedSocieties.includes(soc)}
                      onChange={() => toggleSociety(soc)}
                    />
                    {soc}
                  </label>
                ))}
              </div>
            </>
          )}
          <GreenButton onClick={handleUpdateSocieties}>Save Societies</GreenButton>
          <button type="button" onClick={() => setSocietyEditId(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
        </div>
      )}
      {deleteConfirm && (
        <ConfirmDialog
          message={`Delete delivery boy "${deleteConfirm.name}"? This will deactivate the user and remove all society assignments.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <SortableTh label="Name" sortKeyName="name" activeKey={grid.sortKey} dir={grid.sortDir} onSort={grid.toggleSort} />
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Phone</th>
            <th style={thStyle}>Assigned Societies (Route)</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedBoys.map(db => (
            <tr key={db.userId} style={trStyle}>
              <td style={tdStyle}><strong>{db.name}</strong></td>
              <td style={tdStyle}>{db.email}</td>
              <td style={tdStyle}>{db.phone || 'N/A'}</td>
              <td style={tdStyle}>
                {(db.assignedSocieties ?? []).length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {(db.assignedSocieties ?? []).map((soc: string) => (
                      <span key={soc} style={{
                        padding: '0.2rem 0.5rem',
                        background: '#047857',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>{soc}</span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#999' }}>No societies assigned</span>
                )}
              </td>
              <td style={tdStyle}>
                <StatusBadge isActive={db.isActive}>{db.isActive ? 'Active' : 'Inactive'}</StatusBadge>
              </td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <ActionBtn onClick={() => startEdit(db)} color="var(--tc)">Edit</ActionBtn>
                  <ActionBtn onClick={() => startEditSocieties(db)} color="#1976D2">Societies</ActionBtn>
                  <ActionBtn onClick={() => setDeleteConfirm({ userId: db.userId, name: db.name })} color="#D32F2F">Delete</ActionBtn>
                </div>
              </td>
            </tr>
          ))}
          {pagedBoys.length === 0 && <EmptyRow colSpan={6} />}
        </tbody>
      </table>
      <div className="mobile-card-list">
        {pagedBoys.map(db => (
          <div key={db.userId} className="mobile-card-item">
            <div className="mobile-card-header">
              <span className="mobile-card-title">{db.name}</span>
              <StatusBadge isActive={db.isActive}>{db.isActive ? 'Active' : 'Inactive'}</StatusBadge>
            </div>
            <div className="mobile-card-details">
              <div><span className="mobile-card-detail-label">Email: </span>{db.email}</div>
              <div><span className="mobile-card-detail-label">Phone: </span>{db.phone || 'N/A'}</div>
              <div><span className="mobile-card-detail-label">Route: </span>{(db.assignedSocieties ?? []).length > 0 ? (db.assignedSocieties ?? []).join(', ') : 'No societies assigned'}</div>
            </div>
            <div className="mobile-card-actions">
              <button className="mc-btn mc-btn-edit" onClick={() => startEdit(db)}>✏️ Edit</button>
              <button className="mc-btn mc-btn-edit" onClick={() => startEditSocieties(db)}>🏘️ Societies</button>
              <button className="mc-btn mc-btn-edit" style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }} onClick={() => setDeleteConfirm({ userId: db.userId, name: db.name })}>🗑️ Delete</button>
            </div>
          </div>
        ))}
      </div>
      </div>
      <GridPagination page={grid.page} setPage={grid.setPage} pageSize={grid.pageSize} setPageSize={grid.setPageSize} totalPages={boysTotalPages} />
    </div>
  );
}

// ===== Delivery Logs Panel =====
function DeliveryLogsPanel() {
  const [deliveries, setDeliveries] = useState<DeliveryLogDto[]>([]);
  const [summary, setSummary] = useState<DeliveryLogSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [deliveryBoyUserId, setDeliveryBoyUserId] = useState<number | ''>('');
  const [societyName, setSocietyName] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
  const [addressMasters, setAddressMasters] = useState<AddressMasterDto[]>([]);
  const [buildingMasters, setBuildingMasters] = useState<BuildingMasterDto[]>([]);
  const [selectedSocietyId, setSelectedSocietyId] = useState<number>('');
  const grid = useGridControls('gridPrefs:deliverylogs', 'date');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getDeliveryLogs({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        deliveryBoyUserId: deliveryBoyUserId || undefined,
        societyName: societyName || undefined,
        buildingName: buildingName || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      if (res.data) {
        setDeliveries(res.data.deliveries);
        setSummary(res.data.summary);
      }
      if (!res.success) setMsg(res.message || 'Could not load delivery logs');
    } catch {
      setMsg('Could not load delivery logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.getDeliveryBoys().then(res => { if (res.data) setDeliveryBoys(res.data); });
  }, []);

  useEffect(() => {
    api.getAddressMasters().then(res => { if (res.data) setAddressMasters(res.data.filter(a => a.isActive)); });
  }, []);

  useEffect(() => {
    if (selectedSocietyId) {
      api.getBuildingMasters(selectedSocietyId).then(res => { if (res.data) setBuildingMasters(res.data.filter(b => b.isActive)); });
    } else {
      setBuildingMasters([]);
    }
    setBuildingName('');
  }, [selectedSocietyId]);

  const handleExportCsv = () => {
    const rows: Array<Array<string | number>> = [
      ['Date', 'Subscriber', 'Society', 'Building', 'Flat', 'Milk Type', 'Qty', 'Status', 'Marked By', 'Marked Date'],
      ...deliveries.map(d => [
        d.date?.slice(0, 10) || '',
        d.subscriberName,
        d.societyName || '',
        d.buildingName || '',
        d.flatName || '',
        d.milkTypeName,
        d.quantity,
        d.status,
        d.markedByName || '',
        (d.status === 'Delivered' || d.status === 'Failed') && d.markedDate ? new Date(d.markedDate).toLocaleString() : '',
      ]),
    ];
    downloadCsvFile(`delivery-logs-${today()}.csv`, rows);
  };

  return (
    <div>
      <PanelHeader title="Delivery Logs" count={deliveries.length}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <GreenButton onClick={load}>🔍 Search</GreenButton>
          <ActionBtn onClick={handleExportCsv} color="#2E7D32">⬇ Export CSV</ActionBtn>
        </div>
      </PanelHeader>

      {msg && <MsgBanner>{msg}</MsgBanner>}

      {/* Filters */}
      <div style={{ ...formStyle, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>Delivery Boy</label>
          <select value={deliveryBoyUserId} onChange={e => setDeliveryBoyUserId(e.target.value ? Number(e.target.value) : '')} style={{ ...inputStyle, minWidth: '160px' }}>
            <option value="">All Delivery Boys</option>
            {deliveryBoys.map(db => <option key={db.userId} value={db.userId}>{db.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>Society</label>
          <select value={selectedSocietyId} onChange={e => {
            const val = e.target.value ? Number(e.target.value) : '';
            setSelectedSocietyId(val);
            const society = addressMasters.find(a => a.addressMasterId === val);
            setSocietyName(society?.societyName || '');
          }} style={{ ...inputStyle, minWidth: '160px' }}>
            <option value="">All Societies</option>
            {addressMasters.map(a => <option key={a.addressMasterId} value={a.addressMasterId}>{a.societyName}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>Building</label>
          <select value={buildingName} onChange={e => setBuildingName(e.target.value)} disabled={!selectedSocietyId} style={{ ...inputStyle, minWidth: '160px' }}>
            <option value="">{selectedSocietyId ? 'All Buildings' : 'Select Society First'}</option>
            {buildingMasters.map(b => <option key={b.buildingMasterId} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <label style={{ fontWeight: 600, color: 'var(--tc)', fontSize: '0.8rem' }}>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, minWidth: '120px' }}>
            <option value="all">All</option>
            <option value="Delivered">Delivered</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'var(--tc-light)', padding: '1rem', borderRadius: '10px', flex: 1, minWidth: '120px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--tc)' }}>{summary.totalDeliveries}</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Total</div>
          </div>
          <div style={{ background: '#E8F5E9', padding: '1rem', borderRadius: '10px', flex: 1, minWidth: '120px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2E7D32' }}>{summary.deliveredCount}</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Delivered</div>
          </div>
          <div style={{ background: '#FFF3E0', padding: '1rem', borderRadius: '10px', flex: 1, minWidth: '120px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#E65100' }}>{summary.pendingCount}</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Pending</div>
          </div>
          <div style={{ background: '#FFEBEE', padding: '1rem', borderRadius: '10px', flex: 1, minWidth: '120px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#C62828' }}>{summary.failedCount}</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Failed</div>
          </div>
        </div>
      )}

      {/* Delivery Boy Summary */}
      {summary && summary.byDeliveryBoy.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--tc)', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Delivery Boy Summary</h3>
          <TableWrap>
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}>
                <th style={thStyle}>Delivery Boy</th>
                <th style={thStyle}>Total Marked</th>
                <th style={thStyle}>Delivered</th>
                <th style={thStyle}>Failed</th>
              </tr></thead>
              <tbody>
                {summary.byDeliveryBoy.map(boy => (
                  <tr key={boy.userId} style={trStyle}>
                    <td style={tdStyle}><strong>{boy.name}</strong></td>
                    <td style={tdStyle}>{boy.totalMarked}</td>
                    <td style={tdStyle}><span style={{ color: '#2E7D32', fontWeight: 600 }}>{boy.deliveredCount}</span></td>
                    <td style={tdStyle}><span style={{ color: '#C62828', fontWeight: 600 }}>{boy.failedCount}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </div>
      )}

      {loading && <LoadingSpinner />}

      {/* Delivery Log Table */}
      {!loading && deliveries.length > 0 && (
        <TableWrap>
          <table style={tableStyle}>
            <thead><tr style={thRowStyle}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Subscriber</th>
              <th style={thStyle}>Society</th>
              <th style={thStyle}>Building</th>
              <th style={thStyle}>Flat</th>
              <th style={thStyle}>Milk</th>
              <th style={thStyle}>Qty</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Timestamp</th>
              <th style={thStyle}>Marked By</th>
            </tr></thead>
            <tbody>
              {deliveries.map(d => (
                <tr key={d.deliveryId} style={trStyle}>
                  <td style={tdStyle}>{d.date?.slice(0, 10)}</td>
                  <td style={tdStyle}><strong>{d.subscriberName}</strong></td>
                  <td style={tdStyle}>{d.societyName || '—'}</td>
                  <td style={tdStyle}>{d.buildingName || '—'}</td>
                  <td style={tdStyle}>{d.flatName || '—'}</td>
                  <td style={tdStyle}>{d.milkTypeName}</td>
                  <td style={tdStyle}>{d.quantity} L</td>
                  <td style={tdStyle}><StatusBadge status={d.status === 'Delivered' ? 'delivered' : d.status === 'Failed' ? 'failed' : 'pending'} /></td>
                  <td style={tdStyle}>{(d.status === 'Delivered' || d.status === 'Failed') && d.markedDate ? new Date(d.markedDate).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>{d.markedByName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {!loading && deliveries.length === 0 && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: 12, textAlign: 'center', color: '#888' }}>
          No delivery logs found for the selected filters.
        </div>
      )}
    </div>
  );
}

// ===== Audit Log Panel =====
function AuditLogPanel({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const [logs, setLogs] = useState<AuditLogDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [filters, setFilters] = useState<AuditLogFilters>({ entityType: '', actionType: '', search: '', fromDate: '', toDate: '' });
  const [selectedLog, setSelectedLog] = useState<AuditLogDto | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [showRevertConfirm, setShowRevertConfirm] = useState<number | null>(null);
  const [tenants, setTenants] = useState<{ tenantId: number; name: string }[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(undefined);
  const PAGE_SIZE = 20;

  const load = async (p = page) => {
    setLoading(true);
    const f: AuditLogFilters = { ...filters, page: p, pageSize: PAGE_SIZE };
    if (isSuperAdmin && selectedTenantId) f.page = p;
    const res = await api.getAuditLogs(f);
    if (res.success && res.data) {
      setLogs(res.data.logs);
      setTotalCount(res.data.totalCount);
      setTotalPages(res.data.totalPages);
      setPage(p);
    } else {
      setMsg(res.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(1); }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      api.getTenants().then(res => {
        if (res.success && res.data) setTenants(res.data.map((t: any) => ({ tenantId: t.tenantId, name: t.name })));
      });
    }
  }, [isSuperAdmin]);

  const handleFilter = () => { load(1); };

  const handleRevert = async (logId: number) => {
    const res = await api.revertAuditAction(logId, revertReason || undefined);
    setMsg(res.message);
    setShowRevertConfirm(null);
    setRevertReason('');
    load();
  };

  const isRevertible = (action: string) => {
    return ['Subscriber.Suspend', 'Subscriber.Reactivate', 'Bill.Generate', 'Bill.MarkPaid', 'Bill.Revoke', 'MilkType.Update'].includes(action);
  };

  const entityTypeOptions = ['', 'Subscriber', 'Bill', 'Delivery', 'MilkType', 'Tenant', 'User'];
  const actionTypeOptions = ['', 'UserAction', 'SystemAction'];

  return (
    <div>
      <PanelHeader title="Audit Log" count={totalCount} icon="📝" subtitle="Track all actions with revert capability">
        <ActionBtn onClick={() => load(1)} color="#1565C0">🔄 Refresh</ActionBtn>
      </PanelHeader>

      {msg && <MsgBanner>{msg}</MsgBanner>}

      <div style={{ ...formStyle, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {isSuperAdmin && (
          <select value={selectedTenantId ?? ''} onChange={e => setSelectedTenantId(e.target.value ? +e.target.value : undefined)} style={inputStyle}>
            <option value="">All Tenants</option>
            {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
          </select>
        )}
        <select value={filters.entityType || ''} onChange={e => setFilters({ ...filters, entityType: e.target.value })} style={inputStyle}>
          {entityTypeOptions.map(o => <option key={o} value={o}>{o || 'All Entity Types'}</option>)}
        </select>
        <select value={filters.actionType || ''} onChange={e => setFilters({ ...filters, actionType: e.target.value })} style={inputStyle}>
          {actionTypeOptions.map(o => <option key={o} value={o}>{o || 'All Action Types'}</option>)}
        </select>
        <input type="date" value={filters.fromDate || ''} onChange={e => setFilters({ ...filters, fromDate: e.target.value })} style={inputStyle} placeholder="From" />
        <input type="date" value={filters.toDate || ''} onChange={e => setFilters({ ...filters, toDate: e.target.value })} style={inputStyle} placeholder="To" />
        <input value={filters.search || ''} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Search..." style={inputStyle} />
        <GreenButton onClick={handleFilter}>🔍 Filter</GreenButton>
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          <div className="responsive-table-wrap" style={{ overflowX: 'auto', borderRadius: '12px' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={thRowStyle}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>Entity</th>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? <EmptyRow colSpan={9} text="No audit logs found" /> : logs.map(log => (
                  <tr key={log.auditLogId} style={trStyle}>
                    <td style={tdStyle}>{new Date(log.createdDate).toLocaleString()}</td>
                    <td style={tdStyle}>{log.userName || 'System'}</td>
                    <td style={tdStyle}><StatusBadge status={log.action} /></td>
                    <td style={tdStyle}>{log.entityType}</td>
                    <td style={tdStyle}>{log.entityId}</td>
                    <td style={tdStyle}>{log.description}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, background: log.actionType === 'SystemAction' ? '#FFF3E0' : '#E8F5E9', color: log.actionType === 'SystemAction' ? '#E65100' : '#2E7D32' }}>
                        {log.actionType === 'SystemAction' ? '🤖 System' : '👤 User'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {log.isReverted ? <StatusBadge status="Reverted" /> : <span style={{ color: '#888', fontSize: '0.85rem' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <ActionBtn onClick={() => setSelectedLog(log)} color="#1565C0">👁️ View</ActionBtn>
                        {isRevertible(log.action) && !log.isReverted && (
                          <ActionBtn onClick={() => setShowRevertConfirm(log.auditLogId)} color="#E65100">↩ Revert</ActionBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mobile-card-list" style={{ display: 'none' }}>
              {logs.map(log => (
                <div key={log.auditLogId} style={{ background: 'white', borderRadius: '12px', padding: '1rem', marginBottom: '0.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #E8E8E8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>{log.action}</span>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600, background: log.actionType === 'SystemAction' ? '#FFF3E0' : '#E8F5E9', color: log.actionType === 'SystemAction' ? '#E65100' : '#2E7D32' }}>
                      {log.actionType === 'SystemAction' ? 'System' : 'User'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.5rem' }}>{log.description}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#888' }}>
                    <span>{log.userName || 'System'} · {log.entityType} #{log.entityId}</span>
                    <span>{new Date(log.createdDate).toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <ActionBtn onClick={() => setSelectedLog(log)} color="#1565C0">View</ActionBtn>
                    {isRevertible(log.action) && !log.isReverted && (
                      <ActionBtn onClick={() => setShowRevertConfirm(log.auditLogId)} color="#E65100">Revert</ActionBtn>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <ActionBtn onClick={() => load(page - 1)} color="#757575" disabled={page <= 1}>← Prev</ActionBtn>
              <span style={{ padding: '0.35rem 0.85rem', fontSize: '0.85rem', color: '#333' }}>Page {page} of {totalPages}</span>
              <ActionBtn onClick={() => load(page + 1)} color="#757575" disabled={page >= totalPages}>Next →</ActionBtn>
            </div>
          )}
        </>
      )}

      {selectedLog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setSelectedLog(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem', color: '#1565C0' }}>📋 Audit Log Detail</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div><strong>Action:</strong> {selectedLog.action}</div>
              <div><strong>Entity:</strong> {selectedLog.entityType} #{selectedLog.entityId}</div>
              <div><strong>User:</strong> {selectedLog.userName || 'System'}</div>
              <div><strong>Type:</strong> {selectedLog.actionType}</div>
              <div><strong>Date:</strong> {new Date(selectedLog.createdDate).toLocaleString()}</div>
              <div><strong>Status:</strong> {selectedLog.isReverted ? 'Reverted' : 'Active'}</div>
              {selectedLog.tenantName && <div><strong>Tenant:</strong> {selectedLog.tenantName}</div>}
              <div><strong>Description:</strong> {selectedLog.description}</div>
            </div>
            {selectedLog.oldValues && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Old Values:</strong>
                <pre style={{ background: '#FFF3E0', padding: '0.75rem', borderRadius: '8px', fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>{JSON.stringify(JSON.parse(selectedLog.oldValues), null, 2)}</pre>
              </div>
            )}
            {selectedLog.newValues && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>New Values:</strong>
                <pre style={{ background: '#E8F5E9', padding: '0.75rem', borderRadius: '8px', fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>{JSON.stringify(JSON.parse(selectedLog.newValues), null, 2)}</pre>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedLog(null)} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showRevertConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => { setShowRevertConfirm(null); setRevertReason(''); }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem', color: '#E65100' }}>⚠️ Confirm Revert</h3>
            <p style={{ margin: '0 0 1rem', color: '#333' }}>Are you sure you want to revert this action? This will undo the change made.</p>
            <input value={revertReason} onChange={e => setRevertReason(e.target.value)} placeholder="Reason for revert (optional)" style={inputStyle} />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setShowRevertConfirm(null); setRevertReason(''); }} style={{ ...smallBtn, background: '#757575', color: 'white' }}>Cancel</button>
              <GreenButton onClick={() => handleRevert(showRevertConfirm)}>Yes, Revert</GreenButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Cash Management Panel =====
function CashManagementPanel({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const FALLBACK_CATEGORIES = ['Raw Materials', 'Utilities', 'Salary', 'Petrol / Delivery Cost', 'Maintenance', 'Miscellaneous'];
  const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

  const [categoryNames, setCategoryNames] = useState<string[]>(FALLBACK_CATEGORIES);
  const [selectedDate, setSelectedDate] = useState(todayLocal);
  const [opening, setOpening] = useState<number>(0);
  const [closing, setClosing] = useState<string>('');
  const [entry, setEntry] = useState<DailyCashEntryDto | null>(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // Expense form
  const [expCat, setExpCat] = useState(FALLBACK_CATEGORIES[0]);
  const [expAmt, setExpAmt] = useState<number>(0);
  const [expNotes, setExpNotes] = useState('');

  // Reports
  const [view, setView] = useState<'daily' | 'monthly' | 'yearly' | 'tenants'>('daily');
  const [tenantReportView, setTenantReportView] = useState<'monthly' | 'yearly'>('monthly');
  const [reportMonth, setReportMonth] = useState(currentMonth());
  const [reportYear, setReportYear] = useState(String(new Date().getFullYear()));
  const [monthlySummary, setMonthlySummary] = useState<MonthlyCashSummaryDto | null>(null);
  const [yearlySummary, setYearlySummary] = useState<YearlyCashSummaryDto | null>(null);
  const [tenantReports, setTenantReports] = useState<TenantCashReportDto[]>([]);
  const [tenantYearlyReports, setTenantYearlyReports] = useState<TenantYearlyCashReportDto[]>([]);

  const loadDay = async (date: string) => {
    setLoading(true);
    try {
      const res = await api.getDailyCash(date);
      if (res.data) {
        setEntry(res.data);
        setOpening(res.data.openingCash);
        setClosing(res.data.closingCash !== null ? String(res.data.closingCash) : '');
      } else {
        setEntry(null);
        setOpening(0);
        setClosing('');
      }
    } catch {
      setEntry(null);
      setOpening(0);
      setClosing('');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDay(selectedDate);
    // Load dynamic categories from backend
    api.getExpenseCategories().then(res => {
      if (res.data && res.data.length > 0) {
        const names = res.data.map(c => c.name);
        setCategoryNames(names);
        setExpCat(prev => names.includes(prev) ? prev : names[0]);
      }
    }).catch(() => {});
  }, [selectedDate]);

  const handleSaveCash = async () => {
    try {
      setMsg('');
      const res = await api.saveDailyCash({
        date: selectedDate,
        openingCash: opening,
        closingCash: closing !== '' ? Number(closing) : null,
      });
      setMsg(res.message || 'Daily cash saved successfully');
      if (res.data) { setEntry(res.data); }
      await loadDay(selectedDate);
    } catch (err: any) {
      setMsg('Error: Could not save daily cash. Please try again.');
    }
  };

  const handleAddExpense = async () => {
    if (expAmt <= 0) { setMsg('Enter a valid amount'); return; }
    try {
      setMsg('');
      const res = await api.addExpense({
        date: selectedDate,
        category: expCat,
        amount: expAmt,
        notes: expNotes || undefined,
      });
      setMsg(res.message || 'Expense added successfully');
      setExpAmt(0);
      setExpNotes('');
      await loadDay(selectedDate);
    } catch (err: any) {
      setMsg('Error: Could not add expense. Please try again.');
    }
  };

  const handleDeleteExpense = async (id: number) => {
    if (!confirm('Delete this expense?')) return;
    try {
      await api.deleteExpense(id);
      setMsg('Expense deleted');
      await loadDay(selectedDate);
    } catch (err: any) {
      setMsg('Error: Could not delete expense.');
    }
  };

  const loadMonthlyReport = async (month?: string) => {
    const m = month ?? reportMonth;
    setLoading(true);
    const res = await api.getMonthlyCashSummary(m);
    if (res.data) setMonthlySummary(res.data);
    setLoading(false);
  };

  const loadYearlyReport = async (year?: string) => {
    const y = year ?? reportYear;
    setLoading(true);
    try {
      const res = await api.getYearlyCashSummary(y);
      if (res.data) setYearlySummary(res.data);
    } catch { /* no data */ }
    setLoading(false);
  };

  const loadTenantReports = async (month?: string) => {
    const m = month ?? reportMonth;
    setLoading(true);
    const res = await api.getAllTenantsCashReport(m);
    if (res.data) setTenantReports(res.data);
    setLoading(false);
  };

  const loadTenantYearlyReports = async (year?: string) => {
    const y = year ?? reportYear;
    setLoading(true);
    try {
      const res = await api.getAllTenantsYearlyCashReport(y);
      if (res.data) setTenantYearlyReports(res.data);
    } catch { /* no data */ }
    setLoading(false);
  };

  const netCashFlow = entry && entry.closingCash !== null ? entry.closingCash - entry.openingCash : null;
  const totalExpenses = entry ? entry.totalExpenses : 0;
  const estimatedSales = netCashFlow !== null ? netCashFlow + totalExpenses : null;

  const cardStyle: React.CSSProperties = {
    background: 'white', borderRadius: '12px', padding: '1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '1rem',
  };
  const statCard: React.CSSProperties = {
    padding: '1rem', borderRadius: '10px', textAlign: 'center', flex: 1, minWidth: '140px',
  };

  return (
    <div>
      <PanelHeader title="🏪 Cash Management" />

      {/* View toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {!isSuperAdmin && (
          <GreenButton onClick={() => setView('daily')}>📅 Daily Entry</GreenButton>
        )}
        {!isSuperAdmin && (
          <GreenButton onClick={() => { setView('monthly'); loadMonthlyReport(); }}>📈 Monthly Report</GreenButton>
        )}
        <GreenButton onClick={() => { setView('yearly'); loadYearlyReport(); }}>📊 Yearly Report</GreenButton>
        {isSuperAdmin && (
          <GreenButton onClick={() => { setView('tenants'); loadTenantReports(); }}>🏢 Tenant Reports</GreenButton>
        )}
      </div>

      {/* ===== DAILY ENTRY (Tenant Admin only) ===== */}
      {view === 'daily' && !isSuperAdmin && (
        <>
          {/* Date picker */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <strong style={{ color: 'var(--tc)' }}>📅 Date:</strong>
              <DateInput value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={inputStyle} />
              {loading && <span>⏳ Loading...</span>}
            </div>
          </div>

          {msg && <MsgBanner>{msg}</MsgBanner>}

          {/* Opening / Closing Cash */}
          <div style={cardStyle}>
            <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem 0', fontSize: '1rem' }}>💰 Daily Cash</h3>
            <div className="filter-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Opening Cash (₹)</label>
                <input type="number" min="0" step="0.01" value={opening} onChange={e => setOpening(+e.target.value)}
                  style={{ ...inputStyle, width: '160px' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Closing Cash (₹)</label>
                <input type="number" min="0" step="0.01" value={closing} onChange={e => setClosing(e.target.value)}
                  placeholder="Enter at end of day" style={{ ...inputStyle, width: '160px' }} />
              </div>
              <GreenButton onClick={handleSaveCash}>💾 Save</GreenButton>
            </div>
          </div>

          {/* Quick Expense Entry */}
          <div style={cardStyle}>
            <h3 style={{ color: 'var(--tc)', margin: '0 0 1rem 0', fontSize: '1rem' }}>📝 Add Expense</h3>
            <div className="filter-row" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Category</label>
                <select value={expCat} onChange={e => setExpCat(e.target.value)} style={inputStyle}>
                  {categoryNames.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Amount (₹)</label>
                <input type="number" min="0.01" step="0.01" value={expAmt || ''} onChange={e => setExpAmt(+e.target.value)}
                  style={{ ...inputStyle, width: '120px' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>Notes</label>
                <input type="text" value={expNotes} onChange={e => setExpNotes(e.target.value)}
                  placeholder="Optional" style={{ ...inputStyle, width: '180px' }} />
              </div>
              <GreenButton onClick={handleAddExpense}>➕ Add</GreenButton>
            </div>
          </div>

          {/* Daily Summary Cards */}
          {entry && (
            <div className="stat-row" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <div style={{ ...statCard, background: '#E3F2FD', border: '1px solid #BBDEFB' }}>
                <div style={{ fontSize: '0.75rem', color: '#1565C0', marginBottom: '4px' }}>Opening Cash</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1565C0' }}>₹{entry.openingCash.toFixed(2)}</div>
              </div>
              <div style={{ ...statCard, background: entry.closingCash !== null ? 'var(--tc-light)' : '#FFF3E0', border: entry.closingCash !== null ? '1px solid var(--tc-border)' : '1px solid #FFE0B2' }}>
                <div style={{ fontSize: '0.75rem', color: entry.closingCash !== null ? 'var(--tc)' : '#E65100', marginBottom: '4px' }}>
                  Closing Cash
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: entry.closingCash !== null ? 'var(--tc)' : '#E65100' }}>
                  {entry.closingCash !== null ? `₹${entry.closingCash.toFixed(2)}` : 'Pending'}
                </div>
              </div>
              <div style={{ ...statCard, background: '#FCE4EC', border: '1px solid #F8BBD0' }}>
                <div style={{ fontSize: '0.75rem', color: '#C62828', marginBottom: '4px' }}>Total Expenses</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#C62828' }}>₹{totalExpenses.toFixed(2)}</div>
              </div>
              {netCashFlow !== null && (
                <div style={{ ...statCard, background: netCashFlow >= 0 ? 'var(--tc-light)' : '#FFEBEE', border: netCashFlow >= 0 ? '1px solid var(--tc-border)' : '1px solid #FFCDD2' }}>
                  <div style={{ fontSize: '0.75rem', color: netCashFlow >= 0 ? 'var(--tc)' : '#C62828', marginBottom: '4px' }}>Net Cash Flow</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: netCashFlow >= 0 ? 'var(--tc)' : '#C62828' }}>₹{netCashFlow.toFixed(2)}</div>
                </div>
              )}
              {estimatedSales !== null && (
                <div style={{ ...statCard, background: '#F3E5F5', border: '1px solid #E1BEE7' }}>
                  <div style={{ fontSize: '0.75rem', color: '#7B1FA2', marginBottom: '4px' }}>Est. Sales</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#7B1FA2' }}>₹{estimatedSales.toFixed(2)}</div>
                </div>
              )}
            </div>
          )}

          {/* Expense list for the day */}
          {entry && entry.expenses.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📋 Today's Expenses</h3>
              <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}>
                    <th style={thStyle}>Category</th><th style={thStyle}>Amount</th><th style={thStyle}>Notes</th><th style={thStyle}>Action</th>
                  </tr></thead>
                  <tbody>
                    {entry.expenses.map(exp => (
                      <tr key={exp.dailyExpenseId} style={trStyle}>
                        <td style={tdStyle}>{exp.category}</td>
                        <td style={tdStyle}><strong>₹{exp.amount.toFixed(2)}</strong></td>
                        <td style={tdStyle}>{exp.notes || '-'}</td>
                        <td style={tdStyle}>
                          <ActionBtn onClick={() => handleDeleteExpense(exp.dailyExpenseId)} color="#D32F2F">🗑</ActionBtn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {entry.expenses.map(exp => (
                    <div key={exp.dailyExpenseId} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{exp.category}</span>
                        <span style={{ fontWeight: 700, color: '#C62828' }}>₹{exp.amount.toFixed(2)}</span>
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Notes: </span>{exp.notes || '-'}</div>
                      </div>
                      <div className="mobile-card-actions">
                        <button className="mc-btn mc-btn-edit" style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }} onClick={() => handleDeleteExpense(exp.dailyExpenseId)}>🗑️ Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== MONTHLY REPORT ===== */}
      {view === 'monthly' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Month:</label>
            <input type="month" value={reportMonth} onChange={e => {
              const m = e.target.value;
              setReportMonth(m);
              setMonthlySummary(null);
              loadMonthlyReport(m);
            }} style={inputStyle} />
            <GreenButton onClick={async () => {
              try {
                const blob = await api.exportMonthlyCashExcel(reportMonth);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `cash-report-${reportMonth}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { setMsg('Excel export failed'); }
            }}>📥 Download Excel</GreenButton>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>⏳ Loading...</div>}

          {monthlySummary && !loading && (
            <>
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div style={{ ...statCard, background: '#E3F2FD', border: '1px solid #BBDEFB' }}>
                  <div style={{ fontSize: '0.75rem', color: '#1565C0', marginBottom: '4px' }}>First Opening</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1565C0' }}>₹{monthlySummary.firstOpeningCash.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: 'var(--tc-light)', border: '1px solid var(--tc-border)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--tc)', marginBottom: '4px' }}>Last Closing</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--tc)' }}>₹{monthlySummary.lastClosingCash.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: 'var(--tc-light)', border: '1px solid var(--tc-border)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--tc)', marginBottom: '4px' }}>Total Cash Inflow</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--tc)' }}>₹{monthlySummary.totalCashInflow.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: '#FCE4EC', border: '1px solid #F8BBD0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#C62828', marginBottom: '4px' }}>Total Expenses</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#C62828' }}>₹{monthlySummary.totalExpenses.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: monthlySummary.estimatedProfit >= 0 ? 'var(--tc-light)' : '#FFEBEE', border: monthlySummary.estimatedProfit >= 0 ? '1px solid var(--tc-border)' : '1px solid #FFCDD2' }}>
                  <div style={{ fontSize: '0.75rem', color: monthlySummary.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', marginBottom: '4px' }}>Est. Profit</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: monthlySummary.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828' }}>₹{monthlySummary.estimatedProfit.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: '#FFF3E0', border: '1px solid #FFE0B2' }}>
                  <div style={{ fontSize: '0.75rem', color: '#E65100', marginBottom: '4px' }}>Days Recorded</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#E65100' }}>{monthlySummary.daysRecorded}</div>
                </div>
              </div>

              {/* Category-wise breakdown */}
              {monthlySummary.categoryBreakdown.length > 0 && (
                <div style={cardStyle}>
                  <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📊 Expense Breakdown by Category</h3>
                  <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                    <table style={tableStyle}>
                      <thead><tr style={thRowStyle}>
                        <th style={thStyle}>Category</th><th style={thStyle}>Total</th><th style={thStyle}>Count</th><th style={thStyle}>% of Expenses</th>
                      </tr></thead>
                      <tbody>
                        {monthlySummary.categoryBreakdown.map(c => (
                          <tr key={c.category} style={trStyle}>
                            <td style={tdStyle}><strong>{c.category}</strong></td>
                            <td style={tdStyle}>₹{c.total.toFixed(2)}</td>
                            <td style={tdStyle}>{c.count}</td>
                            <td style={tdStyle}>{monthlySummary.totalExpenses > 0 ? ((c.total / monthlySummary.totalExpenses) * 100).toFixed(1) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mobile-card-list">
                      {monthlySummary.categoryBreakdown.map(c => (
                        <div key={c.category} className="mobile-card-item">
                          <div className="mobile-card-header">
                            <span className="mobile-card-title">{c.category}</span>
                            <span style={{ fontWeight: 700 }}>₹{c.total.toFixed(2)}</span>
                          </div>
                          <div className="mobile-card-details">
                            <div><span className="mobile-card-detail-label">Count: </span>{c.count}</div>
                            <div><span className="mobile-card-detail-label">% of Expenses: </span>{monthlySummary.totalExpenses > 0 ? ((c.total / monthlySummary.totalExpenses) * 100).toFixed(1) : 0}%</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Daily entries table */}
              {monthlySummary.dailyEntries.length > 0 && (
                <div style={cardStyle}>
                  <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📅 Daily Entries</h3>
                  <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                    <table style={tableStyle}>
                      <thead><tr style={thRowStyle}>
                        <th style={thStyle}>Date</th><th style={thStyle}>Opening</th><th style={thStyle}>Closing</th>
                        <th style={thStyle}>Expenses</th><th style={thStyle}>Net Flow</th><th style={thStyle}>Est. Sales</th>
                      </tr></thead>
                      <tbody>
                        {monthlySummary.dailyEntries.map(d => (
                          <tr key={d.dailyCashEntryId} style={trStyle}>
                            <td style={tdStyle}>{fmtDate(d.date)}</td>
                            <td style={tdStyle}>₹{d.openingCash.toFixed(2)}</td>
                            <td style={tdStyle}>{d.closingCash !== null ? `₹${d.closingCash.toFixed(2)}` : <span style={{ color: '#E65100' }}>Pending</span>}</td>
                            <td style={tdStyle}>₹{d.totalExpenses.toFixed(2)}</td>
                            <td style={{ ...tdStyle, color: d.netCashFlow >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 600 }}>
                              ₹{d.netCashFlow.toFixed(2)}
                            </td>
                            <td style={{ ...tdStyle, color: '#7B1FA2', fontWeight: 600 }}>₹{d.estimatedSales.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mobile-card-list">
                      {monthlySummary.dailyEntries.map(d => (
                        <div key={d.dailyCashEntryId} className="mobile-card-item">
                          <div className="mobile-card-header">
                            <span className="mobile-card-title">{fmtDate(d.date)}</span>
                          </div>
                          <div className="mobile-card-details">
                            <div><span className="mobile-card-detail-label">Opening: </span>₹{d.openingCash.toFixed(2)}</div>
                            <div><span className="mobile-card-detail-label">Closing: </span>{d.closingCash !== null ? `₹${d.closingCash.toFixed(2)}` : 'Pending'}</div>
                            <div><span className="mobile-card-detail-label">Expenses: </span>₹{d.totalExpenses.toFixed(2)}</div>
                            <div><span className="mobile-card-detail-label">Net Flow: </span><span style={{ color: d.netCashFlow >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 600 }}>₹{d.netCashFlow.toFixed(2)}</span></div>
                            <div><span className="mobile-card-detail-label">Est. Sales: </span><span style={{ color: '#7B1FA2', fontWeight: 600 }}>₹{d.estimatedSales.toFixed(2)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {monthlySummary.dailyEntries.length === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No cash entries recorded for this month.</div>
              )}
            </>
          )}
        </>
      )}

      {/* ===== YEARLY REPORT ===== */}
      {view === 'yearly' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Year:</label>
            <input type="number" min="2020" max="2099" value={reportYear} onChange={e => {
              const y = e.target.value;
              setReportYear(y);
              setYearlySummary(null);
              if (y.length === 4) loadYearlyReport(y);
            }} style={{ ...inputStyle, width: '90px' }} placeholder="YYYY" />
            <GreenButton onClick={async () => {
              try {
                const blob = await api.exportYearlyCashExcel(reportYear);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `cash-annual-report-${reportYear}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { setMsg('Excel export failed'); }
            }}>📥 Download Yearly Excel</GreenButton>
            {isSuperAdmin && (
              <GreenButton onClick={() => loadTenantYearlyReports(reportYear)}>🏢 Load Tenant Yearly</GreenButton>
            )}
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>⏳ Loading...</div>}

          {yearlySummary && !loading && (
            <>
              {/* Yearly KPI cards */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div style={{ ...statCard, background: 'var(--tc-light)', border: '1px solid var(--tc-border)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--tc)', marginBottom: '4px' }}>Total Cash Inflow</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--tc)' }}>₹{yearlySummary.totalCashInflow.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: '#FCE4EC', border: '1px solid #F8BBD0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#C62828', marginBottom: '4px' }}>Total Expenses</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#C62828' }}>₹{yearlySummary.totalExpenses.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: yearlySummary.estimatedProfit >= 0 ? 'var(--tc-light)' : '#FFEBEE', border: yearlySummary.estimatedProfit >= 0 ? '1px solid var(--tc-border)' : '1px solid #FFCDD2' }}>
                  <div style={{ fontSize: '0.75rem', color: yearlySummary.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', marginBottom: '4px' }}>Est. Profit</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: yearlySummary.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828' }}>₹{yearlySummary.estimatedProfit.toFixed(2)}</div>
                </div>
                <div style={{ ...statCard, background: '#FFF3E0', border: '1px solid #FFE0B2' }}>
                  <div style={{ fontSize: '0.75rem', color: '#E65100', marginBottom: '4px' }}>Days Recorded</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#E65100' }}>{yearlySummary.totalDaysRecorded}</div>
                </div>
              </div>

              {/* Monthly breakdown table */}
              <div style={cardStyle}>
                <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📅 Monthly Breakdown</h3>
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                  <table style={tableStyle}>
                    <thead><tr style={thRowStyle}>
                      <th style={thStyle}>Month</th><th style={thStyle}>Cash Inflow</th><th style={thStyle}>Expenses</th>
                      <th style={thStyle}>Est. Profit</th><th style={thStyle}>Days</th>
                    </tr></thead>
                    <tbody>
                      {yearlySummary.monthlyBreakdown.filter(m => m.daysRecorded > 0).map(m => (
                        <tr key={m.month} style={trStyle}>
                          <td style={tdStyle}><strong>{m.month}</strong></td>
                          <td style={{ ...tdStyle, color: 'var(--tc)', fontWeight: 600 }}>₹{m.totalCashInflow.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: '#C62828', fontWeight: 600 }}>₹{m.totalExpenses.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: m.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{m.estimatedProfit.toFixed(2)}</td>
                          <td style={tdStyle}>{m.daysRecorded}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mobile-card-list">
                    {yearlySummary.monthlyBreakdown.filter(m => m.daysRecorded > 0).map(m => (
                      <div key={m.month} className="mobile-card-item">
                        <div className="mobile-card-header">
                          <span className="mobile-card-title">{m.month}</span>
                        </div>
                        <div className="mobile-card-details">
                          <div><span className="mobile-card-detail-label">Cash Inflow: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>₹{m.totalCashInflow.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Expenses: </span><span style={{ color: '#C62828', fontWeight: 600 }}>₹{m.totalExpenses.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Est. Profit: </span><span style={{ color: m.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{m.estimatedProfit.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Days: </span>{m.daysRecorded}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Category breakdown */}
              {yearlySummary.categoryBreakdown.length > 0 && (
                <div style={cardStyle}>
                  <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>📊 Annual Expense by Category</h3>
                  <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                    <table style={tableStyle}>
                      <thead><tr style={thRowStyle}>
                        <th style={thStyle}>Category</th><th style={thStyle}>Annual Total</th><th style={thStyle}>Count</th><th style={thStyle}>% of Expenses</th>
                      </tr></thead>
                      <tbody>
                        {yearlySummary.categoryBreakdown.map(c => (
                          <tr key={c.category} style={trStyle}>
                            <td style={tdStyle}><strong>{c.category}</strong></td>
                            <td style={tdStyle}>₹{c.total.toFixed(2)}</td>
                            <td style={tdStyle}>{c.count}</td>
                            <td style={tdStyle}>{yearlySummary.totalExpenses > 0 ? ((c.total / yearlySummary.totalExpenses) * 100).toFixed(1) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mobile-card-list">
                      {yearlySummary.categoryBreakdown.map(c => (
                        <div key={c.category} className="mobile-card-item">
                          <div className="mobile-card-header">
                            <span className="mobile-card-title">{c.category}</span>
                            <span style={{ fontWeight: 700 }}>₹{c.total.toFixed(2)}</span>
                          </div>
                          <div className="mobile-card-details">
                            <div><span className="mobile-card-detail-label">Count: </span>{c.count}</div>
                            <div><span className="mobile-card-detail-label">% of Expenses: </span>{yearlySummary.totalExpenses > 0 ? ((c.total / yearlySummary.totalExpenses) * 100).toFixed(1) : 0}%</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {yearlySummary.totalDaysRecorded === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No cash entries recorded for {reportYear}.</div>
              )}
            </>
          )}

          {/* SuperAdmin: tenant yearly reports */}
          {isSuperAdmin && tenantYearlyReports.length > 0 && !loading && (
            <div style={cardStyle}>
              <h3 style={{ color: 'var(--tc)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>🏢 All Tenants — Yearly Summary</h3>
              <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                <table style={tableStyle}>
                  <thead><tr style={thRowStyle}>
                    <th style={thStyle}>Tenant</th><th style={thStyle}>Days</th><th style={thStyle}>Cash Inflow</th>
                    <th style={thStyle}>Expenses</th><th style={thStyle}>Est. Profit</th><th style={thStyle}>Top Expense</th>
                  </tr></thead>
                  <tbody>
                    {tenantYearlyReports.map(t => (
                      <tr key={t.tenantId} style={trStyle}>
                        <td style={tdStyle}><strong>{t.tenantName}</strong></td>
                        <td style={tdStyle}>{t.totalDaysRecorded}</td>
                        <td style={{ ...tdStyle, color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</td>
                        <td style={{ ...tdStyle, color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</td>
                        <td style={{ ...tdStyle, color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{t.estimatedProfit.toFixed(2)}</td>
                        <td style={tdStyle}>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mobile-card-list">
                  {tenantYearlyReports.map(t => (
                    <div key={t.tenantId} className="mobile-card-item">
                      <div className="mobile-card-header">
                        <span className="mobile-card-title">{t.tenantName}</span>
                      </div>
                      <div className="mobile-card-details">
                        <div><span className="mobile-card-detail-label">Days: </span>{t.totalDaysRecorded}</div>
                        <div><span className="mobile-card-detail-label">Cash Inflow: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</span></div>
                        <div><span className="mobile-card-detail-label">Expenses: </span><span style={{ color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</span></div>
                        <div><span className="mobile-card-detail-label">Est. Profit: </span><span style={{ color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{t.estimatedProfit.toFixed(2)}</span></div>
                        <div><span className="mobile-card-detail-label">Top Expense: </span>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== TENANT REPORTS (SuperAdmin only) ===== */}
      {view === 'tenants' && isSuperAdmin && (
        <>
          {/* Monthly / Yearly sub-toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button onClick={() => { setTenantReportView('monthly'); loadTenantReports(); }}
              style={{ ...smallBtn, background: tenantReportView === 'monthly' ? 'var(--tc-btn)' : '#F5F5F5', color: tenantReportView === 'monthly' ? 'white' : '#555', border: 'none' }}>
              📅 Monthly
            </button>
            <button onClick={() => { setTenantReportView('yearly'); loadTenantYearlyReports(reportYear); }}
              style={{ ...smallBtn, background: tenantReportView === 'yearly' ? 'var(--tc-btn)' : '#F5F5F5', color: tenantReportView === 'yearly' ? 'white' : '#555', border: 'none' }}>
              📊 Yearly
            </button>
          </div>

          {/* Monthly tenant reports */}
          {tenantReportView === 'monthly' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Month:</label>
                <input type="month" value={reportMonth} onChange={e => {
                  const m = e.target.value;
                  setReportMonth(m);
                  loadTenantReports(m);
                }} style={inputStyle} />
              </div>
              {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>⏳ Loading...</div>}
              {!loading && tenantReports.length > 0 && (
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                  <table style={tableStyle}>
                    <thead><tr style={thRowStyle}>
                      <th style={thStyle}>Tenant</th><th style={thStyle}>Days</th><th style={thStyle}>Cash Inflow</th>
                      <th style={thStyle}>Expenses</th><th style={thStyle}>Est. Profit</th><th style={thStyle}>Top Expense</th>
                    </tr></thead>
                    <tbody>
                      {tenantReports.map(t => (
                        <tr key={t.tenantId} style={trStyle}>
                          <td style={tdStyle}><strong>{t.tenantName}</strong></td>
                          <td style={tdStyle}>{t.daysRecorded}</td>
                          <td style={{ ...tdStyle, color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>
                            ₹{t.estimatedProfit.toFixed(2)}
                          </td>
                          <td style={tdStyle}>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mobile-card-list">
                    {tenantReports.map(t => (
                      <div key={t.tenantId} className="mobile-card-item">
                        <div className="mobile-card-header">
                          <span className="mobile-card-title">{t.tenantName}</span>
                        </div>
                        <div className="mobile-card-details">
                          <div><span className="mobile-card-detail-label">Days: </span>{t.daysRecorded}</div>
                          <div><span className="mobile-card-detail-label">Cash Inflow: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Expenses: </span><span style={{ color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Est. Profit: </span><span style={{ color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{t.estimatedProfit.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Top Expense: </span>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!loading && tenantReports.length === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No cash data recorded by any tenant for this month.</div>
              )}
            </>
          )}

          {/* Yearly tenant reports */}
          {tenantReportView === 'yearly' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Year:</label>
                <input type="number" min="2020" max="2099" value={reportYear} onChange={e => {
                  const y = e.target.value;
                  setReportYear(y);
                  setTenantYearlyReports([]);
                  if (y.length === 4) loadTenantYearlyReports(y);
                }} style={{ ...inputStyle, width: '90px' }} placeholder="YYYY" />
              </div>
              {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>⏳ Loading...</div>}
              {!loading && tenantYearlyReports.length > 0 && (
                <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
                  <table style={tableStyle}>
                    <thead><tr style={thRowStyle}>
                      <th style={thStyle}>Tenant</th><th style={thStyle}>Days</th><th style={thStyle}>Cash Inflow</th>
                      <th style={thStyle}>Expenses</th><th style={thStyle}>Est. Profit</th><th style={thStyle}>Top Expense</th>
                    </tr></thead>
                    <tbody>
                      {tenantYearlyReports.map(t => (
                        <tr key={t.tenantId} style={trStyle}>
                          <td style={tdStyle}><strong>{t.tenantName}</strong></td>
                          <td style={tdStyle}>{t.totalDaysRecorded}</td>
                          <td style={{ ...tdStyle, color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</td>
                          <td style={{ ...tdStyle, color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{t.estimatedProfit.toFixed(2)}</td>
                          <td style={tdStyle}>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mobile-card-list">
                    {tenantYearlyReports.map(t => (
                      <div key={t.tenantId} className="mobile-card-item">
                        <div className="mobile-card-header">
                          <span className="mobile-card-title">{t.tenantName}</span>
                        </div>
                        <div className="mobile-card-details">
                          <div><span className="mobile-card-detail-label">Days: </span>{t.totalDaysRecorded}</div>
                          <div><span className="mobile-card-detail-label">Cash Inflow: </span><span style={{ color: 'var(--tc)', fontWeight: 600 }}>₹{t.totalCashInflow.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Expenses: </span><span style={{ color: '#C62828', fontWeight: 600 }}>₹{t.totalExpenses.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Est. Profit: </span><span style={{ color: t.estimatedProfit >= 0 ? 'var(--tc)' : '#C62828', fontWeight: 700 }}>₹{t.estimatedProfit.toFixed(2)}</span></div>
                          <div><span className="mobile-card-detail-label">Top Expense: </span>{t.categoryBreakdown.length > 0 ? `${t.categoryBreakdown[0].category} (₹${t.categoryBreakdown[0].total.toFixed(0)})` : '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!loading && tenantYearlyReports.length === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No cash data recorded by any tenant for {reportYear}.</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ===== Reusable UI Components =====

function ConfirmModal({ title, message, onConfirm, onCancel, danger }: { title: string; message: string; onConfirm: () => void; onCancel: () => void; danger?: boolean }) {
  return (
    <div className="confirm-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onCancel}>
      <div className="confirm-modal-content" style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '420px', width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.75rem', color: 'var(--tc)', fontSize: '1.05rem' }}>{title}</h3>
        <p style={{ margin: '0 0 1.25rem', color: '#555', fontSize: '0.9rem', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '0.5rem 1rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, color: '#374151' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '0.5rem 1rem', background: danger ? '#dc2626' : 'var(--tc)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>{danger ? 'Delete' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

function PromptModal({ title, message, placeholder, defaultValue, onConfirm, onCancel, type = 'text' }: { title: string; message: string; placeholder?: string; defaultValue?: string; onConfirm: (value: string) => void; onCancel: () => void; type?: string }) {
  const [value, setValue] = useState(defaultValue || '');
  return (
    <div className="confirm-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onCancel}>
      <div className="confirm-modal-content" style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '420px', width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.5rem', color: 'var(--tc)', fontSize: '1.05rem' }}>{title}</h3>
        {message && <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.9rem', lineHeight: 1.5 }}>{message}</p>}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onConfirm(value); if (e.key === 'Escape') onCancel(); }}
          autoFocus
          style={{ width: '100%', padding: '0.6rem 0.8rem', border: '2px solid #E0E0E0', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', marginBottom: '1rem', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '0.5rem 1rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, color: '#374151' }}>Cancel</button>
          <button onClick={() => onConfirm(value)} style={{ padding: '0.5rem 1rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function PanelHeader({ title, count, children, icon, subtitle }: { title: string; count?: number; children?: React.ReactNode; icon?: string; subtitle?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
      <div>
        <h2 style={{ color: 'var(--tc)', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>
          {icon && <span style={{ marginRight: '0.4rem' }}>{icon}</span>}
          {title} {count !== undefined && <span style={{ color: '#888', fontWeight: 400, fontSize: '1rem' }}>({count})</span>}
        </h2>
        {subtitle && <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.2rem' }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function GreenButton({ children, onClick, type }: { children: React.ReactNode; onClick?: () => void; type?: 'submit' | 'button' }) {
  return (
    <button type={type || 'button'} onClick={onClick} style={btnStyle}
      onMouseEnter={e => (e.target as HTMLElement).style.background = 'var(--tc-hover)'}
      onMouseLeave={e => (e.target as HTMLElement).style.background = 'var(--tc-btn)'}
    >
      {children}
    </button>
  );
}

function ActionBtn({ children, onClick, color }: { children: React.ReactNode; onClick: () => void; color: string }) {
  return (
    <button onClick={onClick} style={{ ...smallBtn, background: color, color: 'white', border: 'none' }}>
      {children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    active: { bg: 'var(--tc-light)', color: 'var(--tc)', label: 'Active' },
    inactive: { bg: '#FFEBEE', color: '#C62828', label: 'Inactive' },
    paused: { bg: '#FFFDE7', color: '#F9A825', label: 'Paused' },
    pending: { bg: '#FFF3E0', color: '#E65100', label: 'Pending' },
    delivered: { bg: 'var(--tc-light)', color: 'var(--tc)', label: 'Delivered' },
    failed: { bg: '#FFEBEE', color: '#C62828', label: 'Failed' },
    skipped: { bg: '#FFF3E0', color: '#E65100', label: 'Skipped' },
    paid: { bg: 'var(--tc-light)', color: 'var(--tc)', label: 'Paid' },
    unpaid: { bg: '#FFEBEE', color: '#C62828', label: 'Unpaid' },
    revoked: { bg: '#F3E5F5', color: '#6A1B9A', label: 'Revoked' },
    suspended: { bg: '#F3E5F5', color: '#6A1B9A', label: 'Suspended' },
    open: { bg: '#FFF3E0', color: '#E65100', label: 'Open' },
    resolved: { bg: 'var(--tc-light)', color: 'var(--tc)', label: 'Resolved' },
    partial: { bg: '#FFF8E1', color: '#F57F17', label: 'Partial' },
    nobill: { bg: '#F5F5F5', color: '#9E9E9E', label: 'No Bill' },
    cash: { bg: '#FFF3E0', color: '#E65100', label: 'Cash' },
    upi: { bg: '#E3F2FD', color: '#1565C0', label: 'UPI' },
  };
  const s = map[status] || { bg: '#F5F5F5', color: '#666', label: status };
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '12px',
      fontSize: '0.8rem', fontWeight: 600, display: 'inline-block',
    }}>
      {s.label}
    </span>
  );
}

function MsgBanner({ children }: { children: React.ReactNode }) {
  const text = typeof children === 'string' ? children : '';
  const isError = text.toLowerCase().includes('fail') || text.toLowerCase().includes('error') || text.toLowerCase().includes('could not') || text.toLowerCase().includes('must:');
  const style: React.CSSProperties = isError
    ? { ...msgStyle, background: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }
    : msgStyle;
  return <div style={style}>{children}</div>;
}

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
      <div style={{ color: 'var(--tc-btn)', fontSize: '1.1rem', fontWeight: 500 }}>Loading...</div>
    </div>
  );
}

function EmptyRow({ colSpan, text }: { colSpan: number; text?: string }) {
  return (
    <tr><td colSpan={colSpan} style={{ ...tdStyle, textAlign: 'center', color: '#888', padding: '2rem' }}>
      {text || 'No data found.'}
    </td></tr>
  );
}

function TableWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
      {children}
    </div>
  );
}

// ===== Common Grid Controls (search, status filter, date range, sort, pagination, saved prefs) =====
// Shared across grids app-wide so every table gets the same filtering/sorting/pagination behavior.
function useGridControls(storageKey: string, defaultSortKey: string) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved) {
        if (saved.statusFilter) setStatusFilter(saved.statusFilter);
        if (saved.sortKey) setSortKey(saved.sortKey);
        if (saved.sortDir) setSortDir(saved.sortDir);
        if (saved.pageSize) setPageSize(saved.pageSize);
      }
    } catch { /* ignore malformed prefs */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ statusFilter, sortKey, sortDir, pageSize }));
  }, [storageKey, statusFilter, sortKey, sortDir, pageSize]);

  useEffect(() => { setPage(1); }, [search, statusFilter, dateFrom, dateTo]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  return { search, setSearch, statusFilter, setStatusFilter, dateFrom, setDateFrom, dateTo, setDateTo, sortKey, sortDir, toggleSort, page, setPage, pageSize, setPageSize };
}

function paginate<T>(items: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paged = items.slice((page - 1) * pageSize, page * pageSize);
  return { paged, totalPages };
}

function SortableTh({ label, sortKeyName, activeKey, dir, onSort }: { label: string; sortKeyName: string; activeKey: string; dir: 'asc' | 'desc'; onSort: (key: string) => void }) {
  return (
    <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => onSort(sortKeyName)}>
      {label} {activeKey === sortKeyName ? (dir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );
}

function GridPagination({ page, setPage, pageSize, setPageSize, totalPages }: { page: number; setPage: (fn: (p: number) => number) => void; pageSize: number; setPageSize: (n: number) => void; totalPages: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.85rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
        Rows per page:
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ ...inputStyle, padding: '0.3rem 0.5rem' }}>
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ ...smallBtn, opacity: page <= 1 ? 0.5 : 1 }}>◀ Prev</button>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>Page {page} of {totalPages}</span>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ ...smallBtn, opacity: page >= totalPages ? 0.5 : 1 }}>Next ▶</button>
      </div>
    </div>
  );
}

function DateInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const longLabel = props.value ? fmtLongDate(props.value as string) : 'Select a date';
  return <input {...props} type="date" title={props.title || longLabel} />;
}

function StatCard({ label, value, color = 'var(--tc)', icon }: { label: string; value: string | number; color?: string; icon?: string }) {
  return (
    <div style={{
      background: 'white', padding: '1.25rem 1.5rem', borderRadius: '12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.4rem' }}>{icon && <span style={{ marginRight: '0.3rem' }}>{icon}</span>}{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

// ===== Forced Password Change Modal (shown when SuperAdmin flags an account for reset) =====
function ForcePasswordChangeModal({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword.length < 6) { setMsg('New password must be at least 6 characters'); return; }
    if (form.newPassword !== form.confirmPassword) { setMsg('New password and confirm password do not match'); return; }

    setSaving(true);
    const res = await api.changeOwnPassword(form.currentPassword, form.newPassword);
    setSaving(false);
    setMsg(res.message);
    if (res.success) onDone();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }}>
      <form onSubmit={handleSubmit} style={{
        background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', gap: '0.85rem',
      }}>
        <h2 style={{ margin: 0, color: 'var(--tc)', fontSize: '1.15rem' }}>🔑 Password Change Required</h2>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          Your administrator requires you to set a new password before continuing.
        </p>
        {msg && <div style={msgStyle}>{msg}</div>}
        <input type="password" placeholder="Current Password" required minLength={6} value={form.currentPassword}
          onChange={e => setForm({ ...form, currentPassword: e.target.value })} style={inputStyle} />
        <input type="password" placeholder="New Password" required minLength={6} value={form.newPassword}
          onChange={e => setForm({ ...form, newPassword: e.target.value })} style={inputStyle} />
        <input type="password" placeholder="Confirm New Password" required minLength={6} value={form.confirmPassword}
          onChange={e => setForm({ ...form, confirmPassword: e.target.value })} style={inputStyle} />
        <button type="submit" disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Set New Password'}</button>
      </form>
    </div>
  );
}

// ===== Styles =====
const btnStyle: React.CSSProperties = {
  padding: '0.55rem 1.25rem', background: 'var(--tc-btn)', color: 'white', border: 'none',
  borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
  boxShadow: '0 1px 4px rgba(0,0,0,0.15)', transition: 'all 0.2s',
};
const smallBtn: React.CSSProperties = {
  padding: '0.35rem 0.85rem', background: '#F5F5F5', border: '1px solid #E0E0E0',
  borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.2s',
};
const inputStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem', border: '2px solid #E0E0E0', borderRadius: '8px',
  fontSize: '0.9rem', outline: 'none', transition: 'border-color 0.2s',
};
const formStyle: React.CSSProperties = {
  display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem',
  flexWrap: 'wrap', background: 'white', padding: '1.25rem',
  borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid var(--tc-light)',
};
const msgStyle: React.CSSProperties = {
  background: 'var(--tc-light)', color: 'var(--tc)', padding: '0.65rem 1rem',
  borderRadius: '8px', marginBottom: '1rem', fontWeight: 500, border: '1px solid var(--tc-border)',
};
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px',
  overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', minWidth: '600px',
};
const thRowStyle: React.CSSProperties = { background: 'var(--tc-light)' };
const thStyle: React.CSSProperties = {
  padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 700,
  borderBottom: '2px solid var(--tc-border)', color: 'var(--tc)', fontSize: '0.88rem',
};
const tdStyle: React.CSSProperties = { padding: '0.75rem 1rem', borderBottom: '1px solid var(--tc-vlight)' };
const trStyle: React.CSSProperties = {};

// ===== Utils =====
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ===== Live Sessions Panel (SuperAdmin: all tenants; Tenant Admin: own tenant) =====
function LiveSessionsPanel({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const [sessions, setSessions] = useState<UserSessionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(true);
  const [confirmLogoff, setConfirmLogoff] = useState<{ sessionId: number; userName: string } | null>(null);
  const [togglingUser, setTogglingUser] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = isSuperAdmin ? await api.getAllSessions() : await api.getTenantSessions();
      if (res.data) setSessions(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleForceLogoff = async () => {
    if (!confirmLogoff) return;
    try {
      await api.forceLogoff(confirmLogoff.sessionId);
      setMsg(`✅ ${confirmLogoff.userName} has been logged off.`);
      setConfirmLogoff(null);
      load();
    } catch {
      setMsg('❌ Force logoff failed.');
    }
  };

  const handleToggleActive = async (session: UserSessionDto) => {
    setTogglingUser(session.userId);
    setMsg('');
    try {
      // Find current active status from the user's most recent session data
      // We toggle based on whether the user appears active in any session
      const userSessions = sessions.filter(s => s.userId === session.userId);
      const isCurrentlyActive = userSessions.some(s => s.isActive);
      if (isCurrentlyActive) {
        await api.deactivateUser(session.userId);
        setMsg(`✅ ${session.userName} has been deactivated.`);
      } else {
        await api.activateUser(session.userId);
        setMsg(`✅ ${session.userName} has been activated.`);
      }
      load();
    } catch {
      setMsg('❌ Toggle failed.');
    } finally {
      setTogglingUser(null);
    }
  };

  const displayed = sessions.filter(s => {
    // Tenant Admin sees only Subscriber sessions
    if (!isSuperAdmin && s.role !== 'Subscriber') return false;
    const matchSearch = s.userName.toLowerCase().includes(search.toLowerCase()) ||
      s.tenantName.toLowerCase().includes(search.toLowerCase()) ||
      s.role.toLowerCase().includes(search.toLowerCase());
    const matchActive = filterActive ? s.isActive : true;
    return matchSearch && matchActive;
  });

  const activeSessions = sessions.filter(s => s.isActive);
  const uniqueTenants = new Set(activeSessions.map(s => s.tenantId)).size;
  const uniqueUsers = new Set(activeSessions.map(s => s.userId)).size;

  const fmtDT = (dt: string) => new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const cardStyle: React.CSSProperties = {
    background: 'white', borderRadius: '10px', padding: '1rem 1.5rem',
    textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    flex: '1 1 140px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.45rem 0.8rem', border: '1px solid var(--tc-border)',
    borderRadius: '6px', fontSize: '0.9rem', outline: 'none',
  };

  const thStyle: React.CSSProperties = {
    padding: '0.65rem 0.85rem', background: 'var(--tc)', color: 'white',
    fontWeight: 600, fontSize: '0.82rem', textAlign: 'left', whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '0.6rem 0.85rem', fontSize: '0.85rem', borderBottom: '1px solid #F0F0F0',
    verticalAlign: 'middle',
  };

  const roleBadge = (role: string) => {
    const colors: Record<string, string> = {
      SuperAdmin: '#7B1FA2', Admin: '#1565C0', Subscriber: '#2E7D32',
    };
    return (
      <span style={{
        background: colors[role] ?? '#757575', color: 'white',
        padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
      }}>{role}</span>
    );
  };

  return (
    <div style={{ fontFamily: 'sans-serif' }}>
      <h2 style={{ margin: '0 0 1.25rem', color: 'var(--tc)', fontSize: '1.3rem' }}>
        🟢 Live Sessions Monitor{!isSuperAdmin && ' — My Tenant'}
      </h2>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--tc)' }}>{activeSessions.length}</div>
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Active Sessions</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1565C0' }}>{uniqueUsers}</div>
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Unique Users Online</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#2E7D32' }}>{uniqueTenants}</div>
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Tenants Online</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#E65100' }}>{sessions.length}</div>
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Total Sessions (All Time)</div>
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem',
          background: msg.startsWith('✅') ? '#E8F5E9' : '#FFEBEE',
          color: msg.startsWith('✅') ? '#2E7D32' : '#C62828', fontSize: '0.88rem',
        }}>{msg}</div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          style={{ ...inputStyle, minWidth: '220px' }}
          placeholder="🔍 Search by name, tenant or role..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.88rem' }}>
          <input
            type="checkbox"
            checked={filterActive}
            onChange={e => setFilterActive(e.target.checked)}
          />
          Show active sessions only
        </label>
        <button
          onClick={load}
          style={{
            padding: '0.45rem 1rem', background: 'var(--tc)', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
          }}
        >
          {loading ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
        <span style={{ fontSize: '0.78rem', color: '#888' }}>Auto-refreshes every 30s</span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>User Name</th>
              <th style={thStyle}>Tenant</th>
              <th style={thStyle}>User Type</th>
              <th style={thStyle}>Login Time</th>
              <th style={thStyle}>IP Address</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#999', padding: '2rem' }}>
                  {loading ? '⏳ Loading sessions...' : '📭 No sessions found.'}
                </td>
              </tr>
            )}
            {displayed.map((s, idx) => (
              <tr key={s.sessionId} style={{ background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                <td style={tdStyle}>{idx + 1}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{s.userName}</td>
                <td style={tdStyle}>{s.tenantName}</td>
                <td style={tdStyle}>{roleBadge(s.role)}</td>
                <td style={tdStyle}>{fmtDT(s.loginTime)}</td>
                <td style={{ ...tdStyle, color: '#666', fontSize: '0.8rem' }}>{s.ipAddress ?? '—'}</td>
                <td style={tdStyle}>
                  {s.isActive
                    ? <span style={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.82rem' }}>🟢 Online</span>
                    : <span style={{ color: '#999', fontSize: '0.82rem' }}>⚫ Logged Out</span>
                  }
                </td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {s.isActive && (
                      <button
                        onClick={() => setConfirmLogoff({ sessionId: s.sessionId, userName: s.userName })}
                        style={{
                          padding: '3px 10px', background: '#C62828', color: 'white',
                          border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                        }}
                        title="Force log off this session"
                      >🔴 Force Logoff</button>
                    )}
                    {isSuperAdmin && (
                      <button
                        onClick={() => handleToggleActive(s)}
                        disabled={togglingUser === s.userId}
                        style={{
                          padding: '3px 10px',
                          background: sessions.filter(x => x.userId === s.userId).some(x => x.isActive) ? '#E65100' : '#2E7D32',
                          color: 'white', border: 'none', borderRadius: '5px',
                          cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                          opacity: togglingUser === s.userId ? 0.6 : 1,
                        }}
                        title="Toggle user account active/inactive"
                      >
                        {sessions.filter(x => x.userId === s.userId).some(x => x.isActive)
                          ? '🚫 Deactivate User'
                          : '✅ Activate User'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Force Logoff Confirmation Modal */}
      {confirmLogoff && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '2rem',
            maxWidth: '380px', width: '90%', textAlign: 'center',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '2.5rem' }}>🔴</div>
            <h3 style={{ margin: '0.75rem 0 0.5rem', color: '#C62828' }}>Force Logoff</h3>
            <p style={{ color: '#444', fontSize: '0.92rem', margin: '0 0 1.5rem' }}>
              Are you sure you want to force log off <strong>{confirmLogoff.userName}</strong>?
              Their session will be terminated immediately.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmLogoff(null)}
                style={{
                  padding: '0.5rem 1.5rem', background: '#F5F5F5', color: '#333',
                  border: '1px solid #DDD', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                }}
              >Cancel</button>
              <button
                onClick={handleForceLogoff}
                style={{
                  padding: '0.5rem 1.5rem', background: '#C62828', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                }}
              >Yes, Force Logoff</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentsPanel() {
  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getDocuments();
      if (res.success) setDocs(res.data ?? []);
    } catch { setMsg('❌ Failed to load documents'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setMsg('Please select a file'); return; }
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['txt', 'pdf', 'docx'].includes(ext || '')) { setMsg('Allowed: .txt, .pdf, .docx'); return; }
    if (file.size > 10 * 1024 * 1024) { setMsg('File too large (max 10 MB)'); return; }
    setUploading(true); setMsg('');
    try {
      const res = await api.uploadDocument(file);
      setMsg(res.success ? '✅ Uploaded!' : res.message);
      if (res.success) { load(); if (fileRef.current) fileRef.current.value = ''; }
    } catch { setMsg('❌ Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      const res = await api.deleteDocument(name);
      setMsg(res.success ? '✅ Deleted' : res.message);
      load();
    } catch { setMsg('❌ Delete failed'); }
  };

  const handleDownload = async (name: string) => {
    try { await api.downloadDocument(name); }
    catch { setMsg('❌ Download failed'); }
  };

  const thStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: '#555', borderBottom: '2px solid var(--tc-border)' };
  const tdStyle: React.CSSProperties = { padding: '0.55rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid #eee' };

  return (
    <div>
      <PanelHeader title="Document Management" count={docs.length} icon="📄" subtitle="Upload knowledge base documents for AI Chat" />
      {msg && <MsgBanner>{msg}</MsgBanner>}

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input ref={fileRef} type="file" accept=".txt,.pdf,.docx"
          style={{ fontSize: '0.85rem', padding: '0.4rem', border: '1px solid var(--tc-border)', borderRadius: '6px' }} />
        <GreenButton onClick={handleUpload}>{uploading ? '⏳ Uploading...' : '📤 Upload'}</GreenButton>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>Allowed: .txt, .pdf, .docx (max 10 MB)</span>
      </div>

      {loading ? <LoadingSpinner /> : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No documents uploaded yet</div>
      ) : (
        <TableWrap>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--tc-vlight)' }}>
              <th style={thStyle}>File Name</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Size</th>
              <th style={thStyle}>Upload Date</th>
              <th style={thStyle}>Actions</th>
            </tr></thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.name} style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--tc-vlight)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td style={tdStyle}><strong>{d.name}</strong></td>
                  <td style={tdStyle}><span style={{ background: '#E3F2FD', color: '#1565C0', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>{d.type}</span></td>
                  <td style={tdStyle}>{(d.size / 1024).toFixed(1)} KB</td>
                  <td style={tdStyle}>{d.uploadDate}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <ActionBtn onClick={() => handleDownload(d.name)} color="#1976D2">📥 Download</ActionBtn>
                      <ActionBtn onClick={() => handleDelete(d.name)} color="#C62828">🗑️ Delete</ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function RecordStorePanel({ isSuperAdmin, tenantId }: { isSuperAdmin: boolean; tenantId?: number }) {
  const [files, setFiles] = useState<RecordFileDto[]>([]);
  const [tenants, setTenants] = useState<RecordStoreTenantDto[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(isSuperAdmin ? undefined : tenantId);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const allowedExts = '.txt,.pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png';

  const loadTenants = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.getRecordStoreTenants();
      if (res.success) setTenants(res.data ?? []);
    } catch { /* ignore */ }
  }, [isSuperAdmin]);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getRecordFiles(isSuperAdmin ? selectedTenantId : tenantId);
      if (res.success) setFiles(res.data ?? []);
      else setMsg('❌ ' + res.message);
    } catch { setMsg('❌ Failed to load files'); }
    setLoading(false);
  }, [isSuperAdmin, selectedTenantId, tenantId]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setMsg('Please select a file'); return; }
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (!allowedExts.split(',').includes(ext)) { setMsg(`Allowed: ${allowedExts}`); return; }
    if (file.size > 20 * 1024 * 1024) { setMsg('File too large (max 20 MB)'); return; }
    const tid = isSuperAdmin ? selectedTenantId : tenantId;
    if (isSuperAdmin && !tid) { setMsg('Please select a tenant first'); return; }
    setUploading(true); setMsg('');
    try {
      const res = await api.uploadRecordFile(file, tid);
      setMsg(res.success ? '✅ Uploaded!' : '❌ ' + res.message);
      if (res.success) { loadFiles(); if (fileRef.current) fileRef.current.value = ''; }
    } catch { setMsg('❌ Upload failed'); }
    setUploading(false);
  };

  const handleDownload = async (name: string, fileTenantId: number) => {
    try { await api.downloadRecordFile(name, fileTenantId); }
    catch { setMsg('❌ Download failed'); }
  };

  const handleDelete = async (name: string, fileTenantId: number) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      const res = await api.deleteRecordFile(name, fileTenantId);
      setMsg(res.success ? '✅ Deleted' : '❌ ' + res.message);
      loadFiles();
    } catch { setMsg('❌ Delete failed'); }
  };

  const thStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: '#555', borderBottom: '2px solid var(--tc-border)' };
  const tdStyle: React.CSSProperties = { padding: '0.55rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid #eee' };

  const displayTenantId = isSuperAdmin ? selectedTenantId : tenantId;

  return (
    <div>
      <PanelHeader title="Record Store" count={files.length} icon="🗂️" subtitle="Upload and manage tenant documents" />
      {msg && <MsgBanner>{msg}</MsgBanner>}

      {isSuperAdmin && (
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Tenant:</label>
          <select
            value={selectedTenantId ?? ''}
            onChange={e => { setSelectedTenantId(e.target.value ? Number(e.target.value) : undefined); setFiles([]); }}
            style={{ fontSize: '0.85rem', padding: '0.35rem 0.6rem', border: '1px solid var(--tc-border)', borderRadius: '6px' }}>
            <option value="">— All Tenants —</option>
            {tenants.map(t => (
              <option key={t.tenantId} value={t.tenantId}>{t.tenantName} ({t.fileCount} files)</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input ref={fileRef} type="file" accept={allowedExts}
          style={{ fontSize: '0.85rem', padding: '0.4rem', border: '1px solid var(--tc-border)', borderRadius: '6px' }} />
        <GreenButton onClick={handleUpload}>{uploading ? '⏳ Uploading...' : '📤 Upload'}</GreenButton>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>Allowed: txt, pdf, docx, xlsx, csv, jpg, png (max 20 MB)</span>
      </div>

      {loading ? <LoadingSpinner /> : files.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          {isSuperAdmin && !selectedTenantId ? 'Select a tenant to view files, or select "All Tenants" to see everything' : 'No files uploaded yet'}
        </div>
      ) : (
        <TableWrap>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--tc-vlight)' }}>
              {isSuperAdmin && !displayTenantId && <th style={thStyle}>Tenant</th>}
              <th style={thStyle}>File Name</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Size</th>
              <th style={thStyle}>Upload Date</th>
              <th style={thStyle}>Actions</th>
            </tr></thead>
            <tbody>
              {files.map((f, i) => {
                const tDomain = tenants.find(t => t.tenantId === f.tenantId);
                return (
                  <tr key={`${f.folder}-${f.name}-${i}`} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--tc-vlight)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    {isSuperAdmin && !displayTenantId && (
                      <td style={tdStyle}><span style={{ fontSize: '0.78rem', color: '#555' }}>{tDomain?.tenantName ?? `Tenant ${f.tenantId}`}</span></td>
                    )}
                    <td style={tdStyle}><strong>{f.name}</strong></td>
                    <td style={tdStyle}><span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>{f.type}</span></td>
                    <td style={tdStyle}>{(f.size / 1024).toFixed(1)} KB</td>
                    <td style={tdStyle}>{f.uploadDate}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <ActionBtn onClick={() => handleDownload(f.name, f.tenantId)} color="#1976D2">📥 Download</ActionBtn>
                        <ActionBtn onClick={() => handleDelete(f.name, f.tenantId)} color="#C62828">🗑️ Delete</ActionBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function ChatPanel() {
  const [messages, setMessages] = useState<{ role: 'user' | 'bot'; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.askChat(q);
      setMessages(prev => [...prev, { role: 'bot', text: res.success ? (res.data?.answer ?? 'No response') : res.message }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: '❌ Failed to get response. Please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div>
      <PanelHeader title="AI Chat Assistant" icon="🤖" subtitle="Ask nutrition questions — powered by uploaded documents + Phi model" />

      <div style={{
        border: '1px solid var(--tc-border)', borderRadius: '10px', background: 'white',
        display: 'flex', flexDirection: 'column', height: '500px',
      }}>
        {/* Messages area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#aaa', marginTop: '4rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🤖</div>
              <p style={{ fontSize: '0.9rem' }}>Ask me anything about dairy nutrition!</p>
              <p style={{ fontSize: '0.78rem', color: '#bbb' }}>Examples: "Protein in cow milk?", "Is paneer good for gym?"</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '0.75rem',
            }}>
              <div style={{
                maxWidth: '75%', padding: '0.65rem 1rem', borderRadius: '12px',
                fontSize: '0.85rem', lineHeight: 1.5, wordBreak: 'break-word',
                background: m.role === 'user' ? 'var(--tc)' : '#F5F5F5',
                color: m.role === 'user' ? 'white' : '#333',
                whiteSpace: 'pre-wrap',
              }}>
                {m.role === 'bot' && <span style={{ fontSize: '0.7rem', color: '#888', display: 'block', marginBottom: '0.2rem' }}>🤖 AI Assistant</span>}
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.75rem' }}>
              <div style={{ padding: '0.65rem 1rem', borderRadius: '12px', background: '#F5F5F5', fontSize: '0.85rem', color: '#888' }}>
                ⏳ Thinking...
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div style={{
          display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem',
          borderTop: '1px solid var(--tc-border)', background: '#FAFAFA',
          borderRadius: '0 0 10px 10px',
        }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Type your question..."
            disabled={loading}
            style={{
              flex: 1, padding: '0.55rem 0.8rem', border: '1px solid var(--tc-border)',
              borderRadius: '8px', fontSize: '0.85rem', outline: 'none',
            }}
          />
          <button onClick={handleSend} disabled={loading || !input.trim()}
            style={{
              padding: '0.55rem 1.2rem', background: 'var(--tc)', color: 'white',
              border: 'none', borderRadius: '8px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading || !input.trim() ? 0.5 : 1, fontSize: '0.85rem',
            }}>
            {loading ? '⏳' : '📤 Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Purchase/Sale Panel =====
function PurchaseSalePanel({ isSuperAdmin, tenantId: _tenantId }: { isSuperAdmin: boolean; tenantId: number }) {
  type PSTab = 'purchases' | 'sales' | 'reports';
  const [activeTab, setActiveTab] = useState<PSTab>('purchases');
  const [purchases, setPurchases] = useState<PurchaseEntryDto[]>([]);
  const [sales, setSales] = useState<SaleEntryDto[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editPurchase, setEditPurchase] = useState<PurchaseEntryDto | null>(null);
  const [editSale, setEditSale] = useState<SaleEntryDto | null>(null);
  const [pFilter, setPFilter] = useState<PurchaseSaleFilter>({});
  const [sFilter, setSFilter] = useState<PurchaseSaleFilter>({});
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reportYear, setReportYear] = useState(new Date().toISOString().slice(0, 4));
  const [reportType, setReportType] = useState<'monthly' | 'yearly'>('monthly');
  const [purchaseMonthReport, setPurchaseMonthReport] = useState<PurchaseMonthlyReport | null>(null);
  const [saleMonthReport, setSaleMonthReport] = useState<SaleMonthlyReport | null>(null);
  const reportRequestSeqRef = useRef(0);
  const [remarksPopup, setRemarksPopup] = useState<{ text: string; title: string } | null>(null);

  const [pForm, setPForm] = useState<CreatePurchaseEntryRequest>({
    entryDate: new Date().toISOString().split('T')[0],
    vendorName: '', quantity: 0, rate: 0, paymentStatus: 'Pending'
  });
  const [sForm, setSForm] = useState<CreateSaleEntryRequest>({
    entryDate: new Date().toISOString().split('T')[0],
    customerName: '', quantity: 0, rate: 0, paymentStatus: 'Pending'
  });

  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);

  // Lookup effective purchase/sale rate from ProductTypeRates by product + date
  const lookupPurchaseRate = async (productName: string, date: string): Promise<number> => {
    const pt = productTypes.find(p => p.name === productName);
    if (!pt) return 0;
    try {
      const res = await api.getEffectiveRate(pt.productTypeId, date);
      return res.data?.purchaseRate ?? pt.currentPurchaseRate ?? 0;
    } catch { return pt.currentPurchaseRate ?? 0; }
  };
  const lookupSaleRate = async (productName: string, date: string): Promise<number> => {
    const pt = productTypes.find(p => p.name === productName);
    if (!pt) return 0;
    try {
      const res = await api.getEffectiveRate(pt.productTypeId, date);
      return res.data?.saleRate ?? pt.currentSaleRate ?? 0;
    } catch { return pt.currentSaleRate ?? 0; }
  };

  // Export report data as Excel (via SpreadsheetML)
  const exportToExcel = (title: string, headers: string[], rows: (string | number)[][]) => {
    const escape = (v: string | number) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const headerRow = headers.map(h => `<Cell><Data ss:Type="String">${escape(h)}</Data></Cell>`).join('');
    const dataRows = rows.map(r =>
      `<Row>${r.map(v => `<Cell><Data ss:Type="${typeof v === 'number' ? 'Number' : 'String'}">${escape(v)}</Data></Cell>`).join('')}</Row>`
    ).join('');
    const xml = `<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="${escape(title)}"><Table><Row>${headerRow}</Row>${dataRows}</Table></Worksheet></Workbook>`;
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${title}.xls`; a.click();
    URL.revokeObjectURL(url);
  };

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const res = await api.getPurchases(pFilter);
      if (res.data) setPurchases(res.data);
    } catch { setMsg('Failed to load purchases'); } finally { setLoading(false); }
  };

  const loadSales = async () => {
    setLoading(true);
    try {
      const res = await api.getSales(sFilter);
      if (res.data) setSales(res.data);
    } catch { setMsg('Failed to load sales'); } finally { setLoading(false); }
  };

  const loadReports = async () => {
    const requestSeq = ++reportRequestSeqRef.current;
    setLoading(true);
    setPurchaseMonthReport(null);
    setSaleMonthReport(null);
    try {
       if (reportType === 'yearly') {
         const y = String(reportYear || '').trim();
         if (!/^\d{4}$/.test(y)) {
           setMsg('Please enter a valid 4-digit year');
           return;
         }
         const promises = [];
         for (let m = 1; m <= 12; m++) {
           const month = `${y}-${String(m).padStart(2, '0')}`;
           promises.push(api.getPurchaseMonthlyReport(month), api.getSaleMonthlyReport(month));
         }
         const results = await Promise.allSettled(promises);
         let aggPurchase: PurchaseMonthlyReport = { totalEntries: 0, totalQuantity: 0, totalAmount: 0, vendorBreakdown: [], profitLoss: 0 };
         let aggSale: SaleMonthlyReport = { totalEntries: 0, totalQuantity: 0, totalAmount: 0, customerBreakdown: [], profitLoss: 0 };
         for (let i = 0; i < results.length; i += 2) {
           if (results[i].status === 'fulfilled' && results[i].value.data) {
             const p = results[i].value.data;
             aggPurchase.totalEntries += p.totalEntries;
             aggPurchase.totalQuantity += p.totalQuantity;
             aggPurchase.totalAmount += p.totalAmount;
             p.vendorBreakdown?.forEach(v => {
               const x = aggPurchase.vendorBreakdown.find(e => e.vendorName === v.vendorName);
               if (x) { x.totalAmount += v.totalAmount; x.totalQuantity += v.totalQuantity; }
               else { aggPurchase.vendorBreakdown.push({...v}); }
             });
           }
           if (results[i + 1].status === 'fulfilled' && results[i + 1].value.data) {
             const s = results[i + 1].value.data;
             aggSale.totalEntries += s.totalEntries;
             aggSale.totalQuantity += s.totalQuantity;
             aggSale.totalAmount += s.totalAmount;
             s.customerBreakdown?.forEach(c => {
               const x = aggSale.customerBreakdown.find(e => e.customerName === c.customerName);
               if (x) { x.totalAmount += c.totalAmount; x.totalQuantity += c.totalQuantity; }
               else { aggSale.customerBreakdown.push({...c}); }
             });
           }
         }
         aggPurchase.vendorBreakdown.sort((a, b) => b.totalAmount - a.totalAmount);
         aggPurchase.vendorBreakdown = aggPurchase.vendorBreakdown.slice(0, 5);
         aggSale.customerBreakdown.sort((a, b) => b.totalAmount - a.totalAmount);
         aggSale.customerBreakdown = aggSale.customerBreakdown.slice(0, 5);
         aggPurchase.profitLoss = aggSale.totalAmount - aggPurchase.totalAmount;
         if (requestSeq !== reportRequestSeqRef.current) return;
         setPurchaseMonthReport(aggPurchase);
         setSaleMonthReport(aggSale);
       } else {
         const [pRes, sRes] = await Promise.allSettled([
           api.getPurchaseMonthlyReport(reportMonth),
           api.getSaleMonthlyReport(reportMonth),
         ]);
         if (requestSeq !== reportRequestSeqRef.current) return;
         if (pRes.status === 'fulfilled' && pRes.value.data) setPurchaseMonthReport(pRes.value.data);
         if (sRes.status === 'fulfilled' && sRes.value.data) setSaleMonthReport(sRes.value.data);
       }
    } catch {
      if (requestSeq === reportRequestSeqRef.current) setMsg('Failed to load reports');
    } finally {
      if (requestSeq === reportRequestSeqRef.current) setLoading(false);
    }
  };

  // Load product types once on mount
  useEffect(() => {
    api.getProductTypes({ isActive: true }).then(res => {
      if (res.data) setProductTypes(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'purchases') loadPurchases();
    else if (activeTab === 'sales') loadSales();
    else if (activeTab === 'reports') loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reportMonth, reportYear, reportType]);

  const handleCreatePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...pForm, vendorName: pForm.vendorName?.trim() || 'Not Applicable' };
    const res = await api.createPurchase(payload);
    setMsg(res.message);
    if (res.success) { setShowForm(false); loadPurchases(); }
  };

  const handleCreateSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...sForm, customerName: sForm.customerName?.trim() || 'Not Applicable' };
    const res = await api.createSale(payload);
    setMsg(res.message);
    if (res.success) { setShowForm(false); loadSales(); }
  };

  const handleUpdatePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editPurchase) return;
    const payload = { ...(pForm as UpdatePurchaseEntryRequest), vendorName: editPurchase.vendorName?.trim() || 'Not Applicable' };
    const res = await api.updatePurchase(editPurchase.purchaseEntryId, payload);
    setMsg(res.message);
    if (res.success) { setEditPurchase(null); loadPurchases(); }
  };

  const handleUpdateSale = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSale) return;
    const payload = { ...(sForm as UpdateSaleEntryRequest), customerName: editSale.customerName?.trim() || 'Not Applicable' };
    const res = await api.updateSale(editSale.saleEntryId, payload);
    setMsg(res.message);
    if (res.success) { setEditSale(null); loadSales(); }
  };

  const handleDeletePurchase = async (id: number) => {
    if (!confirm('Delete this purchase entry?')) return;
    const res = await api.deletePurchase(id);
    setMsg(res.message);
    if (res.success) loadPurchases();
  };

  const handleDeleteSale = async (id: number) => {
    if (!confirm('Delete this sale entry?')) return;
    const res = await api.deleteSale(id);
    setMsg(res.message);
    if (res.success) loadSales();
  };

  const psTabs: { key: PSTab; label: string; icon: string }[] = [
    { key: 'purchases', label: 'Purchase Entries', icon: '📦' },
    { key: 'sales', label: 'Sale Entries', icon: '🛍️' },
    { key: 'reports', label: 'Reports', icon: '📊' },
  ];

  const inputStyle: React.CSSProperties = {
    padding: '0.45rem 0.7rem', border: '1px solid var(--tc-border)', borderRadius: '6px',
    fontSize: '0.85rem', width: '100%', boxSizing: 'border-box',
  };

  const reportPeriodLabel = reportType === 'yearly' ? reportYear : reportMonth;
  const reportPeriodText = reportType === 'yearly' ? `year ${reportYear}` : `month ${reportMonth}`;

  return (
    <div>
      <PanelHeader icon="🛒" title="Purchase / Sale" subtitle="Manage purchase and sale entries" />
      {msg && <MsgBanner onClose={() => setMsg('')}>{msg}</MsgBanner>}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--tc-border)', paddingBottom: '0.5rem' }}>
        {psTabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '0.45rem 1rem', borderRadius: '6px 6px 0 0', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer',
            border: 'none', background: activeTab === t.key ? 'var(--tc)' : 'var(--tc-light)',
            color: activeTab === t.key ? 'white' : 'var(--tc-dark)',
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {/* === PURCHASES TAB === */}
      {activeTab === 'purchases' && (
        <div>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
            <DateInput value={pFilter.from || ''} onChange={e => setPFilter(f => ({ ...f, from: e.target.value }))} style={{ ...inputStyle, width: '150px' }} placeholder="From" />
            <DateInput value={pFilter.to || ''} onChange={e => setPFilter(f => ({ ...f, to: e.target.value }))} style={{ ...inputStyle, width: '150px' }} placeholder="To" />
            <input value={pFilter.vendorOrCustomer || ''} onChange={e => setPFilter(f => ({ ...f, vendorOrCustomer: e.target.value }))} style={{ ...inputStyle, width: '180px' }} placeholder="Vendor name" />
            <select value={pFilter.paymentStatus || ''} onChange={e => setPFilter(f => ({ ...f, paymentStatus: e.target.value }))} style={{ ...inputStyle, width: '140px' }}>
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Partial">Partial</option>
            </select>
            <button onClick={loadPurchases} style={{ padding: '0.45rem 1rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🔍 Filter</button>
            {!isSuperAdmin && (
              <button onClick={() => { setPForm({ entryDate: new Date().toISOString().split('T')[0], vendorName: '', quantity: 0, rate: 0, paymentStatus: 'Pending' }); setShowForm(true); setEditPurchase(null); }}
                style={{ padding: '0.45rem 1rem', background: '#2E7D32', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginLeft: 'auto' }}>
                + Add Purchase
              </button>
            )}
            <button onClick={() => api.exportPurchasesCsv(pFilter)} style={{ padding: '0.45rem 1rem', background: '#0277BD', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>⬇ Export CSV</button>
          </div>

          {/* Add/Edit form */}
          {(showForm || editPurchase) && (
            <form onSubmit={editPurchase ? handleUpdatePurchase : handleCreatePurchase}
              style={{ background: 'white', border: '1px solid var(--tc-border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0' }}>{editPurchase ? 'Edit Purchase Entry' : 'New Purchase Entry'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <div><label style={{ fontSize: '0.8rem' }}>Date *</label><DateInput required value={editPurchase ? (editPurchase.entryDate.slice(0, 10)) : pForm.entryDate} onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const newDate = e.target.value;
                    if (editPurchase) {
                      const rate = editPurchase.productName ? await lookupPurchaseRate(editPurchase.productName, newDate) : editPurchase.rate;
                      setEditPurchase({ ...editPurchase, entryDate: newDate, rate });
                    } else {
                      const rate = pForm.productName ? await lookupPurchaseRate(pForm.productName, newDate) : pForm.rate;
                      setPForm(f => ({ ...f, entryDate: newDate, rate }));
                    }
                  }} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Vendor Name <span style={{ color: '#999', fontSize: '0.72rem' }}>(optional)</span></label><input value={editPurchase ? editPurchase.vendorName : pForm.vendorName} placeholder="Not Applicable" onChange={e => editPurchase ? setEditPurchase({ ...editPurchase, vendorName: e.target.value }) : setPForm(f => ({ ...f, vendorName: e.target.value }))} style={inputStyle} /></div>
                <div>
                  <label style={{ fontSize: '0.8rem' }}>Product *</label>
                  <select required onChange={async e => {
                    const productName = e.target.value;
                    const date = editPurchase ? editPurchase.entryDate.slice(0, 10) : pForm.entryDate;
                    const rate = productName ? await lookupPurchaseRate(productName, date) : 0;
                    if (editPurchase) {
                      setEditPurchase({ ...editPurchase, productName, rate });
                    } else {
                      setPForm(f => ({ ...f, productName, rate }));
                    }
                  }} value={editPurchase ? (editPurchase.productName || '') : (pForm.productName || '')} style={inputStyle}>
                    <option value="">— Select Product —</option>
                    {productTypes.map(pt => <option key={pt.productTypeId} value={pt.name}>{pt.name}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: '0.8rem' }}>Unit</label><input type="text" disabled value={productTypes.find(p => p.name === (editPurchase?.productName || pForm.productName))?.unit || ''} style={{...inputStyle, background: '#f5f5f5', color: '#666'}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Quantity *</label><input type="number" step="0.001" required min="0.001" value={editPurchase ? editPurchase.quantity : pForm.quantity} onChange={e => editPurchase ? setEditPurchase({ ...editPurchase, quantity: +e.target.value }) : setPForm(f => ({ ...f, quantity: +e.target.value }))} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Purchase Rate (effective for date)</label><input type="number" step="0.01" disabled value={editPurchase ? editPurchase.rate : pForm.rate} style={{...inputStyle, background: '#f5f5f5', color: '#666'}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Amount</label><input type="text" disabled value={fmt((editPurchase?.quantity ?? pForm.quantity) * (editPurchase?.rate ?? pForm.rate))} style={{...inputStyle, background: '#f5f5f5', color: '#666', fontWeight: 600}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Payment Status</label>
                  <select value={editPurchase ? editPurchase.paymentStatus : pForm.paymentStatus} onChange={e => editPurchase ? setEditPurchase({ ...editPurchase, paymentStatus: e.target.value }) : setPForm(f => ({ ...f, paymentStatus: e.target.value }))} style={inputStyle}>
                    <option value="Pending">Pending</option><option value="Paid">Paid</option><option value="Partial">Partial</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: '0.8rem' }}>Remarks</label><input value={editPurchase ? (editPurchase.remarks || '') : (pForm.remarks || '')} onChange={e => editPurchase ? setEditPurchase({ ...editPurchase, remarks: e.target.value }) : setPForm(f => ({ ...f, remarks: e.target.value }))} style={inputStyle} /></div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button type="submit" style={{ padding: '0.45rem 1.2rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>💾 Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditPurchase(null); }} style={{ padding: '0.45rem 1.2rem', background: '#777', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          )}

          {loading ? <LoadingSpinner /> : (
            <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--tc-light)', textAlign: 'left' }}>
                    {['Date', 'Vendor', 'Product', 'Qty', 'Rate', 'Amount', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--tc-dark)', borderBottom: '2px solid var(--tc-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {purchases.map(p => (
                    <tr key={p.purchaseEntryId} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{p.entryDate.slice(0, 10)}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{p.vendorName}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{p.productName || p.milkTypeName || '—'}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{p.quantity}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{fmt(p.rate)}</td>
                      <td style={{ padding: '0.45rem 0.75rem', fontWeight: 600 }}>{fmt(p.amount)}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>
                        <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                          background: p.paymentStatus === 'Paid' ? '#E8F5E9' : p.paymentStatus === 'Partial' ? '#FFF3E0' : '#FFEBEE',
                          color: p.paymentStatus === 'Paid' ? '#2E7D32' : p.paymentStatus === 'Partial' ? '#E65100' : '#C62828',
                        }}>{p.paymentStatus}</span>
                      </td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>
                        {p.remarks && (
                          <button onClick={() => setRemarksPopup({ text: p.remarks!, title: `Remarks — ${p.vendorName}` })} style={{ marginRight: '0.4rem', padding: '0.2rem 0.5rem', background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }} title="View Remarks">💬</button>
                        )}
                        {!isSuperAdmin && (
                          <>
                            <button onClick={() => { setEditPurchase(p); setShowForm(false); }} style={{ marginRight: '0.4rem', padding: '0.2rem 0.5rem', background: '#FFF9C4', border: '1px solid #F9A825', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>✏️</button>
                            <button onClick={() => handleDeletePurchase(p.purchaseEntryId)} style={{ padding: '0.2rem 0.5rem', background: '#FFEBEE', border: '1px solid #EF9A9A', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>🗑️</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No purchase entries found.</td></tr>}
                </tbody>
              </table>
              <div className="mobile-card-list">
                {purchases.map(p => (
                  <div key={p.purchaseEntryId} className="mobile-card-item">
                    <div className="mobile-card-header">
                      <span className="mobile-card-title">{p.vendorName || '—'}</span>
                      <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                        background: p.paymentStatus === 'Paid' ? '#E8F5E9' : p.paymentStatus === 'Partial' ? '#FFF3E0' : '#FFEBEE',
                        color: p.paymentStatus === 'Paid' ? '#2E7D32' : p.paymentStatus === 'Partial' ? '#E65100' : '#C62828',
                      }}>{p.paymentStatus}</span>
                    </div>
                    <div className="mobile-card-details">
                      <div><span className="mobile-card-detail-label">Date: </span>{p.entryDate.slice(0, 10)}</div>
                      <div><span className="mobile-card-detail-label">Product: </span>{p.productName || p.milkTypeName || '—'}</div>
                      <div><span className="mobile-card-detail-label">Qty: </span>{p.quantity}</div>
                      <div><span className="mobile-card-detail-label">Rate: </span>{fmt(p.rate)}</div>
                      <div><span className="mobile-card-detail-label">Amount: </span><strong>{fmt(p.amount)}</strong></div>
                    </div>
                    <div className="mobile-card-actions">
                      {p.remarks && (
                        <button className="mc-btn mc-btn-edit" onClick={() => setRemarksPopup({ text: p.remarks!, title: `Remarks — ${p.vendorName}` })}>💬</button>
                      )}
                      {!isSuperAdmin && (
                        <>
                          <button className="mc-btn mc-btn-edit" onClick={() => { setEditPurchase(p); setShowForm(false); }}>✏️ Edit</button>
                          <button className="mc-btn mc-btn-edit" style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }} onClick={() => handleDeletePurchase(p.purchaseEntryId)}>🗑️ Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === SALES TAB === */}
      {activeTab === 'sales' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
            <DateInput value={sFilter.from || ''} onChange={e => setSFilter(f => ({ ...f, from: e.target.value }))} style={{ ...inputStyle, width: '150px' }} />
            <DateInput value={sFilter.to || ''} onChange={e => setSFilter(f => ({ ...f, to: e.target.value }))} style={{ ...inputStyle, width: '150px' }} />
            <input value={sFilter.vendorOrCustomer || ''} onChange={e => setSFilter(f => ({ ...f, vendorOrCustomer: e.target.value }))} style={{ ...inputStyle, width: '180px' }} placeholder="Customer name" />
            <select value={sFilter.paymentStatus || ''} onChange={e => setSFilter(f => ({ ...f, paymentStatus: e.target.value }))} style={{ ...inputStyle, width: '140px' }}>
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Partial">Partial</option>
            </select>
            <button onClick={loadSales} style={{ padding: '0.45rem 1rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🔍 Filter</button>
            {!isSuperAdmin && (
              <button onClick={() => { setSForm({ entryDate: new Date().toISOString().split('T')[0], customerName: '', quantity: 0, rate: 0, paymentStatus: 'Pending' }); setShowForm(true); setEditSale(null); }}
                style={{ padding: '0.45rem 1rem', background: '#2E7D32', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginLeft: 'auto' }}>
                + Add Sale
              </button>
            )}
            <button onClick={() => api.exportSalesCsv(sFilter)} style={{ padding: '0.45rem 1rem', background: '#0277BD', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>⬇ Export CSV</button>
          </div>

          {(showForm || editSale) && (
            <form onSubmit={editSale ? handleUpdateSale : handleCreateSale}
              style={{ background: 'white', border: '1px solid var(--tc-border)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0' }}>{editSale ? 'Edit Sale Entry' : 'New Sale Entry'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <div><label style={{ fontSize: '0.8rem' }}>Date *</label><DateInput required value={editSale ? editSale.entryDate.slice(0, 10) : sForm.entryDate} onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const newDate = e.target.value;
                    if (editSale) {
                      const rate = editSale.productName ? await lookupSaleRate(editSale.productName, newDate) : editSale.rate;
                      setEditSale({ ...editSale, entryDate: newDate, rate });
                    } else {
                      const rate = sForm.productName ? await lookupSaleRate(sForm.productName, newDate) : sForm.rate;
                      setSForm(f => ({ ...f, entryDate: newDate, rate }));
                    }
                  }} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Customer Name <span style={{ color: '#999', fontSize: '0.72rem' }}>(optional)</span></label><input value={editSale ? editSale.customerName : sForm.customerName} placeholder="Not Applicable" onChange={e => editSale ? setEditSale({ ...editSale, customerName: e.target.value }) : setSForm(f => ({ ...f, customerName: e.target.value }))} style={inputStyle} /></div>
                <div>
                  <label style={{ fontSize: '0.8rem' }}>Product *</label>
                  <select required onChange={async e => {
                    const productName = e.target.value;
                    const date = editSale ? editSale.entryDate.slice(0, 10) : sForm.entryDate;
                    const rate = productName ? await lookupSaleRate(productName, date) : 0;
                    if (editSale) {
                      setEditSale({ ...editSale, productName, rate });
                    } else {
                      setSForm(f => ({ ...f, productName, rate }));
                    }
                  }} value={editSale ? (editSale.productName || '') : (sForm.productName || '')} style={inputStyle}>
                    <option value="">— Select Product —</option>
                    {productTypes.map(pt => <option key={pt.productTypeId} value={pt.name}>{pt.name}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: '0.8rem' }}>Unit</label><input type="text" disabled value={productTypes.find(p => p.name === (editSale?.productName || sForm.productName))?.unit || ''} style={{...inputStyle, background: '#f5f5f5', color: '#666'}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Quantity *</label><input type="number" step="0.001" required min="0.001" value={editSale ? editSale.quantity : sForm.quantity} onChange={e => editSale ? setEditSale({ ...editSale, quantity: +e.target.value }) : setSForm(f => ({ ...f, quantity: +e.target.value }))} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Sale Rate (effective for date)</label><input type="number" step="0.01" disabled value={editSale ? editSale.rate : sForm.rate} style={{...inputStyle, background: '#f5f5f5', color: '#666'}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Amount</label><input type="text" disabled value={fmt((editSale?.quantity ?? sForm.quantity) * (editSale?.rate ?? sForm.rate))} style={{...inputStyle, background: '#f5f5f5', color: '#666', fontWeight: 600}} /></div>
                <div><label style={{ fontSize: '0.8rem' }}>Payment Status</label>
                  <select value={editSale ? editSale.paymentStatus : sForm.paymentStatus} onChange={e => editSale ? setEditSale({ ...editSale, paymentStatus: e.target.value }) : setSForm(f => ({ ...f, paymentStatus: e.target.value }))} style={inputStyle}>
                    <option value="Pending">Pending</option><option value="Paid">Paid</option><option value="Partial">Partial</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: '0.8rem' }}>Remarks</label><input value={editSale ? (editSale.remarks || '') : (sForm.remarks || '')} onChange={e => editSale ? setEditSale({ ...editSale, remarks: e.target.value }) : setSForm(f => ({ ...f, remarks: e.target.value }))} style={inputStyle} /></div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button type="submit" style={{ padding: '0.45rem 1.2rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>💾 Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditSale(null); }} style={{ padding: '0.45rem 1.2rem', background: '#777', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          )}

          {loading ? <LoadingSpinner /> : (
            <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--tc-light)', textAlign: 'left' }}>
                    {['Date', 'Customer', 'Product', 'Qty', 'Rate', 'Amount', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--tc-dark)', borderBottom: '2px solid var(--tc-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sales.map(s => (
                    <tr key={s.saleEntryId} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{s.entryDate.slice(0, 10)}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{s.customerName}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{s.productName || s.milkTypeName || '—'}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{s.quantity}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>{fmt(s.rate)}</td>
                      <td style={{ padding: '0.45rem 0.75rem', fontWeight: 600 }}>{fmt(s.amount)}</td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>
                        <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                          background: s.paymentStatus === 'Paid' ? '#E8F5E9' : s.paymentStatus === 'Partial' ? '#FFF3E0' : '#FFEBEE',
                          color: s.paymentStatus === 'Paid' ? '#2E7D32' : s.paymentStatus === 'Partial' ? '#E65100' : '#C62828',
                        }}>{s.paymentStatus}</span>
                      </td>
                      <td style={{ padding: '0.45rem 0.75rem' }}>
                        {s.remarks && (
                          <button onClick={() => setRemarksPopup({ text: s.remarks!, title: `Remarks — ${s.customerName}` })} style={{ marginRight: '0.4rem', padding: '0.2rem 0.5rem', background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }} title="View Remarks">💬</button>
                        )}
                        {!isSuperAdmin && (
                          <>
                            <button onClick={() => { setEditSale(s); setShowForm(false); }} style={{ marginRight: '0.4rem', padding: '0.2rem 0.5rem', background: '#FFF9C4', border: '1px solid #F9A825', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>✏️</button>
                            <button onClick={() => handleDeleteSale(s.saleEntryId)} style={{ padding: '0.2rem 0.5rem', background: '#FFEBEE', border: '1px solid #EF9A9A', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>🗑️</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No sale entries found.</td></tr>}
                </tbody>
              </table>
              <div className="mobile-card-list">
                {sales.map(s => (
                  <div key={s.saleEntryId} className="mobile-card-item">
                    <div className="mobile-card-header">
                      <span className="mobile-card-title">{s.customerName || '—'}</span>
                      <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                        background: s.paymentStatus === 'Paid' ? '#E8F5E9' : s.paymentStatus === 'Partial' ? '#FFF3E0' : '#FFEBEE',
                        color: s.paymentStatus === 'Paid' ? '#2E7D32' : s.paymentStatus === 'Partial' ? '#E65100' : '#C62828',
                      }}>{s.paymentStatus}</span>
                    </div>
                    <div className="mobile-card-details">
                      <div><span className="mobile-card-detail-label">Date: </span>{s.entryDate.slice(0, 10)}</div>
                      <div><span className="mobile-card-detail-label">Product: </span>{s.productName || s.milkTypeName || '—'}</div>
                      <div><span className="mobile-card-detail-label">Qty: </span>{s.quantity}</div>
                      <div><span className="mobile-card-detail-label">Rate: </span>{fmt(s.rate)}</div>
                      <div><span className="mobile-card-detail-label">Amount: </span><strong>{fmt(s.amount)}</strong></div>
                    </div>
                    <div className="mobile-card-actions">
                      {s.remarks && (
                        <button className="mc-btn mc-btn-edit" onClick={() => setRemarksPopup({ text: s.remarks!, title: `Remarks — ${s.customerName}` })}>💬</button>
                      )}
                      {!isSuperAdmin && (
                        <>
                          <button className="mc-btn mc-btn-edit" onClick={() => { setEditSale(s); setShowForm(false); }}>✏️ Edit</button>
                          <button className="mc-btn mc-btn-edit" style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }} onClick={() => handleDeleteSale(s.saleEntryId)}>🗑️ Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === REPORTS TAB === */}
      {activeTab === 'reports' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Report Type:</label>
            <select value={reportType} onChange={e => setReportType(e.target.value as 'monthly' | 'yearly')} style={{ ...inputStyle, width: '140px' }}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            {reportType === 'monthly' && (
              <>
                <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Month:</label>
                <input type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)} style={{ ...inputStyle, width: '160px' }} />
              </>
            )}
            {reportType === 'yearly' && (
              <>
                <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Year:</label>
                <input type="number" min="2020" max="2099" value={reportYear} onChange={e => setReportYear(e.target.value)} style={{ ...inputStyle, width: '120px' }} />
              </>
            )}
            <button onClick={loadReports} style={{ padding: '0.45rem 1rem', background: 'var(--tc)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>📊 Load</button>
          </div>

          {loading ? <LoadingSpinner /> : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Purchase monthly */}
              <div style={{ background: 'white', border: '1px solid var(--tc-border)', borderRadius: '8px', padding: '1rem' }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#7B1FA2' }}>📦 Purchase Summary — {reportPeriodLabel}</h4>
                {purchaseMonthReport ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
                      <button onClick={() => exportToExcel(`Purchase-${reportPeriodLabel}`, ['Vendor', 'Total Amount'], purchaseMonthReport.vendorBreakdown.map(v => [v.vendorName, v.totalAmount]))}
                        style={{ padding: '0.3rem 0.75rem', background: '#1B7B3A', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>⬇ Excel</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <StatCard label="Entries" value={purchaseMonthReport.totalEntries} icon="📋" />
                      <StatCard label="Total Amount" value={fmt(purchaseMonthReport.totalAmount)} icon="💰" color="#7B1FA2" />
                    </div>
                    <h5 style={{ margin: '0.5rem 0 0.4rem', fontSize: '0.8rem', color: '#555' }}>Top Vendors</h5>
                    {purchaseMonthReport.vendorBreakdown.slice(0, 5).map(v => (
                      <div key={v.vendorName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>
                        <span>{v.vendorName}</span><span style={{ fontWeight: 600 }}>{fmt(v.totalAmount)}</span>
                      </div>
                    ))}
                    {(purchaseMonthReport.productTypeBreakdown?.length ?? 0) > 0 && (
                      <>
                        <h5 style={{ margin: '0.75rem 0 0.4rem', fontSize: '0.8rem', color: '#555' }}>Product Type</h5>
                        {purchaseMonthReport.productTypeBreakdown.map(pt => (
                          <div key={pt.productName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>
                            <span>{pt.productName}</span><span style={{ fontWeight: 600 }}>{fmt(pt.totalAmount)} ({pt.totalQuantity.toFixed(2)} qty)</span>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : <p style={{ color: '#999', fontSize: '0.85rem' }}>No purchase data for this {reportType === 'yearly' ? 'year' : 'month'}.</p>}
              </div>

              {/* Sale monthly */}
              <div style={{ background: 'white', border: '1px solid var(--tc-border)', borderRadius: '8px', padding: '1rem' }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#0277BD' }}>🛍️ Sale Summary — {reportPeriodLabel}</h4>
                {saleMonthReport ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
                      <button onClick={() => exportToExcel(`Sale-${reportPeriodLabel}`, ['Customer', 'Total Amount'], saleMonthReport.customerBreakdown.map(c => [c.customerName, c.totalAmount]))}
                        style={{ padding: '0.3rem 0.75rem', background: '#1B7B3A', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>⬇ Excel</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <StatCard label="Entries" value={saleMonthReport.totalEntries} icon="📋" />
                      <StatCard label="Total Amount" value={fmt(saleMonthReport.totalAmount)} icon="💰" color="#0277BD" />
                    </div>
                    <h5 style={{ margin: '0.5rem 0 0.4rem', fontSize: '0.8rem', color: '#555' }}>Top Customers</h5>
                    {saleMonthReport.customerBreakdown.slice(0, 5).map(c => (
                      <div key={c.customerName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>
                        <span>{c.customerName}</span><span style={{ fontWeight: 600 }}>{fmt(c.totalAmount)}</span>
                      </div>
                    ))}
                    {(saleMonthReport.productTypeBreakdown?.length ?? 0) > 0 && (
                      <>
                        <h5 style={{ margin: '0.75rem 0 0.4rem', fontSize: '0.8rem', color: '#555' }}>Product Type</h5>
                        {saleMonthReport.productTypeBreakdown.map(pt => (
                          <div key={pt.productName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>
                            <span>{pt.productName}</span><span style={{ fontWeight: 600 }}>{fmt(pt.totalAmount)} ({pt.totalQuantity.toFixed(2)} qty)</span>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : <p style={{ color: '#999', fontSize: '0.85rem' }}>No sale data for this {reportType === 'yearly' ? 'year' : 'month'}.</p>}
              </div>

              {/* Profit/Loss */}
              {purchaseMonthReport && saleMonthReport && (() => {
                const pl = saleMonthReport.totalAmount - purchaseMonthReport.totalAmount;
                return (
                  <div style={{ gridColumn: '1 / -1', background: pl >= 0 ? '#E8F5E9' : '#FFEBEE', border: `1px solid ${pl >= 0 ? '#A5D6A7' : '#EF9A9A'}`, borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: pl >= 0 ? '#2E7D32' : '#C62828' }}>{pl >= 0 ? '📈 Profit' : '📉 Loss'} for {reportPeriodText}</h4>
                    <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: pl >= 0 ? '#2E7D32' : '#C62828' }}>{fmt(Math.abs(pl))}</p>
                    <p style={{ fontSize: '0.8rem', margin: '0.25rem 0 0', color: '#666' }}>Sale {fmt(saleMonthReport.totalAmount)} − Purchase {fmt(purchaseMonthReport.totalAmount)}</p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Remarks Popup */}
      {remarksPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setRemarksPopup(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '1.5rem', width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.8rem', color: '#1565C0' }}>💬 {remarksPopup.title}</div>
            <div style={{ fontSize: '0.92rem', color: '#333', lineHeight: 1.5 }}>{remarksPopup.text}</div>
            <button onClick={() => setRemarksPopup(null)} style={{ marginTop: '1rem', padding: '0.4rem 1.2rem', background: '#1976D2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Module Access Panel (SuperAdmin) =====
function ModuleAccessPanel() {
  const [matrix, setMatrix] = useState<TenantModuleMatrix[]>([]);
  const [modules, setModules] = useState<AppModuleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [matRes, modRes] = await Promise.all([api.getModuleMatrix(), api.getAllModules()]);
      if (matRes.data) setMatrix(matRes.data);
      if (modRes.data) setModules(modRes.data);
    } catch { setMsg('Failed to load module matrix'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (tenantId: number, moduleKey: string, isEnabled: boolean) => {
    const res = await api.setModuleAccess(tenantId, moduleKey, isEnabled);
    if (res.success !== false) {
      setMatrix(prev => prev.map(t =>
        t.tenantId === tenantId
          ? { ...t, modules: t.modules.map(m => m.moduleKey === moduleKey ? { ...m, isEnabled } : m) }
          : t
      ));
    } else {
      setMsg('Failed to update module access');
    }
  };

  return (
    <div>
      <PanelHeader icon="🔒" title="Module Access" subtitle="Enable or disable modules per tenant" />
      {msg && <MsgBanner onClose={() => setMsg('')}>{msg}</MsgBanner>}
      {loading ? <LoadingSpinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '600px' }}>
            <thead>
              <tr style={{ background: 'var(--tc-light)' }}>
                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', borderBottom: '2px solid var(--tc-border)', color: 'var(--tc-dark)' }}>Tenant</th>
                {modules.map(m => (
                  <th key={m.moduleKey} style={{ padding: '0.6rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--tc-border)', color: 'var(--tc-dark)' }}>{m.displayName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map(t => (
                <tr key={t.tenantId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{t.tenantName}</td>
                  {modules.map(m => {
                    const mod = t.modules.find(x => x.moduleKey === m.moduleKey);
                    const enabled = mod?.isEnabled ?? true;
                    return (
                      <td key={m.moduleKey} style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggle(t.tenantId, m.moduleKey, !enabled)}
                          style={{
                            padding: '0.3rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: 'none',
                            background: enabled ? '#E8F5E9' : '#FFEBEE',
                            color: enabled ? '#2E7D32' : '#C62828',
                          }}
                        >
                          {enabled ? '✅ On' : '⛔ Off'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {matrix.length === 0 && (
                <tr><td colSpan={modules.length + 1} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No tenants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===== Menu Order Panel (SuperAdmin) =====
function MenuOrderPanel({ onMenuOrderChange }: { onMenuOrderChange: () => void }) {
  const allTabsInit: { key: string; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'profile', label: 'My Profile', icon: '👤' },
    { key: 'subscription', label: 'My Subscription', icon: '📋' },
    { key: 'subscribers', label: 'Subscribers', icon: '👥' },
    { key: 'milktypes', label: 'Milk Types', icon: '🥛' },
    { key: 'societymaster', label: 'Society Master', icon: '🏘️' },
    { key: 'buildingmaster', label: 'Building Master', icon: '🏢' },
    { key: 'flatmaster', label: 'Flat Master', icon: '🚪' },
    { key: 'deliveries', label: 'Deliveries', icon: '🚚' },
    { key: 'billing', label: 'Billing', icon: '🧾' },
    { key: 'payments', label: 'Payments', icon: '💳' },
    { key: 'cashmanagement', label: 'Cash Mgmt', icon: '🏪' },
    { key: 'reports', label: 'Reports', icon: '📊' },
    { key: 'emailconfig', label: 'Email Config', icon: '📧' },
    { key: 'notifications', label: 'Notifications', icon: '📢' },
    { key: 'dairysetup', label: 'Dairy Setup', icon: '🎨' },
    { key: 'tenants', label: 'Tenants', icon: '🏢' },
    { key: 'livesessions', label: 'Live Sessions', icon: '🟢' },
    { key: 'documents', label: 'Documents', icon: '📄' },
    { key: 'aichat', label: 'AI Chat', icon: '🤖' },
    { key: 'usermanual', label: 'User Manual', icon: '📖' },
    { key: 'purchasesale', label: 'Purchase/Sale', icon: '🛒' },
    { key: 'producttypes', label: 'Product Types', icon: '📦' },
    { key: 'moduleaccess', label: 'Module Access', icon: '🔒' },
  ];
  const [menuItems, setMenuItems] = useState(allTabsInit);
  const [msg, setMsg] = useState('');

  const parseMenuOrder = (value: string | null) => {
    if (!value) return [] as string[];
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [] as string[];
      return parsed
        .map((x: unknown) => {
          if (typeof x === 'string') return x;
          if (x && typeof x === 'object' && 'key' in (x as Record<string, unknown>)) {
            const key = (x as Record<string, unknown>).key;
            return typeof key === 'string' ? key : null;
          }
          return null;
        })
        .filter((x: string | null): x is string => Boolean(x));
    } catch {
      return [] as string[];
    }
  };

  const persistMenuOrder = async (newItems: { key: string; label: string; icon: string }[]) => {
    const orderJson = JSON.stringify(newItems.map(x => x.key));
    localStorage.setItem('menuOrder', orderJson);
    try {
      await api.updateMenuOrderForAll(orderJson);
      onMenuOrderChange();
      return true;
    } catch {
      // Keep localStorage as a fallback if branding cannot be saved.
      return false;
    }
  };
  
  useEffect(() => {
    const applyOrder = (keys: string[]) => {
      if (keys.length === 0) return;
      const lookup = new Map(allTabsInit.map(t => [t.key, t]));
      const ordered = keys.map(k => lookup.get(k)).filter((x): x is { key: string; label: string; icon: string } => Boolean(x));
      const remaining = allTabsInit.filter(t => !keys.includes(t.key));
      setMenuItems([...ordered, ...remaining]);
    };

    const loadPersistedOrder = async () => {
      try {
        const res = await api.getBranding();
        const brandingOrder = parseMenuOrder(res.data?.menuOrderJson ?? null);
        if (brandingOrder.length > 0) {
          applyOrder(brandingOrder);
          return;
        }
      } catch {
        // fall back to localStorage below
      }

      applyOrder(parseMenuOrder(localStorage.getItem('menuOrder')));
    };

    void loadPersistedOrder();
  }, []);
  
  const moveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...menuItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setMenuItems(newItems);
    void persistMenuOrder(newItems).then((saved) => {
      if (!saved) return;
      setMsg('Menu order updated');
      setTimeout(() => setMsg(''), 2000);
    });
  };
  
  const moveDown = (index: number) => {
    if (index === menuItems.length - 1) return;
    const newItems = [...menuItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setMenuItems(newItems);
    void persistMenuOrder(newItems).then((saved) => {
      if (!saved) return;
      setMsg('Menu order updated');
      setTimeout(() => setMsg(''), 2000);
    });
  };
  
  const reset = () => {
    if (!confirm('Reset menu order to default?')) return;
    setMenuItems(allTabsInit);
    void persistMenuOrder(allTabsInit).then((saved) => {
      if (!saved) return;
      setMsg('Menu order reset to default');
      setTimeout(() => setMsg(''), 2000);
    });
  };
  
  return (
    <div>
      <PanelHeader icon="📋" title="Dashboard Menu Order" subtitle="Reorder menu items displayed to all users" />
      {msg && <MsgBanner onClose={() => setMsg('')}>{msg}</MsgBanner>}
      <div style={{ marginBottom: '1rem' }}>
        <button onClick={reset} style={{ padding: '0.5rem 1rem', background: '#f44336', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>🔄 Reset to Default</button>
      </div>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {menuItems.map((item, idx) => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'white', borderRadius: '8px', border: '1px solid #ddd' }}>
            <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
            <span style={{ flex: 1, fontWeight: 500 }}>{item.label}</span>
            <button onClick={() => moveUp(idx)} disabled={idx === 0} style={{ padding: '0.3rem 0.6rem', background: idx === 0 ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: idx === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>⬆️ Up</button>
            <button onClick={() => moveDown(idx)} disabled={idx === menuItems.length - 1} style={{ padding: '0.3rem 0.6rem', background: idx === menuItems.length - 1 ? '#ccc' : '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: idx === menuItems.length - 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>Down ⬇️</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Product Types Panel (Tenant Admin) =====
function ProductTypesPanel() {
  const [items, setItems] = useState<ProductTypeDto[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ProductTypeDto | null>(null);
  const [form, setForm] = useState<CreateProductTypeRequest>({ name: '', unit: '', description: '' });
  const [saving, setSaving] = useState(false);
  // Rate history state
  const [rates, setRates] = useState<ProductTypeRateDto[]>([]);
  const [rateForm, setRateForm] = useState({ effectiveDate: '', purchaseRate: '', saleRate: '', notes: '' });
  const [rateLoading, setRateLoading] = useState(false);
  const [addingRate, setAddingRate] = useState(false);
  const [editingRateId, setEditingRateId] = useState<number | null>(null);
  const [editRateForm, setEditRateForm] = useState({ effectiveDate: '', purchaseRate: '', saleRate: '', notes: '' });
  const [savingRate, setSavingRate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getProductTypes(search ? { isActive: true, search } : { isActive: true });
      if (res.success && res.data) setItems(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', unit: '', description: '' });
    setMsg('');
    setRates([]);
    setEditingRateId(null);
    setRateForm({ effectiveDate: new Date().toISOString().slice(0, 10), purchaseRate: '', saleRate: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = async (pt: ProductTypeDto) => {
    setEditing(pt);
    setForm({ name: pt.name, unit: pt.unit ?? '', description: pt.description ?? '' });
    setMsg('');
    setRates([]);
    setEditingRateId(null);
    setRateForm({ effectiveDate: new Date().toISOString().slice(0, 10), purchaseRate: '', saleRate: '', notes: '' });
    setShowModal(true);
    setRateLoading(true);
    try {
      const res = await api.getProductTypeRates(pt.productTypeId);
      if (res.success && res.data) setRates(res.data);
    } catch { /* ignore */ }
    setRateLoading(false);
  };

  const handleAddRate = async () => {
    if (!editing) return;
    if (!rateForm.effectiveDate) { setMsg('Effective date is required for rate.'); return; }
    if (!rateForm.purchaseRate || parseFloat(rateForm.purchaseRate) <= 0) { setMsg('Purchase Rate is required.'); return; }
    setAddingRate(true);
    try {
      const res = await api.addProductTypeRate(editing.productTypeId, {
        effectiveDate: rateForm.effectiveDate,
        purchaseRate: parseFloat(rateForm.purchaseRate) || 0,
        saleRate: parseFloat(rateForm.saleRate) || 0,
        notes: rateForm.notes || undefined,
      });
      if (res.success && res.data) {
        setRates(prev => [res.data!, ...prev]);
        setRateForm(f => ({ ...f, purchaseRate: '', saleRate: '', notes: '' }));
      } else {
        setMsg(res.message || 'Failed to add rate.');
      }
    } catch { setMsg('Failed to add rate.'); }
    setAddingRate(false);
  };

  const startEditRate = (r: ProductTypeRateDto) => {
    setEditingRateId(r.productTypeRateId);
    setEditRateForm({
      effectiveDate: r.effectiveDate.slice(0, 10),
      purchaseRate: String(r.purchaseRate),
      saleRate: String(r.saleRate),
      notes: r.notes || '',
    });
  };

  const handleUpdateRate = async () => {
    if (!editing || editingRateId == null) return;
    if (!editRateForm.effectiveDate) { setMsg('Effective date is required.'); return; }
    setSavingRate(true);
    try {
      const res = await api.updateProductTypeRate(editing.productTypeId, editingRateId, {
        effectiveDate: editRateForm.effectiveDate,
        purchaseRate: parseFloat(editRateForm.purchaseRate) || 0,
        saleRate: parseFloat(editRateForm.saleRate) || 0,
        notes: editRateForm.notes || undefined,
      });
      if (res.success && res.data) {
        setRates(prev => prev.map(r => r.productTypeRateId === editingRateId ? res.data! : r));
        setEditingRateId(null);
      } else {
        setMsg(res.message || 'Failed to update rate.');
      }
    } catch { setMsg('Failed to update rate.'); }
    setSavingRate(false);
  };

  const handleDeleteRate = async (r: ProductTypeRateDto) => {
    if (!editing) return;
    if (!confirm(`Delete rate for ${r.effectiveDate.slice(0, 10)}?`)) return;
    try {
      const res = await api.deleteProductTypeRate(editing.productTypeId, r.productTypeRateId);
      if (res.success) {
        setRates(prev => prev.filter(x => x.productTypeRateId !== r.productTypeRateId));
      } else {
        setMsg(res.message || 'Failed to delete rate.');
      }
    } catch { setMsg('Failed to delete rate.'); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('Name is required.'); return; }
    setSaving(true);
    setMsg('');
    try {
      if (editing) {
        const update: UpdateProductTypeRequest = {
          name: form.name.trim(),
          unit: form.unit || undefined,
          description: form.description || undefined,
        };
        const res = await api.updateProductType(editing.productTypeId, update);
        if (res.success) { setShowModal(false); load(); }
        else setMsg(res.message || 'Failed to update.');
      } else {
        const create: CreateProductTypeRequest = {
          name: form.name.trim(),
          unit: form.unit || undefined,
          description: form.description || undefined,
        };
        const res = await api.createProductType(create);
        if (res.success && res.data) {
          // If an initial rate was provided, save it now
          const pr = parseFloat(rateForm.purchaseRate);
          if (rateForm.purchaseRate && pr > 0) {
            await api.addProductTypeRate(res.data.productTypeId, {
              effectiveDate: rateForm.effectiveDate,
              purchaseRate: pr,
              saleRate: parseFloat(rateForm.saleRate) || 0,
              notes: rateForm.notes || undefined,
            });
          }
          setShowModal(false); load();
        } else {
          setMsg((res as { message?: string }).message || 'Failed to create.');
        }
      }
    } catch { setMsg('An error occurred.'); }
    setSaving(false);
  };

  const handleDelete = async (pt: ProductTypeDto) => {
    if (!confirm(`Delete "${pt.name}"?`)) return;
    const res = await api.deleteProductType(pt.productTypeId);
    if (res.success) load();
    else alert('Failed to delete.');
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>📦 Product Types</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={openAdd} style={{ padding: '0.45rem 1.2rem', background: '#1976D2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            + Add Product Type
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          placeholder="Search by name, unit, description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: 6, flex: '1 1 200px' }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading...</div>
      ) : (
        <div className="responsive-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' }}>
            <thead>
              <tr style={{ background: '#E3F2FD' }}>
                {['Name', 'Unit', 'Purchase Rate', 'Sale Rate', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.8rem', textAlign: 'left', borderBottom: '2px solid #BBDEFB', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(pt => (
                <tr key={pt.productTypeId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.6rem 0.8rem', fontWeight: 500 }}>{pt.name}</td>
                  <td style={{ padding: '0.6rem 0.8rem', color: '#555' }}>{pt.unit || '—'}</td>
                  <td style={{ padding: '0.6rem 0.8rem' }}>{pt.currentPurchaseRate != null ? `₹${pt.currentPurchaseRate.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '0.6rem 0.8rem' }}>{pt.currentSaleRate != null ? `₹${pt.currentSaleRate.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '0.6rem 0.8rem' }}>
                    <span style={{ padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.8rem', background: pt.isActive ? '#E8F5E9' : '#FFEBEE', color: pt.isActive ? '#2E7D32' : '#C62828', fontWeight: 600 }}>
                      {pt.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.6rem 0.8rem' }}>
                    <button onClick={() => openEdit(pt)} style={{ marginRight: 6, padding: '0.3rem 0.7rem', background: '#1976D2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>Edit</button>
                    {pt.isActive && (
                      <button onClick={() => handleDelete(pt)} style={{ padding: '0.3rem 0.7rem', background: '#EF5350', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>No product types found.</td></tr>
              )}
            </tbody>
          </table>
          <div className="mobile-card-list">
            {items.map(pt => (
              <div key={pt.productTypeId} className="mobile-card-item">
                <div className="mobile-card-header">
                  <span className="mobile-card-title">{pt.name}</span>
                  <span style={{ padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.8rem', background: pt.isActive ? '#E8F5E9' : '#FFEBEE', color: pt.isActive ? '#2E7D32' : '#C62828', fontWeight: 600 }}>
                    {pt.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="mobile-card-details">
                  <div><span className="mobile-card-detail-label">Unit: </span>{pt.unit || '—'}</div>
                  <div><span className="mobile-card-detail-label">Purchase Rate: </span>{pt.currentPurchaseRate != null ? `₹${pt.currentPurchaseRate.toFixed(2)}` : '—'}</div>
                  <div><span className="mobile-card-detail-label">Sale Rate: </span>{pt.currentSaleRate != null ? `₹${pt.currentSaleRate.toFixed(2)}` : '—'}</div>
                </div>
                <div className="mobile-card-actions">
                  <button className="mc-btn mc-btn-edit" onClick={() => openEdit(pt)}>✏️ Edit</button>
                  {pt.isActive && (
                    <button className="mc-btn mc-btn-edit" style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }} onClick={() => handleDelete(pt)}>🗑️ Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '2rem', width: '100%', maxWidth: 560, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 1.2rem' }}>{editing ? 'Edit Product Type' : 'Add Product Type'}</h3>
            {msg && <div style={{ background: '#FFEBEE', color: '#C62828', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.88rem' }}>{msg}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <label style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                Name *
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ display: 'block', width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 6, marginTop: 4 }} />
              </label>
              <label style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                Unit (e.g. Litre, Kg)
                <input value={form.unit ?? ''} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} style={{ display: 'block', width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 6, marginTop: 4 }} />
              </label>
              <label style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                Description
                <textarea value={form.description ?? ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ display: 'block', width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 6, marginTop: 4, resize: 'vertical' }} />
              </label>
            </div>

            {/* Rate History / Initial Rate section */}
            <div style={{ marginTop: '1.4rem', borderTop: '2px solid #E3F2FD', paddingTop: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.8rem', color: '#1565C0' }}>
                📊 {editing ? 'Rate History' : 'Initial Rate (Optional)'}
              </div>
              {/* Add rate form */}
              <div style={{ background: '#F5F5F5', borderRadius: 6, padding: '0.8rem', marginBottom: '0.8rem' }}>
                {editing && <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: '0.5rem', color: '#555' }}>Add New Rate</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.82rem' }}>
                      Effective Date *
                      <DateInput value={rateForm.effectiveDate} onChange={e => setRateForm(f => ({ ...f, effectiveDate: e.target.value }))} style={{ display: 'block', width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, marginTop: 2 }} />
                    </label>
                    <label style={{ fontSize: '0.82rem' }}>
                      Purchase Rate (₹)
                      <input type="number" step="0.01" min="0" value={rateForm.purchaseRate} onChange={e => setRateForm(f => ({ ...f, purchaseRate: e.target.value }))} style={{ display: 'block', width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, marginTop: 2 }} />
                    </label>
                    <label style={{ fontSize: '0.82rem' }}>
                      Sale Rate (₹)
                      <input type="number" step="0.01" min="0" value={rateForm.saleRate} onChange={e => setRateForm(f => ({ ...f, saleRate: e.target.value }))} style={{ display: 'block', width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, marginTop: 2 }} />
                    </label>
                  </div>
                  <label style={{ fontSize: '0.82rem', marginTop: '0.4rem', display: 'block' }}>
                    Notes
                    <input value={rateForm.notes} onChange={e => setRateForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional note about this rate change" style={{ display: 'block', width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, marginTop: 2 }} />
                  </label>
                  <button onClick={handleAddRate} disabled={addingRate || !rateForm.effectiveDate} style={{ marginTop: '0.5rem', padding: '0.35rem 1rem', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', display: editing ? undefined : 'none' }}>
                    {addingRate ? 'Adding...' : '+ Add Rate'}
                  </button>
                  {!editing && <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#888' }}>Rate will be saved with the product. Leave blank to skip.</div>}
                </div>
                {/* Rate history table - only for existing products */}
                {editing && (rateLoading ? (
                  <div style={{ textAlign: 'center', color: '#888', fontSize: '0.85rem', padding: '0.5rem' }}>Loading rates...</div>
                ) : rates.length === 0 ? (
                  <div style={{ color: '#999', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem' }}>No rate history yet. Add the first rate above.</div>
                ) : (
                  <div style={{ overflowY: 'auto', maxHeight: 200 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#E3F2FD' }}>
                          {['Effective Date', 'Purchase ₹', 'Sale ₹', 'Notes', 'Added On', 'Actions'].map(h => (
                            <th key={h} style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #BBDEFB', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rates.map(r => (
                          editingRateId === r.productTypeRateId ? (
                            <tr key={r.productTypeRateId} style={{ borderBottom: '1px solid #eee', background: '#FFFDE7' }}>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <DateInput value={editRateForm.effectiveDate} onChange={e => setEditRateForm(f => ({ ...f, effectiveDate: e.target.value }))} style={{ width: '100%', padding: '0.2rem 0.3rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.8rem' }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" step="0.01" min="0" value={editRateForm.purchaseRate} onChange={e => setEditRateForm(f => ({ ...f, purchaseRate: e.target.value }))} style={{ width: 70, padding: '0.2rem 0.3rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.8rem' }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" step="0.01" min="0" value={editRateForm.saleRate} onChange={e => setEditRateForm(f => ({ ...f, saleRate: e.target.value }))} style={{ width: 70, padding: '0.2rem 0.3rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.8rem' }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input value={editRateForm.notes} onChange={e => setEditRateForm(f => ({ ...f, notes: e.target.value }))} style={{ width: '100%', padding: '0.2rem 0.3rem', border: '1px solid #ccc', borderRadius: 3, fontSize: '0.8rem' }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem', color: '#aaa', fontSize: '0.78rem' }}>{r.createdDate.slice(0, 10)}</td>
                              <td style={{ padding: '0.25rem 0.4rem', whiteSpace: 'nowrap' }}>
                                <button onClick={handleUpdateRate} disabled={savingRate} style={{ marginRight: 4, padding: '0.2rem 0.5rem', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>{savingRate ? '...' : '💾'}</button>
                                <button onClick={() => setEditingRateId(null)} style={{ padding: '0.2rem 0.5rem', background: '#757575', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>✕</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={r.productTypeRateId} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>{r.effectiveDate.slice(0, 10)}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>₹{r.purchaseRate.toFixed(2)}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>₹{r.saleRate.toFixed(2)}</td>
                              <td style={{ padding: '0.35rem 0.5rem', color: '#777' }}>{r.notes || '—'}</td>
                              <td style={{ padding: '0.35rem 0.5rem', color: '#aaa', fontSize: '0.78rem' }}>{r.createdDate.slice(0, 10)}</td>
                              <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>
                                <button onClick={() => startEditRate(r)} style={{ marginRight: 4, padding: '0.2rem 0.5rem', background: '#1976D2', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>✏️</button>
                                <button onClick={() => handleDeleteRate(r)} style={{ padding: '0.2rem 0.5rem', background: '#EF5350', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>🗑️</button>
                              </td>
                            </tr>
                          )
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1.2rem', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', background: '#f5f5f5' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1.4rem', background: '#1976D2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nextWeek() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// A bill's period can span multiple months (e.g. "1st to today" bills carried over from
// a prior unpaid period). Filtering by startDate alone hides bills whose period merely
// *overlaps* the selected month, making freshly-generated bills appear to "vanish".
function billOverlapsMonth(startDate: string, endDate: string, month: string) {
  const monthStart = `${month}-01`;
  const [y, m] = month.split('-').map(Number);
  const monthEnd = new Date(y, m, 0).toISOString().slice(0, 10); // last day of month
  return startDate.slice(0, 10) <= monthEnd && endDate.slice(0, 10) >= monthStart;
}

function downloadCsvFile(fileName: string, rows: Array<Array<string | number>>) {
  const escapeCell = (value: string | number) => {
    const text = String(value ?? '');
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = rows.map(row => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(s: string) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
}

function fmtLongDate(s: string) {
  if (!s) return 'Select a date';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatStructuredAddress(parts: {
  societyName?: string | null;
  buildingName?: string | null;
  flatName?: string | null;
  areaName?: string | null;
  city?: string | null;
  fallbackAddress?: string | null;
}) {
  const joined = [parts.societyName, parts.buildingName, parts.flatName, parts.areaName, parts.city]
    .map(v => (v ?? '').trim())
    .filter(v => v.length > 0)
    .join(', ');
  if (joined.length > 0) return joined;
  const fallback = (parts.fallbackAddress ?? '').trim();
  return fallback.length > 0 ? fallback : '—';
}

// ===== User Manual Panel =====
const USER_MANUAL_MODULES = [
  {
    key: 'dashboard',
    icon: '📊',
    title: 'Dashboard',
    subtitle: 'Overview of business performance',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Getting Started',
        steps: [
          'After login, the Dashboard is the first screen you see as a Tenant Admin.',
          'It shows key business metrics: total subscribers, active deliveries today, monthly revenue, and pending bills.',
          'Cards are color-coded: green for healthy metrics, amber for caution, red for issues needing attention.',
        ],
      },
      {
        heading: 'Reading the Metrics',
        steps: [
          'Total Subscribers — count of all active subscribers linked to your dairy.',
          'Today\'s Deliveries — number of deliveries scheduled and completed today.',
          'Monthly Revenue — total payments received in the current month.',
          'Pending Bills — bills generated but not yet paid by subscribers.',
          'Purchase/Sale Summary — visible when the Purchase & Sale module is enabled.',
          'Cash Balance — visible when the Cash Management module is enabled.',
        ],
      },
      {
        heading: 'Tips',
        steps: [
          'Click any metric card to navigate to the related module for details.',
          'The dashboard refreshes automatically every 30 seconds.',
          'Use the Dairy Setup module to customise the theme colour and logo shown in the header.',
        ],
      },
    ],
  },
  {
    key: 'subscribers',
    icon: '👥',
    title: 'Subscribers',
    subtitle: 'Manage your milk delivery subscribers',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Adding a Subscriber',
        steps: [
          'Click the "Subscribers" tab in the navigation.',
          'Click "+ Add Subscriber" button at the top right.',
          'Fill in the form: Name, Email (used for login), Phone, Address, Milk Type, and Daily Quantity.',
          'Set a Subscription Start Date.',
          'Click "Save" — the subscriber receives a welcome email with login credentials.',
        ],
      },
      {
        heading: 'Editing a Subscriber',
        steps: [
          'Find the subscriber using the search bar at the top.',
          'Click the ✏️ edit icon on the subscriber row.',
          'Update Email, Phone, Address, or Active status.',
          'Click "Update" to save changes.',
        ],
      },
      {
        heading: 'Pausing a Subscription',
        steps: [
          'Click the ⏸️ pause icon on the subscriber row.',
          'Enter the Start Date and End Date of the pause period.',
          'Click "Save Pause". Deliveries and billing are automatically skipped during this period.',
          'To remove a pause, click the pause icon again and delete the existing pause entry.',
        ],
      },
      {
        heading: 'Delivery Calendar',
        steps: [
          'Click the 📅 calendar icon to open the monthly delivery calendar for a subscriber.',
          'Green dates = delivery completed. Grey dates = paused or no delivery.',
          'Use the ◀ ▶ arrows to navigate months.',
        ],
      },
      {
        heading: 'Extra Milk Entry',
        steps: [
          'Use the Extra Milk button to record a one-time additional delivery for a specific date.',
          'Enter the date, quantity, and milk type, then save.',
        ],
      },
      {
        heading: 'Resetting Password',
        steps: [
          'Click the 🔑 key icon on the subscriber row.',
          'Enter a new password and confirm.',
          'Click "Reset Password". The subscriber can now log in with the new password.',
        ],
      },
      {
        heading: 'Bulk Import via Excel',
        steps: [
          'Download the sample Excel template using the "📥 Template" button.',
          'Fill in subscriber details in the template (Name, Email, Phone, Address, MilkType, Quantity, StartDate).',
          'Click "Upload Excel" and select your filled file.',
          'Review the import summary and fix any errors shown.',
        ],
      },
    ],
  },
  {
    key: 'milktypes',
    icon: '🥛',
    title: 'Milk Types',
    subtitle: 'Define milk varieties and pricing',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Creating a Milk Type',
        steps: [
          'Click the "Milk Types" tab.',
          'Click "+ Add Milk Type".',
          'Enter a Name (e.g., "Full Cream", "Toned", "Buffalo"), Unit (Litre), and Price per Unit.',
          'Click "Save".',
        ],
      },
      {
        heading: 'Editing / Deactivating',
        steps: [
          'Click the ✏️ icon on any milk type row to update its name or price.',
          'Toggle the Active switch to deactivate a milk type — inactive types are hidden from subscriber assignment.',
        ],
      },
      {
        heading: 'Pricing Impact',
        steps: [
          'Changing the price of a milk type affects all future bill generations.',
          'Existing bills already generated are not automatically recalculated.',
          'Always update prices before generating monthly bills.',
        ],
      },
    ],
  },
  {
    key: 'deliveries',
    icon: '🚚',
    title: 'Deliveries',
    subtitle: 'Record and track daily milk deliveries',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Logging Deliveries',
        steps: [
          'Click the "Deliveries" tab.',
          'Select the delivery date using the date picker.',
          'The list shows all active subscribers scheduled for that day.',
          'Toggle the delivered/not-delivered switch for each subscriber.',
          'Click "Save Deliveries" to confirm.',
        ],
      },
      {
        heading: 'Viewing Delivery History',
        steps: [
          'Use the date range filter to view past deliveries.',
          'The table shows subscriber, milk type, quantity, and delivery status for each day.',
          'Export the delivery data as CSV using the "Export" button.',
        ],
      },
      {
        heading: 'Handling Missed Deliveries',
        steps: [
          'Missed deliveries are highlighted in red in the history view.',
          'You can retroactively mark a delivery as completed by selecting the past date and toggling the status.',
          'Billing automatically excludes days marked as not-delivered.',
        ],
      },
    ],
  },
  {
    key: 'billing',
    icon: '🧾',
    title: 'Billing',
    subtitle: 'Generate and manage monthly bills',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Generating Monthly Bills',
        steps: [
          'Click the "Billing" tab.',
          'Select the billing month and year.',
          'Click "Generate Bills" — the system calculates charges based on deliveries and milk type pricing.',
          'Bills are created for all active subscribers who had deliveries that month.',
        ],
      },
      {
        heading: 'Viewing a Bill',
        steps: [
          'Find the subscriber in the bill list.',
          'Click "View" to see the detailed breakdown: each delivery day, quantity, rate, and total.',
          'The bill also shows any advance/credit balance.',
        ],
      },
      {
        heading: 'Sending Bills',
        steps: [
          'Click "Send Bill" on a subscriber row to email the bill PDF to the subscriber.',
          'Ensure Email Config is set up correctly before sending.',
          'Bulk send is available via the "Send All" button.',
        ],
      },
      {
        heading: 'Editing a Bill',
        steps: [
          'Admins can add manual adjustments (credits/debits) to a bill before it is marked as paid.',
          'Click ✏️ on the bill row, enter the adjustment amount and reason, then save.',
        ],
      },
    ],
  },
  {
    key: 'payments',
    icon: '💳',
    title: 'Payments',
    subtitle: 'Record and track subscriber payments',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Recording a Payment',
        steps: [
          'Click the "Payments" tab.',
          'Click "+ Record Payment".',
          'Select the subscriber, enter the amount, payment date, and mode (Cash / UPI / Bank Transfer).',
          'Click "Save". The payment is applied to the subscriber\'s oldest outstanding bill.',
        ],
      },
      {
        heading: 'Viewing Payment History',
        steps: [
          'Use the search and date-range filter to find specific payments.',
          'The table shows: subscriber name, amount, date, mode, and bill reference.',
          'Export as CSV using the "Export" button.',
        ],
      },
      {
        heading: 'Outstanding Balances',
        steps: [
          'Subscribers with outstanding dues appear highlighted.',
          'The "Balance" column shows how much is owed after applying all payments.',
          'Use the Notifications module to send payment reminders.',
        ],
      },
    ],
  },
  {
    key: 'cashmanagement',
    icon: '🏪',
    title: 'Cash Management',
    subtitle: 'Daily cash entries, expenses, and financial summaries',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Daily Cash Entry',
        steps: [
          'Click the "Cash Mgmt" tab.',
          'Select the date and enter the opening cash balance.',
          'Add cash-in entries (e.g., payments received).',
          'Add cash-out entries (e.g., milk purchase, staff wages).',
          'Click "Save Entry". The closing balance is auto-calculated.',
        ],
      },
      {
        heading: 'Expense Tracking',
        steps: [
          'Under the Expenses sub-tab, click "+ Add Expense".',
          'Select an expense category (or create a new one), enter the amount and date.',
          'Expense categories can be managed under "Expense Categories".',
        ],
      },
      {
        heading: 'Monthly Summary',
        steps: [
          'Switch to the "Monthly Summary" tab.',
          'Select the month — the system shows: total cash in, total cash out, and net balance.',
          'The Category-wise breakdown shows which expense categories consumed the most.',
          'Export the summary as PDF or CSV.',
        ],
      },
      {
        heading: 'Yearly Summary',
        steps: [
          'Switch to the "Yearly Summary" tab and select a year.',
          'View month-by-month cash flow in a table and trend chart.',
        ],
      },
    ],
  },
  {
    key: 'reports',
    icon: '📊',
    title: 'Reports',
    subtitle: 'Analytics and reporting for your dairy',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Daily Delivery Report',
        steps: [
          'Click the "Reports" tab and select "Daily Report".',
          'Pick a date to see all deliveries, quantities, and subscriber status for that day.',
          'Export as CSV or PDF.',
        ],
      },
      {
        heading: 'Monthly Delivery Report',
        steps: [
          'Select "Monthly Delivery" and choose the month/year.',
          'Shows each subscriber\'s delivery count, quantity delivered, and amount billed.',
        ],
      },
      {
        heading: 'Monthly Payment Report',
        steps: [
          'Select "Monthly Payment" to see all payments collected in a given month.',
          'Filtered by subscriber, payment mode, or date range.',
        ],
      },
      {
        heading: 'Subscriber-wise Report',
        steps: [
          'Select "Subscriber-wise" to get a full history for a specific subscriber.',
          'Shows: deliveries, bills generated, payments made, and outstanding balance.',
        ],
      },
      {
        heading: 'Yearly Report',
        steps: [
          'Select "Yearly" for a 12-month summary of deliveries and revenue.',
          'Useful for annual business review and tax filing.',
        ],
      },
      {
        heading: 'GST Report',
        steps: [
          'Select "GST Report" to view GST-applicable transactions.',
          'Export for compliance and tax filing purposes.',
        ],
      },
    ],
  },
  {
    key: 'emailconfig',
    icon: '📧',
    title: 'Email Configuration',
    subtitle: 'Set up email delivery for notifications and bills',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Configuring SMTP',
        steps: [
          'Click the "Email Config" tab.',
          'Enter your SMTP Host (e.g., smtp.gmail.com), Port (587 for TLS), and enable SSL.',
          'Enter the sender Email and Password (use an App Password for Gmail).',
          'Enter the From Name shown to recipients.',
          'Click "Save Configuration".',
        ],
      },
      {
        heading: 'Testing Email',
        steps: [
          'After saving, click "Send Test Email".',
          'Enter your own email address as the recipient.',
          'If you receive the test email, the configuration is correct.',
          'If not, verify SMTP settings and check spam folders.',
        ],
      },
    ],
  },
  {
    key: 'notifications',
    icon: '📢',
    title: 'Notifications',
    subtitle: 'Send notifications to subscribers',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Sending a Notification',
        steps: [
          'Click the "Notifications" tab.',
          'Click "+ New Notification".',
          'Enter a Title and Message.',
          'Select recipients: All Subscribers, a specific subscriber, or by milk type.',
          'Click "Send". Notifications appear in the subscriber\'s bell icon.',
        ],
      },
      {
        heading: 'Viewing Sent Notifications',
        steps: [
          'The notification list shows all sent notifications with date, title, and recipient count.',
          'Click any notification to see full details and read/unread status per subscriber.',
        ],
      },
      {
        heading: 'Bell Icon (All Users)',
        steps: [
          'The 🔔 bell icon in the header shows unread notification count.',
          'Click the bell to see the latest 10 notifications.',
          'Click "Mark all read" to clear the badge.',
          'Click "View All Notifications" to see the full list.',
        ],
      },
    ],
  },
  {
    key: 'dairysetup',
    icon: '🎨',
    title: 'Dairy Setup',
    subtitle: 'Branding, theme, and business configuration',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Branding',
        steps: [
          'Click the "Dairy Setup" tab.',
          'Upload your dairy Logo (shown in the header and sidebar).',
          'Upload a Banner Image (shown below the header on all pages).',
          'Set your Theme Colour using the colour picker — this updates the entire app\'s colour scheme.',
          'Enter your dairy\'s Main Wording (tagline shown under the header name).',
          'Click "Save Branding".',
        ],
      },
      {
        heading: 'WhatsApp Integration',
        steps: [
          'Under the WhatsApp tab, enter your WhatsApp Business API credentials.',
          'Configure message templates for bill reminders and delivery updates.',
          'Click "Save WhatsApp Config".',
        ],
      },
      {
        heading: 'Backup & Restore',
        steps: [
          'Under the Backup tab, click "Download Backup" to export all your data as a JSON file.',
          'Store the backup file securely.',
          'To restore, click "Upload Backup", select the backup file, and confirm.',
          'Warning: Restoring overwrites all current data for your tenant.',
        ],
      },
      {
        heading: 'Clearing Data',
        steps: [
          'The "Clear Data" option removes all delivery and billing records for a fresh start.',
          'Subscriber and configuration data is retained.',
          'This action cannot be undone — confirm carefully before proceeding.',
        ],
      },
    ],
  },
  {
    key: 'aichat',
    icon: '🤖',
    title: 'AI Chat',
    subtitle: 'AI-powered assistant for your dairy queries',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Starting a Chat',
        steps: [
          'Click the "AI Chat" tab.',
          'Type your question in the chat box at the bottom.',
          'Press Enter or click "Send".',
          'The AI responds with relevant information about your dairy operations.',
        ],
      },
      {
        heading: 'What You Can Ask',
        steps: [
          'Business queries: "How many deliveries were done today?"',
          'Financial queries: "What is the outstanding balance for subscriber John?"',
          'Operational queries: "List subscribers who haven\'t paid this month."',
          'General dairy advice: "How to handle buffalo milk pricing?"',
        ],
      },
      {
        heading: 'Tips for Best Results',
        steps: [
          'Be specific in your questions for more accurate answers.',
          'The AI has context about your dairy\'s data — use it to get quick insights.',
          'For complex reports, use the dedicated Reports module for full exports.',
        ],
      },
    ],
  },
  {
    key: 'purchasesale',
    icon: '🛒',
    title: 'Purchase & Sale',
    subtitle: 'Record and manage purchases and sales',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Adding a Purchase Entry',
        steps: [
          'Click the "Purchase/Sale" tab and select the "Purchases" sub-tab.',
          'Click "+ Add Purchase".',
          'Select Product Type, enter Quantity, Rate, and Date.',
          'Add notes if needed and click "Save".',
        ],
      },
      {
        heading: 'Adding a Sale Entry',
        steps: [
          'Switch to the "Sales" sub-tab.',
          'Click "+ Add Sale".',
          'Select Product Type, enter Quantity, Rate, and Date.',
          'Click "Save".',
        ],
      },
      {
        heading: 'Monthly Reports',
        steps: [
          'Switch to the "Reports" sub-tab.',
          'Select month and year to view monthly purchase and sale summaries.',
          'The summary shows total quantity, total value, and profit/loss by product type.',
          'Export as CSV or PDF.',
        ],
      },
      {
        heading: 'Dashboard Summary',
        steps: [
          'The main Dashboard shows a quick Purchase/Sale summary for the current month when this module is enabled.',
          'It shows: total purchased, total sold, and net position.',
        ],
      },
    ],
  },
  {
    key: 'producttypes',
    icon: '📦',
    title: 'Product Types',
    subtitle: 'Define products and their rate history',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Creating a Product Type',
        steps: [
          'Click the "Product Types" tab.',
          'Click "+ Add Product Type".',
          'Enter the Name (e.g., "Buffalo Milk"), Unit (e.g., "Litre"), and an optional Description.',
          'Click "Save".',
        ],
      },
      {
        heading: 'Managing Rates',
        steps: [
          'Open a product type by clicking ✏️.',
          'Under the "Rate History" section, click "+ Add Rate".',
          'Enter the Effective Date, Purchase Rate, and Sale Rate.',
          'The system uses the most recent rate for all purchase/sale entries on or after the effective date.',
        ],
      },
      {
        heading: 'Bulk Import',
        steps: [
          'Download the sample CSV template.',
          'Fill in product names, units, and descriptions.',
          'Click "Upload CSV" to import all products at once.',
        ],
      },
    ],
  },
  {
    key: 'recordstore',
    icon: '🗂️',
    title: 'Record Store',
    subtitle: 'Upload and manage tenant documents',
    roles: ['Admin', 'SuperAdmin'],
    sections: [
      {
        heading: 'Uploading a File',
        steps: [
          'Click the "Record Store" tab.',
          'Click "Choose File" and select a document (PDF, DOCX, XLSX, CSV, JPG, PNG — max 20 MB).',
          'Click "Upload". The file is stored securely in your tenant\'s private cloud storage folder.',
        ],
      },
      {
        heading: 'Downloading a File',
        steps: [
          'Find the file in the list.',
          'Click the "⬇️ Download" button. The file downloads to your device.',
        ],
      },
      {
        heading: 'Deleting a File',
        steps: [
          'Click "🗑️ Delete" on the file row.',
          'Confirm the deletion. The file is permanently removed from storage.',
          'Deleted files cannot be recovered.',
        ],
      },
      {
        heading: 'SuperAdmin View',
        steps: [
          'SuperAdmins can select any tenant from the dropdown to view and manage that tenant\'s files.',
          'Each tenant\'s files are stored in isolated folders.',
        ],
      },
    ],
  },
  {
    key: 'livesessions',
    icon: '🟢',
    title: 'Live Sessions',
    subtitle: 'Track active user login sessions',
    roles: ['SuperAdmin'],
    sections: [
      {
        heading: 'Viewing Active Sessions',
        steps: [
          'Click the "Live Sessions" tab.',
          'The list shows all currently logged-in users: name, role, login time, and last activity.',
          'Sessions are colour-coded: green = active in the last 5 minutes, grey = idle.',
        ],
      },
      {
        heading: 'Terminating a Session',
        steps: [
          'Click "Terminate" on a session row to force-logout that user.',
          'The user will be redirected to the login page on their next action.',
          'Use this if a shared device was left logged in or a user account needs to be secured.',
        ],
      },
    ],
  },
  {
    key: 'profile',
    icon: '👤',
    title: 'My Profile',
    subtitle: 'Manage your account settings',
    sections: [
      {
        heading: 'Updating Profile',
        steps: [
          'Click the "My Profile" tab.',
          'Update your Name, Phone, and Address.',
          'Click "Save Profile".',
        ],
      },
      {
        heading: 'Changing Password',
        steps: [
          'Scroll to the "Change Password" section.',
          'Enter your Current Password, then enter and confirm the New Password.',
          'Passwords must be at least 6 characters.',
          'Click "Change Password".',
        ],
      },
    ],
  },
  {
    key: 'moduleaccess',
    icon: '🔒',
    title: 'Module Access',
    subtitle: 'Enable or disable modules per tenant (SuperAdmin)',
    roles: ['SuperAdmin'],
    sections: [
      {
        heading: 'Overview',
        steps: [
          'This panel is only visible to SuperAdmins.',
          'It shows a matrix of all tenants (rows) and all available modules (columns).',
          'Each cell has a toggle: ✅ On = module is enabled for that tenant, ⛔ Off = disabled.',
        ],
      },
      {
        heading: 'Toggling a Module',
        steps: [
          'Click the ✅/⛔ button in the cell for the tenant and module you want to change.',
          'The change is saved immediately — no extra "Save" step required.',
          'The tenant will see the module appear or disappear from their menu on the next page load.',
        ],
      },
      {
        heading: 'Available Modules',
        steps: [
          'AI Chat — AI-powered chat assistant.',
          'Cash Management — Daily cash and expense tracking.',
          'Purchase / Sale — Purchase and sale entry management.',
          'Reports — Reports and analytics dashboards.',
          'Live Session — Live delivery session tracking.',
          'Record Store — Tenant document cloud storage.',
          'User Manual — Step-by-step user guide (this module).',
        ],
      },
    ],
  },
  // Subscriber-specific modules
  {
    key: 'subscription',
    icon: '📦',
    title: 'My Subscription',
    subtitle: 'View and manage your milk subscription',
    roles: ['Subscriber'],
    sections: [
      {
        heading: 'Viewing Your Subscription',
        steps: [
          'After login, your subscription details are shown on the main screen.',
          'You can see: milk type, daily quantity, price per liter, total daily cost, and subscription status.',
          'Status can be: Active (green) or Paused (gray).',
        ],
      },
      {
        heading: 'Pausing Your Subscription',
        steps: [
          'Click "Pause Subscription" button.',
          'Select the start date and end date for the pause.',
          'Click "Save Pause" — no deliveries will be made during this period.',
          'You will not be charged for paused days.',
        ],
      },
      {
        heading: 'Resuming Your Subscription',
        steps: [
          'If your subscription is paused, click "Resume Subscription".',
          'You can also unpause specific days within a pause period using the calendar.',
          'Green dates = active delivery, Gray dates = paused.',
        ],
      },
    ],
  },
  {
    key: 'mybills',
    icon: '🧾',
    title: 'My Bills',
    subtitle: 'View your bills and payment status',
    roles: ['Subscriber'],
    sections: [
      {
        heading: 'Viewing Bills',
        steps: [
          'Click the "My Bills" tab to see all bills generated for your account.',
          'Each bill shows: billing period, amount, status (Paid/Unpaid), and payment date.',
          'Click "View" to see the detailed breakdown of daily deliveries and charges.',
        ],
      },
      {
        heading: 'Understanding Your Bill',
        steps: [
          'The bill lists each delivery day with quantity, rate, and total.',
          'Your total is calculated as: sum of (quantity x price per liter) for each delivery day.',
          'Any payments you have made are deducted from the total.',
        ],
      },
    ],
  },
  // Delivery Boy module
  {
    key: 'mydeliveries',
    icon: '🚚',
    title: 'My Deliveries',
    subtitle: 'View and manage your daily delivery assignments',
    roles: ['DeliveryBoy'],
    sections: [
      {
        heading: 'Viewing Today\'s Deliveries',
        steps: [
          'After login, the "My Deliveries" tab shows all deliveries assigned to you for today.',
          'Deliveries are organized by Society > Building > Flat hierarchy.',
          'Use the drill-down buttons to navigate: click a Society to see its Buildings, click a Building to see its Flats.',
        ],
      },
      {
        heading: 'Marking Deliveries',
        steps: [
          'For each flat/delivery, click "Delivered" (green) or "Failed" (red).',
          'Progress bars show completion percentage at each level.',
          'Failed deliveries: enter a reason (e.g., "Customer not home").',
        ],
      },
      {
        heading: 'Progress Tracking',
        steps: [
          'The top of the page shows overall progress: X of Y deliveries completed.',
          'Each society shows its own progress bar.',
          'Extra deliveries are shown separately and can be expanded for details.',
        ],
      },
      {
        heading: 'Past and Future Deliveries',
        steps: [
          'Use the date navigation to view past delivery history (read-only).',
          'Future scheduled deliveries can also be viewed (read-only).',
          'Only today\'s deliveries can be marked as delivered or failed.',
        ],
      },
    ],
  },
  // SuperAdmin-specific modules
  {
    key: 'tenants',
    icon: '🏢',
    title: 'Tenant Management',
    subtitle: 'Create and manage dairy tenants',
    roles: ['SuperAdmin'],
    sections: [
      {
        heading: 'Creating a New Tenant',
        steps: [
          'Click the "Tenants" tab in the navigation.',
          'Click "+ Add Tenant" button.',
          'Fill in: Tenant Name, Contact Email, Phone, Address, Brand Name, Subscription Rate.',
          'Click "Save" — the tenant is created with a default admin user.',
        ],
      },
      {
        heading: 'Address Setup Onboarding',
        steps: [
          'After creating a tenant, you can run the 3-step CSV onboarding wizard.',
          'Step 1: Upload Society/Address Master CSV (SocietyName, Area, City).',
          'Step 2: Upload Building Master CSV (BuildingName, SocietyName).',
          'Step 3: Upload Milk Types CSV (Name, PricePerLiter, Description).',
          'Each step has a "Download Sample CSV" button for the correct format.',
        ],
      },
      {
        heading: 'Managing Tenants',
        steps: [
          'View all tenants in a grid with key metrics.',
          'Edit tenant details (name, contact, branding).',
          'Reset tenant admin password.',
          'Download tenant backup (JSON file).',
          'Restore tenant from a backup file.',
          'Copy tenant (creates a duplicate with all data).',
        ],
      },
      {
        heading: 'Tenant Performance',
        steps: [
          'Click "Performance" on a tenant row to view detailed stats.',
          'See monthly delivery count, payment collection, subscriber growth.',
          'Compare tenants side-by-side in the Tenant Performance report.',
        ],
      },
    ],
  },
  {
    key: 'documents',
    icon: '📄',
    title: 'Documents',
    subtitle: 'Upload and manage documents for tenants',
    roles: ['SuperAdmin'],
    sections: [
      {
        heading: 'Uploading Documents',
        steps: [
          'Click the "Documents" tab.',
          'Click "Choose File" and select a document (PDF, DOCX, XLSX, CSV, JPG, PNG).',
          'Max file size: 20 MB.',
          'Click "Upload" to store the file securely.',
        ],
      },
      {
        heading: 'Downloading Documents',
        steps: [
          'Find the file in the list.',
          'Click the "Download" button to save it to your device.',
        ],
      },
    ],
  },
  {
    key: 'menuorder',
    icon: '📋',
    title: 'Menu Order',
    subtitle: 'Customize tab ordering per tenant',
    roles: ['SuperAdmin'],
    sections: [
      {
        heading: 'Reordering Tabs',
        steps: [
          'Click the "Menu Order" tab.',
          'Select a tenant from the dropdown.',
          'Drag and drop tabs to reorder them.',
          'The new order is saved automatically and reflected in that tenant\'s dashboard.',
        ],
      },
    ],
  },
];

function UserManualPanel({ role }: { role: string }) {
  const roleFiltered = USER_MANUAL_MODULES.filter(m => !m.roles || m.roles.includes(role));
  const [selectedKey, setSelectedKey] = useState(roleFiltered[0]?.key ?? 'profile');
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const filtered = search.trim()
    ? roleFiltered.filter(m =>
        m.title.toLowerCase().includes(search.toLowerCase()) ||
        m.subtitle.toLowerCase().includes(search.toLowerCase()) ||
        m.sections.some(s =>
          s.heading.toLowerCase().includes(search.toLowerCase()) ||
          s.steps.some(step => step.toLowerCase().includes(search.toLowerCase()))
        )
      )
    : roleFiltered;

  const selected = roleFiltered.find(m => m.key === selectedKey) ?? roleFiltered[0];

  const toggleSection = (heading: string) => {
    setExpandedSections(prev => ({ ...prev, [heading]: !prev[heading] }));
  };

  const allExpanded = selected.sections.every(s => expandedSections[selected.key + ':' + s.heading] !== false);

  const expandAll = () => {
    const next: Record<string, boolean> = { ...expandedSections };
    selected.sections.forEach(s => { next[selected.key + ':' + s.heading] = true; });
    setExpandedSections(next);
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = { ...expandedSections };
    selected.sections.forEach(s => { next[selected.key + ':' + s.heading] = false; });
    setExpandedSections(next);
  };

  const isSectionOpen = (sectionKey: string) => expandedSections[sectionKey] !== false;

  return (
    <div>
      <PanelHeader icon="📖" title="User Manual" subtitle="Step-by-step guide for all modules" />

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Left sidebar — module list */}
        <div style={{
          width: '220px', flexShrink: 0, background: 'white', borderRadius: '10px',
          border: '1px solid var(--tc-border)', overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--tc-border)', background: 'var(--tc-vlight)' }}>
            <input
              type="text"
              placeholder="🔍 Search modules..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '0.45rem 0.6rem', border: '1px solid var(--tc-border)',
                borderRadius: '6px', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box',
                background: 'white',
              }}
            />
          </div>
          <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
            {filtered.map(m => (
              <button
                key={m.key}
                onClick={() => { setSelectedKey(m.key); setSearch(''); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.65rem 0.9rem', border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: selectedKey === m.key ? 'var(--tc-light)' : 'transparent',
                  color: selectedKey === m.key ? 'var(--tc)' : '#555',
                  fontWeight: selectedKey === m.key ? 700 : 400,
                  fontSize: '0.82rem',
                  borderLeft: selectedKey === m.key ? '3px solid var(--tc)' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (selectedKey !== m.key) (e.currentTarget as HTMLElement).style.background = 'var(--tc-vlight)'; }}
                onMouseLeave={e => { if (selectedKey !== m.key) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>{m.icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#999', fontSize: '0.82rem' }}>No modules match</div>
            )}
          </div>
        </div>

        {/* Right content — step-by-step guide */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            background: 'white', borderRadius: '10px', border: '1px solid var(--tc-border)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden',
          }}>
            {/* Module header */}
            <div style={{
              padding: '1rem 1.25rem', borderBottom: '1px solid var(--tc-border)',
              background: 'var(--tc-light)', display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <span style={{
                fontSize: '2rem', width: '48px', height: '48px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', background: 'var(--tc-vlight)',
                borderRadius: '10px', border: '1px solid var(--tc-border)',
              }}>{selected.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--tc-dark)' }}>{selected.title}</h2>
                <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#666' }}>{selected.subtitle}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button onClick={expandAll} style={{
                  padding: '0.35rem 0.75rem', background: 'var(--tc)', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                }}>Expand All</button>
                <button onClick={collapseAll} style={{
                  padding: '0.35rem 0.75rem', background: '#f5f5f5', color: '#555',
                  border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem',
                }}>Collapse All</button>
              </div>
            </div>

            {/* Sections */}
            <div style={{ padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {selected.sections.map((section, si) => {
                const sKey = selected.key + ':' + section.heading;
                const open = isSectionOpen(sKey);
                return (
                  <div key={si} style={{
                    border: '1px solid var(--tc-border)', borderRadius: '8px', overflow: 'hidden',
                  }}>
                    <button
                      onClick={() => toggleSection(sKey)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.75rem 1rem', background: open ? 'var(--tc-vlight)' : '#fafafa',
                        border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{
                          width: '22px', height: '22px', borderRadius: '50%', background: 'var(--tc)',
                          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                        }}>{si + 1}</span>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>{section.heading}</span>
                      </div>
                      <span style={{ color: 'var(--tc)', fontSize: '0.85rem', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
                    </button>
                    {open && (
                      <div style={{ padding: '0.75rem 1rem 1rem', background: 'white' }}>
                        <ol style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {section.steps.map((step, idx) => (
                            <li key={idx} style={{
                              fontSize: '0.85rem', color: '#444', lineHeight: 1.55,
                              paddingLeft: '0.4rem',
                            }}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation footer */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', gap: '0.5rem',
          }}>
            {(() => {
              const idx = roleFiltered.findIndex(m => m.key === selectedKey);
              const prev = idx > 0 ? roleFiltered[idx - 1] : null;
              const next = idx < roleFiltered.length - 1 ? roleFiltered[idx + 1] : null;
              return (
                <>
                  {prev ? (
                    <button onClick={() => setSelectedKey(prev.key)} style={{
                      padding: '0.5rem 1rem', background: 'white', border: '1px solid var(--tc-border)',
                      borderRadius: '7px', cursor: 'pointer', fontSize: '0.82rem', color: '#555', fontWeight: 500,
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}>
                      ◀ {prev.icon} {prev.title}
                    </button>
                  ) : <span />}
                  {next ? (
                    <button onClick={() => setSelectedKey(next.key)} style={{
                      padding: '0.5rem 1rem', background: 'var(--tc)', color: 'white',
                      border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}>
                      {next.icon} {next.title} ▶
                    </button>
                  ) : <span />}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}


function sanitizeHtml(html: string): string {
  // Remove script tags, event handlers, and javascript: URLs for XSS protection
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript\s*:/gi, '');
}
